// src/Buttons/Button.tsx
import React from "react";
import styled, { css, keyframes } from "styled-components";
import {
  brand,
  controlHeight,
  focus,
  glass,
  inkGlass,
  inkTint,
  line,
  OFF_RAMP,
  radius,
  rgba,
  semanticColor,
  surface,
  ink,
  motion,
  typeRole
} from "@repo/design-tokens";

// src/internal/variant.ts
function variant(table, key, fallback, component, prop) {
  if (typeof key === "string" && Object.prototype.hasOwnProperty.call(table, key)) {
    return key;
  }
  if (key !== void 0 && key !== null && process.env.NODE_ENV !== "production") {
    console.warn(
      `[@repo/design-system] ${component}: unknown \`${prop}\` value ${JSON.stringify(key)}. Expected one of ${Object.keys(table).map((k) => `"${k}"`).join(" | ")}. Falling back to "${String(fallback)}".`
    );
  }
  return fallback;
}

// src/Buttons/Button.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var BASE_VARIANT = {
  primary: "primary",
  frosted: "frosted",
  secondary: "frosted",
  ghost: "ghost",
  tertiary: "ghost",
  destructive: "destructive",
  success: "success",
  text: "text"
};
var SIZE = {
  xs: {
    height: "24px",
    padding: "0 10px",
    font: "12px",
    weight: typeRole.button.fontWeight,
    icon: "12px",
    gap: "6px",
    iconOnly: "24px",
    iconOnlyGlyph: "12px"
  },
  // New 2026-10-03 (card 01, *sm · 28*): row actions. Label 12 at 600; an icon-only glyph of 10.
  sm: {
    height: controlHeight.sm,
    padding: "0 10px",
    font: "12px",
    weight: 600,
    icon: "12px",
    gap: "6px",
    iconOnly: controlHeight.sm,
    iconOnlyGlyph: "10px"
  },
  inline: {
    height: "36px",
    padding: "0 18px",
    font: "14px",
    weight: typeRole.button.fontWeight,
    icon: "16px",
    iconOnly: "36px",
    iconOnlyGlyph: "16px"
  },
  default: {
    height: "44px",
    padding: "0 24px",
    font: "14px",
    weight: typeRole.button.fontWeight,
    icon: "20px",
    iconOnly: "44px",
    iconOnlyGlyph: "20px"
  },
  full: {
    height: "60px",
    padding: "0 2rem",
    font: "16px",
    weight: typeRole.button.fontWeight,
    icon: "24px",
    iconOnly: "60px",
    iconOnlyGlyph: "24px"
  }
};
var TEXT_SIZE = {
  xs: {
    height: "20px",
    padding: "2px 6px",
    font: "12px",
    weight: typeRole.button.fontWeight,
    icon: "12px",
    gap: "4px",
    iconOnly: "20px"
  },
  /*
   * The card draws no Text `sm`. This is its *In total* cell — `tl-btn-text sz-inline` overridden to
   * 12 px at 600 — so a 28 px Text action pairs with the 28 px row buttons beside it. Recorded in
   * design-cards/PROVENANCE.md as an interpretation for Rob.
   */
  sm: {
    height: controlHeight.sm,
    padding: "4px 8px",
    font: "12px",
    weight: 600,
    icon: "12px",
    gap: "6px",
    iconOnly: controlHeight.sm
  },
  inline: {
    height: "28px",
    padding: "4px 8px",
    font: "13px",
    weight: typeRole.button.fontWeight,
    icon: "14px",
    gap: "6px",
    iconOnly: "28px"
  },
  default: {
    height: "32px",
    padding: "6px 10px",
    font: "14px",
    weight: typeRole.button.fontWeight,
    icon: "16px",
    gap: "8px",
    iconOnly: "32px"
  },
  full: {
    height: "40px",
    padding: "8px 14px",
    font: "15px",
    weight: typeRole.button.fontWeight,
    icon: "18px",
    gap: "10px",
    iconOnly: "40px"
  }
};
var CONTROL_RADIUS = "10px";
var spin = keyframes`from{transform:rotate(0deg);}to{transform:rotate(360deg);}`;
var firefly = keyframes`0%{offset-distance:0%;}100%{offset-distance:100%;}`;
var bounce = keyframes`
  0%{transform:translateY(0) scale(1);}
  50%{transform:translateY(0) scale(.985);}
  100%{transform:translateY(0) scale(1);}
`;
var iconWrapperCss = (px2) => css`
  .tl-btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${px2};
    height: ${px2};
    font-size: ${px2};
    line-height: 1;
    flex-shrink: 0;
    color: currentColor;
  }
  .tl-btn-icon > svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
  }
  .tl-btn-icon > img {
    width: 100%;
    height: 100%;
    display: block;
  }
  .tl-btn-icon > i {
    font-size: inherit;
    line-height: 1;
  }
  &.has-icon-right .tl-btn-icon {
    order: 2;
  }
`;
var loadingSpinnerCss = (ringColor) => css`
  &.is-loading {
    cursor: wait;
    position: relative;
  }
  &.is-loading > *:not(.tl-btn-loading-sweep) {
    position: relative;
    z-index: 2;
  }
  &.is-loading .tl-btn-label {
    opacity: 0.55;
  }
  &.is-loading .tl-btn-icon > * {
    display: none;
  }
  &.is-loading .tl-btn-icon {
    position: relative;
  }
  &.is-loading .tl-btn-icon::before {
    content: '';
    position: absolute;
    inset: 0;
    margin: auto;
    width: 78%;
    height: 78%;
    border-radius: 50%;
    border: 1.75px solid ${ringColor};
    border-top-color: transparent;
    border-right-color: transparent;
    opacity: 0.9;
    animation: ${spin} 0.7s linear infinite;
    box-sizing: border-box;
  }
  /* No icon at all → spinner before the label. */
  &.is-loading:not(:has(.tl-btn-icon)) .tl-btn-label::before {
    content: '';
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-right: 8px;
    vertical-align: -2px;
    border-radius: 50%;
    border: 1.75px solid ${ringColor};
    border-top-color: transparent;
    border-right-color: transparent;
    opacity: 0.85;
    animation: ${spin} 0.7s linear infinite;
    box-sizing: border-box;
  }
  .tl-btn-loading-sweep {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
    display: none;
    overflow: hidden;
  }
  &.is-loading > .tl-btn-loading-sweep {
    display: block;
  }
  .tl-btn-loading-sweep::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: var(--tl-firefly-size, 34px);
    height: var(--tl-firefly-size, 34px);
    margin: calc(var(--tl-firefly-size, 34px) / -2) 0 0 calc(var(--tl-firefly-size, 34px) / -2);
    border-radius: 50%;
    background: radial-gradient(
      closest-side,
      var(--tl-firefly-core, rgba(228, 255, 254, 1)) 0%,
      var(--tl-firefly-mid, rgba(0, 187, 204, 0.7)) 35%,
      var(--tl-firefly-edge, rgba(0, 187, 204, 0)) 70%
    );
    offset-path: inset(
      0 round var(--tl-btn-radius)
    ); /* set by sizingCss: the button's own corner */
    offset-distance: 0%;
    offset-rotate: 0deg;
    animation: ${firefly} 1.8s linear infinite;
    filter: blur(0.5px);
  }
  @media (prefers-reduced-motion: reduce) {
    .tl-btn-loading-sweep::before {
      animation: none;
    }
    &.is-loading .tl-btn-icon::before {
      animation-duration: 1.4s;
    }
  }
`;
var openCss = (background, open, focusRing) => css`
  &[aria-expanded='true']:not(:disabled, .is-unavailable):not(.is-loading),
  &[aria-pressed='true']:not(:disabled, .is-unavailable):not(.is-loading),
  &[aria-expanded='true']:hover:not(:disabled, .is-unavailable):not(.is-loading):not(:active):not(
      :focus-visible
    ),
  &[aria-pressed='true']:hover:not(:disabled, .is-unavailable):not(.is-loading):not(:active):not(
      :focus-visible
    ) {
    background: ${background};
    ${open}
  }
  &[aria-expanded='true']:focus-visible:not(:disabled, .is-unavailable):not(.is-loading),
  &[aria-pressed='true']:focus-visible:not(:disabled, .is-unavailable):not(.is-loading) {
    background: ${background};
    ${focusRing}
  }
`;
var primaryCss = css`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: ${brand.gelIdle};
  color: ${ink[1]};
  box-shadow: ${brand.glow};
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
  transition:
    box-shadow ${motion.durFast} ${motion.easeUi},
    transform 0.12s ease-out,
    background ${motion.durFast} ${motion.easeUi};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 55%;
    border-radius: inherit;
    pointer-events: none;
    background:
      radial-gradient(60% 100% at 12% 0%, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 70%),
      radial-gradient(60% 100% at 88% 0%, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0) 70%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
    mix-blend-mode: screen;
  }
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 40%;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(
      115deg,
      transparent 44%,
      rgba(255, 255, 255, 0.14) 50%,
      transparent 56%
    );
    transform: translateX(-120%);
    transition: transform 1.1s ${motion.easeUi};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${brand.glow}, ${brand.focusRing};
  }
  &:hover:not(:disabled, .is-unavailable):not(.is-loading) {
    background: ${brand.gelHover};
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: ${brand.glowHover};
  }
  &:hover:not(:disabled, .is-unavailable)::after {
    transform: translateX(120%);
  }
  ${openCss(
  brand.gelHover,
  css`
      box-shadow:
        0 0 0 2px ${surface[0]},
        0 0 16px 4px ${rgba(brand.cyan, 0.45)};
    `,
  css`
      box-shadow: ${brand.glow}, ${brand.focusRing};
    `
)}
  &:active:not(:disabled, .is-unavailable):not(.is-loading) {
    background: ${brand.gelPress};
    border-color: rgba(255, 255, 255, 0.25);
    box-shadow: ${brand.pressInset}, ${brand.glow};
    transform: translateY(1px);
    animation: ${bounce} 0.14s ease-out;
  }
  &:disabled,
  &.is-unavailable {
    /* --tl-line-4 → --tl-line-1 → --tl-ramp-teal-med-light-off; ink --tl-ramp-blue-bright-off. */
    background: linear-gradient(180deg, ${line[4]} 0%, ${line[1]} 50%, ${OFF_RAMP[3]} 100%);
    border-color: rgba(255, 255, 255, 0.35);
    color: ${OFF_RAMP[8]};
    box-shadow: 0 6px 20px rgba(120, 120, 120, 0.18);
    transform: none;
    cursor: not-allowed;
    text-shadow: none;
  }
  &:disabled::after,
  &.is-unavailable::after {
    display: none;
  }
  /*
   * Loading is a BUSY state, not a focus state — so it takes the frost bloom ALONE, never the
   * full ring. Canonical defines the bloom as "pure signature" sitting outside the compliant
   * line, which makes it the one layer safe to reuse as decoration: borrowing the whole ring
   * here would paint a 4.03:1 focus affordance on a button nobody has focused.
   *
   * Invisible before row 30 only because the old focus ring was a ~1.15:1 mint haze. Ratcheting
   * it to a compliant outline turned a harmless decoration into a false focus signal, so the
   * layer split is applied here rather than the controller's original instruction that both
   * primary focus sites take the full ring — which had assumed both were focus states.
   *
   * NB no backticks in this comment: it sits inside a styled-components template literal, where
   * one would terminate the literal and fail the file with a bare TS1005.
   */
  &.is-loading {
    box-shadow: ${brand.glow}, ${focus.bloom};
  }
  &.is-loading .tl-btn-loading-sweep {
    --tl-firefly-size: 40px;
    --tl-firefly-core: rgba(255, 255, 255, 1);
    --tl-firefly-mid: rgba(255, 255, 255, 0.55);
    --tl-firefly-edge: rgba(255, 255, 255, 0);
  }
  ${loadingSpinnerCss("rgba(255,255,255,.95)")}
`;
var frostedShapeBase = css`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition:
    background ${motion.durFast} ${motion.easeUi},
    border-color ${motion.durFast} ${motion.easeUi},
    color ${motion.durFast} ${motion.easeUi},
    box-shadow ${motion.durFast} ${motion.easeUi},
    transform 0.08s ${motion.easeUi};
`;
var FROSTED_BRAND_EDGE = 0.45;
var FROSTED_LIT = `linear-gradient(180deg, ${rgba(surface[0], 0.92)} 0%, ${rgba(brand.frost, 0.78)} 100%)`;
var frostedCss = css`
  ${frostedShapeBase}
  background: linear-gradient(180deg, ${glass.bg} 0%, rgba(255, 255, 255, 0.55) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.7);
  color: ${ink[1]};
  box-shadow:
    0 0 12px 3px rgba(228, 255, 254, 0),
    0 1px 0 rgba(255, 255, 255, 0.6) inset,
    0 4px 14px rgba(63, 69, 78, 0.06);

  /* sm · 28 — the row action's label takes ink-2 at rest. */
  &.sz-sm {
    color: ${ink[2]};
  }
  &:hover:not(:disabled, .is-unavailable):not(.is-loading) {
    background: ${FROSTED_LIT};
    border-color: rgba(228, 255, 254, 0.9);
    box-shadow:
      0 0 12px 3px ${brand.frost},
      0 1px 0 rgba(255, 255, 255, 0.8) inset,
      0 4px 14px rgba(27, 182, 217, 0.1);
  }
  ${openCss(
  FROSTED_LIT,
  css`
      border-color: ${rgba(brand.cyan, 0.45)};
      box-shadow:
        0 0 0 2px ${surface[0]},
        0 0 12px 3px ${rgba(brand.cyan, 0.3)};
    `,
  css`
      box-shadow:
        0 0 0 2px ${brand.cyan},
        0 0 12px 3px ${brand.frost},
        0 1px 0 rgba(255, 255, 255, 0.8) inset;
    `
)}
  &:active:not(:disabled, .is-unavailable):not(.is-loading) {
    background: rgba(207, 247, 247, 0.75);
    border-color: rgba(27, 182, 217, 0.3);
    box-shadow: inset 0 2px 4px rgba(63, 69, 78, 0.08);
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: none;
    background: ${FROSTED_LIT};
    border-color: rgba(228, 255, 254, 0.9);
    box-shadow:
      0 0 0 2px ${brand.cyan},
      0 0 12px 3px ${brand.frost},
      0 1px 0 rgba(255, 255, 255, 0.8) inset;
  }
  /*
   * Brand — the bordered brand chip (*Frosted · brand*; the Floor Plan Item Cards canvas's
   * .chip.cyan, *Add to Register*): a cyan edge and aqua ink at rest. Hover, focus and press
   * keep Frosted's own fill, edge and ring; the ink stays aqua. Disabled keeps its own look.
   */
  &.tone-brand:not(:disabled, .is-unavailable) {
    color: ${inkTint.aqua};
  }
  &.tone-brand:not(:disabled, .is-unavailable):not(:hover):not(:focus-visible):not(:active) {
    border-color: ${rgba(brand.cyan, FROSTED_BRAND_EDGE)};
  }
  /*
   * Hot — the 24 px round remove when its source is about to go (card 01, *Circle · open state*).
   * Caller-driven, not a pointer state, so hover and press keep it. Keyboard focus keeps
   * Frosted's ring over the hot fill. A disabled button shows disabled.
   */
  &.is-circle.is-hot:not(:disabled, .is-unavailable),
  &.is-circle.is-hot:hover:not(:disabled, .is-unavailable):not(.is-loading),
  &.is-circle.is-hot:active:not(:disabled, .is-unavailable):not(.is-loading) {
    color: ${semanticColor.errorStrong};
    background: ${rgba(surface[0], 0.9)};
    border-color: ${rgba(semanticColor.errorStrong, 0.3)};
    box-shadow: none;
  }
  &.is-circle.is-hot:focus-visible:not(:disabled, .is-unavailable),
  &.is-circle.is-hot:focus-visible:hover:not(:disabled, .is-unavailable):not(.is-loading) {
    box-shadow:
      0 0 0 2px ${brand.cyan},
      0 0 12px 3px ${brand.frost};
  }
  &:disabled,
  &.is-unavailable {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    color: ${ink[5]};
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
  ${loadingSpinnerCss("currentColor")}
`;
var ghostCss = css`
  ${frostedShapeBase}
  background: transparent;
  border: 1.5px solid rgba(63, 69, 78, 0.25);
  color: ${ink[2]};

  /* On glass the .25 ink border vanishes over a busy scene: a frost border and a faint fill. */
  &.on-glass {
    background: ${glass.bgFaint};
    backdrop-filter: ${glass.blurSm};
    -webkit-backdrop-filter: ${glass.blurSm};
    border: 1.5px solid ${rgba(surface[0], 0.75)};
    color: ${ink[1]};
  }
  &:hover:not(:disabled, .is-unavailable):not(.is-loading) {
    background: rgba(0, 187, 204, 0.06);
    border-color: rgba(0, 187, 204, 0.5);
    color: ${ink[1]};
  }
  /* Over glass, hover lifts the glass; it excludes a press so :active (0,4,0) still wins. */
  &.on-glass:hover:not(:disabled, .is-unavailable):not(.is-loading):not(:active) {
    background: ${glass.bgSoft};
    border-color: ${brand.frost};
  }
  &:active:not(:disabled, .is-unavailable):not(.is-loading) {
    background: rgba(0, 187, 204, 0.12);
    border-color: rgba(0, 187, 204, 0.65);
    color: ${brand.cyanPress};
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: none;
    background: rgba(0, 187, 204, 0.06);
    border-color: rgba(0, 187, 204, 0.6);
    color: ${brand.cyanPress};
    box-shadow:
      0 0 0 2px ${brand.cyan},
      0 0 10px 2px rgba(0, 187, 204, 0.25);
  }
  &:disabled,
  &.is-unavailable {
    background: transparent;
    border-color: rgba(63, 69, 78, 0.12);
    color: ${ink[5]};
    cursor: not-allowed;
    transform: none;
  }
  ${loadingSpinnerCss("currentColor")}
`;
var destructiveCss = css`
  ${frostedShapeBase}
  background: linear-gradient(180deg, ${glass.bg} 0%, rgba(255, 255, 255, 0.55) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(197, 15, 31, 0.3);
  color: ${semanticColor.errorStrong};
  box-shadow:
    0 0 12px 3px rgba(197, 15, 31, 0),
    0 1px 0 rgba(255, 255, 255, 0.6) inset,
    0 4px 14px rgba(63, 69, 78, 0.06);

  &:hover:not(:disabled, .is-unavailable):not(.is-loading) {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.92) 0%,
      rgba(255, 225, 225, 0.78) 100%
    );
    border-color: rgba(197, 15, 31, 0.55);
    box-shadow:
      0 0 12px 3px rgba(255, 180, 180, 0.55),
      0 1px 0 rgba(255, 255, 255, 0.8) inset,
      0 4px 14px rgba(197, 15, 31, 0.1);
  }
  &:active:not(:disabled, .is-unavailable):not(.is-loading) {
    background: rgba(247, 210, 210, 0.75);
    border-color: rgba(197, 15, 31, 0.45);
    color: ${inkGlass.error};
    box-shadow: inset 0 2px 4px rgba(63, 69, 78, 0.08);
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: none;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.92) 0%,
      rgba(255, 225, 225, 0.78) 100%
    );
    border-color: rgba(197, 15, 31, 0.55);
    box-shadow:
      0 0 0 2px ${semanticColor.errorStrong},
      0 0 12px 3px rgba(255, 180, 180, 0.6),
      0 1px 0 rgba(255, 255, 255, 0.8) inset;
  }
  &:disabled,
  &.is-unavailable {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    color: ${ink[5]};
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
  ${loadingSpinnerCss("currentColor")}
`;
var successCss = css`
  ${frostedShapeBase}
  background: linear-gradient(180deg, ${glass.bg} 0%, rgba(255, 255, 255, 0.55) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(95, 216, 223, 0.4);
  color: ${inkGlass.success};
  box-shadow:
    0 0 12px 3px rgba(95, 216, 223, 0),
    0 1px 0 rgba(255, 255, 255, 0.6) inset,
    0 4px 14px rgba(63, 69, 78, 0.06);

  &:hover:not(:disabled, .is-unavailable):not(.is-loading) {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.92) 0%,
      rgba(214, 247, 249, 0.78) 100%
    );
    border-color: rgba(95, 216, 223, 0.75);
    box-shadow:
      0 0 12px 3px rgba(95, 216, 223, 0.55),
      0 1px 0 rgba(255, 255, 255, 0.8) inset,
      0 4px 14px rgba(31, 138, 145, 0.1);
  }
  &:active:not(:disabled, .is-unavailable):not(.is-loading) {
    background: rgba(186, 239, 242, 0.75);
    border-color: rgba(31, 138, 145, 0.45);
    color: ${inkGlass.success};
    box-shadow: inset 0 2px 4px rgba(63, 69, 78, 0.08);
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: none;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.92) 0%,
      rgba(214, 247, 249, 0.78) 100%
    );
    border-color: rgba(95, 216, 223, 0.75);
    box-shadow:
      0 0 0 2px ${semanticColor.success},
      0 0 12px 3px rgba(95, 216, 223, 0.6),
      0 1px 0 rgba(255, 255, 255, 0.8) inset;
  }
  &:disabled,
  &.is-unavailable {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    color: ${ink[5]};
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
  ${loadingSpinnerCss("currentColor")}
`;
var textCss = css`
  appearance: none;
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: none;
  color: ${ink[1]};
  transition:
    color ${motion.durFast} ${motion.easeUi},
    background-color ${motion.durFast} ${motion.easeUi};

  svg {
    width: 16px;
    height: 16px;
    color: currentColor;
  }
  &:focus-visible {
    outline: none;
    color: ${brand.cyanDeep};
    background: rgba(0, 187, 204, 0.08);
    box-shadow: ${focus.ring};
  }
  &:hover:not(:disabled, .is-unavailable):not(.is-loading):not(.is-done) {
    color: ${brand.cyan};
    background: rgba(0, 187, 204, 0.08);
  }
  &:active:not(:disabled, .is-unavailable):not(.is-loading):not(.is-done) {
    color: ${brand.cyanPress};
    background: rgba(0, 187, 204, 0.15);
  }
  &:disabled,
  &.is-unavailable {
    color: ${ink[1]};
    cursor: not-allowed;
    background: transparent;
  }
  &:disabled {
    opacity: 0.4;
  }
  /* Unavailable stays focusable, so it fades its content, not itself: the focus ring stays whole. */
  &.is-unavailable > * {
    opacity: 0.4;
  }
  /*
   * On glass there is no chrome to strengthen: full ink with a white lift, kept through hover,
   * press and focus (card 01's cascade). A red tone or a done action keeps its own ink.
   */
  &.on-glass {
    text-shadow: 0 1px 1px ${rgba(surface[0], 0.55)};
  }
  &.on-glass:not(.tone-destructive):not(.tone-brand):not(.is-done),
  &.on-glass:not(.tone-destructive):not(.tone-brand):not(.is-done):hover,
  &.on-glass:not(.tone-destructive):not(.tone-brand):not(.is-done):active,
  &.on-glass:not(.tone-destructive):not(.tone-brand):not(.is-done):focus-visible {
    color: ${ink[1]};
  }
  &.on-glass:hover:not(:disabled, .is-unavailable):not(.is-loading):not(.is-done):not(
      .tone-destructive
    ):not(:active) {
    background: ${glass.bgSoft};
  }
  /* Destructive tone — the red footer action, where a filled Destructive would outweigh Done. */
  &.tone-destructive {
    color: ${semanticColor.errorStrong};
  }
  &.tone-destructive:hover:not(:disabled, .is-unavailable):not(.is-loading):not(.is-done),
  &.tone-destructive:active:not(:disabled, .is-unavailable):not(.is-loading):not(.is-done) {
    color: ${semanticColor.errorStrong};
    background: ${rgba(semanticColor.errorStrong, 0.06)};
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  &.tone-destructive:focus-visible:not(.is-done) {
    color: ${semanticColor.errorStrong};
    box-shadow: ${focus.tight};
  }
  /*
   * Brand tone — the inline row action (Equipment frame 08's *Change*, *Use Product's*): cyan-press
   * at rest so it reads as an action beside a value, the text action's own hover and press, and the
   * tight ring because it sits in a dense row whose edge would clip the bloom (as Toast's Undo).
   */
  &.tone-brand {
    color: ${brand.cyanPress};
  }
  &.tone-brand:focus-visible:not(.is-done) {
    color: ${brand.cyanPress};
    box-shadow: ${focus.tight};
  }
  /* Done — final (*In total*): reads as finished, cannot be pressed, is not aria-pressed. */
  &.is-done {
    color: ${ink[3]};
    cursor: default;
  }
  ${loadingSpinnerCss("currentColor")}
`;
var VARIANT_CSS = {
  primary: primaryCss,
  frosted: frostedCss,
  ghost: ghostCss,
  destructive: destructiveCss,
  success: successCss,
  text: textCss
};
var sizingCss = (variant2, size, iconOnly, circle) => {
  const corner = circle ? radius.pill : CONTROL_RADIUS;
  if (variant2 === "text") {
    const s2 = TEXT_SIZE[size];
    return css`
      border-radius: ${corner};
      --tl-btn-radius: ${corner};
      height: ${s2.height};
      padding: ${iconOnly ? "0" : s2.padding};
      font: ${s2.weight} ${s2.font} / 1 ${typeRole.button.fontFamily};
      letter-spacing: 0;
      gap: ${iconOnly ? "0" : s2.gap};
      ${iconWrapperCss(s2.icon)}
      ${iconOnly ? css`
            width: ${s2.iconOnly};
            justify-content: center;
          ` : ""}
    `;
  }
  const s = SIZE[size];
  return css`
    border-radius: ${corner};
    --tl-btn-radius: ${corner};
    height: ${s.height};
    padding: ${iconOnly ? "0" : s.padding};
    font: ${s.weight} ${s.font} / 1 ${typeRole.button.fontFamily};
    letter-spacing: ${typeRole.button.letterSpacing};
    cursor: pointer;
    ${"gap" in s ? css`
          gap: ${s.gap};
        ` : ""}
    ${size === "full" ? css`
          width: 100%;
        ` : ""}
    ${iconWrapperCss(iconOnly ? s.iconOnlyGlyph : s.icon)}
    ${iconOnly ? css`
          width: ${s.iconOnly};
          padding: 0;
          gap: 0;
        ` : ""}
  `;
};
var StyledButton = styled.button`
  ${({ $variant }) => VARIANT_CSS[$variant]}
  ${({ $variant, $size, $iconOnly, $circle }) => sizingCss($variant, $size, $iconOnly, $circle)}
  /*
   * Unavailable (aria-disabled) stays in the tab order, so it keeps a keyboard focus ring over its
   * disabled look. It outranks the variants' disabled box-shadow by specificity (0,3,0 over 0,2,0);
   * a text tone's own tight ring (0,4,0) still wins over it.
   */
  &.is-unavailable:focus-visible {
    box-shadow: ${focus.ring};
  }
`;
var Button = React.forwardRef(function Button2(props, ref) {
  const {
    variant: variantProp = "frosted",
    size: sizeProp = "default",
    iconLeft,
    iconRight,
    loading = false,
    children,
    className,
    type = "button",
    disabled,
    onClick,
    /**
     * ⛔ DESTRUCTURED PURELY SO IT DOES NOT REACH THE DOM.
     *
     * The value is consumed below and passed on as the TRANSIENT `$iconOnly`, which is exactly
     * right — but reading it via `props.iconOnly` while leaving it in `...rest` meant it was ALSO
     * spread onto the native `<button>`, and React logged
     * "does not recognize the iconOnly prop on a DOM element" on every render of every icon-only
     * button. Found in the metrics console, where `DetailPanelShell`'s close button made it fire on
     * every panel render. Naming it here is what removes it from `rest`.
     */
    iconOnly: iconOnlyProp,
    // Same reason as `iconOnly`: each is consumed below and must not reach the DOM.
    circle: circleProp,
    hot = false,
    tone,
    done = false,
    surface: surfaceProp,
    ...rest
  } = props;
  const iconOnly = Boolean(iconOnlyProp);
  const circle = iconOnly && Boolean(circleProp);
  const size = variant(SIZE, sizeProp, "default", "Button", "size");
  const variant2 = variant(
    BASE_VARIANT,
    variantProp,
    "frosted",
    "Button",
    "variant"
  );
  const base = BASE_VARIANT[variant2];
  const isDone = done && base === "text";
  const ariaDisabled = rest["aria-disabled"];
  const unavailable = !isDone && (ariaDisabled === true || ariaDisabled === "true");
  const appliedTone = base === "text" || base === "frosted" && tone === "brand" ? tone : void 0;
  if (process.env.NODE_ENV !== "production") {
    if (iconOnly && !props["aria-label"]) {
      console.warn("Button: `iconOnly` requires an `aria-label` for accessibility.");
    }
    if (sizeProp === "compact") {
      console.warn("Button: the 24 px size `compact` was renamed `xs` (card 01).");
    }
    if (iconLeft && iconRight) {
      console.warn(
        "Button: `iconLeft` and `iconRight` are mutually exclusive \u2014 only `iconLeft` renders. Pass one."
      );
    }
    if (hot && !(base === "frosted" && circle)) {
      console.warn("Button: hot is drawn only on a frosted circle icon button.");
    }
    if (tone === "destructive" && base !== "text") {
      console.warn(
        'Button: tone="destructive" is drawn only on the text variant and is ignored here; use variant="destructive".'
      );
    }
    if (tone === "brand" && base !== "text" && base !== "frosted") {
      console.warn(
        'Button: tone="brand" is drawn only on the text and frosted variants and is ignored here.'
      );
    }
    if (done && base !== "text") {
      console.warn("Button: done applies only to the text variant and is ignored here.");
    }
  }
  const icon2 = iconLeft ?? iconRight;
  const hasIconRight = !iconLeft && Boolean(iconRight);
  const classes = [
    `tl-btn-${variant2}`,
    `sz-${size}`,
    iconOnly ? "is-icon-only" : "",
    circle ? "is-circle" : "",
    hot ? "is-hot" : "",
    appliedTone ? `tone-${appliedTone}` : "",
    isDone ? "is-done" : "",
    unavailable ? "is-unavailable" : "",
    surfaceProp ?? "",
    hasIconRight ? "has-icon-right" : "",
    loading ? "is-loading" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  const handleClick = (event) => {
    if (loading || disabled || isDone) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };
  return /* @__PURE__ */ jsxs(
    StyledButton,
    {
      ref,
      type,
      className: classes,
      $variant: base,
      $size: size,
      $iconOnly: iconOnly,
      $circle: circle,
      disabled,
      "aria-busy": loading || void 0,
      onClick: handleClick,
      ...rest,
      "aria-disabled": isDone ? true : ariaDisabled,
      children: [
        loading ? /* @__PURE__ */ jsx("span", { className: "tl-btn-loading-sweep", "aria-hidden": "true" }) : null,
        icon2 ? /* @__PURE__ */ jsx("span", { className: "tl-btn-icon", "aria-hidden": "true", children: icon2 }) : null,
        !iconOnly && children != null ? /* @__PURE__ */ jsx("span", { className: "tl-btn-label", children }) : null
      ]
    }
  );
});
Button.displayName = "Button";

// src/Cards/Card.tsx
import React2 from "react";
import styled2, { css as css2 } from "styled-components";
import {
  ink as ink2,
  line as line2,
  surface as surface2,
  brand as brand2,
  glass as glass2,
  glassCard,
  radius as radius2,
  motion as motion2,
  fontMono,
  fontSans,
  semantic,
  rgba as rgba2
} from "@repo/design-tokens";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var SIZE2 = {
  sm: { pad: "12px", radius: "10px", gap: "8px" },
  md: { pad: "16px", radius: radius2.lg, gap: "12px" },
  lg: { pad: "20px", radius: radius2.xl, gap: "16px" }
};
var INLAY_RADIUS = "8px";
var INLAY_RADIUS_DEEP = "5px";
var baseCss = css2`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  color: ${ink2[1]};
  font-family: ${fontSans};
  transition:
    box-shadow ${motion2.durMid} ${motion2.easeUi},
    border-color ${motion2.durMid} ${motion2.easeUi},
    background ${motion2.durMid} ${motion2.easeUi},
    transform ${motion2.durFast} ${motion2.easeUi};
`;
var onSceneCss = css2`
  background: linear-gradient(
    135deg,
    rgba(${glassCard.tint}, ${glassCard.fillTop}) 0%,
    rgba(${glassCard.tint}, ${glassCard.fill}) 100%
  );
  backdrop-filter: blur(${glassCard.blur}) saturate(${glassCard.sat});
  -webkit-backdrop-filter: blur(${glassCard.blur}) saturate(${glassCard.sat});
  border: 2px solid rgba(255, 255, 255, calc(${glassCard.border} * 0.5));
  box-shadow:
    inset 0 1.5px 0.5px rgba(255, 255, 255, ${glassCard.bevel}),
    inset 0 0 ${glassCard.bevelSize} rgba(255, 255, 255, calc(${glassCard.bevel} * 0.6)),
    inset 0 -1px 1px rgba(255, 255, 255, calc(${glassCard.bevel} * 0.4)),
    0 0 11px 1px rgba(${glassCard.frost}, ${glassCard.glow}),
    0 10px 26px rgba(0, 0, 0, ${glassCard.drop});

  /* Gloss highlight, behind the content. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.3) 40%,
      rgba(255, 255, 255, 0) 70%
    );
    z-index: 0;
  }
  > * {
    position: relative;
    z-index: 1;
  }

  &.is-interactive:hover {
    background: ${glass2.bgSoft};
    border: 2px dashed ${brand2.frost};
    box-shadow:
      0 0 12px 3px ${brand2.frost},
      0 2px 5px rgba(0, 0, 0, 0.7);
    transform: translateY(-1px);
  }
  &.is-selected {
    background: ${glass2.bgSoft};
    border: 2px solid ${brand2.frost};
    box-shadow:
      0 0 12px 2px ${rgba2(brand2.frost, 1)},
      0 2px 5px rgba(0, 0, 0, 0.9);
  }
`;
var onGlassCss = css2`
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    0 1px 2px rgba(40, 30, 80, 0.1);

  &.is-interactive:hover {
    background: rgba(255, 255, 255, 0.72);
    border-color: ${brand2.frost};
    box-shadow:
      0 0 10px 1px ${brand2.frostGlow},
      inset 0 1px 0 rgba(255, 255, 255, 0.7);
  }
  &.is-selected {
    background: ${rgba2(brand2.frost, 0.55)};
    border-color: ${brand2.frost};
    box-shadow:
      0 0 10px 2px ${brand2.frost},
      inset 0 1px 0 rgba(255, 255, 255, 0.7);
  }
`;
var onSolidCss = css2`
  background: ${surface2[0]};
  border: 1px solid ${line2[2]};
  box-shadow: 0 1px 2px rgba(40, 30, 80, 0.05);

  &.is-interactive:hover {
    border-color: ${rgba2(brand2.cyan, 0.45)};
    box-shadow:
      0 0 0 3px ${rgba2(brand2.cyan, 0.08)},
      0 4px 14px rgba(40, 30, 80, 0.08);
    transform: translateY(-1px);
  }
  &.is-selected {
    border-color: ${brand2.cyan};
    box-shadow:
      0 0 0 3px ${rgba2(brand2.cyan, 0.16)},
      0 2px 8px rgba(40, 30, 80, 0.06);
  }
`;
var SURFACE_CSS = {
  "on-scene": onSceneCss,
  "on-glass": onGlassCss,
  "on-solid": onSolidCss
};
var emptyCss = (surfaceName) => css2`
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  min-height: 140px;
  border-style: dashed;
  ${surfaceName === "on-solid" ? css2`
        border-color: ${line2[1]};
        background: ${surface2[1]};
        box-shadow: none;
      ` : ""}
  ${surfaceName === "on-glass" ? css2`
        border-color: rgba(255, 255, 255, 0.7);
      ` : ""}
`;
var StyledCard = styled2.div`
  ${baseCss}
  padding: ${({ $size }) => SIZE2[$size].pad};
  gap: ${({ $size }) => SIZE2[$size].gap};
  border-radius: ${({ $size, $inlay }) => $inlay ? INLAY_RADIUS : SIZE2[$size].radius};
  ${({ $surface }) => SURFACE_CSS[$surface]}
  ${({ $empty, $surface }) => $empty ? emptyCss($surface) : ""}

  /* Concentric corners: a card inside a card steps down, twice-nested steps again. */
  && & {
    border-radius: ${INLAY_RADIUS};
  }
  && & & {
    border-radius: ${INLAY_RADIUS_DEEP};
  }

  &.is-interactive {
    cursor: pointer;
  }
  &.is-disabled {
    opacity: 0.5;
    filter: saturate(0.7);
    pointer-events: none;
  }
  &.is-interactive:focus-visible {
    outline: none;
    box-shadow: ${brand2.focusRing};
  }
  /* Skeletons inside a translucent card need a lighter track to stay visible. */
  ${({ $surface }) => $surface === "on-scene" || $surface === "on-glass" ? css2`
          .tl-skel {
            background: linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.35),
              rgba(255, 255, 255, 0.6),
              rgba(255, 255, 255, 0.35)
            );
            background-size: 220% 100%;
          }
        ` : ""}
`;
var Card = React2.forwardRef(function Card2(props, ref) {
  const {
    surface: surfaceName,
    size = "md",
    interactive = false,
    selected = false,
    disabled = false,
    loading = false,
    empty = false,
    inlay = false,
    className,
    children,
    tabIndex,
    ...rest
  } = props;
  const classes = [
    "tl-card",
    `sz-${size}`,
    surfaceName,
    interactive ? "is-interactive" : "",
    selected ? "is-selected" : "",
    disabled ? "is-disabled" : "",
    loading ? "is-loading" : "",
    empty ? "is-empty" : "",
    inlay ? "is-inlay" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx2(
    StyledCard,
    {
      ref,
      className: classes,
      $surface: surfaceName,
      $size: variant(SIZE2, size, "md", "Card", "size"),
      $inlay: inlay,
      $empty: empty,
      tabIndex: interactive ? tabIndex ?? 0 : tabIndex,
      role: interactive ? "button" : void 0,
      "aria-disabled": disabled || void 0,
      "aria-busy": loading || void 0,
      ...rest,
      onKeyDown: interactive ? (event) => {
        rest.onKeyDown?.(event);
        if (event.defaultPrevented || disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          rest.onClick?.(event);
        }
      } : rest.onKeyDown,
      children
    }
  );
});
Card.displayName = "Card";
var KPI_MIN_HEIGHT = "118px";
var KPI_MIN_HEIGHT_SPARK = "158px";
var KpiLayout = styled2.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  height: 100%;
  min-height: ${({ $hasSparkline }) => $hasSparkline ? KPI_MIN_HEIGHT_SPARK : KPI_MIN_HEIGHT};
`;
var StyledKpiCard = styled2(Card)`
  gap: 6px;
  min-height: ${({ $hasSparkline }) => $hasSparkline ? KPI_MIN_HEIGHT_SPARK : KPI_MIN_HEIGHT};
`;
var KpiIcon = styled2.span.attrs({ "aria-hidden": true, className: "kpi-ico" })`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 14px;
  line-height: 1;
  color: ${ink2[4]};

  > svg,
  > img {
    width: 100%;
    height: 100%;
  }
`;
var KpiCard = React2.forwardRef(function KpiCard2(props, ref) {
  const { icon: icon2, hasSparkline = false, className, children, ...rest } = props;
  return /* @__PURE__ */ jsxs2(
    StyledKpiCard,
    {
      ref,
      className: ["v-kpi", className ?? ""].filter(Boolean).join(" "),
      $hasSparkline: hasSparkline,
      ...rest,
      children: [
        icon2 ? /* @__PURE__ */ jsx2(KpiIcon, { children: icon2 }) : null,
        children
      ]
    }
  );
});
KpiCard.displayName = "KpiCard";
var KpiLabel = styled2.div.attrs({ className: "kpi-lbl" })`
  display: flex;
  align-items: center;
  gap: 6px;
  font: 500 10px/1.4 ${fontMono};
  color: ${ink2[4]};
  /* Deliberately NOT tracking.label (0.1em). The Cards card specifies 0.14em for the
     .v-kpi eyebrow specifically: mono at 10px set against a large value, where the 0.1em
     column-header tracking reads cramped. Two values on purpose, not an oversight — if Rob
     wants one number for both, that is a token-layer decision. See the token's own doc. */
  letter-spacing: 0.14em;
  text-transform: uppercase;
  /* TWO lines reserved (brief 16 §7): 2 × 1.4em. */
  min-height: 2.8em;
`;
var KpiValue = styled2.div.attrs({ className: "kpi-val" })`
  font: 600 28px/1.02 ${fontSans};
  color: ${ink2[1]};
  letter-spacing: -0.6px;
  display: flex;
  align-items: baseline;
  gap: 4px;

  small {
    font: 600 14px/1 ${fontSans};
    color: ${ink2[3]};
    letter-spacing: 0;
  }
`;
var KpiSub = styled2.div.attrs({ className: "kpi-sub" })`
  font: 400 11px/1.4 ${fontMono};
  color: ${ink2[3]};
  display: flex;
  align-items: center;
  gap: 5px;
  /* Pinned to the card bottom. */
  margin-top: auto;

  .up {
    color: ${semantic.text.success};
  }
  .down {
    color: ${semantic.text.error};
  }
`;
var KpiSpark = styled2.div.attrs({ className: "spark" })`
  display: block;
  width: 100%;
  height: 34px;
  margin-top: auto;
  padding-top: 2px;
`;
var ARROW_PATH = {
  // 12×12 box. Stem + chevron head, so the glyph reads at 11px.
  up: "M6 10V2.6M6 2.6L2.9 5.7M6 2.6l3.1 3.1",
  down: "M6 2v7.4M6 9.4l3.1-3.1M6 9.4L2.9 6.3",
  none: "M2.5 6h7"
};
var KpiDeltaArrow = styled2.svg.attrs(
  ({ $direction }) => ({
    className: "kpi-delta-arrow",
    viewBox: "0 0 12 12",
    "aria-hidden": true,
    focusable: false,
    children: /* @__PURE__ */ jsx2(
      "path",
      {
        d: ARROW_PATH[$direction],
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 1.8,
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  })
)`
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  /* Inherits the tone colour from the .up / .down class on the row. */
  color: currentColor;
`;
function kpiToneClass(tone) {
  if (tone === "positive") return "up";
  if (tone === "negative") return "down";
  return "";
}

// src/Cards/DockedCard.tsx
import React3 from "react";
import styled4, { css as css4 } from "styled-components";
import {
  brand as brand4,
  fontSans as fontSans2,
  ink as ink3,
  line as line3,
  motion as motion3,
  radius as radius3,
  rgba as rgba3,
  semantic as semantic2,
  shadow,
  surface as surface3,
  typeRole as typeRole2,
  weight
} from "@repo/design-tokens";

// src/Icons/Icon.tsx
import styled3 from "styled-components";
import { icon, inset } from "@repo/design-tokens";
import { jsx as jsx3 } from "react/jsx-runtime";
var VARIANT_CLASS = {
  regular: "fa-regular",
  solid: "fa-solid"
};
var StyledIcon = styled3.i`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${({ $size }) => icon[$size]};
    height: ${({ $size }) => icon[$size]};
    font-size: ${({ $size }) => icon[$size]};
    line-height: 1;
    flex-shrink: 0;
  }
  .tl-btn-icon > && {
    width: 100%;
    height: 100%;
  }
  /* SourceChip's glyph slot (card 05): the card draws the glyph at 9 px, below the 12 px ramp.
     Unlike the button slot, no outer rule sets this glyph's font-size, so this one does. */
  .tl-src-icon > && {
    width: 9px;
    height: 9px;
    font-size: 9px;
  }
  /* Tooltip's glyph slot (card 08, \`.tl-tooltip i { font-size: 10.5px }\`): below the ramp too,
     and for the same reason set here — this box rule outranks anything Tooltip could write. */
  .tl-tooltip-icon > && {
    width: 10.5px;
    height: 10.5px;
    font-size: 10.5px;
  }
  /* The plan camera's glyph slots (card 24, \`.pc-mark\` and \`.pc-h\`, built in \`@repo/ui\`'s
     \`PlanCameraLayer\`): 9 px in a 22 px marker, 11 px in the 26 px selected one, 8 px in a 20 px
     handle — all below the ramp, so set here like the two slots above. */
  .tl-plan-cam-mark > && {
    width: 9px;
    height: 9px;
    font-size: 9px;
  }
  .tl-plan-cam-mark.is-selected > && {
    width: 11px;
    height: 11px;
    font-size: 11px;
  }
  .tl-plan-cam-handle > && {
    width: 8px;
    height: 8px;
    font-size: 8px;
  }
  /* Card 24's drop crosshair (\`.pc-cross\`, built in \`@repo/ui\`'s \`StreamDropOverlay\`): 22 px,
     between the icon ramp's 18 and 24 — the spacing ramp's \`inset[6]\` — so set here like the slots
     above. */
  .tl-plan-cam-cross > && {
    width: ${inset[6]};
    height: ${inset[6]};
    font-size: ${inset[6]};
  }
`;
var Icon = ({
  name,
  variant: variant2 = "regular",
  size = "md",
  label,
  className
}) => {
  const classes = [VARIANT_CLASS[variant2], `fa-${name}`, className].filter(Boolean).join(" ");
  const a11y = label ? { role: "img", "aria-label": label } : { "aria-hidden": true };
  return /* @__PURE__ */ jsx3(StyledIcon, { className: classes, $size: size, ...a11y });
};

// src/internal/toneRibbon.ts
import { accent, brand as brand3, inkGlass as inkGlass2, inkOn, semanticColor as semanticColor2, support } from "@repo/design-tokens";
var GLASS_BLUR = "blur(40px) saturate(120%)";
var TONE = {
  cyan: {
    wash: support.aqua,
    ink: inkOn.aqua,
    glow: brand3.cyan,
    top: 0.42,
    bottom: 0.1,
    halo: 0.42
  },
  peach: {
    wash: accent.peach,
    ink: inkOn.pink,
    glow: accent.peach,
    top: 0.36,
    bottom: 0.08,
    halo: 0.55
  },
  lavender: {
    wash: support.lavender,
    ink: inkOn.lav,
    glow: accent.violet,
    top: 0.42,
    bottom: 0.1,
    halo: 0.45
  },
  butter: {
    wash: accent.butter,
    // `inkGlass.warn`, NOT `inkOn.butter` — the two differ by 4.09:1 vs 5.16:1 against
    // this ribbon's own composited background. See the contrast note in `Overlays/Modal.tsx`'s doc comment.
    ink: inkGlass2.warn,
    glow: accent.butter,
    top: 0.46,
    bottom: 0.1,
    halo: 0.6
  },
  coral: {
    wash: semanticColor2.error,
    ink: inkOn.error,
    glow: semanticColor2.error,
    top: 0.32,
    bottom: 0.08,
    halo: 0.4
  },
  mint: {
    wash: semanticColor2.success,
    ink: inkOn.mint,
    glow: semanticColor2.success,
    top: 0.4,
    bottom: 0.1,
    halo: 0.55
  }
};
var RIBBON_TONE = {
  ...TONE,
  peri: {
    wash: support.periwinkle,
    ink: inkOn.peri,
    top: 0.36,
    bottom: 0.1
  }
};

// src/internal/visuallyHidden.ts
import { css as css3 } from "styled-components";
var visuallyHiddenCss = css3`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

// src/Cards/DockedCard.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var HEAD_HEIGHT = "56px";
var HEAD_GAP = "10px";
var HEAD_PADDING = "0 12px 0 14px";
var DISC_SIZE = "32px";
var DISC_GLYPH = "13px";
var CLOSE_SIZE = "28px";
var CLOSE_GLYPH = "12px";
var TITLE_ROW_GAP = "2px";
var BESIDE_WIDTH = "448px";
var INSET_WIDTH = "340px";
var NARROW_WIDTH = "360px";
var DOCKED_CARD_WIDTHS = {
  beside: BESIDE_WIDTH,
  inset: INSET_WIDTH,
  narrow: NARROW_WIDTH
};
var BODY_PADDING = "14px";
var BODY_GAP = "10px";
var FOOTER_GAP = "8px";
var FOOTER_PADDING = "10px 14px";
var INSET_FOOTER_LINE = rgba3(line3[2], 0.9);
var SUB_OPACITY = 0.8;
var BESIDE_GLASS = 0.62;
var INSET_GLASS = 0.55;
var TONE_KEYS = Object.keys(RIBBON_TONE);
var TONES = {
  none: true,
  cyan: true,
  peach: true,
  lavender: true,
  butter: true,
  coral: true,
  mint: true,
  peri: true
};
var fade = (prop) => css4`
  transition: ${prop} ${motion3.durMid} ${motion3.easeUi};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
var HeadRoot = styled4.div`
  position: relative;
  flex: none;
  height: ${HEAD_HEIGHT};
  min-width: 0;
  font-family: ${fontSans2};

  .tl-docked-ribbon {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: ${HEAD_HEIGHT};
    pointer-events: none;
    opacity: 0;
    ${fade("opacity")}
  }
  .tl-docked-ribbon[data-active='true'] {
    opacity: 1;
  }
  ${TONE_KEYS.map(
  (k) => css4`
      .tl-docked-ribbon[data-tone='${k}'] {
        background: linear-gradient(
          180deg,
          ${rgba3(RIBBON_TONE[k].wash, RIBBON_TONE[k].top)},
          ${rgba3(RIBBON_TONE[k].wash, RIBBON_TONE[k].bottom)}
        );
      }
    `
)}

  .tl-docked-head {
    position: relative;
    z-index: 1;
    height: 100%;
    display: flex;
    align-items: center;
    gap: ${HEAD_GAP};
    padding: ${HEAD_PADDING};
    color: ${semantic2.text.primary};
    ${fade("color")}
  }
  ${TONE_KEYS.map(
  (k) => css4`
      &[data-tone='${k}'] .tl-docked-head {
        color: ${RIBBON_TONE[k].ink};
      }
    `
)}

  .tl-docked-disc {
    flex: none;
    width: ${DISC_SIZE};
    height: ${DISC_SIZE};
    border-radius: ${radius3.pill};
    display: grid;
    place-items: center;
    font-size: ${DISC_GLYPH};
    background: ${rgba3(surface3[0], 0.8)};
    border: 1px solid ${rgba3(surface3[0], 0.95)};
  }

  .tl-docked-titles {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }
  .tl-docked-title-row {
    display: flex;
    align-items: center;
    gap: ${TITLE_ROW_GAP};
    min-width: 0;
  }
  .tl-docked-title-action {
    flex: none;
    display: inline-flex;
    align-items: center;
  }
  .tl-docked-title-editor {
    flex: 1 1 auto;
    min-width: 0;
  }
  .tl-docked-title.is-hidden {
    ${visuallyHiddenCss}
  }
  .tl-docked-title {
    min-width: 0;
    margin: 0;
    font-size: ${typeRole2.subtitle2.fontSize};
    line-height: ${typeRole2.subtitle2.lineHeight};
    font-weight: ${weight.semibold};
    color: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .tl-docked-sub {
    font-size: ${typeRole2.caption1.fontSize};
    line-height: ${typeRole2.caption1.lineHeight};
    font-weight: ${weight.medium};
    opacity: ${SUB_OPACITY};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tl-docked-state,
  .tl-docked-actions {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: ${HEAD_GAP};
  }

  .tl-docked-close {
    flex: none;
    appearance: none;
    width: ${CLOSE_SIZE};
    height: ${CLOSE_SIZE};
    padding: 0;
    border-radius: ${radius3.pill};
    cursor: pointer;
    display: grid;
    place-items: center;
    font-size: ${CLOSE_GLYPH};
    background: ${rgba3(surface3[0], 0.6)};
    border: 1px solid ${rgba3(surface3[0], 0.7)};
    color: ${ink3[2]};
    transition:
      background ${motion3.durFast} ${motion3.easeUi},
      color ${motion3.durFast} ${motion3.easeUi};

    /* The card draws no hover or focus for its close; these are Modal's (PROVENANCE, reading 2). */
    &:hover {
      background: ${rgba3(surface3[0], 0.85)};
      color: ${ink3[1]};
    }
    &:focus-visible {
      outline: none;
      box-shadow: ${brand4.focusRing};
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;
var DockedCardFloatingContext = React3.createContext(null);
var DockedCardFloating = ({ grip, children }) => {
  const value = React3.useMemo(() => ({ grip }), [grip]);
  return /* @__PURE__ */ jsx4(DockedCardFloatingContext.Provider, { value, children });
};
var DockedCardHead = ({
  tone: toneProp = "none",
  icon: icon2,
  title,
  titleId,
  headingLevel = 2,
  titleAction,
  titleEditor,
  sub,
  state,
  headerActions,
  onClose,
  closeLabel = "Close",
  className
}) => {
  const tone = variant(TONES, toneProp, "none", "DockedCardHead", "tone");
  const Heading = `h${headingLevel}`;
  const floating = React3.useContext(DockedCardFloatingContext);
  const editing = titleEditor !== void 0 && titleEditor !== null && titleEditor !== false;
  return /* @__PURE__ */ jsxs3(HeadRoot, { className: ["tl-docked-headroot", className ?? ""].join(" ").trim(), "data-tone": tone, children: [
    TONE_KEYS.map((k) => /* @__PURE__ */ jsx4(
      "span",
      {
        className: "tl-docked-ribbon",
        "data-tone": k,
        "data-active": String(k === tone),
        "aria-hidden": "true"
      },
      k
    )),
    /* @__PURE__ */ jsxs3("div", { className: "tl-docked-head", children: [
      floating?.grip && /* @__PURE__ */ jsx4("span", { className: "tl-docked-grip", children: floating.grip }),
      icon2 && /* @__PURE__ */ jsx4("span", { className: "tl-docked-disc", "aria-hidden": "true", children: icon2 }),
      /* @__PURE__ */ jsxs3("div", { className: "tl-docked-titles", children: [
        /* @__PURE__ */ jsxs3("div", { className: "tl-docked-title-row", children: [
          /* @__PURE__ */ jsx4(
            Heading,
            {
              className: editing ? "tl-docked-title is-hidden" : "tl-docked-title",
              id: titleId,
              children: title
            }
          ),
          editing && /* @__PURE__ */ jsx4("div", { className: "tl-docked-title-editor", children: titleEditor }),
          !editing && titleAction && /* @__PURE__ */ jsx4("span", { className: "tl-docked-title-action", children: titleAction })
        ] }),
        sub && /* @__PURE__ */ jsx4("span", { className: "tl-docked-sub", children: sub })
      ] }),
      state && /* @__PURE__ */ jsx4("span", { className: "tl-docked-state", children: state }),
      headerActions && /* @__PURE__ */ jsx4("span", { className: "tl-docked-actions", children: headerActions }),
      onClose && /* @__PURE__ */ jsx4(
        "button",
        {
          type: "button",
          className: "tl-docked-close",
          "aria-label": closeLabel,
          onClick: onClose,
          children: /* @__PURE__ */ jsx4(Icon, { name: "xmark", variant: "solid", size: "xs" })
        }
      )
    ] })
  ] });
};
var glassChromeCss = css4`
  background: ${rgba3(surface3[0], BESIDE_GLASS)};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: 1.5px solid ${rgba3(surface3[0], 0.85)};
  border-radius: ${radius3.xl};
  box-shadow:
    ${shadow.glassSm},
    0 12px 30px rgba(0, 0, 0, 0.1);

  /* Gloss, over everything and never catching the pointer. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(160deg, ${rgba3(surface3[0], 0.35)} 0%, ${rgba3(surface3[0], 0)} 40%);
  }
`;
var besideCss = css4`
  width: ${BESIDE_WIDTH};
  ${glassChromeCss}
`;
var floatingCss = css4`
  ${glassChromeCss}
  /* Shrinks to its slot's limit (the inset placement alone does not), so the body scrolls. */
  flex: 0 1 auto;
  max-height: 100%;
  pointer-events: auto;
`;
var narrowCss = css4`
  ${besideCss}
  width: ${NARROW_WIDTH};
`;
var insetCss = css4`
  width: ${INSET_WIDTH};
  flex: none;
  background: ${rgba3(surface3[0], INSET_GLASS)};
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: 0;
  border-left: 1px solid ${rgba3(surface3[0], 0.85)};
  border-radius: 0;
  box-shadow: -8px 0 24px rgba(${shadow.inkGlassRgb}, 0.05);

  .tl-docked-footer {
    border-top: 1px solid ${INSET_FOOTER_LINE};
    background: ${rgba3(surface3[0], 0.4)};
  }
`;
var PanelRoot = styled4.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  min-height: 0;
  overflow: hidden;
  font-family: ${fontSans2};

  .tl-docked-body {
    position: relative;
    z-index: 1;
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: ${BODY_PADDING};
    display: flex;
    flex-direction: column;
    gap: ${BODY_GAP};
  }

  .tl-docked-footer {
    position: relative;
    z-index: 1;
    flex: none;
    display: flex;
    align-items: center;
    gap: ${FOOTER_GAP};
    padding: ${FOOTER_PADDING};
    border-top: 1px solid ${rgba3(surface3[0], 0.7)};
  }

  /* LAST, so the placement's own rules (the inset footer's hairline) win the cascade. */
  ${({ $placement }) => $placement === "inset" ? insetCss : $placement === "narrow" ? narrowCss : besideCss}
  ${({ $floating }) => $floating ? floatingCss : ""}
`;
var PLACEMENTS = { beside: true, inset: true, narrow: true };
var focusable = (el) => !!el && el.isConnected && typeof el.focus === "function";
var DockedCard = React3.forwardRef(
  function DockedCard2(props, ref) {
    const {
      placement: placementProp = "beside",
      footer,
      returnFocusTo,
      children,
      tone,
      icon: icon2,
      title,
      headingLevel,
      titleAction,
      titleEditor,
      sub,
      state,
      headerActions,
      onClose,
      closeLabel,
      className,
      onKeyDown,
      onFocus,
      onBlur,
      ...rest
    } = props;
    const placement = variant(
      PLACEMENTS,
      placementProp,
      "beside",
      "DockedCard",
      "placement"
    );
    const titleId = React3.useId();
    const floating = React3.useContext(DockedCardFloatingContext) !== null;
    const sectionRef = React3.useRef(null);
    const setRef = React3.useCallback(
      (node) => {
        sectionRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );
    const returnRef = React3.useRef(returnFocusTo);
    returnRef.current = returnFocusTo;
    const [opener] = React3.useState(
      () => typeof document === "undefined" ? null : document.activeElement
    );
    const focusInTree = React3.useRef(false);
    const movedFrom = React3.useRef(null);
    React3.useLayoutEffect(() => {
      const replayed = movedFrom.current;
      movedFrom.current = null;
      if (replayed && replayed.isConnected && document.activeElement !== replayed) replayed.focus();
      return () => {
        const active = document.activeElement;
        const inside = focusInTree.current || active === null || active === document.body || !!sectionRef.current && sectionRef.current.contains(active);
        if (!inside) return;
        const section = sectionRef.current;
        const usable = (el) => focusable(el) && !(section && section.contains(el));
        const target = usable(returnRef.current?.current) ? returnRef.current.current : usable(opener) ? opener : null;
        if (!target) return;
        movedFrom.current = active instanceof HTMLElement ? active : null;
        target.focus();
        setTimeout(() => {
          const now = document.activeElement;
          if ((now === null || now === document.body) && target.isConnected) target.focus();
        }, 0);
      };
    }, []);
    const handleKeyDown = (e) => {
      onKeyDown?.(e);
      if (e.key === "Escape" && onClose && !e.defaultPrevented && !!sectionRef.current && sectionRef.current.contains(e.target)) {
        e.preventDefault();
        onClose();
      }
    };
    return /* @__PURE__ */ jsxs3(
      PanelRoot,
      {
        ...rest,
        ref: setRef,
        role: "region",
        "aria-labelledby": titleId,
        $placement: placement,
        $floating: floating,
        className: ["tl-docked", `is-${placement}`, floating ? "is-floating" : "", className ?? ""].join(" ").replace(/\s+/g, " ").trim(),
        onKeyDown: handleKeyDown,
        onFocus: (e) => {
          focusInTree.current = true;
          onFocus?.(e);
        },
        onBlur: (e) => {
          focusInTree.current = false;
          onBlur?.(e);
        },
        children: [
          /* @__PURE__ */ jsx4(
            DockedCardHead,
            {
              tone,
              icon: icon2,
              title,
              titleId,
              headingLevel,
              titleAction,
              titleEditor,
              sub,
              state,
              headerActions,
              onClose,
              closeLabel
            }
          ),
          /* @__PURE__ */ jsx4("div", { className: "tl-docked-body", children }),
          footer && /* @__PURE__ */ jsx4("div", { className: "tl-docked-footer", children: footer })
        ]
      }
    );
  }
);

// src/Cards/ListPanel.tsx
import React5 from "react";
import styled7 from "styled-components";
import { fontSans as fontSans4, radius as radius6, rgba as rgba6, semantic as semantic3, space, typeRole as typeRole3, weight as weight2 } from "@repo/design-tokens";

// src/Inputs/Search.tsx
import React4 from "react";
import styled6, { css as css6 } from "styled-components";
import { brand as brand7, focus as focus2, fontSans as fontSans3, ink as ink6, motion as motion5, radius as radius5 } from "@repo/design-tokens";

// src/Overlays/Kbd.tsx
import styled5 from "styled-components";
import { brand as brand5, fontMono as fontMono2, ink as ink4, line as line4, radius as radius4, rgba as rgba4, surface as surface4 } from "@repo/design-tokens";
import { jsx as jsx5 } from "react/jsx-runtime";
var MIN_WIDTH = "16px";
var PAD = "1px 5px";
var TYPE = "500 10px/1.2";
var TRACKING = ".02em";
var BORDER_WIDTH = "1px";
var GROUP_GAP = "2px";
var TOOLTIP_GAP = "4px";
var AQUA = {
  background: { hex: brand5.cyan, alpha: 0.12 },
  border: { hex: brand5.cyan, alpha: 0.25 },
  ink: brand5.cyanPress
};
var KBD_TONE = {
  // The card defines no solid aqua cap, so aqua is the same on both surfaces.
  aqua: { "on-glass": AQUA, "on-solid": AQUA },
  neutral: {
    "on-glass": {
      background: { hex: surface4[0], alpha: 0.8 },
      border: { hex: ink4[1], alpha: 0.18 },
      ink: ink4[3]
    },
    "on-solid": {
      background: { hex: surface4[1], alpha: 1 },
      border: { hex: line4[2], alpha: 1 },
      ink: ink4[3]
    }
  }
};
var paint = ({ hex, alpha }) => alpha === 1 ? hex : rgba4(hex, alpha);
var Cap = styled5.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: ${MIN_WIDTH};
  padding: ${PAD};
  border-radius: ${radius4.sm};
  background: ${({ $tone, $surface }) => paint(KBD_TONE[$tone][$surface].background)};
  border: ${BORDER_WIDTH} solid ${({ $tone, $surface }) => paint(KBD_TONE[$tone][$surface].border)};
  font: ${TYPE} ${fontMono2};
  color: ${({ $tone, $surface }) => KBD_TONE[$tone][$surface].ink};
  letter-spacing: ${TRACKING};
`;
var Group = styled5.kbd`
  display: inline-flex;
  align-items: center;
  gap: ${GROUP_GAP};
`;
var capClass = (tone, surface31) => ["tl-kbd", tone === "neutral" && "is-neutral", surface31 === "on-solid" && "on-solid"].filter(Boolean).join(" ");
var kbdKeys = (keys) => (typeof keys === "string" ? [keys] : Array.isArray(keys) ? keys : []).filter(
  (k) => typeof k === "string" && k !== ""
);
var Kbd = ({
  keys,
  tone: toneProp,
  surface: surfaceProp,
  className
}) => {
  const list = kbdKeys(keys);
  if (list.length === 0) return null;
  const tone = variant(KBD_TONE, toneProp, "aqua", "Kbd", "tone");
  const surface31 = variant(KBD_TONE.aqua, surfaceProp, "on-glass", "Kbd", "surface");
  const cls = capClass(tone, surface31);
  if (list.length === 1) {
    return /* @__PURE__ */ jsx5(Cap, { className: [cls, className].filter(Boolean).join(" "), $tone: tone, $surface: surface31, children: list[0] });
  }
  return /* @__PURE__ */ jsx5(Group, { className: ["tl-kbd-group", className].filter(Boolean).join(" "), children: list.map((k, i) => /* @__PURE__ */ jsx5(Cap, { className: cls, $tone: tone, $surface: surface31, children: k }, `${i}-${k}`)) });
};

// src/internal/searchLead.ts
import { css as css5 } from "styled-components";
import { ink as ink5, motion as motion4 } from "@repo/design-tokens";

// src/internal/fieldChassis.ts
import { brand as brand6, rgba as rgba5 } from "@repo/design-tokens";
var FIELD_SIZE = {
  compact: { height: "24px", padding: "0 26px 0 8px", font: "12px", border: "1px", radius: "6px" },
  sm: { height: "32px", padding: "0 32px 0 10px", font: "13px", border: "1.5px", radius: "8px" },
  md: { height: "38px", padding: "0 36px 0 10px", font: "14px", border: "1.5px", radius: "8px" },
  lg: { height: "48px", padding: "0 40px 0 12px", font: "15px", border: "1.5px", radius: "8px" }
};
var FIELD_PAD_X = {
  compact: "8px",
  sm: "10px",
  md: "10px",
  lg: "12px"
};
var PREFIX_FONT = {
  compact: "11px",
  sm: "12px",
  md: "13px",
  lg: "14px"
};
var COMBO_TRAIL_PAD = {
  compact: "44px",
  sm: "50px",
  md: "54px",
  lg: "58px"
};
var FIELD_TRAIL_INSET = {
  compact: "6px",
  sm: "10px",
  md: "10px",
  lg: "10px"
};
var SEARCH_GEOMETRY = {
  compact: { padding: "0 24px 0 26px", leadInset: "7px", clearInset: "5px" },
  sm: { padding: "0 30px 0 32px", leadInset: "10px", clearInset: "8px" },
  md: { padding: "0 32px 0 34px", leadInset: "10px", clearInset: "8px" },
  lg: { padding: "0 38px 0 40px", leadInset: "14px", clearInset: "12px" }
};
var SEARCH_LEAD_ICON = { compact: "xs", sm: "sm", md: "md", lg: "lg" };
var SEARCH_PAD_LEFT = Object.fromEntries(
  Object.entries(SEARCH_GEOMETRY).map(([size, g]) => {
    const left = g.padding.split(" ")[3];
    if (left == null) throw new Error(`SEARCH_GEOMETRY.${size}.padding needs four values`);
    return [size, left];
  })
);
var SEARCH_CLEAR_PAD = "3px";
var FIELD_CHROME = {
  /** Resting border. */
  border: "#d9d9d9",
  /** Hover border — darker on solid surfaces. */
  borderHover: "#b8b8b8",
  /** The periwinkle-tinted field fill. */
  fill: "#fdf9fd",
  /** Value ink. */
  ink: "#242424",
  /** Focus lifts the fill to pure white — the cards' signature, shared by both controls. */
  fillFocus: "#fff",
  errorBorder: "#d13438",
  errorFill: "rgba(209, 52, 56, 0.03)",
  errorRing: "rgba(209, 52, 56, 0.12)",
  disabledFill: "#f5f5f5",
  disabledBorder: "#e8e8e8",
  readOnlyFill: "#fafafa",
  readOnlyBorder: "#ececec",
  readOnlyInk: "#424242",
  glassFill: "rgba(253, 249, 253, 0.6)",
  glassBorder: "rgba(36, 36, 36, 0.18)",
  glassFillHover: "rgba(253, 249, 253, 0.78)",
  glassBorderHover: "rgba(36, 36, 36, 0.32)",
  glassFillOpaque: "rgba(253, 249, 253, 0.9)",
  glassBorderOpaque: "rgba(36, 36, 36, 0.14)",
  glassBlur: "blur(8px)"
};
var FIELD_LABEL = {
  labelFont: "500 13px/1.2",
  labelInk: FIELD_CHROME.ink,
  /** Helper and error are MONO in the card, unlike the label. */
  helpFont: "400 11px/1.4",
  helpInk: "#8a8a8a",
  errFont: "500 11px/1.4",
  errInk: FIELD_CHROME.errorBorder,
  /** Vertical rhythm between label, control and message. */
  gap: "6px",
  gapCompact: "3px"
};
var fieldFocusRing = (size) => `0 0 0 ${size === "compact" ? "2px" : "3px"} ${rgba5(brand6.cyan, size === "compact" ? 0.18 : 0.12)}`;

// src/internal/searchLead.ts
var searchLeadCss = css5`
  position: absolute;
  left: ${({ $size }) => SEARCH_GEOMETRY[$size].leadInset};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  color: ${ink5[4]};
  /* Decorative: a click on it must reach the field underneath. */
  pointer-events: none;
  transition: color 0.15s ${motion4.easeUi};
`;

// src/Inputs/Search.tsx
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
var EDITABLE = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="combobox"], [role="searchbox"]';
var isShortcutBlocked = (event, input) => {
  if (event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented) return true;
  const origin = event.composedPath?.()[0] ?? event.target;
  if (origin instanceof Element && origin.closest(EDITABLE)) return true;
  if (origin instanceof HTMLElement && origin.isContentEditable) return true;
  if (input.closest("[inert]")) return true;
  const modals = document.querySelectorAll('[aria-modal="true"]');
  const topmost = modals[modals.length - 1];
  return topmost != null && !topmost.contains(input);
};
var UNREPRESENTABLE = /^[+\s]$/;
var singleKey = (shortcut) => shortcut != null && [...shortcut].length === 1 && !UNREPRESENTABLE.test(shortcut) ? shortcut : void 0;
var CLEAR_ICON = { compact: "xs", sm: "xs", md: "xs", lg: "sm" };
var Wrap = styled6.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 100%;
`;
var Box = styled6.input`
  appearance: none;
  width: 100%;
  box-sizing: border-box;
  height: ${({ $size }) => FIELD_SIZE[$size].height};
  padding: ${({ $size }) => SEARCH_GEOMETRY[$size].padding};
  border-radius: ${({ $size }) => FIELD_SIZE[$size].radius};
  border: ${({ $size }) => FIELD_SIZE[$size].border} solid ${FIELD_CHROME.border};
  background: ${FIELD_CHROME.fill};
  font-family: ${fontSans3};
  font-weight: 400;
  font-size: ${({ $size }) => FIELD_SIZE[$size].font};
  line-height: 1;
  color: ${FIELD_CHROME.ink};
  outline: none;
  transition:
    border-color 0.15s ${motion5.easeUi},
    box-shadow 0.15s ${motion5.easeUi},
    background-color 0.15s ${motion5.easeUi};

  /* The browser's own cancel cross would be a second clear beside the card's. */
  &::-webkit-search-cancel-button,
  &::-webkit-search-decoration {
    -webkit-appearance: none;
  }

  &::placeholder {
    color: ${ink6[5]};
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${FIELD_CHROME.borderHover};
  }

  &:focus {
    border-color: ${brand7.cyan};
    background: ${FIELD_CHROME.fillFocus};
    box-shadow: ${({ $size }) => fieldFocusRing($size)};
  }

  &:disabled {
    background: ${FIELD_CHROME.disabledFill};
    border-color: ${FIELD_CHROME.disabledBorder};
    color: ${ink6[5]};
    cursor: not-allowed;
  }

  &.on-glass {
    background: ${FIELD_CHROME.glassFill};
    border-color: ${FIELD_CHROME.glassBorder};
    backdrop-filter: ${FIELD_CHROME.glassBlur};
    -webkit-backdrop-filter: ${FIELD_CHROME.glassBlur};
  }
  &.on-glass::placeholder {
    color: ${FIELD_CHROME.glassBorderHover};
  }
  &.on-glass:hover:not(:disabled):not(:focus) {
    background: ${FIELD_CHROME.glassFillHover};
    border-color: ${FIELD_CHROME.glassBorderHover};
  }
  &.on-glass:focus {
    background: ${FIELD_CHROME.fillFocus};
    border-color: ${brand7.cyan};
  }
  &.on-glass.on-opaque {
    background: ${FIELD_CHROME.glassFillOpaque};
    border-color: ${FIELD_CHROME.glassBorderOpaque};
  }
  &.on-glass.on-opaque:focus {
    background: ${FIELD_CHROME.fillFocus};
  }

  /* The magnifier follows the field's focus — the card's \`input:focus + .lead\`. */
  &:focus + .tl-search-lead {
    color: ${brand7.cyan};
  }
`;
var Lead = styled6.span`
  ${searchLeadCss}
`;
var trailingSlot = css6`
  position: absolute;
  right: ${({ $size }) => SEARCH_GEOMETRY[$size].clearInset};
  top: 50%;
  transform: translateY(-50%);
`;
var Hint = styled6.span`
  ${trailingSlot}
  display: flex;
  /* Decorative: a click on it must reach the field underneath. */
  pointer-events: none;
`;
var Clear = styled6.button`
  ${trailingSlot}
  display: inline-flex;
  align-items: center;
  padding: ${SEARCH_CLEAR_PAD};
  border: none;
  border-radius: ${radius5.sm};
  background: none;
  color: ${ink6[3]};
  cursor: pointer;

  &:hover {
    color: ${brand7.cyan};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${focus2.tight};
  }
`;
var Search = React4.forwardRef(function Search2({
  value,
  onChange,
  onClear,
  size,
  onGlass = false,
  onOpaque = false,
  clearLabel = "Clear search",
  className,
  onKeyDown,
  onFocus,
  onBlur,
  disabled,
  shortcut: shortcutProp,
  "aria-keyshortcuts": ariaKeyShortcuts,
  ...rest
}, ref) {
  const shortcut = singleKey(shortcutProp);
  const warnedKey = React4.useRef(false);
  React4.useEffect(() => {
    if (shortcutProp != null && shortcut == null && !warnedKey.current) {
      warnedKey.current = true;
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `Search: \`shortcut\` must be one character other than + or whitespace; "${shortcutProp}" is ignored.`
        );
      }
    }
  }, [shortcutProp, shortcut]);
  const [focused, setFocused] = React4.useState(false);
  const sz = variant(FIELD_SIZE, size, "md", "Search", "size");
  const inputRef = React4.useRef(null);
  const setRefs = React4.useCallback(
    (node) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );
  React4.useEffect(() => {
    if (!shortcut || disabled) return void 0;
    const onShortcut = (e) => {
      const input = inputRef.current;
      if (e.key !== shortcut || !input || isShortcutBlocked(e, input)) return;
      input.focus();
      if (input.ownerDocument.activeElement !== input) return;
      e.preventDefault();
      input.select();
    };
    document.addEventListener("keydown", onShortcut);
    return () => document.removeEventListener("keydown", onShortcut);
  }, [shortcut, disabled]);
  React4.useEffect(() => {
    setFocused(shortcut != null && document.activeElement === inputRef.current);
  }, [shortcut, disabled]);
  const clear = () => {
    onChange("");
    onClear?.();
    inputRef.current?.focus();
  };
  return /* @__PURE__ */ jsxs4(Wrap, { className: `tl-search sz-${sz}`, children: [
    /* @__PURE__ */ jsx6(
      Box,
      {
        ...rest,
        ref: setRefs,
        type: "search",
        $size: sz,
        value,
        disabled,
        "aria-keyshortcuts": (disabled ? void 0 : shortcut) ?? ariaKeyShortcuts,
        onFocus: (e) => {
          onFocus?.(e);
          if (shortcut != null) setFocused(true);
        },
        onBlur: (e) => {
          onBlur?.(e);
          if (shortcut != null) setFocused(false);
        },
        onChange: (e) => onChange(e.target.value),
        onKeyDown: (e) => {
          onKeyDown?.(e);
          if (e.key === "Escape" && !e.defaultPrevented && value !== "") {
            e.preventDefault();
            clear();
          }
        },
        className: [
          "tl-search-input",
          className,
          onGlass ? "on-glass" : null,
          onGlass && onOpaque ? "on-opaque" : null
        ].filter(Boolean).join(" ")
      }
    ),
    /* @__PURE__ */ jsx6(Lead, { $size: sz, className: "tl-search-lead", "aria-hidden": "true", children: /* @__PURE__ */ jsx6(Icon, { name: "magnifying-glass", size: SEARCH_LEAD_ICON[sz] }) }),
    shortcut != null && !disabled && !focused && value === "" && /* @__PURE__ */ jsx6(Hint, { $size: sz, className: "tl-search-hint", "aria-hidden": "true", children: /* @__PURE__ */ jsx6(Kbd, { keys: shortcut, tone: "neutral", surface: onGlass ? "on-glass" : "on-solid" }) }),
    value !== "" && !disabled && /* @__PURE__ */ jsx6(
      Clear,
      {
        type: "button",
        $size: sz,
        className: "tl-search-clear",
        "aria-label": clearLabel,
        onClick: clear,
        children: /* @__PURE__ */ jsx6(Icon, { name: "xmark", size: CLEAR_ICON[sz] })
      }
    )
  ] });
});

// src/Cards/ListPanel.tsx
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var LIST_PANEL_FOLDED_WIDTH = "320px";
var WASH = RIBBON_TONE.lavender;
var Root = styled7.section`
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
  font-family: ${fontSans4};
  color: ${semantic3.text.primary};
  ${glassChromeCss}
  overflow: hidden;
  pointer-events: auto;

  &[data-open='true'] {
    width: ${(p) => p.$width};
    height: 100%;
  }

  &[data-open='false'] {
    width: ${LIST_PANEL_FOLDED_WIDTH};
    height: auto;
    align-self: flex-start;
  }

  .tl-list-panel-head {
    position: relative;
    /* Above the body: a host's search results open below the header, over the list. */
    z-index: 2;
    flex: none;
    display: flex;
    flex-direction: column;
    gap: ${space[3]};
    padding: ${space[3]};
    background: linear-gradient(
      180deg,
      ${rgba6(WASH.wash, WASH.top)},
      ${rgba6(WASH.wash, WASH.bottom)}
    );
  }

  .tl-list-panel-search {
    display: flex;
    align-items: center;
    gap: ${space[2]};

    > :first-child {
      flex: 1;
      min-width: 0;
    }
  }

  .tl-list-panel-body {
    position: relative;
    z-index: 1;
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .tl-list-panel-body[hidden],
  .tl-list-panel-head[hidden] {
    display: none;
  }

  .tl-list-panel-bar {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: ${space[2]};
    padding: ${space[2]};
    background: linear-gradient(
      180deg,
      ${rgba6(WASH.wash, WASH.top)},
      ${rgba6(WASH.wash, WASH.bottom)}
    );
  }

  .tl-list-panel-name {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    border-radius: ${radius6.sm};

    &:focus-visible {
      outline: none;
      box-shadow: ${semantic3.interactive.focusRing};
    }
  }

  .tl-list-panel-title {
    font-size: ${typeRole3.subtitle2.fontSize};
    line-height: ${typeRole3.subtitle2.lineHeight};
    font-weight: ${weight2.semibold};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .tl-list-panel-summary {
    font-size: ${typeRole3.caption1.fontSize};
    line-height: ${typeRole3.caption1.lineHeight};
    font-weight: ${weight2.medium};
    opacity: ${SUB_OPACITY};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
`;
var ListPanel = ({
  name,
  summary,
  search,
  searchSlot,
  searchLabel,
  foldedLead,
  foldedTrail,
  header,
  children,
  open,
  onOpenChange,
  width,
  shortcutEnabled = true,
  foldLabel,
  openLabel,
  "aria-label": ariaLabel,
  className,
  style
}) => {
  const searchRef = React5.useRef(null);
  const barRef = React5.useRef(null);
  const searchRowRef = React5.useRef(null);
  const foldRef = React5.useRef(null);
  const openerRef = React5.useRef(null);
  const focusOnOpen = React5.useRef(null);
  const focusOnFold = React5.useRef(false);
  const rootRef = React5.useRef(null);
  const wasOpen = React5.useRef(open);
  if (wasOpen.current !== open) {
    if (wasOpen.current && typeof document !== "undefined" && rootRef.current?.contains(document.activeElement))
      focusOnFold.current = true;
    wasOpen.current = open;
  }
  const reopen = (focus15) => {
    focusOnOpen.current = focus15;
    onOpenChange(true);
  };
  React5.useEffect(() => {
    if (open && focusOnOpen.current) {
      const searchTarget = searchRef.current ?? searchRowRef.current?.querySelector("input") ?? null;
      const target = focusOnOpen.current === "search" ? searchTarget : foldRef.current;
      focusOnOpen.current = null;
      target?.focus();
    }
    if (!open && focusOnFold.current) {
      focusOnFold.current = false;
      openerRef.current?.focus();
    }
  }, [open]);
  React5.useEffect(() => {
    if (open || !shortcutEnabled) return void 0;
    const onKey = (e) => {
      const anchor = barRef.current;
      if (e.key !== "/" || !anchor || isShortcutBlocked(e, anchor)) return;
      e.preventDefault();
      reopen("search");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, shortcutEnabled, onOpenChange]);
  const regionName = ariaLabel ?? name;
  return /* @__PURE__ */ jsxs5(
    Root,
    {
      ref: rootRef,
      $width: width,
      className,
      style,
      role: "region",
      "aria-label": regionName,
      "data-open": open ? "true" : "false",
      "data-list-panel": "",
      children: [
        open ? null : /* @__PURE__ */ jsxs5("div", { className: "tl-list-panel-bar", ref: barRef, "data-testid": "list-panel-bar", children: [
          /* @__PURE__ */ jsx7(
            Button,
            {
              ref: openerRef,
              variant: "frosted",
              size: "inline",
              iconOnly: true,
              "aria-label": openLabel ?? `Open the ${name} list`,
              onClick: () => reopen("fold"),
              iconLeft: /* @__PURE__ */ jsx7(Icon, { name: "angles-right" })
            }
          ),
          foldedLead,
          /* @__PURE__ */ jsxs5("button", { type: "button", className: "tl-list-panel-name", onClick: () => reopen("fold"), children: [
            /* @__PURE__ */ jsx7("span", { className: "tl-list-panel-title", children: name }),
            summary ? /* @__PURE__ */ jsx7("span", { className: "tl-list-panel-summary", children: summary }) : null
          ] }),
          foldedTrail,
          /* @__PURE__ */ jsx7(
            Button,
            {
              variant: "frosted",
              size: "inline",
              iconOnly: true,
              "aria-label": search?.label ?? searchLabel ?? `Search the ${name} list`,
              onClick: () => reopen("search"),
              iconLeft: /* @__PURE__ */ jsx7(Icon, { name: "magnifying-glass" })
            }
          )
        ] }),
        /* @__PURE__ */ jsxs5("div", { className: "tl-list-panel-head", hidden: !open, children: [
          /* @__PURE__ */ jsxs5("div", { className: "tl-list-panel-search", ref: searchRowRef, children: [
            searchSlot ?? (search ? /* @__PURE__ */ jsx7(
              Search,
              {
                ref: searchRef,
                onGlass: true,
                size: "md",
                value: search.value,
                onChange: search.onChange,
                placeholder: search.placeholder,
                "aria-label": search.label,
                shortcut: shortcutEnabled && open ? "/" : void 0
              }
            ) : null),
            /* @__PURE__ */ jsx7(
              Button,
              {
                ref: foldRef,
                variant: "frosted",
                size: "inline",
                iconOnly: true,
                "aria-label": foldLabel ?? `Fold the ${name} list`,
                onClick: () => {
                  focusOnFold.current = true;
                  onOpenChange(false);
                },
                iconLeft: /* @__PURE__ */ jsx7(Icon, { name: "angles-left" })
              }
            )
          ] }),
          header
        ] }),
        /* @__PURE__ */ jsx7("div", { className: "tl-list-panel-body", hidden: !open, "data-testid": "list-panel-body", children })
      ]
    }
  );
};

// src/Cards/ActionCard.tsx
import React6 from "react";
import styled8 from "styled-components";
import {
  brand as brand10,
  fontSans as fontSans5,
  ink as ink8,
  motion as motion6,
  rgba as rgba9,
  shadow as shadow2,
  surface as surfaceToken,
  typeScale,
  weight as weight3
} from "@repo/design-tokens";

// src/internal/focusables.ts
var FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'area[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'iframe:not([tabindex="-1"])',
  'audio[controls]:not([tabindex="-1"])',
  'video[controls]:not([tabindex="-1"])',
  '[contenteditable]:not([contenteditable="false"]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])'
].join(", ");
function isHidden(el) {
  if (el.hasAttribute("hidden")) return true;
  if (el.getAttribute("aria-hidden") === "true") return true;
  const cs = typeof window !== "undefined" && typeof window.getComputedStyle === "function" ? window.getComputedStyle(el) : null;
  if (cs && (cs.visibility === "hidden" || cs.visibility === "collapse")) return true;
  if (cs && cs.contentVisibility === "hidden") return true;
  if (cs && cs.display === "none") return true;
  if (el.offsetParent !== null) return false;
  return (cs?.position ?? el.style.position) !== "fixed";
}
function getFocusables(root) {
  const all = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR));
  const laidOut = root.offsetWidth > 0 || root.offsetHeight > 0 || root.getClientRects().length > 0;
  if (!laidOut) return all;
  return all.filter((el) => !isHidden(el));
}

// src/internal/glassStates.ts
import { css as css7 } from "styled-components";
import { brand as brand8, focus as focus3, rgba as rgba7, surface as surface5 } from "@repo/design-tokens";
var glassHoverCss = css7`
  background: ${rgba7(surface5[0], 0.72)};
  border-color: ${brand8.frost};
`;
var glassFocusCss = css7`
  outline: none;
  box-shadow: ${focus3.ring};
`;
var glassDisabledCss = css7`
  opacity: 0.5;
  filter: saturate(0.7);
`;
var GLASS_REST = {
  fill: surface5[0],
  fillAlpha: 0.7
};
var glassRestCss = css7`
  border: 1.5px solid ${rgba7(surface5[0], 0.95)};
  background: ${rgba7(GLASS_REST.fill, GLASS_REST.fillAlpha)};
`;

// src/internal/iconDisc.ts
import { css as css8 } from "styled-components";
import { brand as brand9, ink as ink7, inkOn as inkOn2, radius as radius7, rgba as rgba8, support as support2, surface as surface6 } from "@repo/design-tokens";
var DISC_TONES = {
  /** An unselected selection card — the panel-tint step at .95. */
  neutral: { fill: rgba8(surface6[2], 0.95), glyph: ink7[2], glow: null },
  /** A selected selection card — solid brand cyan, white glyph. */
  selected: { fill: brand9.cyan, glyph: surface6[0], glow: null },
  /** An action card, the default tone. */
  aqua: { fill: rgba8(brand9.frost, 0.9), glyph: inkOn2.aqua, glow: rgba8(brand9.cyan, 0.3) },
  /** An action card in periwinkle — frame 05's *Copy another twin*. */
  peri: {
    fill: rgba8(support2.periwinkle, 0.24),
    glyph: inkOn2.peri,
    glow: rgba8(support2.periwinkle, 0.35)
  }
};
var DISC_GLYPH_SIZE = { 34: "sm", 40: "md" };
var iconDiscCss = (size) => css8`
  width: ${size}px;
  height: ${size}px;
  flex: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${radius7.pill};
  border: ${size === 40 ? "2px" : "1.5px"} solid ${surface6[0]};
`;
var discToneCss = (tone) => {
  const t = DISC_TONES[tone];
  return css8`
    background: ${t.fill};
    color: ${t.glyph};
    ${t.glow ? `box-shadow: 0 0 10px 2px ${t.glow};` : ""}
  `;
};

// src/Cards/ActionCard.tsx
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var TONES2 = { aqua: true, peri: true };
var ACTION_CARD_COLOURS = {
  title: ink8[1],
  description: ink8[2],
  /** The shared glass rest (`internal/glassStates.ts`), which the CSS paints. */
  fill: GLASS_REST.fill,
  fillAlpha: GLASS_REST.fillAlpha,
  /** What the contrast test composites onto. */
  backdrop: surfaceToken[0]
};
var ACTION_CARD_RADIUS = "14px";
var Root2 = styled8.button`
  && {
    appearance: none;
    margin: 0;
    width: 100%;
    font: inherit;
    font-family: ${fontSans5};
    text-align: left;
    color: inherit;
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 16px;
    overflow: hidden;
    cursor: pointer;
    border-radius: ${ACTION_CARD_RADIUS};
    ${glassRestCss}
    box-shadow:
      0 0 12px 2px ${rgba9(brand10.frost, 0.6)},
      0 6px 16px rgba(${shadow2.inkGlassRgb}, 0.08);
    transition:
      background ${motion6.durFast} ${motion6.easeUi},
      border-color ${motion6.durFast} ${motion6.easeUi},
      box-shadow ${motion6.durFast} ${motion6.easeUi};
  }

  /* The 135° gloss sweep, behind the content. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: ${shadow2.glossOverlay};
  }
  & > * {
    position: relative;
  }

  .tl-ac-disc {
    ${iconDiscCss(40)}
  }
  &.tone-aqua .tl-ac-disc {
    ${discToneCss("aqua")}
  }
  &.tone-peri .tl-ac-disc {
    ${discToneCss("peri")}
  }

  .tl-ac-title {
    font-size: ${typeScale.subtitle2.fontSize};
    line-height: ${typeScale.subtitle2.lineHeight};
    font-weight: ${weight3.semibold};
    color: ${ACTION_CARD_COLOURS.title};
  }
  .tl-ac-desc {
    /* 12 / 17 has no step on the type scale: frame 05's value, on Rob's list in PROVENANCE. */
    font-size: 12px;
    line-height: 17px;
    font-weight: ${weight3.regular};
    color: ${ACTION_CARD_COLOURS.description};
  }
  .tl-ac-slot {
    display: block;
    align-self: stretch;
    margin-top: 2px;
  }

  /* Hover: the glass hairline and fill, with card 06's 0 0 12px 3px frost glow over the card's
     own drop (the quieter frostGlow is weaker than this card's resting glow). */
  &&:hover:not(:disabled) {
    ${glassHoverCss}
    box-shadow:
      0 0 12px 3px ${brand10.frost},
      0 6px 16px rgba(${shadow2.inkGlassRgb}, 0.08);
  }

  /* Focus: the system ring, keyboard only — and it wins over hover. The second selector out-ranks
     the hover rule (0,5,0 against 0,4,0) rather than leaning on source order, and it does not use
     :not(:focus-visible), which Safari 15.0–15.3 cannot parse and would drop the whole hover rule. */
  &&:focus-visible,
  &&:hover:not(:disabled):focus-visible {
    ${glassFocusCss}
  }

  /* Disabled: the built Card's dimming. */
  &&:disabled {
    cursor: not-allowed;
    ${glassDisabledCss}
  }
`;
var ActionCard = React6.forwardRef(
  function ActionCard2(props, ref) {
    const {
      icon: icon2,
      title,
      description,
      tone,
      children,
      disabled = false,
      className,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      ...rest
    } = props;
    const resolvedTone = variant(TONES2, tone ?? "aqua", "aqua", "ActionCard", "tone");
    const autoId = React6.useId();
    const titleId = `${autoId}-title`;
    const descId = `${autoId}-desc`;
    const slotRef = React6.useRef(null);
    const warned = React6.useRef(false);
    const hasDescription = description != null && description !== false && description !== "";
    const hasSlot = children != null && children !== false;
    React6.useEffect(() => {
      if (process.env.NODE_ENV === "production" || warned.current || !slotRef.current) return;
      if (getFocusables(slotRef.current).length > 0) {
        warned.current = true;
        console.warn(
          "[@repo/design-system] ActionCard: the picture slot holds a focusable element; the card is a button, so nothing inside it may be interactive."
        );
      }
    }, [children]);
    const labelledBy = [titleId, ariaLabelledBy].filter(Boolean).join(" ");
    const describedBy = [hasDescription ? descId : null, ariaDescribedBy].filter(Boolean).join(" ") || void 0;
    const classes = ["tl-ac", `tone-${resolvedTone}`, className ?? ""].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsxs6(
      Root2,
      {
        ...rest,
        ref,
        type: "button",
        className: classes,
        disabled,
        "aria-labelledby": labelledBy,
        "aria-describedby": describedBy,
        children: [
          /* @__PURE__ */ jsx8("span", { className: "tl-ac-disc", "aria-hidden": "true", children: /* @__PURE__ */ jsx8(Icon, { name: icon2, size: DISC_GLYPH_SIZE[40] }) }),
          /* @__PURE__ */ jsx8("span", { id: titleId, className: "tl-ac-title", children: title }),
          hasDescription ? /* @__PURE__ */ jsx8("span", { id: descId, className: "tl-ac-desc", children: description }) : null,
          hasSlot ? /* @__PURE__ */ jsx8("span", { ref: slotRef, className: "tl-ac-slot", children }) : null
        ]
      }
    );
  }
);
ActionCard.displayName = "ActionCard";

// src/Cards/OverrideRows.tsx
import React7, { useEffect, useId } from "react";
import styled10 from "styled-components";
import {
  accent as accent2,
  brand as brand11,
  controlHeight as controlHeight2,
  fontMono as fontMono3,
  fontSans as fontSans6,
  ink as ink9,
  inkOn as inkOn3,
  inkGlass as inkGlass3,
  inset as inset2,
  line as line5,
  radius as radius8,
  rgba as rgba10,
  space as space2,
  surface as surface7,
  typeScale as typeScale2,
  weight as weight4
} from "@repo/design-tokens";

// src/internal/emptyMark.tsx
import styled9 from "styled-components";
import { Fragment, jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var EMPTY_MARK = "\u2013";
var EMPTY_SPOKEN = "not set";
var isEmptyValue = (value) => value === null || value === void 0 || value === "" || typeof value === "boolean";
var Spoken = styled9.span`
  ${visuallyHiddenCss}
`;
var EmptyMark = () => /* @__PURE__ */ jsxs7(Fragment, { children: [
  /* @__PURE__ */ jsx9("span", { "aria-hidden": "true", children: EMPTY_MARK }),
  /* @__PURE__ */ jsx9(Spoken, { children: EMPTY_SPOKEN })
] });

// src/Cards/OverrideRows.tsx
import { Fragment as Fragment2, jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var BUTTER_WASH = 0.14;
var OVERRIDE_COLOURS = {
  label: ink9[3],
  value: ink9[1],
  source: ink9[4],
  sourceOverridden: inkGlass3.warn,
  /** The `brand` text tone's ink (`Button`); held here for the contrast pin. */
  action: brand11.cyanPress,
  fill: surface7[0],
  wash: accent2.butter
};
var LABEL_COLUMN = "68px";
var COLUMN_GAP = space2[3];
var ROW_PADDING = `6px 4px 6px ${space2[3]}`;
var ROW_MIN_HEIGHT = "45px";
var CAPTION_GAP = "6px";
var VALUE_LINE_HEIGHT = "18px";
var CAPTION_STEP = `${typeScale2.caption1.fontSize}/${typeScale2.caption1.lineHeight}`;
var LABEL_TYPE = `${weight4.regular} ${CAPTION_STEP}`;
var VALUE_TYPE = `${weight4.semibold} ${typeScale2.body1.fontSize}/${VALUE_LINE_HEIGHT}`;
var SOURCE_TYPE = `${weight4.regular} ${typeScale2.captionCompact.fontSize}/${typeScale2.captionCompact.lineHeight}`;
var DIVIDER = `1px solid ${line5[1]}`;
var Root3 = styled10.div`
  min-width: 0;
  font-family: ${fontSans6};
`;
var Caption = styled10.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space2[2]};
  margin-bottom: ${CAPTION_GAP};
`;
var Title = styled10.span`
  font: ${weight4.semibold} ${CAPTION_STEP} ${fontSans6};
  color: ${ink9[2]};
`;
var Aside = styled10.span`
  margin-left: auto;
  font: ${weight4.regular} ${CAPTION_STEP} ${fontSans6};
  color: ${ink9[3]};
`;
var List = styled10.dl`
  margin: 0;
  background: ${OVERRIDE_COLOURS.fill};
  border: ${DIVIDER};
  border-radius: ${radius8.md};
  overflow: hidden;
`;
var Row = styled10.div`
  display: grid;
  grid-template-columns: ${LABEL_COLUMN} minmax(0, 1fr);
  column-gap: ${COLUMN_GAP};
  align-items: center;
  min-height: ${ROW_MIN_HEIGHT};
  padding: ${ROW_PADDING};
  box-sizing: border-box;

  & + & {
    border-top: ${DIVIDER};
  }

  &[data-overridden='true'] {
    background: ${rgba10(OVERRIDE_COLOURS.wash, BUTTER_WASH)};
  }
`;
var Label = styled10.dt`
  min-width: 0;
  font: ${LABEL_TYPE} ${fontSans6};
  color: ${OVERRIDE_COLOURS.label};
`;
var Definition = styled10.dd`
  display: flex;
  align-items: center;
  gap: ${COLUMN_GAP};
  min-width: 0;
  margin: 0;

  > button {
    flex: none;
    white-space: nowrap;
  }
`;
var ValueBlock = styled10.div`
  flex: 1 1 auto;
  min-width: 0;
`;
var Value = styled10.div`
  font: ${VALUE_TYPE} ${fontSans6};
  color: ${OVERRIDE_COLOURS.value};
  overflow-wrap: anywhere;
`;
var OVERRIDDEN_SPOKEN = ", overridden";
var Spoken2 = styled10.span`
  ${visuallyHiddenCss}
`;
var saysNothing = (source) => isEmptyValue(source) || typeof source === "string" && source.trim() === "";
var Source = styled10.div`
  font: ${SOURCE_TYPE} ${fontSans6};
  color: ${OVERRIDE_COLOURS.source};
  overflow-wrap: anywhere;

  ${Row}[data-overridden='true'] & {
    color: ${OVERRIDE_COLOURS.sourceOverridden};
  }
`;
var LINE_LABEL_COLUMN = "100px";
var LINE_COLUMN_GAP = space2[2];
var LINE_ROW_MIN_HEIGHT = controlHeight2.md;
var LINE_VALUE_GAP = inset2[2];
var LINE_EDIT_PADDING = space2[2];
var LINE_EDIT_MARGIN = `${inset2[1]} -${space2[2]}`;
var LINE_EDIT_ROW_GAP = space2[1];
var LINE_EDIT_FILL = 0.78;
var LINE_EDIT_EDGE = 0.22;
var NOTE_SHRINK = 1e3;
var LINE_COLOURS = {
  label: ink9[3],
  value: ink9[1],
  note: ink9[3],
  muted: ink9[3],
  help: ink9[3],
  helpError: inkOn3.error,
  divider: line5[4]
};
var LineList = styled10.dl`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;
var LineRow = styled10.div`
  display: grid;
  grid-template-columns: ${LINE_LABEL_COLUMN} minmax(0, 1fr) auto;
  align-items: center;
  column-gap: ${LINE_COLUMN_GAP};
  min-height: ${LINE_ROW_MIN_HEIGHT};
  border-bottom: 1px solid ${LINE_COLOURS.divider};

  &:last-child {
    border-bottom: 0;
  }

  &[data-editing='true'] {
    row-gap: ${LINE_EDIT_ROW_GAP};
    padding: ${LINE_EDIT_PADDING};
    margin: ${LINE_EDIT_MARGIN};
    border-radius: ${radius8.md};
    border-bottom-color: transparent;
    background: ${rgba10(surface7[0], LINE_EDIT_FILL)};
    box-shadow: 0 0 0 1px ${rgba10(brand11.cyan, LINE_EDIT_EDGE)};
  }

  &[data-detail='true'] {
    padding-bottom: ${space2[2]};
  }
`;
var LineLabel = styled10.dt`
  margin: 0;
  min-width: 0;
  font: ${LABEL_TYPE} ${fontSans6};
  color: ${LINE_COLOURS.label};
  white-space: nowrap;
`;
var LineValue = styled10.dd`
  margin: 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${LINE_VALUE_GAP};
  font: ${weight4.regular} ${typeScale2.body1.fontSize} / ${typeScale2.body1.lineHeight} ${fontSans6};
  color: ${LINE_COLOURS.value};
  white-space: nowrap;
  overflow: hidden;

  &[data-mono='true'] {
    font-family: ${fontMono3};
    font-size: ${typeScale2.caption1.fontSize};
  }
  /* cards.css .v.none: a "not set" statement, in grey italic (the face stays the row's own). */
  &[data-muted='true'] {
    color: ${LINE_COLOURS.muted};
    font-style: italic;
  }
  /* An editor (its focus ring, an open menu) must not be clipped by the one-line value cell. */
  &[data-editing='true'] {
    overflow: visible;
  }
  &[data-editing='true'] > * {
    flex: 1 1 auto;
    min-width: 0;
  }

  .tl-override-line-text {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .tl-override-line-note {
    flex: 0 ${NOTE_SHRINK} auto;
    min-width: 0;
    font-family: ${fontSans6};
    font-size: ${typeScale2.caption1.fontSize};
    color: ${LINE_COLOURS.note};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
var LineAction = styled10.dd`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${space2[1]};

  > button {
    white-space: nowrap;
  }
`;
var LineHelp = styled10.dd`
  grid-column: 1 / -1;
  margin: 0;
  font: ${weight4.regular} ${typeScale2.captionCompact.fontSize} /
    ${typeScale2.captionCompact.lineHeight} ${fontSans6};
  color: ${LINE_COLOURS.help};

  &[data-tone='error'] {
    color: ${LINE_COLOURS.helpError};
  }
`;
var LineDetail = styled10.dd`
  grid-column: 2 / -1;
  margin: 0;
  min-width: 0;
`;
var fullText = (value) => typeof value === "string" || typeof value === "number" ? String(value) : void 0;
var isIconOnly = (action) => !action.label;
var LineRowAction = ({ row, action }) => {
  const shared = {
    type: "button",
    size: "xs",
    "data-override-row": row.id,
    disabled: action.disabled,
    loading: action.loading,
    onClick: action.onAction,
    "aria-expanded": action["aria-expanded"],
    "aria-controls": action["aria-controls"],
    "aria-describedby": action["aria-describedby"]
  };
  if (isIconOnly(action)) {
    return /* @__PURE__ */ jsx10(
      Button,
      {
        ...shared,
        variant: "text",
        iconOnly: true,
        circle: true,
        "aria-label": action["aria-label"],
        iconLeft: /* @__PURE__ */ jsx10(Icon, { name: action.icon, size: "xs" })
      }
    );
  }
  const name = action["aria-label"] ?? `${action.label} ${row.label}`;
  if (action.icon) {
    return /* @__PURE__ */ jsx10(
      Button,
      {
        ...shared,
        variant: "frosted",
        "aria-label": name,
        iconLeft: /* @__PURE__ */ jsx10(Icon, { name: action.icon, size: "xs" }),
        children: action.label
      }
    );
  }
  return /* @__PURE__ */ jsx10(Button, { ...shared, variant: "text", tone: "brand", "aria-label": name, children: action.label });
};
var LineRows = ({ rows }) => /* @__PURE__ */ jsx10(LineList, { className: "tl-override-lines", children: rows.map((row) => {
  const editing = !isEmptyValue(row.editing);
  const hasDetail = !isEmptyValue(row.detail);
  return /* @__PURE__ */ jsxs8(
    LineRow,
    {
      className: "tl-override-row",
      "data-density": "line",
      "data-overridden": row.overridden ? "true" : "false",
      "data-editing": editing ? "true" : "false",
      "data-detail": hasDetail ? "true" : "false",
      children: [
        /* @__PURE__ */ jsxs8(LineLabel, { children: [
          row.label,
          row.overridden && /* @__PURE__ */ jsx10(Spoken2, { children: OVERRIDDEN_SPOKEN })
        ] }),
        /* @__PURE__ */ jsx10(
          LineValue,
          {
            className: "tl-override-row-value",
            "data-mono": row.mono ? "true" : "false",
            "data-muted": row.muted && !editing ? "true" : "false",
            "data-editing": editing ? "true" : "false",
            children: editing ? row.editing : /* @__PURE__ */ jsxs8(Fragment2, { children: [
              /* @__PURE__ */ jsx10("span", { className: "tl-override-line-text", title: fullText(row.value), children: isEmptyValue(row.value) ? /* @__PURE__ */ jsx10(EmptyMark, {}) : row.value }),
              !isEmptyValue(row.source) && /* @__PURE__ */ jsx10("span", { className: "tl-override-line-note tl-override-row-source", children: row.source })
            ] })
          }
        ),
        /* @__PURE__ */ jsx10(LineAction, { className: "tl-override-row-action", children: !editing && row.action && /* @__PURE__ */ jsx10(LineRowAction, { row, action: row.action }) }),
        hasDetail && /* @__PURE__ */ jsx10(LineDetail, { className: "tl-override-row-detail", children: row.detail }),
        row.help && !isEmptyValue(row.help.text) && /* @__PURE__ */ jsx10(LineHelp, { id: row.help.id, className: "tl-override-row-help", "data-tone": row.help.tone, children: row.help.text })
      ]
    },
    row.id
  );
}) });
var StackedHelp = styled10.div`
  font: ${SOURCE_TYPE} ${fontSans6};
  color: ${LINE_COLOURS.help};

  &[data-tone='error'] {
    color: ${LINE_COLOURS.helpError};
  }
`;
var MonoValue = styled10.span`
  font-family: ${fontMono3};
  font-size: ${typeScale2.caption1.fontSize};
`;
var OverrideRows = React7.forwardRef(
  function OverrideRows2(props, ref) {
    const { rows, density = "stacked", title, aside, className, ...rest } = props;
    const captionId = useId();
    const unsaid = rows.filter((row) => row.overridden && saysNothing(row.source)).map((row) => row.label).join(", ");
    useEffect(() => {
      if (process.env.NODE_ENV !== "production" && unsaid) {
        console.warn(
          `OverrideRows: overridden with no source line (${unsaid}). Butter is colour only \u2014 say "overridden" in the source.`
        );
      }
    }, [unsaid]);
    if (rows.length === 0) return null;
    const hasAside = !isEmptyValue(aside);
    const named = !!title || !!rest["aria-label"];
    const groupProps = title ? { role: "group", "aria-labelledby": captionId, "aria-label": void 0 } : named ? { role: "group" } : {};
    return /* @__PURE__ */ jsxs8(
      Root3,
      {
        ref,
        ...rest,
        ...groupProps,
        className: className ? `tl-override-rows ${className}` : "tl-override-rows",
        children: [
          (title || hasAside) && /* @__PURE__ */ jsxs8(Caption, { className: "tl-override-rows-caption", children: [
            title && /* @__PURE__ */ jsx10(Title, { id: captionId, children: title }),
            hasAside && /* @__PURE__ */ jsx10(Aside, { className: "tl-override-rows-aside", children: aside })
          ] }),
          density === "line" ? /* @__PURE__ */ jsx10(LineRows, { rows }) : /* @__PURE__ */ jsx10(List, { children: rows.map((row) => /* @__PURE__ */ jsxs8(
            Row,
            {
              className: "tl-override-row",
              "data-overridden": row.overridden ? "true" : "false",
              children: [
                /* @__PURE__ */ jsxs8(Label, { children: [
                  row.label,
                  row.overridden && /* @__PURE__ */ jsx10(Spoken2, { children: OVERRIDDEN_SPOKEN })
                ] }),
                /* @__PURE__ */ jsxs8(Definition, { children: [
                  /* @__PURE__ */ jsxs8(ValueBlock, { children: [
                    /* @__PURE__ */ jsx10(Value, { className: "tl-override-row-value", children: !isEmptyValue(row.editing) ? row.editing : isEmptyValue(row.value) ? /* @__PURE__ */ jsx10(EmptyMark, {}) : row.mono ? /* @__PURE__ */ jsx10(MonoValue, { children: row.value }) : row.value }),
                    !isEmptyValue(row.source) && /* @__PURE__ */ jsx10(Source, { className: "tl-override-row-source", children: row.source }),
                    !isEmptyValue(row.detail) && /* @__PURE__ */ jsx10("div", { className: "tl-override-row-detail", children: row.detail }),
                    row.help && !isEmptyValue(row.help.text) && /* @__PURE__ */ jsx10(
                      StackedHelp,
                      {
                        id: row.help.id,
                        className: "tl-override-row-help",
                        "data-tone": row.help.tone,
                        children: row.help.text
                      }
                    )
                  ] }),
                  row.action && /* @__PURE__ */ jsx10(
                    Button,
                    {
                      type: "button",
                      variant: "text",
                      size: "xs",
                      tone: "brand",
                      "data-override-row": row.id,
                      "aria-label": row.action["aria-label"] ?? `${row.action.label} ${row.label}`,
                      disabled: row.action.disabled,
                      loading: row.action.loading,
                      onClick: row.action.onAction,
                      "aria-expanded": row.action["aria-expanded"],
                      "aria-controls": row.action["aria-controls"],
                      "aria-describedby": row.action["aria-describedby"],
                      iconLeft: row.action.icon ? /* @__PURE__ */ jsx10(Icon, { name: row.action.icon, size: "xs" }) : void 0,
                      children: row.action.label
                    }
                  )
                ] })
              ]
            },
            row.id
          )) })
        ]
      }
    );
  }
);

// src/Selection/Tag.tsx
import React8 from "react";
import styled11, { css as css10 } from "styled-components";

// src/internal/dotHue.ts
import { accent as accent3, ink as ink10, semanticColor as semanticColor3, support as support3 } from "@repo/design-tokens";
var DOT_HUE = {
  mint: support3.mint,
  violet: accent3.violet,
  peach: accent3.peach,
  peri: support3.periwinkle,
  "ink-5": ink10[5],
  error: semanticColor3.error
};
var isDotTone = (key) => typeof key === "string" && Object.prototype.hasOwnProperty.call(DOT_HUE, key);

// src/internal/selectedLook.ts
import { css as css9 } from "styled-components";
import { brand as brand12, rgba as rgba11, surface as surfaceColor } from "@repo/design-tokens";
var GLASS_ACTIVE_ALPHA = 0.97;
var selectedOnSolidCss = css9`
  background: ${rgba11(brand12.cyan, 0.14)};
  color: ${brand12.cyanSelected};
  border-color: ${rgba11(brand12.cyan, 0.25)};
`;
var selectedOnGlassCss = css9`
  background-color: ${rgba11(brand12.frost, GLASS_ACTIVE_ALPHA)};
  border-color: ${brand12.frost};
  box-shadow:
    0 0 8px ${rgba11(brand12.frost, 0.7)},
    inset 0 1px 0 ${rgba11(surfaceColor[0], 0.5)};
`;

// src/Selection/Tag.tsx
import {
  ink as ink11,
  line as line6,
  brand as brand13,
  radius as radius9,
  fontSans as fontSans7,
  inkOn as inkOn4,
  inkGlass as inkGlass4,
  statusSurface,
  surface as surfaceColor2,
  semanticColor as semanticColor4,
  accent as accent4,
  support as support4,
  motion as motion7,
  rgba as rgba12,
  typeScale as typeScale3,
  space as space3,
  inset as inset3,
  fontMono as fontMono4
} from "@repo/design-tokens";
import { Fragment as Fragment3, jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var SIZE3 = {
  /*
   * Canvas (Onboarding Glass Panels, the "At 1280" frames): the 1280 chips are drawn at about 0.9 of
   * the 1440 ones (chip 23 vs 26 px tall, a status row 380 vs 405 px wide, measured on screen
   * against the 420 and 480 px walls on 2026-11-01). So `xs` is one step under `dense`: the same
   * smallest type step (no new font size), with the side padding and the gap narrowed by that ratio.
   * Recorded in design-cards/PROVENANCE.md for Rob.
   */
  xs: {
    font: typeScale3.caption2.fontSize,
    padding: `${inset3[1]} 5px 3px`,
    gap: "3px",
    className: "is-xs"
  },
  // Card: `.is-dense{font-size:var(--tl-fs-caption2);padding:var(--tl-inset-1) 7px 3px;gap:var(--tl-space-1)}`
  dense: {
    font: typeScale3.caption2.fontSize,
    padding: `${inset3[1]} 7px 3px`,
    gap: space3[1],
    className: "is-dense"
  },
  // Card: the base `.tl-tag` — font 11.5px, padding 3px 9px 4px, gap 5px.
  default: { font: "11.5px", padding: "3px 9px 4px", gap: "5px", className: "" },
  // Card: `.is-roomy{font-size:var(--tl-fs-caption1);padding:var(--tl-space-1) 11px 5px}` — gap stays the base 5px.
  roomy: {
    font: typeScale3.caption1.fontSize,
    padding: `${space3[1]} 11px 5px`,
    gap: "5px",
    className: "is-roomy"
  }
};
var baseCss2 = css10`
  display: inline-flex;
  align-items: center;
  border-radius: ${radius9.pill};
  border: 1px solid transparent;
  font-family: ${fontSans7};
  font-weight: 500;
  line-height: 1;
  color: ${ink11[1]};
  white-space: nowrap;
  letter-spacing: -0.05px;
  /* The card writes var(--tl-dur-fast), which the design project values at 0.15s. This repo's
     motion.durFast is 0.2s (a recorded divergence, design-source/PROVENANCE.md), so the card's
     value is held as a literal rather than restyling the tag's timing. */
  transition:
    background 0.15s ${motion7.easeUi},
    border-color 0.15s ${motion7.easeUi},
    transform 0.15s ${motion7.easeUi},
    box-shadow 0.15s ${motion7.easeUi};

  .tl-tag-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.7;
    flex-shrink: 0;
  }
  /* Card 05 "Tag with a count": .tl-tag .n, .is-dense .n, .n.is-zero and
     .on-solid.is-active .n. The count follows the label at the tag's own gap. */
  .n {
    font: 500 10.5px / 14px ${fontMono4};
    letter-spacing: 0;
    color: ${ink11[3]};
    font-variant-numeric: tabular-nums;
    margin-left: 1px;
  }
  &.is-dense .n {
    font-size: 10px;
  }
  &.is-xs .n {
    font-size: 10px;
  }
  .n.is-zero {
    color: ${ink11[5]};
  }
  &.on-solid.is-active .n {
    color: ${brand13.cyanSelected};
    opacity: 0.75;
  }
  .tl-tag-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: currentColor;
    line-height: 1;
  }
  .tl-tag-icon > svg {
    width: 1em;
    height: 1em;
    fill: currentColor;
  }
`;
var GLASS_BASE_ALPHA = 0.95;
var GLASS_BASE = rgba12(surfaceColor2[0], GLASS_BASE_ALPHA);
var ACCENT_TONES = {
  pink: {
    hue: accent4.peach,
    alpha: 0.28,
    ink: inkOn4.pink,
    solidBorder: rgba12(accent4.peach, 0.4)
  },
  butter: {
    hue: accent4.butter,
    alpha: 0.32,
    ink: inkOn4.butter,
    solidBorder: "rgba(230, 170, 60, 0.4)"
  },
  lavender: {
    hue: support4.lavender,
    alpha: 0.34,
    ink: inkOn4.lav,
    solidBorder: rgba12(support4.lavender, 0.6)
  },
  peri: {
    hue: support4.periwinkle,
    alpha: 0.24,
    ink: inkOn4.peri,
    solidBorder: rgba12(support4.periwinkle, 0.4)
  },
  mint: {
    hue: support4.mint,
    alpha: 0.3,
    ink: inkOn4.mint,
    solidBorder: "rgba(90, 200, 170, 0.45)"
  }
};
var GLASS_STATUS_WASH = {
  info: { hue: brand13.cyan, alpha: 0.3, ink: inkGlass4.info },
  success: { hue: semanticColor4.success, alpha: 0.42, ink: inkGlass4.success },
  warn: { hue: accent4.butter, alpha: 0.62, ink: inkGlass4.warn },
  error: { hue: semanticColor4.error, alpha: 0.42, ink: inkGlass4.error }
};
var glassWash = ({ hue, alpha }) => {
  const w = rgba12(hue, alpha);
  return `background-color: ${GLASS_BASE}; background-image: linear-gradient(${w}, ${w});`;
};
var onGlassCss2 = css10`
  background-color: ${GLASS_BASE};
  border-color: ${rgba12(surfaceColor2[0], 0.95)};
  box-shadow:
    0 1px 2px rgba(40, 30, 80, 0.12),
    inset 0 1px 0 ${rgba12(surfaceColor2[0], 0.5)};

  &.is-toggle:hover:not(:disabled) {
    background-color: ${surfaceColor2[0]};
  }
  &.is-active {
    ${selectedOnGlassCss}
  }
`;
var onSolidCss2 = css10`
  background: ${surfaceColor2[0]};
  color: ${ink11[1]};
  border-color: rgba(40, 30, 80, 0.08);
  box-shadow: 0 1px 2px rgba(40, 30, 80, 0.08);

  &.is-toggle:hover:not(:disabled) {
    background: ${surfaceColor2[1]};
  }
  &.is-active {
    ${selectedOnSolidCss}
  }
`;
var SURFACE_CSS2 = {
  "on-glass": onGlassCss2,
  "on-solid": onSolidCss2
};
var accentSolid = (t) => css10`
  background: ${rgba12(t.hue, t.alpha)};
  color: ${t.ink};
  border-color: ${t.solidBorder};
`;
var statusGlass = (t) => css10`
  ${glassWash(t)}
  color: ${t.ink};
  border-color: ${rgba12(surfaceColor2[0], 0.95)};
`;
var accentGlass = (t) => css10`
  ${statusGlass(t)}
  box-shadow:
    0 3px 11px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 ${rgba12(surfaceColor2[0], 0.7)};
`;
var codeGeometryCss = css10`
  font: 500 10.5px/14px ${fontMono4};
  color: ${ink11[2]};
  padding: 1px 6px;
  border-radius: ${radius9.md};
  letter-spacing: 0;
  box-shadow: none;
`;
var TONE_CSS = {
  "on-solid": {
    info: css10`
      background: ${statusSurface.info};
      color: ${brand13.cyanSelected};
      border-color: ${rgba12(brand13.cyan, 0.22)};
    `,
    success: css10`
      background: ${statusSurface.success};
      color: ${inkOn4.mint};
      border-color: ${rgba12(semanticColor4.success, 0.35)};
    `,
    warn: css10`
      background: ${statusSurface.warn};
      color: ${inkOn4.butter};
      border-color: ${rgba12(semanticColor4.warn, 0.25)};
    `,
    error: css10`
      background: ${statusSurface.error};
      color: ${inkOn4.error};
      border-color: ${rgba12(semanticColor4.error, 0.28)};
    `,
    pink: accentSolid(ACCENT_TONES.pink),
    butter: accentSolid(ACCENT_TONES.butter),
    lavender: accentSolid(ACCENT_TONES.lavender),
    peri: accentSolid(ACCENT_TONES.peri),
    mint: accentSolid(ACCENT_TONES.mint),
    code: css10`
      ${codeGeometryCss}
      background: ${surfaceColor2[1]};
      border-color: ${line6[2]};
    `
  },
  "on-glass": {
    info: statusGlass(GLASS_STATUS_WASH.info),
    success: statusGlass(GLASS_STATUS_WASH.success),
    warn: statusGlass(GLASS_STATUS_WASH.warn),
    error: statusGlass(GLASS_STATUS_WASH.error),
    pink: accentGlass(ACCENT_TONES.pink),
    butter: accentGlass(ACCENT_TONES.butter),
    lavender: accentGlass(ACCENT_TONES.lavender),
    peri: accentGlass(ACCENT_TONES.peri),
    mint: accentGlass(ACCENT_TONES.mint),
    code: css10`
      ${codeGeometryCss}
      background-color: ${GLASS_BASE};
      border-color: ${rgba12(line6[2], 0.9)};
    `
  }
};
var TONE_KEYS2 = {
  neutral: true,
  info: true,
  success: true,
  warn: true,
  error: true,
  pink: true,
  butter: true,
  lavender: true,
  peri: true,
  mint: true,
  code: true
};
var removeButtonCss = css10`
  .tl-tag-x {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 13px;
    height: 13px;
    padding: 0;
    border: none;
    background: transparent;
    border-radius: 50%;
    cursor: pointer;
    color: inherit;
    opacity: 0.55;
    margin: 1px -5px 0 0;
    transition:
      opacity 0.12s ${motion7.easeUi},
      background 0.12s ${motion7.easeUi};
  }
  .tl-tag-x:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.08);
  }
  .tl-tag-x:focus-visible {
    outline: none;
    opacity: 1;
    box-shadow: ${brand13.focusRing};
  }
  .tl-tag-x svg {
    width: 8px;
    height: 8px;
  }
  .tl-tag-sr {
    ${visuallyHiddenCss}
  }
`;
var dotToneCss = (tone) => tone ? css10`
        .tl-tag-dot.dt-${tone} {
          background: ${DOT_HUE[tone]};
          opacity: 1;
        }
      ` : "";
var tagCss = css10`
  ${baseCss2}
  font-size: ${({ $size }) => SIZE3[$size].font};
  padding: ${({ $size }) => SIZE3[$size].padding};
  gap: ${({ $size }) => SIZE3[$size].gap};
  ${({ $dotTone }) => dotToneCss($dotTone)}
  ${({ $surface }) => SURFACE_CSS2[$surface]}
  /* Tone comes AFTER surface so it overrides the surface's own fill/ink. */
  ${({ $surface, $tone }) => $tone === "neutral" ? "" : TONE_CSS[$surface][$tone] ?? ""}
  ${removeButtonCss}

  &.is-toggle {
    cursor: pointer;
  }
  &:disabled,
  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${brand13.focusRing};
  }
`;
var StyledTagSpan = styled11.span`
  ${tagCss}
`;
var StyledTagButton = styled11.button`
  appearance: none;
  ${tagCss}
`;
var formatTagCount = (n) => {
  if (!Number.isFinite(n) || n < 1e3) return String(n);
  const k = Math.round(n / 100) / 10;
  return `${Number.isInteger(k) ? k.toFixed(0) : k.toFixed(1)}k`;
};
var CrossIcon = /* @__PURE__ */ jsx11("svg", { viewBox: "0 0 10 10", fill: "none", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx11("path", { d: "M2 2l6 6M8 2l-6 6" }) });
var Tag = React8.forwardRef(function Tag2(props, ref) {
  const {
    surface: surface31,
    tone = "neutral",
    size = "default",
    dot = false,
    dotTone,
    icon: icon2,
    count,
    removable: removableProp = false,
    onRemove,
    removeLabel,
    disabled = false,
    className,
    children
  } = props;
  const { "aria-label": ariaLabel, tabIndex, id, onKeyDown } = props;
  const title = props.title || void 0;
  const toneKey = variant(
    TONE_KEYS2,
    tone === "peach" ? "pink" : tone,
    "neutral",
    "Tag",
    "tone"
  );
  const isCode = toneKey === "code";
  const toggle = props.toggle === true && !isCode;
  const removable = removableProp && !isCode;
  const sizeKey = isCode ? "default" : variant(SIZE3, size, "default", "Tag", "size");
  const surfaceKey = variant(SURFACE_CSS2, surface31, "on-glass", "Tag", "surface");
  const namedRemovable = !toggle && removable && !!ariaLabel;
  const active = toggle ? props.active : false;
  const onToggle = toggle ? props.onToggle : void 0;
  const dotToneKey = isDotTone(dotTone) ? dotTone : void 0;
  if (process.env.NODE_ENV !== "production") {
    if (dotTone !== void 0 && dotToneKey === void 0) {
      console.warn(
        `[@repo/design-system] Tag: unknown \`dotTone\` value ${JSON.stringify(dotTone)}. Expected one of ${Object.keys(DOT_HUE).map((k) => `"${k}"`).join(" | ")}. Falling back to the tag's ink.`
      );
    }
    if (dotTone !== void 0 && !dot) {
      console.warn("Tag: `dotTone` has no effect without `dot`.");
    }
    if (removable && !onRemove) {
      console.warn("Tag: `removable` requires `onRemove` \u2014 the dismiss control would do nothing.");
    }
    if (isCode && (props.toggle === true || removableProp)) {
      console.warn(
        "Tag: the `code` chip is static only (card 05) \u2014 `toggle` and `removable` are ignored."
      );
    }
  }
  const classes = [
    "tl-tag",
    surfaceKey,
    SIZE3[sizeKey].className,
    toneKey === "neutral" ? "" : `t-${toneKey}`,
    toggle ? "is-toggle" : "",
    active ? "is-active" : "",
    removable ? "is-removable" : "",
    disabled ? "is-disabled" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  const content = /* @__PURE__ */ jsxs9(Fragment3, { children: [
    dot ? /* @__PURE__ */ jsx11(
      "span",
      {
        className: dotToneKey ? `tl-tag-dot dt-${dotToneKey}` : "tl-tag-dot",
        "aria-hidden": "true"
      }
    ) : null,
    icon2 ? /* @__PURE__ */ jsx11("span", { className: "tl-tag-icon", "aria-hidden": "true", children: icon2 }) : null,
    namedRemovable ? /* @__PURE__ */ jsxs9(Fragment3, { children: [
      /* @__PURE__ */ jsx11("span", { "aria-hidden": "true", children }),
      /* @__PURE__ */ jsx11("span", { className: "tl-tag-sr", children: ariaLabel })
    ] }) : children,
    typeof count === "number" ? /* @__PURE__ */ jsxs9(Fragment3, { children: [
      " ",
      /* @__PURE__ */ jsx11("span", { className: count === 0 ? "n is-zero" : "n", children: formatTagCount(count) })
    ] }) : null,
    removable ? /* @__PURE__ */ jsx11(
      "button",
      {
        type: "button",
        className: "tl-tag-x",
        "aria-label": removeLabel ?? `Remove ${typeof children === "string" ? children : "tag"}`,
        disabled,
        onClick: (event) => {
          event.stopPropagation();
          onRemove?.();
        },
        children: CrossIcon
      }
    ) : null
  ] });
  if (toggle) {
    return /* @__PURE__ */ jsx11(
      StyledTagButton,
      {
        ref,
        type: "button",
        className: classes,
        $surface: surfaceKey,
        $tone: toneKey,
        $size: sizeKey,
        $dotTone: dot ? dotToneKey : void 0,
        "aria-pressed": active,
        "aria-label": ariaLabel,
        title,
        tabIndex,
        id,
        onKeyDown,
        disabled,
        onClick: onToggle,
        children: content
      }
    );
  }
  return /* @__PURE__ */ jsx11(
    StyledTagSpan,
    {
      ref,
      className: classes,
      $surface: surfaceKey,
      $tone: toneKey,
      $size: sizeKey,
      $dotTone: dot ? dotToneKey : void 0,
      role: ariaLabel && !namedRemovable ? "img" : void 0,
      "aria-label": namedRemovable ? void 0 : ariaLabel,
      title,
      children: content
    }
  );
});
Tag.displayName = "Tag";

// src/Selection/FilterChipGroup.tsx
import { useId as useId2 } from "react";
import styled12 from "styled-components";
import {
  fontMono as fontMono5,
  inset as inset4,
  ink as ink12,
  radius as radius10,
  space as space4,
  tracking,
  typeRole as typeRole4,
  weight as weight5
} from "@repo/design-tokens";
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var FilterChipCaption = styled12.span`
  font-family: ${fontMono5};
  font-size: ${typeRole4.captionCompact.fontSize};
  line-height: 1;
  letter-spacing: ${tracking.label};
  text-transform: uppercase;
  font-weight: ${weight5.medium};
  color: ${ink12[4]};
`;
var Row2 = styled12.div`
  display: flex;
  align-items: center;
  gap: ${space4[2]};
  flex-wrap: wrap;
`;
var Stack = styled12.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${inset4[3]};
`;
var ChipDot = styled12.span`
  width: 7px;
  height: 7px;
  border-radius: ${radius10.pill};
  background: ${(p) => p.$color};
  flex: none;
`;
function FilterChipGroup({
  label,
  caption,
  chips,
  onToggle,
  size = "dense",
  className
}) {
  const captionId = `filter-chip-caption-${useId2()}`;
  if (process.env.NODE_ENV !== "production") {
    const seen = /* @__PURE__ */ new Set();
    for (const chip of chips) {
      if (seen.has(chip.key)) {
        console.warn(
          `[@repo/design-system] FilterChipGroup "${label}": two chips share the key ${JSON.stringify(chip.key)}.`
        );
      }
      seen.add(chip.key);
    }
  }
  const tags = chips.map((chip) => /* @__PURE__ */ jsx12(
    Tag,
    {
      surface: "on-glass",
      size,
      toggle: true,
      active: chip.active,
      onToggle: () => onToggle(chip.key),
      count: chip.count,
      "aria-label": chip["aria-label"],
      ...chip.dot ? { icon: /* @__PURE__ */ jsx12(ChipDot, { $color: chip.dot }) } : {},
      children: chip.label
    },
    chip.key
  ));
  if (caption === "above") {
    return /* @__PURE__ */ jsxs10(Stack, { role: "group", "aria-labelledby": captionId, className, children: [
      /* @__PURE__ */ jsx12(FilterChipCaption, { id: captionId, "aria-hidden": "true", children: label }),
      /* @__PURE__ */ jsx12(Row2, { children: tags })
    ] });
  }
  if (caption === "inline") {
    return /* @__PURE__ */ jsxs10(Row2, { role: "group", "aria-labelledby": captionId, className, children: [
      /* @__PURE__ */ jsx12(FilterChipCaption, { id: captionId, "aria-hidden": "true", children: label }),
      tags
    ] });
  }
  return /* @__PURE__ */ jsx12(Row2, { role: "group", "aria-label": label, className, children: tags });
}

// src/Selection/SourceChip.tsx
import React10, { useEffect as useEffect2, useRef } from "react";
import styled13, { css as css11 } from "styled-components";
import { brand as brand14, focus as focus4, ink as ink13, motion as motion8, radius as radius11, rgba as rgba13, surface as surface8, typeRole as typeRole5 } from "@repo/design-tokens";
import { Fragment as Fragment4, jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var VARIANTS = { source: true, automatic: true };
var DATA_CLASS = {
  has: "has-data",
  none: "no-data",
  refused: "refused-data",
  excluded: "excluded-data",
  gone: "gone-data"
};
function usePreview(onPreview, enabled) {
  const callback = useRef(onPreview);
  callback.current = onPreview;
  const owed = useRef(void 0);
  const state = useRef({ hovered: false, focused: false, active: false });
  const report = (next) => {
    if (next) {
      owed.current = callback.current;
      owed.current?.(true);
    } else {
      const cb = owed.current ?? callback.current;
      owed.current = void 0;
      cb?.(false);
    }
  };
  const sync = () => {
    const s = state.current;
    const next = enabled && (s.hovered || s.focused);
    if (next !== s.active) {
      s.active = next;
      report(next);
    }
  };
  const keyboardFocused = (el) => {
    try {
      return el.matches(":focus-visible");
    } catch {
      return true;
    }
  };
  useEffect2(() => {
    if (!enabled) {
      state.current.hovered = false;
      state.current.focused = false;
      sync();
    }
  }, [enabled]);
  useEffect2(
    () => () => {
      if (state.current.active) {
        state.current.active = false;
        report(false);
      }
    },
    []
  );
  return {
    onPointerEnter: (e) => {
      if (e.pointerType === "touch") return;
      state.current.hovered = true;
      sync();
    },
    onPointerLeave: (e) => {
      if (e.pointerType === "touch") return;
      state.current.hovered = false;
      sync();
    },
    onFocus: (e) => {
      state.current.focused = keyboardFocused(e.currentTarget);
      sync();
    },
    // `:focus-visible` can switch on while focus stays put — a key pressed on a chip focused by a
    // click — so a key on the focused chip samples it again.
    onKeyDown: (e) => {
      state.current.focused = keyboardFocused(e.currentTarget);
      sync();
    },
    onKeyUp: (e) => {
      state.current.focused = keyboardFocused(e.currentTarget);
      sync();
    },
    onBlur: () => {
      state.current.focused = false;
      sync();
    }
  };
}
var REST_ALPHA = 0.7;
var HOVER_ALPHA = 0.95;
var AUTO_ALPHA = 0.5;
var chipCss = css11`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  margin: 0;
  padding: 2px 8px 3px;
  border-radius: ${radius11.pill};
  background: ${rgba13(surface8[0], REST_ALPHA)};
  border: 1px solid ${rgba13(surface8[0], 0.95)};
  box-shadow: 0 1px 2px ${rgba13(ink13[1], 0.08)};
  color: ${ink13[2]};
  font-family: ${typeRole5.caption1Strong.fontFamily};
  font-size: ${typeRole5.caption1Strong.fontSize};
  line-height: ${typeRole5.caption1Strong.lineHeight};
  font-weight: ${typeRole5.caption1Strong.fontWeight};
  text-align: left;
  /* The card writes var(--tl-dur-fast); held as the card's 0.15s for the reason Tag gives. */
  transition:
    background 0.15s ${motion8.easeUi},
    border-color 0.15s ${motion8.easeUi},
    box-shadow 0.15s ${motion8.easeUi},
    color 0.15s ${motion8.easeUi};

  .tl-src-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex: none;
    opacity: 1;
  }
  .tl-src-dot.has-data {
    background: ${DOT_HUE.mint};
  }
  .tl-src-dot.no-data {
    background: ${DOT_HUE.violet};
  }
  .tl-src-dot.refused-data,
  .tl-src-dot.gone-data {
    background: ${DOT_HUE.error};
  }
  .tl-src-dot.excluded-data {
    background: ${DOT_HUE["ink-5"]};
  }
  .tl-src-icon {
    display: inline-flex;
    align-items: center;
    flex: none;
    color: ${ink13[3]};
    line-height: 1;
  }
  .tl-src-sr {
    ${visuallyHiddenCss}
  }
  /* The label truncates, not the pill (EntitySpotlight's fix for the same trap). */
  .tl-src-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
var previewLookCss = css11`
  background: ${rgba13(brand14.frost, HOVER_ALPHA)};
  border-color: ${rgba13(brand14.cyan, 0.45)};
  color: ${brand14.cyanPress};
  text-decoration: underline;
  text-underline-offset: 2px;
  outline: none;
`;
var StyledChipButton = styled13.button`
  appearance: none;
  ${chipCss}
  cursor: pointer;

  /* Hover only where the device really hovers: a touch screen can leave :hover matched after a
     tap, which would show the preview look while onPreview (which ignores touch) says false. */
  @media (hover: hover) {
    &:hover {
      ${previewLookCss}
      box-shadow: 0 0 0 3px ${rgba13(brand14.cyan, 0.3)};
    }
  }
  /* After the hover rule, so a keyboard-focused chip under the pointer keeps the tight ring. */
  &:focus-visible {
    ${previewLookCss}
    box-shadow: ${focus4.tight};
  }
`;
var StyledChipSpan = styled13.span`
  ${chipCss}
  cursor: default;

  &.is-automatic {
    background: ${rgba13(surface8[0], AUTO_ALPHA)};
    box-shadow: none;
    color: ${ink13[3]};
  }
`;
var SourceChip = React10.forwardRef(
  function SourceChip2(props, ref) {
    const {
      children,
      data,
      dotColor,
      icon: icon2,
      onClick,
      onPreview,
      variant: variant2 = "source",
      className,
      id,
      title
    } = props;
    const automatic = variant(VARIANTS, variant2, "source", "SourceChip", "variant") === "automatic";
    const clickable = !!onClick && !automatic;
    const preview = usePreview(onPreview, clickable);
    const ariaLabel = props["aria-label"];
    const ariaDescribedby = props["aria-describedby"];
    const knownData = data !== void 0 && Object.prototype.hasOwnProperty.call(DATA_CLASS, data) ? data : void 0;
    if (process.env.NODE_ENV !== "production") {
      if (automatic && data !== void 0) {
        console.warn("SourceChip: the automatic chip has no dot (card 05) \u2014 `data` is ignored.");
      }
      if (automatic && onClick) {
        console.warn("SourceChip: the automatic chip is static (card 05) \u2014 `onClick` is ignored.");
      }
      if (onPreview && !clickable) {
        console.warn(
          "SourceChip: `onPreview` needs a clickable chip \u2014 a static chip never previews."
        );
      }
      if (data !== void 0 && knownData === void 0) {
        console.warn(
          `[@repo/design-system] SourceChip: unknown \`data\` value ${JSON.stringify(data)}. Expected "has" | "none" | "refused" | "excluded" | "gone". Drawing no dot.`
        );
      }
      if (!clickable && !automatic && knownData && ariaDescribedby && !ariaLabel) {
        console.warn(
          "SourceChip: a static chip does not reliably announce `aria-describedby` \u2014 put the data state in `aria-label`."
        );
      }
    }
    const dotClass = automatic || !knownData ? null : DATA_CLASS[knownData];
    const namedStatic = !clickable && !!ariaLabel;
    const content = /* @__PURE__ */ jsxs11(Fragment4, { children: [
      dotClass ? /* @__PURE__ */ jsx13(
        "span",
        {
          className: `tl-src-dot ${dotClass}`,
          style: dotColor ? { background: dotColor } : void 0,
          "aria-hidden": "true"
        }
      ) : null,
      icon2 ? /* @__PURE__ */ jsx13("span", { className: "tl-src-icon", "aria-hidden": "true", children: icon2 }) : null,
      /* @__PURE__ */ jsx13("span", { className: "tl-src-label", "aria-hidden": namedStatic ? true : void 0, children }),
      namedStatic ? /* @__PURE__ */ jsx13("span", { className: "tl-src-sr", children: ariaLabel }) : null
    ] });
    const shared = { id, title, "aria-describedby": ariaDescribedby };
    if (clickable) {
      return /* @__PURE__ */ jsx13(
        StyledChipButton,
        {
          ref,
          type: "button",
          className: ["tl-src", className].filter(Boolean).join(" "),
          onClick,
          ...preview,
          "aria-label": ariaLabel,
          ...shared,
          children: content
        }
      );
    }
    return /* @__PURE__ */ jsx13(
      StyledChipSpan,
      {
        ref,
        className: ["tl-src", automatic ? "is-automatic" : "", className].filter(Boolean).join(" "),
        ...shared,
        children: content
      }
    );
  }
);
SourceChip.displayName = "SourceChip";

// src/Selection/Dot.tsx
import React11 from "react";
import styled14 from "styled-components";
import { ink as ink14, fontSans as fontSans8, semanticColor as semanticColor5, SELECTED_RAMP, clampStop } from "@repo/design-tokens";
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
var HEX_COLOUR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
var warn = (message) => {
  console.warn(`[@repo/design-system] Dot: ${message}`);
};
var describeSources = (tone, stop, color) => [
  tone !== void 0 && tone !== null ? `tone=${JSON.stringify(tone)}` : "",
  stop !== void 0 && stop !== null ? `stop=${JSON.stringify(stop)}` : "",
  color !== void 0 && color !== null ? `color=${JSON.stringify(color)}` : ""
].filter(Boolean).join(", ");
var TONE_FILL = {
  success: semanticColor5.success,
  warn: semanticColor5.warn,
  error: semanticColor5.error,
  neutral: ink14[5]
};
var StyledDot = styled14.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${fontSans8};
  font-weight: 500;
  font-size: 11.5px;
  line-height: 1;
  color: ${ink14[1]};

  .tl-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: var(--tl-dot-fill);
  }
`;
var HiddenLabel = styled14.span`
  ${visuallyHiddenCss}
`;
var Dot = React11.forwardRef(function Dot2(props, ref) {
  const { label, labelHidden = false, className, tone, stop, color, style, ...rest } = props;
  const hasColor = typeof color === "string" && HEX_COLOUR.test(color);
  const hasStop = typeof stop === "number" && Number.isFinite(stop);
  const resolvedStop = hasStop ? clampStop(stop) : void 0;
  const resolvedTone = hasColor || hasStop ? void 0 : variant(TONE_FILL, tone, "neutral", "Dot", "tone");
  const fill = hasColor ? color : resolvedStop !== void 0 ? SELECTED_RAMP[resolvedStop - 1] : TONE_FILL[resolvedTone ?? "neutral"];
  if (process.env.NODE_ENV !== "production") {
    const given = [tone !== void 0 && tone !== null, hasStop, hasColor].filter(Boolean).length;
    if (given === 0) {
      warn('exactly one of `tone`, `stop` or `color` is required; falling back to "neutral".');
    } else if (given > 1) {
      warn(
        `more than one colour source was given (${describeSources(tone, stop, color)}); exactly one is expected. Resolving by precedence: color, then stop, then tone.`
      );
    }
    if (stop !== void 0 && stop !== null && !hasStop) {
      warn(`\`stop\` must be a finite number; received ${JSON.stringify(stop)}.`);
    } else if (hasStop && (stop < 1 || stop > SELECTED_RAMP.length)) {
      warn(`\`stop\` ${stop} is outside 1-${SELECTED_RAMP.length}; clamped to ${resolvedStop}.`);
    }
    if (color !== void 0 && color !== null && !hasColor) {
      warn(
        `\`color\` must be a hex colour such as "#1BB6D9"; received ${JSON.stringify(color)}. Falling back to the neutral tone.`
      );
    }
  }
  const classes = [
    "tl-status",
    resolvedTone !== void 0 ? `t-${resolvedTone}` : "",
    resolvedStop !== void 0 ? `stop-${resolvedStop}` : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs12(
    StyledDot,
    {
      ref,
      ...rest,
      className: classes,
      style: { ...style, "--tl-dot-fill": fill },
      children: [
        /* @__PURE__ */ jsx14("span", { className: "tl-status-dot", "aria-hidden": "true" }),
        labelHidden ? /* @__PURE__ */ jsx14(HiddenLabel, { children: label }) : label
      ]
    }
  );
});
Dot.displayName = "Dot";

// src/Selection/Avatar.tsx
import { useState } from "react";
import styled15 from "styled-components";
import {
  controlHeight as controlHeight3,
  fontSans as fontSans9,
  glass as glass3,
  ink as ink15,
  shadow as shadow3,
  typeScale as typeScale4,
  weight as weight6
} from "@repo/design-tokens";
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
var initialsFor = (name) => {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const first = words[0].charAt(0);
  if (words.length === 1) return first.toUpperCase();
  return `${first}${words[words.length - 1].charAt(0)}`.toUpperCase();
};
var AvatarComponent = ({ name, src, size = "md", className }) => {
  const [photo, setPhoto] = useState({
    src,
    status: "loading"
  });
  if (photo.src !== src) setPhoto({ src, status: "loading" });
  const status = photo.src === src ? photo.status : "loading";
  const settle = (next) => () => setPhoto((current) => current.src === src ? { src, status: next } : current);
  const showImage = !!src && status === "loaded";
  const classes = ["tl-avatar", `sz-${size}`, showImage ? "has-img" : "", className ?? ""].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs13("span", { className: classes, "aria-hidden": "true", children: [
    src && status !== "failed" ? /* @__PURE__ */ jsx15("img", { src, alt: "", onLoad: settle("loaded"), onError: settle("failed") }, src) : null,
    showImage ? null : /* @__PURE__ */ jsx15("span", { className: "initials", children: initialsFor(name) })
  ] });
};
var Avatar = styled15(AvatarComponent)`
  /* md is the base: card 05's \`.avatar\`. */
  position: relative;
  width: ${controlHeight3.md};
  height: ${controlHeight3.md};
  flex: none;
  border-radius: 50%;
  overflow: hidden;
  background: ${glass3.bg};
  backdrop-filter: ${glass3.blurSm};
  -webkit-backdrop-filter: ${glass3.blurSm};
  box-shadow: ${shadow3.glassSm};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  color: ${ink15[1]};
  font: ${weight6.medium} ${typeScale4.body1.fontSize} / 1 ${fontSans9};

  &.sz-xs {
    width: ${controlHeight3.xs};
    height: ${controlHeight3.xs};
    font-size: ${typeScale4.caption2.fontSize};
  }
  &.sz-sm {
    width: ${controlHeight3.sm};
    height: ${controlHeight3.sm};
    font-size: ${typeScale4.captionCompact.fontSize};
  }
  &.sz-lg {
    width: ${controlHeight3.lg};
    height: ${controlHeight3.lg};
    font-size: ${typeScale4.subtitle2.fontSize};
  }

  /* The photo is laid over the circle; until it has loaded it is invisible and the initials show. */
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
  }
  &.has-img {
    background: ${ink15[4]};
    color: ${ink15.inverse};
  }
  &.has-img img {
    opacity: 1;
  }
`;

// src/Selection/avatarIdentity.ts
var isBlank = (value) => !value?.trim();
var avatarInitialsName = (name, email) => {
  if (!isBlank(name)) return name;
  if (!isBlank(email)) return email.trim().charAt(0);
  return void 0;
};
var accountMenuLabel = (name) => isBlank(name) ? "Account menu" : `Account menu for ${name}`;

// src/Toggles/Checkbox.tsx
import React13 from "react";
import styled16, { css as css12 } from "styled-components";

// src/internal/toggleHues.ts
import {
  SELECTED_RAMP as SELECTED_RAMP2,
  accent as accent5,
  rgbTriple,
  support as support5,
  surface as surfaceToken2
} from "@repo/design-tokens";
var TOGGLE_HUES = {
  aqua: { off: rgbTriple(SELECTED_RAMP2[1]), on: rgbTriple(SELECTED_RAMP2[6]) },
  mint: { off: rgbTriple(SELECTED_RAMP2[1]), on: rgbTriple(support5.mint) },
  peri: { off: "199,217,247", on: rgbTriple(support5.periwinkle) },
  lavender: { off: rgbTriple(surfaceToken2[4]), on: rgbTriple(support5.lavender) },
  pink: { off: "247,219,235", on: rgbTriple(accent5.peach) },
  peach: { off: "255,234,220", on: rgbTriple(accent5.butter) }
};
var CONTROL_HUES = {
  aqua: TOGGLE_HUES.aqua,
  mint: TOGGLE_HUES.mint,
  peri: TOGGLE_HUES.peri,
  lavender: TOGGLE_HUES.lavender
};

// src/Toggles/Checkbox.tsx
import { ink as ink16, surface as surfaceToken3, semanticColor as semanticColor6, fontSans as fontSans10, rgba as rgba14 } from "@repo/design-tokens";
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
var BOX = "18px";
var BOX_RADIUS = "5px";
var TICK = "12px";
var onGlassBoxCss = css12`
  .tl-cb-box {
    background: rgba(var(--tl-cb-off), 0.22);
    border: 1px solid rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.55),
      0 0 6px 1px rgba(var(--tl-cb-off), 0.18);
  }
  input:checked + .tl-cb-box,
  &.is-indeterminate .tl-cb-box {
    background: rgba(var(--tl-cb-on), 0.65);
    border-color: rgba(255, 255, 255, 0.7);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.55),
      0 0 10px 2px rgba(var(--tl-cb-on), 0.45);
  }
`;
var onSolidBoxCss = css12`
  .tl-cb-box {
    background: linear-gradient(
      180deg,
      rgba(var(--tl-cb-on), 0.1) 0%,
      rgba(var(--tl-cb-off), 0.16) 100%
    );
    border: 1px solid transparent;
    border-top-color: rgba(var(--tl-cb-on), 0.55);
    border-left-color: rgba(var(--tl-cb-on), 0.5);
    border-bottom-color: rgba(var(--tl-cb-on), 0.28);
    border-right-color: rgba(var(--tl-cb-on), 0.3);
    box-shadow:
      inset 1px 1px 0 rgba(255, 255, 255, 0.55),
      1px 2px 4px -2px rgba(var(--tl-cb-on), 0.3),
      0 0 0 1px rgba(var(--tl-cb-on), 0.06);
  }
  input:checked + .tl-cb-box,
  &.is-indeterminate .tl-cb-box {
    background: linear-gradient(
      180deg,
      rgba(var(--tl-cb-on), 0.95) 0%,
      rgba(var(--tl-cb-on), 0.75) 100%
    );
    border-top-color: rgba(var(--tl-cb-on), 0.95);
    border-left-color: rgba(var(--tl-cb-on), 0.85);
    border-bottom-color: rgba(var(--tl-cb-on), 0.55);
    border-right-color: rgba(var(--tl-cb-on), 0.6);
    box-shadow:
      inset 1px 1px 0 rgba(255, 255, 255, 0.45),
      1px 2px 5px -1px rgba(var(--tl-cb-on), 0.5),
      0 0 0 1px rgba(var(--tl-cb-on), 0.1);
  }
`;
var StyledLabel = styled16.label`
  --tl-cb-off: ${({ $hue }) => CONTROL_HUES[$hue].off};
  --tl-cb-on: ${({ $hue }) => CONTROL_HUES[$hue].on};

  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: ${fontSans10};
  font-size: 13px;
  line-height: 1.35;
  color: ${ink16[1]};

  /* The native input carries all state + semantics; it is visually hidden, NOT
     removed — display:none or visibility:hidden would take it out of the
     accessibility tree and off the focus path. */
  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
  }

  .tl-cb-box {
    width: ${BOX};
    height: ${BOX};
    border-radius: ${BOX_RADIUS};
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
    transition: all 0.18s ease;
  }
  .tl-cb-box svg {
    width: ${TICK};
    height: ${TICK};
    color: ${surfaceToken3[0]};
    opacity: 0;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  input:checked + .tl-cb-box svg {
    opacity: 1;
  }

  ${({ $surface }) => $surface === "on-glass" ? onGlassBoxCss : onSolidBoxCss}

  /* INDETERMINATE — the minus bar. Suppresses the tick even when checked is also
     true, matching native precedence. */
  &.is-indeterminate .tl-cb-box svg {
    opacity: 0;
  }
  &.is-indeterminate .tl-cb-box::after {
    content: '';
    display: block;
    position: absolute;
    width: 10px;
    height: 2px;
    border-radius: 1px;
    background: ${surfaceToken3[0]};
  }

  /* Focus is driven by the real input's :focus-visible, so it only shows for keyboard. */
  input:focus-visible + .tl-cb-box {
    outline: none;
    box-shadow:
      0 0 0 3px rgba(var(--tl-cb-on), 0.32),
      inset 1px 1px 0 rgba(255, 255, 255, 0.55);
  }

  &.is-disabled {
    cursor: not-allowed;
    color: ${ink16[4]};
  }
  &.is-disabled .tl-cb-box {
    opacity: 0.5;
  }

  /* Card 03 draws the checkbox's and the switch's error with the same --tl-error hairline. */
  &.is-error .tl-cb-box {
    border-color: ${semanticColor6.error};
    box-shadow:
      0 0 0 1px ${semanticColor6.error},
      1px 2px 4px -2px ${rgba14(semanticColor6.error, 0.4)};
  }
`;
var TickIcon = /* @__PURE__ */ jsx16("svg", { viewBox: "0 0 14 14", fill: "none", stroke: "currentColor", strokeWidth: 2.2, "aria-hidden": "true", children: /* @__PURE__ */ jsx16("path", { d: "M2.5 7.5l3 3 6-6.5" }) });
var Checkbox = React13.forwardRef(
  function Checkbox2(props, ref) {
    const {
      children,
      surface: surface31 = "on-glass",
      hue = "aqua",
      indeterminate = false,
      error = false,
      disabled = false,
      className,
      ...inputProps
    } = props;
    const innerRef = React13.useRef(null);
    React13.useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = indeterminate;
    });
    const setRefs = React13.useCallback(
      (node) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );
    const classes = [
      "altcb",
      surface31,
      `hue-${hue}`,
      indeterminate ? "is-indeterminate" : "",
      disabled ? "is-disabled" : "",
      error ? "is-error" : "",
      className ?? ""
    ].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsxs14(
      StyledLabel,
      {
        className: classes,
        $surface: surface31 === "on-glass" ? "on-glass" : "on-solid",
        $hue: variant(CONTROL_HUES, hue, "aqua", "Checkbox", "hue"),
        children: [
          /* @__PURE__ */ jsx16(
            "input",
            {
              ref: setRefs,
              type: "checkbox",
              disabled,
              "aria-checked": indeterminate ? "mixed" : void 0,
              "aria-invalid": error || void 0,
              ...inputProps
            }
          ),
          /* @__PURE__ */ jsx16("span", { className: "tl-cb-box", "aria-hidden": "true", children: TickIcon }),
          children != null ? /* @__PURE__ */ jsx16("span", { className: "tl-cb-label", children }) : null
        ]
      }
    );
  }
);
Checkbox.displayName = "Checkbox";

// src/Toggles/Switch.tsx
import React14 from "react";
import styled17, { css as css13 } from "styled-components";
import {
  brand as brand15,
  ink as ink17,
  inkGlass as inkGlass5,
  inkOn as inkOn5,
  surface as surfaceToken4,
  semanticColor as semanticColor7,
  fontSans as fontSans11,
  fontMono as fontMono6,
  motion as motion9,
  radius as radius12,
  rgba as rgba15
} from "@repo/design-tokens";
import { Fragment as Fragment5, jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var WHITE = surfaceToken4[0];
var onGlassCss3 = css13`
  --tl-sw-focus: ${rgba15(brand15.cyan, 0.25)};

  .altsw-track {
    background: rgba(var(--tl-sw-off), 0.28);
    border: 1px solid ${rgba15(WHITE, 0.55)};
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    box-shadow:
      inset 0 1px 1px ${rgba15(WHITE, 0.6)},
      0 0 8px 1px rgba(var(--tl-sw-off), 0.18);
  }
  .altsw-thumb {
    background: linear-gradient(180deg, ${WHITE}, ${surfaceToken4[3]});
    box-shadow:
      0 1px 3px ${rgba15(ink17[1], 0.2)},
      inset 0 1px 0 ${rgba15(WHITE, 0.95)};
  }
  input:checked + .altsw-track {
    background: rgba(var(--tl-sw-on), 0.5);
    border-color: ${rgba15(WHITE, 0.75)};
    box-shadow:
      inset 0 1px 1px ${rgba15(WHITE, 0.65)},
      0 0 14px 2px ${rgba15(brand15.frost, 0.65)};
  }
  input:focus-visible + .altsw-track {
    box-shadow:
      0 0 0 3px var(--tl-sw-focus),
      inset 0 1px 1px ${rgba15(WHITE, 0.55)};
  }
  .altsw-sub {
    color: ${rgba15(ink17[1], 0.75)};
  }
  .altsw-side {
    color: ${rgba15(ink17[1], 0.65)};
  }
  .altsw-sub.is-error {
    color: ${inkGlass5.error};
  }
`;
var onSolidCss3 = css13`
  --tl-sw-focus: rgba(var(--tl-sw-on), 0.32);

  .altsw-track {
    background: linear-gradient(
      180deg,
      rgba(var(--tl-sw-on), 0.1) 0%,
      rgba(var(--tl-sw-off), 0.18) 100%
    );
    border: 1px solid transparent;
    border-top-color: rgba(var(--tl-sw-on), 0.65);
    border-left-color: rgba(var(--tl-sw-on), 0.55);
    border-bottom-color: rgba(var(--tl-sw-on), 0.28);
    border-right-color: rgba(var(--tl-sw-on), 0.32);
    box-shadow:
      inset 1px 1px 0 ${rgba15(WHITE, 0.55)},
      inset -1px -1px 1px rgba(var(--tl-sw-on), 0.18),
      2px 3px 6px -2px rgba(var(--tl-sw-on), 0.32),
      0 0 0 1px rgba(var(--tl-sw-on), 0.06);
  }
  .altsw-thumb {
    background: linear-gradient(180deg, ${WHITE} 0%, ${surfaceToken4[3]} 100%);
    box-shadow:
      0 1px 2px rgba(var(--tl-sw-on), 0.3),
      inset 0 1px 0 ${WHITE},
      inset 0 -1px 1px rgba(var(--tl-sw-on), 0.1);
  }
  input:checked + .altsw-track {
    background: linear-gradient(
      180deg,
      rgba(var(--tl-sw-on), 0.62) 0%,
      rgba(var(--tl-sw-on), 0.42) 100%
    );
    border-top-color: rgba(var(--tl-sw-on), 0.85);
    border-left-color: rgba(var(--tl-sw-on), 0.75);
    border-bottom-color: rgba(var(--tl-sw-on), 0.5);
    border-right-color: rgba(var(--tl-sw-on), 0.55);
    box-shadow:
      inset 1px 1px 0 ${rgba15(WHITE, 0.65)},
      inset -1px -1px 1px rgba(var(--tl-sw-on), 0.3),
      2px 3px 8px -2px rgba(var(--tl-sw-on), 0.45),
      0 0 0 1px rgba(var(--tl-sw-on), 0.1);
  }
  /* Hue-aware focus halo that keeps the bezel and the bloom. */
  input:focus-visible + .altsw-track {
    box-shadow:
      0 0 0 3px var(--tl-sw-focus),
      inset 1px 1px 0 ${rgba15(WHITE, 0.55)},
      2px 3px 6px -2px rgba(var(--tl-sw-on), 0.4);
  }
  .altsw-sub {
    color: ${ink17[3]};
  }
  .altsw-side {
    color: ${ink17[3]};
  }
  .altsw-sub.is-error {
    color: ${inkOn5.error};
  }
`;
var StyledRoot = styled17.label`
  --tl-sw-off: ${({ $hue }) => TOGGLE_HUES[$hue].off};
  --tl-sw-on: ${({ $hue }) => TOGGLE_HUES[$hue].on};

  /* Tripled class (0,3,0) so a host's descendant-label rule — AdminForm's
     "label { width: 220px }" (0,1,1), a page's ".field label { display: block }" (0,2,1) —
     cannot restyle the row. */
  &&& {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    width: auto;
    min-width: 0;
    margin: 0;
    padding: 0;
    font-family: ${fontSans11};
    font-weight: 400;
    color: ${ink17[1]};
    cursor: pointer;
  }

  /* Trailing row: the whole row is the hit target, at least 44px tall (card 03). */
  &&&.has-text {
    display: flex;
    min-height: 44px;
    padding: 4px 0;
    box-sizing: border-box;
  }

  /* Bipolar: the description wraps to its own line under the row. */
  &&&.is-bipolar {
    flex-wrap: wrap;
    row-gap: 2px;
  }
  &.is-bipolar .altsw-text {
    flex-basis: 100%;
  }

  .altsw-control {
    position: relative;
    display: inline-block;
    width: 50px;
    height: 26px;
    flex-shrink: 0;
  }

  /* The native input carries all state and semantics. Visually hidden, NOT removed —
     display:none would take it out of the accessibility tree and off the focus path. */
  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
  }

  .altsw-track {
    position: absolute;
    inset: 0;
    border-radius: ${radius12.pill};
    box-sizing: border-box;
    transition: all ${motion9.durFast} ${motion9.easeUi};
  }
  .altsw-thumb {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    transition:
      transform ${motion9.durFast} ${motion9.easeUi},
      box-shadow ${motion9.durFast} ${motion9.easeUi},
      background ${motion9.durFast} ${motion9.easeUi};
  }
  input:checked + .altsw-track .altsw-thumb {
    transform: translateX(24px);
  }

  .altsw-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  /* A bare control keeps its (empty) live region in the accessibility tree but out of the
     layout: absolutely positioned, it is not a flex item, so it adds no gap. */
  &:not(.has-text):not(.has-desc) .altsw-text {
    position: absolute;
    width: 0;
    height: 0;
    overflow: hidden;
  }
  .altsw-label {
    font: 500 13px/1.3 ${fontSans11};
    color: ${ink17[1]};
  }
  .altsw-sub {
    font: 400 11px/1.4 ${fontMono6};
  }
  /* Empty: clipped out of the layout but kept in the accessibility tree — display:none would
     remove the live region, and one that appears together with its text is not announced. */
  .altsw-sub:empty {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .altsw-side {
    font: 500 12.5px/1.3 ${fontSans11};
    transition: color ${motion9.durFast} ${motion9.easeUi};
  }

  ${({ $surface }) => $surface === "on-glass" ? onGlassCss3 : onSolidCss3}

  /* After the surface rules, so the active side wins over the muted side colour. Read from
     the input's own state, never a React copy of it. */
  &:has(input:checked) .altsw-side-on,
  &:not(:has(input:checked)) .altsw-side-off {
    color: ${ink17[1]};
  }

  /* Error — the same hairline as the checkbox's, hue-agnostic. Doubled selectors so it beats
     the surface's checked rule (input:checked + track) as well as its off rule. */
  &.is-error input + .altsw-track,
  &.is-error input:checked + .altsw-track {
    border-color: ${semanticColor7.error};
    box-shadow:
      0 0 0 1px ${semanticColor7.error},
      inset 0 1px 1px ${rgba15(WHITE, 0.55)};
  }
  /* ...but keyboard focus stays visible on an errored switch: the surface's focus ring, outside
     the error hairline. */
  &.is-error input:focus-visible + .altsw-track {
    box-shadow:
      0 0 0 1px ${semanticColor7.error},
      0 0 0 4px var(--tl-sw-focus),
      inset 0 1px 1px ${rgba15(WHITE, 0.55)};
  }

  /* Disabled is hue-neutral: an opacity drop on the control; the whole row dims together. */
  &.is-disabled {
    cursor: not-allowed;
  }
  &.is-disabled .altsw-track {
    opacity: 0.5;
  }
  &.is-disabled .altsw-thumb {
    box-shadow: 0 1px 2px ${rgba15(ink17[1], 0.1)};
  }
  &.is-disabled .altsw-label,
  &.is-disabled .altsw-sub,
  &.is-disabled .altsw-side {
    color: ${ink17[4]};
    opacity: 0.65;
  }
`;
var Switch = React14.forwardRef(function Switch2(props, ref) {
  const {
    children,
    description,
    offLabel,
    onLabel,
    surface: surface31 = "on-glass",
    hue = "aqua",
    error = false,
    disabled = false,
    className,
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    ...inputProps
  } = props;
  const autoId = React14.useId();
  const baseId = id ?? `tl-switch-${autoId.replace(/:/g, "")}`;
  const descId = `${baseId}-desc`;
  const onSideId = `${baseId}-on`;
  const labelId = `${baseId}-label`;
  const hasOff = offLabel != null;
  const hasOn = onLabel != null;
  const bipolar = hasOff && hasOn;
  React14.useEffect(() => {
    if (process.env.NODE_ENV !== "production" && hasOff !== hasOn) {
      console.error(
        "Switch: pass both `offLabel` and `onLabel` for the bipolar pattern, or neither. Rendering the trailing-label pattern."
      );
    }
  }, [hasOff, hasOn]);
  const hasDescription = description != null && description !== false && description !== "";
  const hasText = !bipolar && (children != null || hasDescription);
  const describedBy = [ariaDescribedBy, hasDescription ? descId : void 0].filter(Boolean).join(" ") || void 0;
  const labelledBy = ariaLabelledBy ?? (ariaLabel != null ? void 0 : bipolar ? onSideId : children != null ? labelId : void 0);
  const classes = [
    "altsw",
    surface31,
    `hue-${hue}`,
    hasText ? "has-text" : "",
    bipolar ? "is-bipolar" : "",
    bipolar && hasDescription ? "has-desc" : "",
    disabled ? "is-disabled" : "",
    error ? "is-error" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  const control = /* @__PURE__ */ jsxs15("span", { className: "altsw-control", children: [
    /* @__PURE__ */ jsx17(
      "input",
      {
        ...inputProps,
        ref,
        id: baseId,
        type: "checkbox",
        role: "switch",
        disabled,
        "aria-label": ariaLabel,
        "aria-labelledby": labelledBy,
        "aria-describedby": describedBy,
        "aria-invalid": error || void 0
      }
    ),
    /* @__PURE__ */ jsx17("span", { className: "altsw-track", "aria-hidden": "true", children: /* @__PURE__ */ jsx17("span", { className: "altsw-thumb" }) })
  ] });
  return /* @__PURE__ */ jsxs15(
    StyledRoot,
    {
      className: classes,
      $surface: surface31 === "on-solid" ? "on-solid" : "on-glass",
      $hue: variant(TOGGLE_HUES, hue, "aqua", "Switch", "hue"),
      children: [
        bipolar ? /* @__PURE__ */ jsxs15(Fragment5, { children: [
          /* @__PURE__ */ jsx17("span", { className: "altsw-side altsw-side-off", children: offLabel }),
          control,
          /* @__PURE__ */ jsx17("span", { id: onSideId, className: "altsw-side altsw-side-on", children: onLabel })
        ] }) : control,
        /* @__PURE__ */ jsxs15("span", { className: "altsw-text", children: [
          !bipolar && children != null ? /* @__PURE__ */ jsx17("span", { id: labelId, className: "altsw-label", children }) : null,
          /* @__PURE__ */ jsx17("span", { id: descId, className: `altsw-sub${error ? " is-error" : ""}`, "aria-live": "polite", children: description })
        ] })
      ]
    }
  );
});
Switch.displayName = "Switch";

// src/Toggles/Radio.tsx
import React16 from "react";
import styled19, { css as css14 } from "styled-components";
import { focus as focus5, ink as ink18, motion as motion10, surface as surfaceToken5, fontSans as fontSans13 } from "@repo/design-tokens";

// src/Toggles/RadioGroup.tsx
import React15 from "react";
import styled18 from "styled-components";
import { fontSans as fontSans12 } from "@repo/design-tokens";
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
var RadioGroupContext = React15.createContext(null);
var Root4 = styled18.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap};
  min-width: 0;
`;
var Options = styled18.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: 10px;
  min-width: 0;
`;
var VALID_COLUMNS = [2, 3, 4, 5];
var GroupLabel = styled18.div`
  font: ${FIELD_LABEL.labelFont} ${fontSans12};
  color: ${FIELD_LABEL.labelInk};
  margin-bottom: ${FIELD_LABEL.gapCompact};
`;
function RadioGroup(props) {
  const {
    value,
    onChange,
    name,
    surface: surface31,
    hue,
    disabled = false,
    variant: variant2 = "radio",
    columns,
    label,
    children,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...rest
  } = props;
  const autoId = React15.useId();
  const groupName = name ?? `tl-radio-${autoId}`;
  const labelId = `${autoId}-label`;
  const rootRef = React15.useRef(null);
  const hasLabel = label != null && label !== false && label !== "";
  const resolvedHue = hue === void 0 ? void 0 : variant(CONTROL_HUES, hue, "aqua", "RadioGroup", "hue");
  React15.useEffect(() => {
    if (process.env.NODE_ENV !== "production" && !hasLabel && !ariaLabel && !ariaLabelledBy) {
      console.warn(
        "[@repo/design-system] RadioGroup: the group has no name. Pass `label`, `aria-label` or `aria-labelledby`."
      );
    }
  }, [hasLabel, ariaLabel, ariaLabelledBy]);
  const gridColumns = columns !== void 0 && variant2 === "card" && VALID_COLUMNS.includes(columns) ? columns : void 0;
  React15.useEffect(() => {
    if (process.env.NODE_ENV === "production" || columns === void 0) return;
    if (variant2 !== "card") {
      console.warn(
        '[@repo/design-system] RadioGroup: `columns` applies to option cards (`variant="card"`) only. Plain radios side by side are a segmented control (card 03), so it is ignored.'
      );
    } else if (!VALID_COLUMNS.includes(columns)) {
      console.warn(
        `[@repo/design-system] RadioGroup: \`columns\` must be 2 to 5 (card 03 caps a group at five); got ${JSON.stringify(columns)}, so it is ignored.`
      );
    }
  }, [columns, variant2]);
  React15.useEffect(() => {
    if (process.env.NODE_ENV === "production" || !rootRef.current || value === "") return;
    const values = Array.from(
      rootRef.current.querySelectorAll('input[type="radio"]')
    ).filter((input) => !input.closest(".tl-rdc-slot")).map((input) => input.value);
    if (values.length > 0 && !values.includes(value)) {
      console.warn(
        `[@repo/design-system] RadioGroup "${groupName}": value ${JSON.stringify(value)} matches no option, so nothing is selected.`
      );
    }
  }, [value, groupName, children]);
  const ctx = React15.useMemo(
    () => ({ name: groupName, value, onChange, surface: surface31, hue: resolvedHue, disabled }),
    [groupName, value, onChange, surface31, resolvedHue, disabled]
  );
  const labelledBy = [hasLabel ? labelId : null, ariaLabelledBy].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx18(RadioGroupContext.Provider, { value: ctx, children: /* @__PURE__ */ jsxs16(
    Root4,
    {
      ...rest,
      ref: rootRef,
      role: "radiogroup",
      "aria-label": ariaLabel,
      "aria-labelledby": labelledBy || void 0,
      "aria-disabled": disabled || void 0,
      $gap: variant2 === "card" ? "8px" : "10px",
      children: [
        hasLabel ? /* @__PURE__ */ jsx18(GroupLabel, { id: labelId, children: label }) : null,
        gridColumns !== void 0 ? /* @__PURE__ */ jsx18(Options, { className: "tl-rg-options", $columns: gridColumns, children }) : children
      ]
    }
  ) });
}
RadioGroup.displayName = "RadioGroup";

// src/Toggles/Radio.tsx
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
var RADIO_COLOURS = {
  /** Label ink. */
  label: ink18[1],
  /** Description ink. */
  description: ink18[2],
  /** Disabled label and description ink. */
  disabled: ink18[4]
};
var DOT = "18px";
var INNER = "8px";
var onGlassDotCss = css14`
  .tl-rd-dot {
    background: rgba(var(--tl-rd-off), 0.22);
    border: 1px solid rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.55),
      0 0 6px 1px rgba(var(--tl-rd-off), 0.18);
  }
  input:checked + .tl-rd-dot {
    background: rgba(var(--tl-rd-on), 0.65);
    border-color: rgba(255, 255, 255, 0.7);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.55),
      0 0 10px 2px rgba(var(--tl-rd-on), 0.45);
  }
`;
var onSolidDotCss = css14`
  .tl-rd-dot {
    background: linear-gradient(
      180deg,
      rgba(var(--tl-rd-on), 0.1) 0%,
      rgba(var(--tl-rd-off), 0.16) 100%
    );
    border: 1px solid transparent;
    border-top-color: rgba(var(--tl-rd-on), 0.55);
    border-left-color: rgba(var(--tl-rd-on), 0.5);
    border-bottom-color: rgba(var(--tl-rd-on), 0.28);
    border-right-color: rgba(var(--tl-rd-on), 0.3);
    box-shadow:
      inset 1px 1px 0 rgba(255, 255, 255, 0.55),
      1px 2px 4px -2px rgba(var(--tl-rd-on), 0.3),
      0 0 0 1px rgba(var(--tl-rd-on), 0.06);
  }
  input:checked + .tl-rd-dot {
    background: linear-gradient(
      180deg,
      rgba(var(--tl-rd-on), 0.95) 0%,
      rgba(var(--tl-rd-on), 0.75) 100%
    );
    border-top-color: rgba(var(--tl-rd-on), 0.95);
    border-left-color: rgba(var(--tl-rd-on), 0.85);
    border-bottom-color: rgba(var(--tl-rd-on), 0.55);
    border-right-color: rgba(var(--tl-rd-on), 0.6);
    box-shadow:
      inset 1px 1px 0 rgba(255, 255, 255, 0.45),
      1px 2px 5px -1px rgba(var(--tl-rd-on), 0.5);
  }
`;
var StyledLabel2 = styled19.label`
  --tl-rd-off: ${({ $hue }) => CONTROL_HUES[$hue].off};
  --tl-rd-on: ${({ $hue }) => CONTROL_HUES[$hue].on};

  /* The root is a <label>, and admin pages style every descendant label — AdminForm's
     "label { width: 220px; flex-shrink: 0 }" (0,1,1), a page's ".field label { display: block;
     margin-bottom: 0.5rem }" (0,2,1). Tripled class specificity (0,3,0) holds the row's own box
     against them, as Switch does. */
  &&& {
    position: relative;
    display: inline-flex;
    align-items: flex-start;
    gap: 10px;
    width: auto;
    min-width: 0;
    flex-shrink: 1;
    margin: 0;
    cursor: pointer;
    font-family: ${fontSans13};
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: ${RADIO_COLOURS.label};
  }

  /* The native input carries all state + semantics; visually hidden, NOT removed —
     display:none would take it out of the accessibility tree and off the focus path. */
  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
  }

  .tl-rd-dot {
    width: ${DOT};
    height: ${DOT};
    border-radius: 50%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
    transition: all ${motion10.durFast} ${motion10.easeUi};
  }
  .tl-rd-dot::after {
    content: '';
    width: ${INNER};
    height: ${INNER};
    border-radius: 50%;
    background: ${surfaceToken5[0]};
    opacity: 0;
    transition: opacity ${motion10.durFast} ${motion10.easeUi};
  }
  input:checked + .tl-rd-dot::after {
    opacity: 1;
  }

  ${({ $surface }) => $surface === "on-glass" ? onGlassDotCss : onSolidDotCss}

  /* Focus is driven by the real input's :focus-visible, so it only shows for keyboard. It is the
     design system's sticker ring (focus.ring: a white die-cut and a 4.03:1 cyan-deep line, then
     the frost bloom), as Slider uses, so it passes 1.4.11's 3:1 on any surface. Card 03's hue halo
     (the on-tint at 32 %) is about 1.2:1 on white and is not used here. */
  input:focus-visible + .tl-rd-dot {
    outline: none;
    box-shadow: ${focus5.ring};
  }

  .tl-rd-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    /* Centre a one-line label on the 18px ring. */
    padding-top: 1px;
  }
  .tl-rd-desc {
    font-weight: 400;
    font-size: 12px;
    line-height: 1.45;
    color: ${RADIO_COLOURS.description};
  }

  &&&.is-disabled {
    cursor: not-allowed;
    color: ${RADIO_COLOURS.disabled};
  }
  &.is-disabled .tl-rd-desc {
    color: ${RADIO_COLOURS.disabled};
  }
  &.is-disabled .tl-rd-dot {
    opacity: 0.5;
  }
`;
var Radio = React16.forwardRef(function Radio2(props, ref) {
  const {
    value,
    children,
    description,
    surface: surface31,
    hue,
    disabled,
    checked,
    name,
    onChange,
    className,
    "aria-describedby": ariaDescribedBy,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...inputProps
  } = props;
  const ctx = React16.useContext(RadioGroupContext);
  const autoId = React16.useId();
  const labelId = `${autoId}-label`;
  const descId = `${autoId}-desc`;
  const hasDescription = description != null && description !== false && description !== "";
  const describedBy = [hasDescription ? descId : null, ariaDescribedBy].filter(Boolean).join(" ") || void 0;
  const labelledBy = ariaLabelledBy ?? (ariaLabel != null ? void 0 : children != null ? labelId : void 0);
  const isDisabled = Boolean(disabled) || Boolean(ctx?.disabled);
  const resolvedSurface = surface31 ?? ctx?.surface ?? "on-glass";
  const resolvedHue = hue !== void 0 ? variant(CONTROL_HUES, hue, "aqua", "Radio", "hue") : ctx?.hue ?? "aqua";
  const handleChange = (event) => {
    onChange?.(event);
    if (ctx && event.target.checked) ctx.onChange(value);
  };
  const classes = [
    "altrd",
    resolvedSurface,
    `hue-${resolvedHue}`,
    isDisabled ? "is-disabled" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs17(
    StyledLabel2,
    {
      className: classes,
      $surface: resolvedSurface === "on-solid" ? "on-solid" : "on-glass",
      $hue: resolvedHue,
      children: [
        /* @__PURE__ */ jsx19(
          "input",
          {
            ...inputProps,
            ref,
            type: "radio",
            value,
            name: ctx ? ctx.name : name,
            checked: ctx ? ctx.value === value : checked,
            disabled: isDisabled,
            "aria-label": ariaLabel,
            "aria-labelledby": labelledBy,
            "aria-describedby": describedBy,
            onChange: handleChange
          }
        ),
        /* @__PURE__ */ jsx19("span", { className: "tl-rd-dot", "aria-hidden": "true" }),
        children != null || hasDescription ? /* @__PURE__ */ jsxs17("span", { className: "tl-rd-text", children: [
          children != null ? /* @__PURE__ */ jsx19("span", { id: labelId, className: "tl-rd-label", children }) : null,
          hasDescription ? /* @__PURE__ */ jsx19("span", { id: descId, className: "tl-rd-desc", children: description }) : null
        ] }) : null
      ]
    }
  );
});
Radio.displayName = "Radio";

// src/Toggles/RadioCard.tsx
import React17 from "react";
import styled20 from "styled-components";
import {
  SELECTED_RAMP as SELECTED_RAMP3,
  brand as brand16,
  fontSans as fontSans14,
  ink as ink19,
  motion as motion11,
  radius as radius13,
  rgba as rgba16,
  shadow as shadow4,
  surface as surfaceToken6,
  typeScale as typeScale5,
  weight as weight7
} from "@repo/design-tokens";
import { jsx as jsx20, jsxs as jsxs18 } from "react/jsx-runtime";
var RADIO_CARD_VARIANTS = { option: true, selection: true };
var WASH2 = SELECTED_RAMP3[0];
var SELECTED_ALPHA = 0.55;
var HOVER_ALPHA2 = 0.35;
var RADIO_CARD_COLOURS = {
  /** The wash's base colour — ramp stop 1 (initial). */
  wash: WASH2,
  /** Selected wash alpha, over the white modal. */
  selectedAlpha: SELECTED_ALPHA,
  /** Selected wash. */
  selectedWash: rgba16(WASH2, SELECTED_ALPHA),
  /** Unselected hover — the same stop, fainter. */
  hoverWash: rgba16(WASH2, HOVER_ALPHA2),
  /** Title ink. */
  title: ink19[1],
  /** Sentence ink. */
  description: ink19[2],
  /** Disabled ink, title and sentence. */
  disabled: ink19[4],
  /** The white the modal paints behind the card. */
  backdrop: surfaceToken6[0]
};
var TEXT_INDENT = "28px";
var Card3 = styled20.div`
  --tl-rdc-on: ${({ $on }) => $on};

  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${radius13.control};
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition:
    background ${motion11.durFast} ${motion11.easeUi},
    border-color ${motion11.durFast} ${motion11.easeUi},
    box-shadow ${motion11.durFast} ${motion11.easeUi};

  &:hover:not(.is-selected):not(.is-disabled) {
    background: ${RADIO_CARD_COLOURS.hoverWash};
  }

  &.is-selected {
    background: ${RADIO_CARD_COLOURS.selectedWash};
    border-color: rgba(var(--tl-rdc-on), 0.18);
    box-shadow:
      inset 1px 1px 0 rgba(255, 255, 255, 0.7),
      0 4px 14px -4px rgba(var(--tl-rdc-on), 0.3);
    cursor: default;
  }

  &.is-disabled {
    cursor: not-allowed;
  }

  .tl-rd-label {
    font-weight: 600;
    color: ${RADIO_CARD_COLOURS.title};
  }
  .tl-rd-desc {
    color: ${RADIO_CARD_COLOURS.description};
  }
  &.is-disabled .tl-rd-label,
  &.is-disabled .tl-rd-desc {
    color: ${RADIO_CARD_COLOURS.disabled};
  }

  .tl-rdc-slot {
    /* A bare fieldset box: no border, padding or min-width of its own. */
    border: 0;
    margin: 0;
    min-width: 0;
    padding: 0 0 0 ${TEXT_INDENT};
    cursor: auto;
  }
`;
function RadioCard(props) {
  const look = variant(
    RADIO_CARD_VARIANTS,
    props.variant ?? "option",
    "option",
    "RadioCard",
    "variant"
  );
  return look === "selection" ? /* @__PURE__ */ jsx20(SelectionCard, { ...props }) : /* @__PURE__ */ jsx20(OptionCard, { ...props });
}
RadioCard.displayName = "RadioCard";
function OptionCard(props) {
  const {
    variant: _variant,
    value,
    title,
    description,
    children,
    disabled = false,
    className,
    onClick,
    ...rest
  } = props;
  const ctx = React17.useContext(RadioGroupContext);
  const inputRef = React17.useRef(null);
  const slotRef = React17.useRef(null);
  React17.useEffect(() => {
    if (process.env.NODE_ENV !== "production" && !ctx) {
      console.warn("[@repo/design-system] RadioCard: must be used inside a RadioGroup.");
    }
  }, [ctx]);
  React17.useEffect(() => {
    if (process.env.NODE_ENV === "production" || !slotRef.current) return;
    const stray = Array.from(
      slotRef.current.querySelectorAll('input[type="radio"]')
    ).filter(
      (input) => input.closest('[role="radiogroup"]') === slotRef.current?.closest('[role="radiogroup"]')
    );
    if (stray.length > 0) {
      console.warn(
        `[@repo/design-system] RadioCard "${value}": a radio in the nested slot is not in its own RadioGroup, so assistive technology lists it as an option of the outer group. Wrap it in a RadioGroup.`
      );
    }
  }, [children, ctx?.value, value]);
  const selected = ctx?.value === value;
  const isDisabled = disabled || Boolean(ctx?.disabled);
  const hue = ctx?.hue ?? "aqua";
  const handleClick = (event) => {
    if (isDisabled) return;
    onClick?.(event);
    if (event.defaultPrevented) return;
    if (!ctx || isDisabled || selected) return;
    const radioRoot = inputRef.current?.closest(".altrd");
    if (radioRoot?.contains(event.target)) return;
    ctx.onChange(value);
    inputRef.current?.focus({ preventScroll: true });
  };
  const classes = [
    "tl-rdc",
    selected ? "is-selected" : "",
    isDisabled ? "is-disabled" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs18(Card3, { className: classes, $on: CONTROL_HUES[hue].on, onClick: handleClick, ...rest, children: [
    /* @__PURE__ */ jsx20(
      Radio,
      {
        ref: inputRef,
        value,
        surface: "on-solid",
        disabled,
        description,
        children: title
      }
    ),
    selected && children != null ? (
      // A fieldset, so a disabled card disables every form control inside its slot natively.
      /* @__PURE__ */ jsx20("fieldset", { ref: slotRef, className: "tl-rdc-slot", disabled: isDisabled, children: /* @__PURE__ */ jsx20(RadioGroupContext.Provider, { value: null, children }) })
    ) : null
  ] });
}
var RADIO_SELECTION_COLOURS = {
  /** Title ink — `--tl-ink`. */
  title: ink19[1],
  /**
   * Second-line ink — `ink[2]`, one step darker than frame 01's `--tl-ink-3`. Over white the
   * frame's ink clears 4.5:1, but composited over a saturated glass stage it falls to about
   * 3.6:1, so the spec's own fallback (R3.7) applies; recorded for Rob in PROVENANCE.
   */
  description: ink19[2],
  /** Unselected fill and its alpha — the shared glass rest (`internal/glassStates.ts`). */
  plainFill: GLASS_REST.fill,
  plainAlpha: GLASS_REST.fillAlpha,
  /** Selected gradient, top and bottom stops. */
  selectedTop: surfaceToken6[0],
  selectedTopAlpha: 0.95,
  selectedBottom: brand16.frost,
  selectedBottomAlpha: 0.8,
  /** The check. */
  check: brand16.cyan,
  /** What the contrast test composites onto. */
  backdrop: surfaceToken6[0]
};
var SelectionRoot = styled20.label`
  /* The root is a <label>, and admin pages style every descendant label (see Radio). Tripled class
     specificity holds the card's own box against them — so every rule here that changes the
     root's own box or cursor is tripled too, or the guard would override it. */
  &&& {
    display: block;
    position: relative;
    width: auto;
    min-width: 0;
    margin: 0;
    cursor: pointer;
    font-family: ${fontSans14};
  }
  &&&.is-selected {
    /* A press on the chosen card does nothing, so it does not offer one. */
    cursor: default;
  }
  &&&.is-disabled {
    cursor: not-allowed;
  }

  .tl-rsc-face {
    box-sizing: border-box;
    height: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: ${radius13.lg};
    ${glassRestCss}
    box-shadow: 0 2px 6px rgba(${shadow4.inkGlassRgb}, 0.06);
    transition:
      background ${motion11.durFast} ${motion11.easeUi},
      border-color ${motion11.durFast} ${motion11.easeUi},
      box-shadow ${motion11.durFast} ${motion11.easeUi};
  }

  .tl-rsc-disc {
    ${iconDiscCss(34)}
    ${discToneCss("neutral")}
  }

  .tl-rsc-text {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    /* A long unbroken word wraps inside the card rather than running under the check. */
    overflow-wrap: anywhere;
  }
  .tl-rsc-title {
    font-size: ${typeScale5.body1.fontSize};
    line-height: ${typeScale5.body1.lineHeight};
    font-weight: ${weight7.semibold};
    color: ${RADIO_SELECTION_COLOURS.title};
  }
  .tl-rsc-desc {
    /* 11.5 / 15 has no step on the type scale: frame 01's value, on Rob's list in PROVENANCE. */
    font-size: 11.5px;
    line-height: 15px;
    font-weight: ${weight7.regular};
    color: ${RADIO_SELECTION_COLOURS.description};
  }
  .tl-rsc-check {
    margin-left: auto;
    flex: none;
    display: inline-flex;
    color: ${RADIO_SELECTION_COLOURS.check};
  }

  &.is-selected .tl-rsc-face {
    background: linear-gradient(
      180deg,
      ${rgba16(RADIO_SELECTION_COLOURS.selectedTop, RADIO_SELECTION_COLOURS.selectedTopAlpha)},
      ${rgba16(RADIO_SELECTION_COLOURS.selectedBottom, RADIO_SELECTION_COLOURS.selectedBottomAlpha)}
    );
    border-color: ${rgba16(brand16.cyan, 0.45)};
    /* The frame's drop is black at .18. Shadow inks are "deliberately never pure black" (ruling of
       2026-07-31, shadow.inkGlassRgb), so it takes the glass drop ink — recorded for Rob. */
    box-shadow:
      0 0 12px 2px ${rgba16(brand16.frost, 0.9)},
      0 2px 5px rgba(${shadow4.inkGlassRgb}, 0.18);
  }
  &.is-selected .tl-rsc-disc {
    ${discToneCss("selected")}
  }

  /* Hover on an unchosen, enabled card: the built Card's on-glass inlay hover. */
  &:hover:not(.is-selected):not(.is-disabled) .tl-rsc-face {
    ${glassHoverCss}
    box-shadow: 0 0 10px 1px ${brand16.frostGlow};
  }

  /* Focus: the system ring around the face, keyboard only — and it wins over hover. The second
     selector out-ranks the hover rule (0,6,1 against 0,5,0) rather than leaning on source order,
     without :not(:focus-visible), which Safari 15.0–15.3 cannot parse. */
  input:focus-visible + .tl-rsc-face,
  &:hover:not(.is-selected):not(.is-disabled) input:focus-visible + .tl-rsc-face {
    ${glassFocusCss}
  }

  /* Disabled: the built Card's dimming. */
  &.is-disabled .tl-rsc-face {
    ${glassDisabledCss}
  }
`;
var HiddenInput = styled20.input`
  ${visuallyHiddenCss}
`;
function SelectionCard(props) {
  const {
    variant: _variant,
    value,
    title,
    description,
    icon: icon2,
    disabled = false,
    className,
    onClick,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    ...rest
  } = props;
  const ctx = React17.useContext(RadioGroupContext);
  const autoId = React17.useId();
  const titleId = `${autoId}-title`;
  const descId = `${autoId}-desc`;
  const rootRef = React17.useRef(null);
  const inputRef = React17.useRef(null);
  const warnedOnSolid = React17.useRef(false);
  React17.useEffect(() => {
    if (process.env.NODE_ENV !== "production" && !ctx) {
      console.warn("[@repo/design-system] RadioCard: must be used inside a RadioGroup.");
    }
  }, [ctx]);
  const onSolid = ctx?.surface === "on-solid";
  React17.useEffect(() => {
    if (process.env.NODE_ENV === "production" || !onSolid || warnedOnSolid.current) return;
    const root = rootRef.current;
    const group = root?.closest('[role="radiogroup"]');
    if (!root || !group || group.querySelector(".tl-rsc") !== root) return;
    warnedOnSolid.current = true;
    console.warn(
      '[@repo/design-system] RadioCard: the selection look is drawn on glass only; the group\'s `surface="on-solid"` is ignored.'
    );
  }, [onSolid]);
  const selected = ctx?.value === value;
  const isDisabled = disabled || Boolean(ctx?.disabled);
  const hasDescription = description != null && description !== false && description !== "";
  const labelledBy = [titleId, ariaLabelledBy].filter(Boolean).join(" ");
  const describedBy = [hasDescription ? descId : null, ariaDescribedBy].filter(Boolean).join(" ") || void 0;
  const redispatchPending = React17.useRef(false);
  const handleClick = (event) => {
    if (isDisabled) return;
    if (event.target === inputRef.current) {
      if (redispatchPending.current) {
        redispatchPending.current = false;
        return;
      }
      onClick?.(event);
      return;
    }
    onClick?.(event);
    if (event.defaultPrevented) return;
    redispatchPending.current = true;
    setTimeout(() => {
      redispatchPending.current = false;
    }, 0);
  };
  const classes = [
    "tl-rsc",
    selected ? "is-selected" : "",
    isDisabled ? "is-disabled" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs18(SelectionRoot, { ...rest, ref: rootRef, className: classes, onClick: handleClick, children: [
    /* @__PURE__ */ jsx20(
      HiddenInput,
      {
        ref: inputRef,
        type: "radio",
        name: ctx?.name,
        value,
        checked: selected,
        disabled: isDisabled,
        "aria-labelledby": labelledBy,
        "aria-describedby": describedBy,
        onChange: (event) => {
          if (event.nativeEvent.defaultPrevented) return;
          if (ctx && event.target.checked) ctx.onChange(value);
        }
      }
    ),
    /* @__PURE__ */ jsxs18("span", { className: "tl-rsc-face", children: [
      /* @__PURE__ */ jsx20("span", { className: "tl-rsc-disc", "aria-hidden": "true", children: /* @__PURE__ */ jsx20(Icon, { name: icon2, size: DISC_GLYPH_SIZE[34], variant: selected ? "solid" : "regular" }) }),
      /* @__PURE__ */ jsxs18("span", { className: "tl-rsc-text", children: [
        /* @__PURE__ */ jsx20("span", { id: titleId, className: "tl-rsc-title", children: title }),
        hasDescription ? /* @__PURE__ */ jsx20("span", { id: descId, className: "tl-rsc-desc", children: description }) : null
      ] }),
      selected ? /* @__PURE__ */ jsx20("span", { className: "tl-rsc-check", "aria-hidden": "true", children: /* @__PURE__ */ jsx20(Icon, { name: "circle-check", variant: "solid", size: "sm" }) }) : null
    ] })
  ] });
}

// src/Feedback/Feedback.tsx
import React18 from "react";
import styled21 from "styled-components";
import { ink as ink20, surface as surface9, fontSans as fontSans15, typeRole as typeRole6 } from "@repo/design-tokens";
import { jsx as jsx21, jsxs as jsxs19 } from "react/jsx-runtime";
var StyledSkeleton = styled21.div`
  /* Intentionally almost empty — see the module note. The ONLY thing added on top of the
     canonical classes is the flush override, which is layout, not appearance.

     The doubled ampersand is load-bearing, not a style tic. It repeats the generated class
     in the selector to raise specificity. The canonical rule being overridden is
     .tl-skel.line { margin-bottom: 8px } in tokens.css — TWO classes, specificity (0,2,0).
     styled-components emits ONE class, (0,1,0), and specificity is compared before source
     order, so the plain form lost every time: flush was a prop that looked implemented, was
     unit-tested, and did nothing in a browser. cssFor greps the emitted rule text and
     structurally cannot see a cascade loss, which is why its test stayed green.
     Found by /review-code, 2026-07-29. */
  ${({ $flush }) => $flush ? "&& { margin-bottom: 0; }" : ""}
`;
var Skeleton = React18.forwardRef(
  function Skeleton2(props, ref) {
    const { shape = "line", width, flush = false, className, ...rest } = props;
    if (process.env.NODE_ENV !== "production") {
      if (width !== void 0 && shape !== "line") {
        console.warn(
          `Skeleton: \`width\` only applies to shape="line" (got shape="${shape}") \u2014 the canonical stylesheet defines .w-* for lines only.`
        );
      }
    }
    const classes = [
      "tl-skel",
      shape,
      width !== void 0 && shape === "line" ? `w-${width}` : "",
      className ?? ""
    ].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsx21(StyledSkeleton, { ref, className: classes, $flush: flush, "aria-hidden": "true", ...rest });
  }
);
Skeleton.displayName = "Skeleton";
var StyledSkeletonPanel = styled21.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 100%;
`;
var ValueBlock2 = styled21(Skeleton)`
  && {
    height: ${({ $tall }) => $tall ? "100%" : "48px"};
    min-height: ${({ $tall }) => $tall ? "80px" : "48px"};
    flex: ${({ $tall }) => $tall ? "1 1 auto" : "0 0 auto"};
    width: 100%;
    margin-bottom: 0;
  }
`;
var SkeletonPanel = React18.forwardRef(
  function SkeletonPanel2(props, ref) {
    const { tall = false, subLines = 1, className, ...rest } = props;
    return /* @__PURE__ */ jsxs19(StyledSkeletonPanel, { ref, className, "aria-busy": "true", ...rest, children: [
      /* @__PURE__ */ jsx21(Skeleton, { shape: "line", width: 40, flush: true }),
      /* @__PURE__ */ jsx21(ValueBlock2, { shape: "block", $tall: tall, flush: true }),
      Array.from({ length: Math.max(0, subLines) }, (_unused, i) => /* @__PURE__ */ jsx21(Skeleton, { shape: "line", width: 70, flush: true }, i))
    ] });
  }
);
SkeletonPanel.displayName = "SkeletonPanel";
var StyledEmptyInline = styled21.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 60px;
  padding: 12px;
  box-sizing: border-box;
  text-align: center;
  background: ${surface9[1]};
  color: ${ink20.muted};
  font-family: ${fontSans15};
  font-size: ${typeRole6.body1.fontSize};
  line-height: ${typeRole6.body1.lineHeight};
  font-weight: ${typeRole6.body1.fontWeight};
`;
var EmptyInline = React18.forwardRef(
  function EmptyInline2(props, ref) {
    const { message = "No data available", role = "status", className, children, ...rest } = props;
    return /* @__PURE__ */ jsx21(
      StyledEmptyInline,
      {
        ref,
        className: ["tl-empty-inline", className ?? ""].filter(Boolean).join(" "),
        role,
        ...rest,
        children: children ?? message
      }
    );
  }
);
EmptyInline.displayName = "EmptyInline";

// src/Feedback/EmptyPane.tsx
import React19 from "react";
import styled22 from "styled-components";
import {
  fontSans as fontSans16,
  ink as ink21,
  inkTint as inkTint2,
  radius as radius14,
  rgba as rgba17,
  shadow as shadow5,
  space as space5,
  support as support6,
  surface as surface10,
  weight as weight8
} from "@repo/design-tokens";
import { jsx as jsx22, jsxs as jsxs20 } from "react/jsx-runtime";
var EMPTY_PANE_TONES = {
  peri: { hue: support6.periwinkle, tint: 0.26, tintSoft: 0.1, glyph: inkTint2.peri, shadow: 0.2 }
};
var EMPTY_PANE_BASE_ALPHA = 0.55;
var BASE_MIX = `${Math.round(EMPTY_PANE_BASE_ALPHA * 100)}%`;
var EMPTY_PANE_TITLE_INK = ink21[1];
var EMPTY_PANE_TEXT_INK = ink21[2];
var PANE_PADDING = "32px 26px 28px";
var DISC_SIZE2 = "44px";
var TITLE_FONT = `${weight8.semibold} 15px / 1.3 ${fontSans16}`;
var TITLE_TRACKING = "-0.1px";
var TEXT_FONT = `${weight8.regular} 12.5px / 1.55 ${fontSans16}`;
var TEXT_MAX_WIDTH = "300px";
var PANE_BLUR = "blur(22px) saturate(1.35)";
var white = (a) => rgba17(surface10[0], a);
var inkGlassShadow = (a) => `rgba(${shadow5.inkGlassRgb}, ${a})`;
var toneShadow = ({ $tone }) => rgba17(EMPTY_PANE_TONES[$tone].glyph, EMPTY_PANE_TONES[$tone].shadow);
var StyledPane = styled22.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space5[3]};
  padding: ${PANE_PADDING};
  border-radius: ${radius14.xl};
  text-align: center;
  background:
    linear-gradient(
      180deg,
      ${({ $tone }) => rgba17(EMPTY_PANE_TONES[$tone].hue, EMPTY_PANE_TONES[$tone].tint)} 0%,
      ${({ $tone }) => rgba17(EMPTY_PANE_TONES[$tone].hue, EMPTY_PANE_TONES[$tone].tintSoft)} 100%
    ),
    color-mix(in srgb, ${surface10[1]} ${BASE_MIX}, transparent);
  border: 1px solid ${white(0.7)};
  -webkit-backdrop-filter: ${PANE_BLUR};
  backdrop-filter: ${PANE_BLUR};
  box-shadow:
    inset 0 1px 0 ${white(0.9)},
    0 1px 2px ${inkGlassShadow(0.05)},
    0 14px 34px ${toneShadow};

  .tl-empty-pane-disc {
    width: ${DISC_SIZE2};
    height: ${DISC_SIZE2};
    border-radius: ${radius14.xl};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ $tone }) => EMPTY_PANE_TONES[$tone].glyph};
    background: linear-gradient(180deg, ${white(0.7)} 0%, ${white(0.45)} 100%);
    border: 1px solid ${white(0.85)};
    box-shadow:
      inset 0 1px 0 ${white(0.95)},
      0 4px 12px ${toneShadow};
  }

  .tl-empty-pane-title {
    margin: 2px 0 0;
    font: ${TITLE_FONT};
    letter-spacing: ${TITLE_TRACKING};
    color: ${EMPTY_PANE_TITLE_INK};
  }

  .tl-empty-pane-text {
    margin: 0 0 6px;
    max-width: ${TEXT_MAX_WIDTH};
    font: ${TEXT_FONT};
    color: ${EMPTY_PANE_TEXT_INK};
    text-wrap: pretty;
  }

  .tl-empty-pane-actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${space5[2]};
    align-items: center;
    justify-content: center;
    margin-top: 2px;
  }
`;
var EmptyPane = React19.forwardRef(
  function EmptyPane2(props, ref) {
    const {
      icon: icon2,
      title,
      headingLevel = 3,
      children,
      actions,
      tone: toneProp = "peri",
      className,
      ...rest
    } = props;
    const tone = variant(EMPTY_PANE_TONES, toneProp, "peri", "EmptyPane", "tone");
    const Heading = `h${headingLevel}`;
    return /* @__PURE__ */ jsxs20(
      StyledPane,
      {
        ref,
        className: ["tl-empty-pane", `tl-empty-pane-${tone}`, className ?? ""].filter(Boolean).join(" "),
        $tone: tone,
        ...rest,
        children: [
          /* @__PURE__ */ jsx22("span", { className: "tl-empty-pane-disc", "aria-hidden": "true", children: icon2 }),
          /* @__PURE__ */ jsx22(Heading, { className: "tl-empty-pane-title", children: title }),
          children ? /* @__PURE__ */ jsx22("p", { className: "tl-empty-pane-text", children }) : null,
          actions ? /* @__PURE__ */ jsx22("div", { className: "tl-empty-pane-actions", children: actions }) : null
        ]
      }
    );
  }
);
EmptyPane.displayName = "EmptyPane";

// src/Feedback/Banner.tsx
import React20 from "react";
import styled23, { css as css15 } from "styled-components";
import {
  accent as accent6,
  brand as brand17,
  focus as focus6,
  ink as ink22,
  inkOn as inkOn6,
  line as line7,
  motion as motion12,
  radius as radius15,
  rgba as rgba18,
  semanticColor as semanticColor8,
  shadow as shadow6,
  space as space6,
  surface as surface11,
  fontSans as fontSans17
} from "@repo/design-tokens";

// src/internal/frostGlass.ts
var FROST_GLASS_BLUR = "blur(20px) saturate(120%)";

// src/Feedback/Banner.tsx
import { jsx as jsx23, jsxs as jsxs21 } from "react/jsx-runtime";
var TONE2 = {
  info: {
    hue: brand17.cyan,
    lineHue: brand17.cyan,
    ink: inkOn6.aqua,
    wash: 0.22,
    glow: 0.42,
    solidLine: 0.32,
    solidWash: 0.1,
    solidTint: 0.16
  },
  success: {
    hue: semanticColor8.success,
    lineHue: semanticColor8.success,
    ink: inkOn6.mint,
    wash: 0.32,
    glow: 0.55,
    solidLine: 0.5,
    solidWash: 0.16,
    solidTint: 0.28
  },
  warning: {
    hue: accent6.butter,
    lineHue: semanticColor8.warn,
    ink: inkOn6.butter,
    wash: 0.45,
    glow: 0.55,
    solidLine: 0.34,
    solidWash: 0.24,
    solidTint: 0.42
  },
  error: {
    hue: semanticColor8.error,
    lineHue: semanticColor8.error,
    ink: inkOn6.error,
    wash: 0.3,
    glow: 0.4,
    solidLine: 0.38,
    solidWash: 0.12,
    solidTint: 0.18
  }
};
var PAD_X = "14px";
var BODY_TYPE = "400 13px/1.5";
var ICON_SIZE = "30px";
var ICON_GLYPH_SIZE = "13px";
var CLOSE_SIZE2 = "24px";
var CLOSE_GLYPH_SIZE = "10px";
var GLASS_BORDER_WIDTH = "1.5px";
var GLASS_DROP_INK = shadow6.inkGlassRgb;
var ON_SOLID_DROP_INK = shadow6.inkSolidRgb;
var glassCss = (t) => css15`
  background: ${rgba18(surface11[0], 0.78)};
  backdrop-filter: ${FROST_GLASS_BLUR};
  -webkit-backdrop-filter: ${FROST_GLASS_BLUR};
  border: ${GLASS_BORDER_WIDTH} solid ${rgba18(surface11[0], 0.95)};
  box-shadow:
    0 0 12px 2px ${rgba18(t.hue, t.glow)},
    0 4px 12px rgba(${GLASS_DROP_INK}, 0.1),
    0 1px 2px rgba(${GLASS_DROP_INK}, 0.08),
    inset 0 1px 0 ${rgba18(surface11[0], 0.7)};

  /* Tone wash, left→right fade-out. Behind the content, per the card's z-index: -1 against
     the isolation context the base establishes. */
  &::before {
    background: linear-gradient(95deg, ${rgba18(t.hue, t.wash)} 0%, ${rgba18(surface11[0], 0)} 70%);
  }

  /* Diagonal gloss sheen — the shared dialog/popover highlight. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(
      135deg,
      ${rgba18(surface11[0], 0)} 0%,
      ${rgba18(surface11[0], 0.28)} 40%,
      ${rgba18(surface11[0], 0)} 70%
    );
    mix-blend-mode: screen;
  }

  .tl-banner-icon {
    background: ${rgba18(surface11[0], 0.85)};
    border: 1px solid ${rgba18(surface11[0], 0.95)};
    /* The disc halo takes the TONE's glow alpha, not a fixed one. The card writes
       var(--tone-glow, rgba(228,255,254,.55)) here and every tone class sets --tone-glow, so
       the .55 is the card's FALLBACK and is never what it resolves to. Reading it as the value
       made info (.42) and error (.40) glow ~30% too strongly. */
    box-shadow:
      0 0 6px 1px ${rgba18(t.hue, t.glow)},
      inset 0 1px 0 ${rgba18(surface11[0], 0.7)};
  }

  .tl-banner-close {
    background: ${rgba18(surface11[0], 0.6)};
    border: 1px solid ${rgba18(surface11[0], 0.85)};
    box-shadow: inset 0 1px 0 ${rgba18(surface11[0], 0.6)};
    color: ${ink22[2]};

    &:hover {
      background: ${rgba18(surface11[0], 0.92)};
    }
  }
`;
var onSolidCss4 = (t) => css15`
  background: ${surface11[0]};
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: 1px solid ${rgba18(t.lineHue, t.solidLine)};
  box-shadow:
    0 1px 2px rgba(${ON_SOLID_DROP_INK}, 0.04),
    0 6px 18px rgba(${ON_SOLID_DROP_INK}, 0.06);

  &::before {
    background: linear-gradient(95deg, ${rgba18(t.hue, t.solidWash)} 0%, ${rgba18(surface11[0], 0)} 70%);
  }

  /* No gloss on a solid panel — the card removes the layer rather than fading it. */
  &::after {
    display: none;
  }

  .tl-banner-icon {
    background: ${rgba18(t.hue, t.solidTint)};
    border: none;
    box-shadow: inset 0 0 0 1px ${rgba18(surface11[0], 0.5)};
  }

  .tl-banner-close {
    background: transparent;
    border: 1px solid ${line7[2]};
    box-shadow: none;
    color: ${ink22[3]};

    &:hover {
      background: ${surface11.hover};
      color: ${ink22[1]};
    }
  }
`;
var SURFACE_CSS3 = {
  "on-glass": {
    info: glassCss(TONE2.info),
    success: glassCss(TONE2.success),
    warning: glassCss(TONE2.warning),
    error: glassCss(TONE2.error)
  },
  "on-solid": {
    info: onSolidCss4(TONE2.info),
    success: onSolidCss4(TONE2.success),
    warning: onSolidCss4(TONE2.warning),
    error: onSolidCss4(TONE2.error)
  }
};
var StyledBanner = styled23.div`
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: flex-start;
  /* The resolved tone ink, published so a host can reference it instead of re-picking a colour
     that then drifts from the tone. The BODY is deliberately not inked with it — the card keeps
     the body neutral and reserves the tone for the lead — so a host element that should read as
     part of the tone (a remedy link, say) sets its colour from this property.

     (NO BACKTICKS IN COMMENTS IN HERE. One inside a comment inside a styled-components template
     literal terminates the literal, and the file then fails with a bare TS1005 pointing at an
     unrelated line. Tripped exactly that way writing this block.) */
  --tl-banner-tone-ink: ${({ $tone }) => TONE2[$tone].ink};

  gap: ${space6[3]};
  padding: ${space6[3]} ${PAD_X};
  border-radius: ${radius15.lg};
  /* Required, so the tone wash pseudo clips to the border radius — NOT a truncation policy.
     The body therefore has to be able to break anything, see .tl-banner-body below. */
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    z-index: -1;
  }

  .tl-banner-icon {
    flex-shrink: 0;
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
    border-radius: ${radius15.pill};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${ICON_GLYPH_SIZE};
    color: ${({ $tone }) => TONE2[$tone].ink};
  }

  .tl-banner-body {
    flex: 1;
    font: ${BODY_TYPE} ${fontSans17};
    color: ${ink22[2]};
    padding-top: 1px;
    min-width: 0;
    /* Break anything, including a run with no break opportunity. Both error consumers render a
       SERVER message verbatim, and those arrive as space-free JSON bodies
       ({"code":"23503","details":"..."}); against the root's overflow:hidden that would clip
       mid-token with no ellipsis and nothing selectable. The rule this replaced sat inside
       .mc-panel-body, whose overflow-y:auto gave it a horizontal scrollbar instead. */
    overflow-wrap: anywhere;
  }

  .tl-banner-title {
    font-weight: 600;
    color: ${({ $tone }) => TONE2[$tone].ink};
    margin-right: ${space6[1]};
  }

  .tl-banner-close {
    width: ${CLOSE_SIZE2};
    height: ${CLOSE_SIZE2};
    border-radius: ${radius15.pill};
    flex-shrink: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${CLOSE_GLYPH_SIZE};
    /* The card authors .15s/.12s; these are the nearest motion tokens, which is the
       token-bound form rule 8 asks for on a brand motion value. */
    transition:
      background ${motion12.durFast} ${motion12.easeUi},
      transform ${motion12.durFast} ${motion12.easeUi};

    /* Hover pop lives HERE, not per-surface. The card layers .banner.on-solid .close over the
       base .banner .close rule and restates only background and colour, so an on-solid close
       still scales. Putting it in the glass block alone silently dropped it on solid. */
    &:hover {
      transform: scale(1.05);
    }

    /* Every other interactive primitive in this package binds the focus tokens; without this the
       control falls back to the UA outline, which over a translucent glass banner on the live 3D
       scene is the effectively-invisible case primitives/focus.ts exists to prevent.
       focus.tight, not focus.ring: the root sets overflow hidden, which would clip the bloom. */
    &:focus-visible {
      outline: none;
      box-shadow: ${focus6.tight};
    }
  }

  ${({ $tone, $surface }) => SURFACE_CSS3[$surface][$tone]}
`;
var Banner = React20.forwardRef(function Banner2(props, ref) {
  const {
    tone: toneProp = "info",
    surface: surfaceProp = "on-glass",
    icon: icon2,
    title,
    children,
    onDismiss,
    dismissLabel = "Dismiss",
    role,
    className,
    ...rest
  } = props;
  const tone = variant(TONE2, toneProp, "info", "Banner", "tone");
  const bannerSurface = variant(SURFACE_CSS3, surfaceProp, "on-glass", "Banner", "surface");
  return /* @__PURE__ */ jsxs21(
    StyledBanner,
    {
      ref,
      className: ["tl-banner", `tl-banner-${tone}`, `tl-banner-${bannerSurface}`, className ?? ""].filter(Boolean).join(" "),
      $tone: tone,
      $surface: bannerSurface,
      ...rest,
      role: role ?? (tone === "error" ? "alert" : "status"),
      "data-tone": tone,
      "data-surface": bannerSurface,
      children: [
        icon2 ? /* @__PURE__ */ jsx23("span", { className: "tl-banner-icon", "aria-hidden": "true", children: icon2 }) : null,
        /* @__PURE__ */ jsxs21("div", { className: "tl-banner-body", children: [
          title ? /* @__PURE__ */ jsx23("b", { className: "tl-banner-title", children: title }) : null,
          children
        ] }),
        typeof onDismiss === "function" ? /* @__PURE__ */ jsx23(
          "button",
          {
            type: "button",
            className: "tl-banner-close",
            "aria-label": dismissLabel,
            onClick: onDismiss,
            children: /* @__PURE__ */ jsx23("span", { "aria-hidden": "true", children: "\xD7" })
          }
        ) : null
      ]
    }
  );
});
Banner.displayName = "Banner";

// src/Feedback/Note.tsx
import React21 from "react";
import styled24, { css as css16 } from "styled-components";
import {
  accent as accent7,
  fontSans as fontSans18,
  inkGlass as inkGlass6,
  ink as ink23,
  inkOn as inkOn7,
  inset as inset5,
  radius as radius16,
  rgba as rgba19,
  semantic as semantic4,
  semanticColor as semanticColor9,
  space as space7,
  support as support7,
  typeScale as typeScale6,
  weight as weight9
} from "@repo/design-tokens";
import { jsx as jsx24, jsxs as jsxs22 } from "react/jsx-runtime";
var NOTE_TINT_ALPHA = 0.18;
var NOTE_TONES = {
  butter: { hue: accent7.butter, ink: inkGlass6.warn },
  pink: { hue: accent7.peach, ink: inkOn7.pink },
  lavender: { hue: support7.lavender, ink: inkOn7.lav },
  peri: { hue: support7.periwinkle, ink: inkOn7.peri },
  // Frame 07 of the twin details epic: *No overlap with other special periods* on a mint wash.
  mint: { hue: support7.mint, ink: inkOn7.mint },
  error: { hue: semanticColor9.error, ink: semanticColor9.errorStrong },
  // No tint (`hue: null` → no fill in either form); the glyph is a step lighter than the sentence.
  neutral: { hue: null, ink: semantic4.text.secondary, glyph: ink23[3] }
};
var NOTE_LINE_HEIGHT = "18px";
var bareCss = css16`
  background: none;
  padding: 0;
  gap: ${inset5[2]};
`;
var StyledNote = styled24.div`
  display: flex;
  align-items: flex-start;
  gap: ${space7[2]};
  padding: ${space7[2]} ${inset5[3]};
  border-radius: ${radius16.md};
  background: ${({ $tone }) => {
  const { hue } = NOTE_TONES[$tone];
  return hue === null ? "none" : rgba19(hue, NOTE_TINT_ALPHA);
}};
  color: ${({ $tone }) => NOTE_TONES[$tone].ink};
  font: ${weight9.medium} ${typeScale6.caption1.fontSize} / ${NOTE_LINE_HEIGHT} ${fontSans18};
  text-wrap: pretty;

  .tl-note-icon {
    flex: none;
    display: inline-flex;
    align-items: center;
    height: ${NOTE_LINE_HEIGHT};
    /* Only neutral sets a glyph ink; every other tone's glyph inherits the sentence's. */
    ${({ $tone }) => {
  const { glyph } = NOTE_TONES[$tone];
  return glyph ? `color: ${glyph};` : "";
}}
  }

  .tl-note-text {
    min-width: 0;
  }

  ${({ $bare }) => $bare ? bareCss : ""}
`;
var Note = React21.forwardRef(function Note2(props, ref) {
  const { tone: toneProp, bare = false, icon: icon2, children, className, ...rest } = props;
  const tone = variant(NOTE_TONES, toneProp, "peri", "Note", "tone");
  return /* @__PURE__ */ jsxs22(
    StyledNote,
    {
      ref,
      className: ["tl-note", `tl-note-${tone}`, bare ? "tl-note-bare" : "", className ?? ""].filter(Boolean).join(" "),
      $tone: tone,
      $bare: bare,
      ...rest,
      children: [
        icon2 ? /* @__PURE__ */ jsx24("span", { className: "tl-note-icon", "aria-hidden": "true", children: icon2 }) : null,
        /* @__PURE__ */ jsx24("span", { className: "tl-note-text", children })
      ]
    }
  );
});
Note.displayName = "Note";

// src/Feedback/Toast.tsx
import React22 from "react";
import styled25, { css as css17 } from "styled-components";
import {
  accent as accent8,
  brand as brand18,
  focus as focus7,
  fontMono as fontMono7,
  fontSans as fontSans19,
  ink as ink24,
  inkOn as inkOn8,
  line as line8,
  motion as motion13,
  radius as radius17,
  rgba as rgba20,
  semanticColor as semanticColor10,
  shadow as shadow7,
  surface as surface12,
  typeRole as typeRole7
} from "@repo/design-tokens";
import { jsx as jsx25, jsxs as jsxs23 } from "react/jsx-runtime";
var TONE3 = {
  info: {
    tint: brand18.cyan,
    tintAlpha: 0.22,
    ink: inkOn8.aqua,
    solidLine: rgba20(brand18.cyan, 0.32),
    prefix: ""
  },
  success: {
    tint: semanticColor10.success,
    tintAlpha: 0.32,
    ink: inkOn8.mint,
    solidLine: rgba20(semanticColor10.success, 0.5),
    prefix: ""
  },
  warning: {
    tint: accent8.butter,
    tintAlpha: 0.45,
    ink: inkOn8.butter,
    solidLine: rgba20(semanticColor10.warn, 0.34),
    prefix: "Warning: "
  },
  error: {
    tint: semanticColor10.error,
    tintAlpha: 0.32,
    ink: inkOn8.error,
    solidLine: rgba20(semanticColor10.error, 0.38),
    prefix: "Error: "
  },
  neutral: {
    // ink[1] is #3F454E, the card's rgba(63,69,78,.10); its glyph ink is --tl-ink-2.
    tint: ink24[1],
    tintAlpha: 0.1,
    ink: ink24[2],
    solidLine: line8[2],
    prefix: ""
  }
};
var TOAST_W = "320px";
var MIN_H = "32px";
var PAD_Y = "6px";
var ICON_OFFSET = "6px";
var ICON_SIZE2 = "20px";
var ICON_GLYPH_SIZE2 = "10px";
var CLOSE_OFFSET = "7px";
var CLOSE_SIZE3 = "18px";
var CLOSE_GLYPH_SIZE2 = "9px";
var RADIUS = "16px";
var ANIM_DURATION = "0.34s";
var ANIM_EASE = "cubic-bezier(.45,.05,.20,1.12)";
var GLASS_BLUR2 = "blur(14px) saturate(1.15)";
var TYPE2 = "500 12px/1.35";
var DETAIL_TYPE = "400 12px/1.5";
var META_TYPE = "500 10.5px/1";
var INLINE_ACTION_TYPE = "600 12px/1";
var INLINE_ACTION_ROOM = "58px";
var GLASS_DROP_INK2 = shadow7.inkGlassRgb;
var ON_SOLID_DROP_INK2 = shadow7.inkSolidRgb;
var glassCss2 = css17`
  background: ${rgba20(surface12[0], 0.72)};
  backdrop-filter: ${GLASS_BLUR2};
  -webkit-backdrop-filter: ${GLASS_BLUR2};
  border: 1px solid ${rgba20(surface12[0], 0.85)};
  box-shadow:
    0 1px 0 ${rgba20(surface12[0], 0.9)} inset,
    0 4px 10px rgba(${GLASS_DROP_INK2}, 0.1),
    0 1px 2px rgba(${GLASS_DROP_INK2}, 0.06);

  &.tl-toast-expanded {
    background: ${rgba20(surface12[0], 0.82)};
    box-shadow:
      0 1px 0 ${rgba20(surface12[0], 0.95)} inset,
      0 12px 28px rgba(${GLASS_DROP_INK2}, 0.18),
      0 2px 6px rgba(${GLASS_DROP_INK2}, 0.08);
  }

  .tl-toast-inline-action:hover {
    background: ${rgba20(brand18.cyan, 0.1)};
  }
`;
var onSolidCss5 = (solidLine) => css17`
  background: ${surface12[0]};
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: 1px solid ${solidLine};
  box-shadow:
    0 1px 2px rgba(${ON_SOLID_DROP_INK2}, 0.05),
    0 6px 18px rgba(${ON_SOLID_DROP_INK2}, 0.1);

  &.tl-toast-expanded {
    background: ${surface12[0]};
    box-shadow:
      0 2px 4px rgba(${ON_SOLID_DROP_INK2}, 0.06),
      0 14px 32px rgba(${ON_SOLID_DROP_INK2}, 0.16);
  }

  .tl-toast-inline-action:hover {
    background: ${rgba20(brand18.cyan, 0.06)};
  }

  /* The card's .toast.on-solid .tl-btn-destructive: on a solid panel the destructive mini-button
     drops Button's frost for a white body, a 1.5px error-strong hairline and no shadow. Scoped
     here, as the card scopes it — it is the toast's on-solid treatment, not a Button variant. The
     selector repeats the class to outrank Button's own hover rule. */
  .tl-toast-actions .tl-btn-destructive.tl-btn-destructive {
    background: ${surface12[0]};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border: 1.5px solid ${rgba20(semanticColor10.errorStrong, 0.3)};
    box-shadow: none;

    &:hover:not(:disabled):not(.is-loading) {
      background: ${rgba20(semanticColor10.errorStrong, 0.06)};
      border-color: ${rgba20(semanticColor10.errorStrong, 0.55)};
      box-shadow: none;
    }
  }
`;
var SURFACE_CSS4 = {
  "on-glass": {
    info: glassCss2,
    success: glassCss2,
    warning: glassCss2,
    error: glassCss2,
    neutral: glassCss2
  },
  "on-solid": {
    info: onSolidCss5(TONE3.info.solidLine),
    success: onSolidCss5(TONE3.success.solidLine),
    warning: onSolidCss5(TONE3.warning.solidLine),
    error: onSolidCss5(TONE3.error.solidLine),
    neutral: onSolidCss5(TONE3.neutral.solidLine)
  }
};
var focusRingCss = css17`
  &:focus-visible {
    outline: none;
    box-shadow: ${focus7.tight};
  }
`;
var StyledToast = styled25.div`
  box-sizing: border-box;
  position: relative;
  width: ${TOAST_W};
  max-width: 100%;
  min-height: ${MIN_H};
  padding: ${PAD_Y} calc(${CLOSE_OFFSET} + ${CLOSE_SIZE3} + 6px) ${PAD_Y}
    calc(${ICON_OFFSET} + ${ICON_SIZE2} + 8px);
  border-radius: ${RADIUS};
  color: ${ink24[2]};
  font: ${TYPE2} ${fontSans19};
  text-align: left;
  transition:
    box-shadow 0.32s ${motion13.easeUi},
    background 0.32s ${motion13.easeUi};

  &.tl-toast-has-inline-action {
    padding-right: calc(${CLOSE_OFFSET} + ${CLOSE_SIZE3} + ${INLINE_ACTION_ROOM});
  }

  .tl-toast-icon {
    position: absolute;
    top: ${ICON_OFFSET};
    left: ${ICON_OFFSET};
    width: ${ICON_SIZE2};
    height: ${ICON_SIZE2};
    border-radius: ${radius17.pill};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${ICON_GLYPH_SIZE2};
    background: ${({ $tone }) => rgba20(TONE3[$tone].tint, TONE3[$tone].tintAlpha)};
    color: ${({ $tone }) => TONE3[$tone].ink};
    box-shadow: inset 0 0 0 1px ${rgba20(surface12[0], 0.7)};

    i {
      font-size: ${ICON_GLYPH_SIZE2};
      width: auto;
      height: auto;
    }
  }

  .tl-toast-body {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  /* The title row. A button when the toast is expandable, a span otherwise; either way it is
     laid out identically, so the first line never shifts between the two states. */
  .tl-toast-title {
    display: block;
    width: 100%;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    font: ${TYPE2} ${fontSans19};
    color: ${ink24[2]};
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: ${radius17.sm};
  }

  button.tl-toast-title {
    cursor: pointer;
    ${focusRingCss}
  }

  .tl-toast-reveal {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows ${ANIM_DURATION} ${ANIM_EASE};
  }

  .tl-toast-reveal-inner {
    min-height: 0;
    overflow: hidden;
    overflow: clip;
    overflow-clip-margin-inline: 24px;
    opacity: 0;
    transform: translateY(-2px);
    /* Collapsed content must be out of the tab order and the accessibility tree, or Tab lands on
       an invisible Retry button. visibility does both, and its delayed transition keeps the
       content painted until the close animation has finished. */
    visibility: hidden;
    transition:
      opacity calc(${ANIM_DURATION} * 0.8) ease-out,
      transform ${ANIM_DURATION} ${ANIM_EASE},
      visibility 0s linear ${ANIM_DURATION};
  }

  &.tl-toast-expanded .tl-toast-reveal {
    grid-template-rows: 1fr;
  }

  &.tl-toast-expanded .tl-toast-reveal-inner {
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
    transition-delay: 0s;
  }

  .tl-toast-sub {
    display: block;
    min-width: 0;
    /* Frame 05's \`toast.sub\` (12/16, ink 3) is caption1: the token, not a literal (a reading of a
       page frame rather than of card 09 — PROVENANCE, \`floor-plan-page-identify\`). */
    ${typeRole7.caption1};
    color: ${ink24[3]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Frame 05 centres Undo against both lines. */
  &.tl-toast-has-sub .tl-toast-inline-action {
    top: 50%;
    transform: translateY(-50%);
  }

  .tl-toast-detail {
    display: block;
    padding-top: 6px;
    font: ${DETAIL_TYPE} ${fontSans19};
    color: ${ink24[2]};
    overflow-wrap: anywhere;
  }

  .tl-toast-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font: ${META_TYPE} ${fontMono7};
    letter-spacing: 0.02em;
    color: ${ink24[3]};
  }

  .tl-toast-actions {
    display: flex;
    gap: 6px;
    margin-top: 10px;
    justify-content: flex-end;
  }

  .tl-toast-close {
    position: absolute;
    top: ${CLOSE_OFFSET};
    right: ${CLOSE_OFFSET};
    width: ${CLOSE_SIZE3};
    height: ${CLOSE_SIZE3};
    padding: 0;
    border: none;
    border-radius: ${radius17.pill};
    background: none;
    color: ${ink24[3]};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${CLOSE_GLYPH_SIZE2};

    i {
      font-size: ${CLOSE_GLYPH_SIZE2};
      width: auto;
      height: auto;
    }

    /* The card's hover wash is rgba(0,0,0,.06). Black has no token; ink[1] — the colour the card
       already uses for the hovered glyph — at the same alpha is the token-bound form, and is
       indistinguishable at 6%. Deliberate; do not "restore" the literal. */
    &:hover {
      background: ${rgba20(ink24[1], 0.06)};
      color: ${ink24[1]};
    }

    ${focusRingCss}
  }

  .tl-toast-inline-action {
    position: absolute;
    top: 3px;
    right: calc(${CLOSE_OFFSET} + ${CLOSE_SIZE3} + 4px);
    height: 24px;
    padding: 0 9px;
    border: none;
    border-radius: ${radius17.pill};
    background: none;
    cursor: pointer;
    font: ${INLINE_ACTION_TYPE} ${fontSans19};
    letter-spacing: -0.1px;
    color: ${brand18.cyanPress};
    transition: background ${motion13.durFast} ${motion13.easeUi};

    ${focusRingCss}
  }

  .tl-toast-sr {
    ${visuallyHiddenCss}
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    .tl-toast-reveal,
    .tl-toast-reveal-inner,
    .tl-toast-inline-action {
      transition: none;
    }
  }

  ${({ $tone, $surface }) => SURFACE_CSS4[$surface][$tone]}
`;
var isRenderable = (node) => node !== null && node !== void 0 && node !== false && node !== "";
var Toast = React22.forwardRef(function Toast2(props, ref) {
  const {
    tone: toneProp = "info",
    surface: surfaceProp = "on-glass",
    icon: icon2,
    title,
    onDismiss,
    dismissLabel = "Dismiss notification",
    onPauseChange,
    className,
    detail,
    meta,
    actions,
    inlineAction,
    sub,
    defaultExpanded = false,
    ...rest
  } = props;
  const hasSub = !!inlineAction && typeof sub === "string" && sub.trim() !== "";
  const tone = variant(TONE3, toneProp, "info", "Toast", "tone");
  const toastSurface = variant(SURFACE_CSS4, surfaceProp, "on-glass", "Toast", "surface");
  const hasDetail = isRenderable(detail);
  const hasMeta = isRenderable(meta);
  const expandable = !inlineAction && (hasDetail || hasMeta || actions !== void 0 && actions.length > 0);
  const [expanded, setExpanded] = React22.useState(expandable && defaultExpanded);
  const revealId = React22.useId();
  const hovered = React22.useRef(false);
  const focused = React22.useRef(false);
  const reported = React22.useRef(false);
  const report = () => {
    const paused = hovered.current || focused.current;
    if (paused !== reported.current) {
      reported.current = paused;
      onPauseChange?.(paused);
    }
  };
  const onPauseChangeRef = React22.useRef(onPauseChange);
  onPauseChangeRef.current = onPauseChange;
  React22.useEffect(
    () => () => {
      if (reported.current) {
        reported.current = false;
        onPauseChangeRef.current?.(false);
      }
    },
    []
  );
  const onBlur = (e) => {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    focused.current = false;
    report();
  };
  const onKeyDown = (e) => {
    if (e.key === "Escape" && onDismiss) {
      e.stopPropagation();
      onDismiss();
    }
  };
  const actionVariant = toastSurface === "on-solid" ? "ghost" : "frosted";
  const prefix = TONE3[tone].prefix;
  return /* @__PURE__ */ jsxs23(
    StyledToast,
    {
      ...rest,
      ref,
      className: [
        "tl-toast",
        `tl-toast-${tone}`,
        `tl-toast-${toastSurface}`,
        expanded ? "tl-toast-expanded" : "",
        inlineAction ? "tl-toast-has-inline-action" : "",
        hasSub ? "tl-toast-has-sub" : "",
        className ?? ""
      ].filter(Boolean).join(" "),
      $tone: tone,
      $surface: toastSurface,
      "data-tone": tone,
      "data-surface": toastSurface,
      onPointerEnter: () => {
        hovered.current = true;
        report();
      },
      onPointerLeave: () => {
        hovered.current = false;
        report();
      },
      onFocus: () => {
        focused.current = true;
        report();
      },
      onBlur,
      onKeyDown,
      children: [
        /* @__PURE__ */ jsx25("span", { className: "tl-toast-icon", "aria-hidden": "true", children: icon2 }),
        /* @__PURE__ */ jsxs23("div", { className: "tl-toast-body", children: [
          expandable ? /* @__PURE__ */ jsxs23(
            "button",
            {
              type: "button",
              className: "tl-toast-title",
              "aria-expanded": expanded,
              "aria-controls": revealId,
              onClick: () => setExpanded((v) => !v),
              children: [
                prefix ? /* @__PURE__ */ jsx25("span", { className: "tl-toast-sr", children: prefix }) : null,
                title
              ]
            }
          ) : /* @__PURE__ */ jsxs23("span", { className: "tl-toast-title", children: [
            prefix ? /* @__PURE__ */ jsx25("span", { className: "tl-toast-sr", children: prefix }) : null,
            title
          ] }),
          hasSub ? /* @__PURE__ */ jsx25("span", { className: "tl-toast-sub", children: sub }) : null,
          expandable ? /* @__PURE__ */ jsx25("div", { className: "tl-toast-reveal", id: revealId, children: /* @__PURE__ */ jsxs23("div", { className: "tl-toast-reveal-inner", children: [
            hasDetail ? /* @__PURE__ */ jsx25("span", { className: "tl-toast-detail", children: detail }) : null,
            hasMeta ? /* @__PURE__ */ jsx25("span", { className: "tl-toast-meta", children: meta }) : null,
            actions && actions.length > 0 ? /* @__PURE__ */ jsx25("span", { className: "tl-toast-actions", children: actions.map((a) => /* @__PURE__ */ jsx25(
              Button,
              {
                size: "xs",
                variant: a.destructive ? "destructive" : actionVariant,
                onClick: a.onAction,
                children: a.label
              },
              a.label
            )) }) : null
          ] }) }) : null
        ] }),
        inlineAction ? /* @__PURE__ */ jsx25(
          "button",
          {
            type: "button",
            className: "tl-toast-inline-action",
            onClick: () => {
              try {
                inlineAction.onAction();
              } finally {
                onDismiss?.();
              }
            },
            children: inlineAction.label
          }
        ) : null,
        onDismiss ? /* @__PURE__ */ jsx25(
          "button",
          {
            type: "button",
            className: "tl-toast-close",
            "aria-label": dismissLabel,
            onClick: onDismiss,
            children: /* @__PURE__ */ jsx25(Icon, { name: "xmark", variant: "solid", size: "xs" })
          }
        ) : null
      ]
    }
  );
});
Toast.displayName = "Toast";

// src/Feedback/ToastProvider.tsx
import React23 from "react";
import { createPortal } from "react-dom";
import styled26, { css as css18 } from "styled-components";
import { space as space8, zIndex } from "@repo/design-tokens";

// src/internal/liveLayer.ts
var LIVE_LAYER_ATTR = "data-tl-live-layer";

// src/Feedback/toastQueue.ts
var VIEWPORT_REGION = "__viewport__";
var seq = 0;
function nextToastId() {
  seq += 1;
  return `tl-toast-${seq}`;
}
function addToast(queue, entry) {
  return [entry, ...queue];
}
function dismissToast(queue, id) {
  if (!queue.some((e) => e.id === id)) return queue;
  return queue.filter((e) => e.id !== id);
}
function rehomeToasts(queue, region) {
  if (!queue.some((e) => e.region === region)) return queue;
  return queue.map((e) => e.region === region ? { ...e, region: VIEWPORT_REGION } : e);
}
function toastsIn(queue, region) {
  return queue.filter((e) => e.region === region);
}
function resolveRegion(requested, mounted) {
  if (requested === void 0) return { region: VIEWPORT_REGION, fellBack: false };
  if (mounted.has(requested)) return { region: requested, fellBack: false };
  return { region: VIEWPORT_REGION, fellBack: true };
}

// src/Feedback/ToastProvider.tsx
import { Fragment as Fragment6, jsx as jsx26, jsxs as jsxs24 } from "react/jsx-runtime";
var TOAST_TIMEOUT_MS = 5e3;
var TOAST_HOTKEY = "F8";
var ToastApiContext = React23.createContext(null);
var ToastStateContext = React23.createContext(null);
var hasDom = () => typeof document !== "undefined" && !!document.body;
var useIsoLayoutEffect = typeof window !== "undefined" ? React23.useLayoutEffect : React23.useEffect;
var warn2 = (message) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[@repo/design-system] ${message}`);
  }
};
function announcementFor(content) {
  const prefix = content.tone === "error" ? "Error: " : content.tone === "warning" ? "Warning: " : "";
  const title = content.title.trim();
  if (!content.inlineAction) return `${prefix}${title}`;
  const end = (text) => /[.!?…]$/.test(text) ? text : `${text}.`;
  const sub = content.sub?.trim();
  const said = sub ? `${end(title)} ${end(sub)}` : end(title);
  return `${prefix}${said} ${content.inlineAction.label} available.`;
}
var BOTTOM_INSET_VAR = "--tl-toast-bottom-inset";
var PLACEMENT_CSS = {
  viewport: css18`
    position: fixed;
    bottom: 16px;
    right: 16px;
    align-items: flex-end;
  `,
  "in-container": css18`
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
  `,
  /*
   * The onboarding glass steps' placement (Rob's Onboarding Glass Panels canvas, adopted by the
   * onboarding-persistent-scene epic; `onboarding-persistent-scene-floor-plan-glass` R6): centred in
   * its container, its bottom edge at the host's inset, growing upwards with the newest toast
   * nearest the bottom edge.
   */
  "bottom-centre": css18`
    position: absolute;
    bottom: var(${BOTTOM_INSET_VAR});
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
    flex-direction: column-reverse;
  `
};
var StyledStack = styled26.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: calc(100% - 24px);
  z-index: ${zIndex.popover};
  /* An empty or narrow stack must not swallow clicks meant for what is under it. */
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  ${({ $placement }) => PLACEMENT_CSS[$placement]}
`;
var Announcer = styled26.div`
  ${visuallyHiddenCss}
`;
var isFocusable = (el) => el instanceof HTMLElement && !el.hasAttribute("disabled") && el.tabIndex >= 0;
function focusablesIn(root) {
  return Array.from(root.querySelectorAll("button")).filter(
    (el) => isFocusable(el) && !el.closest(".tl-toast:not(.tl-toast-expanded) .tl-toast-reveal")
  );
}
function Stack2({
  region,
  placement,
  bottomInset
}) {
  const ctx = React23.useContext(ToastStateContext);
  if (!ctx) throw new Error("Toast stack rendered outside <ToastProvider>");
  const entries = toastsIn(ctx.queue, region);
  const rootRef = React23.useRef(null);
  const origin = React23.useRef(null);
  const lastFocused = React23.useRef(null);
  const returnFocus = () => {
    const to = origin.current;
    origin.current = null;
    if (to && to.isConnected) to.focus();
  };
  const onFocusCapture = (e) => {
    const from = e.relatedTarget;
    if (from instanceof HTMLElement && !e.currentTarget.contains(from)) origin.current = from;
    if (e.target instanceof HTMLElement) lastFocused.current = e.target;
  };
  const onBlurCapture = (e) => {
    const to = e.relatedTarget;
    if (to instanceof Node && e.currentTarget.contains(to)) return;
    if (to) {
      lastFocused.current = null;
      return;
    }
    const was = e.target;
    void Promise.resolve().then(() => {
      if (lastFocused.current === was && was instanceof HTMLElement && was.isConnected) {
        lastFocused.current = null;
      }
    });
  };
  const ids = entries.map((e) => e.id).join(" ");
  useIsoLayoutEffect(() => {
    const root = rootRef.current;
    const lost = lastFocused.current;
    if (!root || !lost || lost.isConnected) return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    lastFocused.current = null;
    const next = focusablesIn(root)[0];
    if (next) next.focus();
    else returnFocus();
  }, [ids]);
  const onKeyDownCapture = (e) => {
    if (placement !== "viewport") return;
    if (e.key !== "Tab" || !rootRef.current || !origin.current) return;
    const items = focusablesIn(rootRef.current);
    const atEnd = e.shiftKey ? document.activeElement === items[0] : document.activeElement === items[items.length - 1];
    if (atEnd) {
      e.preventDefault();
      returnFocus();
    }
  };
  const onKeyDown = (e) => {
    if (placement === "viewport" && e.key === "Tab") e.stopPropagation();
  };
  return /* @__PURE__ */ jsx26(
    StyledStack,
    {
      ref: rootRef,
      className: `tl-toast-stack tl-toast-stack-${placement}`,
      "data-region": region,
      $placement: placement,
      style: placement === "bottom-centre" ? { [BOTTOM_INSET_VAR]: bottomInset } : void 0,
      ...placement === "viewport" ? { role: "region", "aria-label": "Notifications" } : {},
      onFocusCapture,
      onBlurCapture,
      onKeyDownCapture,
      onKeyDown,
      children: entries.map(({ id, options }) => /* @__PURE__ */ jsx26(
        Toast,
        {
          ...options,
          id,
          "data-toast-id": id,
          onDismiss: () => ctx.dismiss(id),
          onPauseChange: (paused) => ctx.setPaused(id, paused)
        },
        id
      ))
    }
  );
}
function ToastProvider({ children }) {
  const [queue, setQueue] = React23.useState([]);
  const [announcements, setAnnouncements] = React23.useState([]);
  const [mounted, setMounted] = React23.useState(false);
  const pendingAnnouncements = React23.useRef([]);
  const mountedRef = React23.useRef(false);
  const previousIds = React23.useRef(/* @__PURE__ */ new Set());
  const [ownersVersion, setOwnersVersion] = React23.useState(0);
  const timers = React23.useRef(/* @__PURE__ */ new Map());
  const paused = React23.useRef(/* @__PURE__ */ new Set());
  const owners = React23.useRef(/* @__PURE__ */ new Map());
  const queueRef = React23.useRef(queue);
  queueRef.current = queue;
  const clearTimer = React23.useCallback((id) => {
    const t = timers.current.get(id);
    if (t !== void 0) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);
  const startTimer = React23.useCallback(
    (id) => {
      clearTimer(id);
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          setQueue((q) => dismissToast(q, id));
        }, TOAST_TIMEOUT_MS)
      );
    },
    [clearTimer]
  );
  const dismiss = React23.useCallback(
    (id) => {
      clearTimer(id);
      paused.current.delete(id);
      setQueue((q) => dismissToast(q, id));
    },
    [clearTimer]
  );
  const show = React23.useCallback((options) => {
    const id = nextToastId();
    const { region: requested, ...content } = options;
    const { region, fellBack } = resolveRegion(requested, new Set(owners.current.keys()));
    if (fellBack) {
      warn2(
        `useToast().show: no <ToastRegion name="${requested}" /> is mounted; the toast was shown in the viewport stack instead.`
      );
    }
    setQueue((q) => addToast(q, { id, region, options: content }));
    const announcement = {
      id,
      text: announcementFor(content),
      channel: content.tone === "error" ? "assertive" : "polite"
    };
    if (mountedRef.current) setAnnouncements((list) => [...list, announcement]);
    else pendingAnnouncements.current.push(announcement);
    return id;
  }, []);
  const setPaused = React23.useCallback(
    (id, isPaused) => {
      if (isPaused) {
        paused.current.add(id);
        clearTimer(id);
      } else {
        paused.current.delete(id);
        if (mountedRef.current && queueRef.current.some((e) => e.id === id)) startTimer(id);
      }
    },
    [clearTimer, startTimer]
  );
  const registerRegion = React23.useCallback(
    (name, token) => {
      const list = owners.current.get(name) ?? [];
      if (list.length > 0) {
        warn2(
          `<ToastRegion name="${name}" /> is mounted twice; the most recently mounted one is used.`
        );
      }
      owners.current.set(name, [...list, token]);
      setOwnersVersion((v) => v + 1);
      return () => {
        const remaining = (owners.current.get(name) ?? []).filter((t) => t !== token);
        if (remaining.length > 0) owners.current.set(name, remaining);
        else owners.current.delete(name);
        setOwnersVersion((v) => v + 1);
        void Promise.resolve().then(() => {
          if (owners.current.has(name)) return;
          const moved = queueRef.current.filter((e) => e.region === name);
          if (moved.length === 0) return;
          moved.forEach((e) => {
            paused.current.delete(e.id);
            clearTimer(e.id);
          });
          setQueue((q) => rehomeToasts(q, name));
        });
      };
    },
    [clearTimer]
  );
  const regionOwner = React23.useCallback((name) => {
    const list = owners.current.get(name);
    return list ? list[list.length - 1] : void 0;
  }, []);
  React23.useEffect(() => {
    const present = new Set(queue.map((e) => e.id));
    queue.forEach((e) => {
      if (!timers.current.has(e.id) && !paused.current.has(e.id)) startTimer(e.id);
    });
    timers.current.forEach((_t, id) => {
      if (!present.has(id)) clearTimer(id);
    });
    paused.current.forEach((id) => {
      if (!present.has(id)) paused.current.delete(id);
    });
    const left = new Set([...previousIds.current].filter((id) => !present.has(id)));
    previousIds.current = present;
    if (left.size > 0) {
      setAnnouncements(
        (list) => list.some((a) => left.has(a.id)) ? list.filter((a) => !left.has(a.id)) : list
      );
    }
  }, [queue, startTimer, clearTimer]);
  React23.useEffect(() => {
    const map = timers.current;
    mountedRef.current = true;
    setMounted(true);
    return () => {
      mountedRef.current = false;
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);
  React23.useEffect(() => {
    if (!mounted || pendingAnnouncements.current.length === 0) return;
    const held = pendingAnnouncements.current.splice(0);
    setAnnouncements((list) => [...list, ...held]);
  }, [mounted]);
  React23.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== TOAST_HOTKEY || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      for (const entry of queueRef.current) {
        const el = document.querySelector(`[data-toast-id="${entry.id}"]`);
        const target = el?.querySelector("button");
        if (target instanceof HTMLElement && !target.closest("[inert]")) {
          e.preventDefault();
          target.focus();
          return;
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
  const api = React23.useMemo(() => ({ show, dismiss }), [show, dismiss]);
  const state = React23.useMemo(
    () => ({ queue, dismiss, setPaused, registerRegion, regionOwner, ownersVersion }),
    [queue, dismiss, setPaused, registerRegion, regionOwner, ownersVersion]
  );
  return /* @__PURE__ */ jsx26(ToastApiContext.Provider, { value: api, children: /* @__PURE__ */ jsxs24(ToastStateContext.Provider, { value: state, children: [
    children,
    mounted && hasDom() ? createPortal(
      /* ONE marked body child, so an open Modal leaves it live (internal/liveLayer.ts).
         A plain block: no position, z-index or transform, so it starts no stacking
         context and the stack's own zIndex.popover still compares against the dialog's. */
      /* @__PURE__ */ jsxs24("div", { className: "tl-toast-layer", ...{ [LIVE_LAYER_ATTR]: "toast" }, children: [
        /* @__PURE__ */ jsx26(Stack2, { region: VIEWPORT_REGION, placement: "viewport" }),
        /* @__PURE__ */ jsx26(
          Announcer,
          {
            className: "tl-toast-announcer",
            "aria-live": "polite",
            "aria-atomic": "false",
            role: "status",
            children: announcements.filter((a) => a.channel === "polite").map((a) => /* @__PURE__ */ jsx26("span", { children: a.text }, a.id))
          }
        ),
        /* @__PURE__ */ jsx26(Announcer, { className: "tl-toast-announcer", role: "alert", "aria-atomic": "false", children: announcements.filter((a) => a.channel === "assertive").map((a) => /* @__PURE__ */ jsx26("span", { children: a.text }, a.id)) })
      ] }),
      document.body
    ) : null
  ] }) });
}
function ToastRegion({
  name,
  placement = "top-centre",
  bottomInset = space8[2]
}) {
  const ctx = React23.useContext(ToastStateContext);
  if (!ctx) throw new Error("ToastRegion must be used inside <ToastProvider>");
  const [token] = React23.useState(() => Symbol(name));
  const probe = React23.useRef(null);
  const { registerRegion } = ctx;
  useIsoLayoutEffect(() => registerRegion(name, token), [registerRegion, name, token]);
  useIsoLayoutEffect(() => {
    const parent = probe.current?.parentElement;
    const position = parent ? window.getComputedStyle(parent).position : "relative";
    if (position === "static" || position === "") {
      warn2(
        `<ToastRegion name="${name}" />'s container is not positioned, so the stack will place itself against some other ancestor. Give the container position: relative.`
      );
    }
  }, [name]);
  return /* @__PURE__ */ jsxs24(Fragment6, { children: [
    /* @__PURE__ */ jsx26("span", { ref: probe, hidden: true }),
    ctx.regionOwner(name) === token ? placement === "bottom-centre" ? /* @__PURE__ */ jsx26(Stack2, { region: name, placement: "bottom-centre", bottomInset }) : /* @__PURE__ */ jsx26(Stack2, { region: name, placement: "in-container" }) : null
  ] });
}
function useToast() {
  const api = React23.useContext(ToastApiContext);
  if (!api) throw new Error("useToast must be used inside <ToastProvider>");
  return api;
}

// src/Feedback/Progress.tsx
import React24 from "react";
import styled27, { keyframes as keyframes2 } from "styled-components";
import { brand as brand19, motion as motion14, radius as radius18, rgba as rgba21, SELECTED_RAMP as SELECTED_RAMP4, shadow as shadow8, surface as surface13 } from "@repo/design-tokens";
import { jsx as jsx27 } from "react/jsx-runtime";
var PROGRESS_GLOSS_MS = 2600;
var PROGRESS_SLIDE_MS = 1600;
var DEFAULT_MAX = 100;
var TEAL_ACCENT = SELECTED_RAMP4[5];
var TEAL_MED_LIGHT = SELECTED_RAMP4[3];
var GLOW = SELECTED_RAMP4[6];
var warn3 = (message) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`Progress: ${message}`);
  }
};
function progressPercent(value, max = DEFAULT_MAX) {
  const invalidMax = !(Number.isFinite(max) && max > 0);
  const invalidValue = !Number.isFinite(value);
  const effectiveMax = invalidMax ? DEFAULT_MAX : max;
  const now = Math.min(Math.max(invalidValue ? 0 : value, 0), effectiveMax);
  return { percent: now / effectiveMax * 100, now, max: effectiveMax, invalidMax, invalidValue };
}
var gloss = keyframes2`
  0% { transform: translateX(-100%); }
  60%, 100% { transform: translateX(100%); }
`;
var slide = keyframes2`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;
var Track = styled27.div`
  width: 100%;
  height: 8px;
  border-radius: ${radius18.pill};
  position: relative;
  overflow: hidden;
  background: linear-gradient(90deg, ${surface13[1]} 0%, ${surface13[3]} 100%);
  box-shadow:
    inset 0 1px 2px rgba(${shadow8.inkGlassRgb}, 0.06),
    0 0 0 1px ${rgba21(surface13[0], 0.7)},
    0 0 10px 1px ${rgba21(brand19.frost, 0.45)};

  .tl-progress-fill {
    height: 100%;
    border-radius: ${radius18.pill};
    position: relative;
    overflow: hidden;
    background: linear-gradient(
      90deg,
      ${TEAL_ACCENT} 0%,
      ${TEAL_ACCENT} 55%,
      ${TEAL_MED_LIGHT} 100%
    );
    box-shadow:
      inset 0 1px 0 ${rgba21(surface13[0], 0.55)},
      inset 0 -2px 4px ${rgba21(GLOW, 0.18)},
      0 0 8px 1px ${rgba21(GLOW, 0.35)},
      0 0 14px 2px ${rgba21(brand19.frost, 0.55)};
    transition: width ${motion14.durSlow} ${motion14.easeUi};
  }

  .tl-progress-fill::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
      105deg,
      transparent 30%,
      ${rgba21(surface13[0], 0.55)} 50%,
      transparent 70%
    );
    transform: translateX(-100%);
    animation: ${gloss} ${PROGRESS_GLOSS_MS}ms ease-in-out infinite;
    mix-blend-mode: screen;
    pointer-events: none;
  }

  &.tl-progress-indeterminate .tl-progress-fill {
    width: 40%;
    transition: none;
    animation: ${slide} ${PROGRESS_SLIDE_MS}ms ease-in-out infinite;
  }

  &.tl-progress-indeterminate .tl-progress-fill::after {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .tl-progress-fill {
      transition: none;
    }
    .tl-progress-fill::after {
      display: none;
    }
  }
`;
var Progress = React24.forwardRef(
  function Progress2(props, ref) {
    const {
      value,
      max,
      valueText,
      label,
      "aria-labelledby": labelledBy,
      className,
      ...rest
    } = props;
    const indeterminate = value === void 0;
    const resolved = indeterminate ? null : progressPercent(value, max);
    const invalidMax = resolved?.invalidMax ?? false;
    const invalidValue = resolved?.invalidValue ?? false;
    const emptyLabel = label !== void 0 && label.trim() === "";
    const warned = React24.useRef("");
    React24.useEffect(() => {
      const signature = `${invalidMax ? String(max) : ""}|${invalidValue ? String(value) : ""}|${emptyLabel}`;
      if (signature === warned.current) return;
      warned.current = signature;
      if (invalidMax) {
        warn3(`max must be a finite number above zero; got ${String(max)}, using ${DEFAULT_MAX}.`);
      }
      if (invalidValue) warn3(`value must be a finite number; got ${String(value)}, using 0.`);
      if (emptyLabel) warn3("label is empty, so the bar has no accessible name.");
    });
    return /* @__PURE__ */ jsx27(
      Track,
      {
        ...rest,
        ref,
        role: "progressbar",
        tabIndex: void 0,
        className: [
          "tl-progress",
          indeterminate ? "tl-progress-indeterminate" : "",
          className ?? ""
        ].filter(Boolean).join(" "),
        "aria-label": label,
        "aria-labelledby": label === void 0 ? labelledBy : void 0,
        "aria-valuemin": 0,
        "aria-valuemax": resolved?.max,
        "aria-valuenow": resolved?.now,
        "aria-valuetext": resolved ? valueText : void 0,
        "aria-busy": indeterminate ? true : void 0,
        children: /* @__PURE__ */ jsx27(
          "div",
          {
            className: "tl-progress-fill",
            style: resolved ? { width: `${resolved.percent}%` } : void 0
          },
          indeterminate ? "indeterminate" : "determinate"
        )
      }
    );
  }
);
Progress.displayName = "Progress";

// src/Feedback/Spinner.tsx
import styled28 from "styled-components";
import { jsx as jsx28 } from "react/jsx-runtime";
var SPINNER_SIZES = {
  sm: "24px",
  md: "48px",
  lg: "96px",
  xl: "160px"
};
var Loop = styled28.span`
  display: inline-block;
  flex: none;
  width: ${(p) => p.$size};
  height: ${(p) => p.$size};
  background: ${(p) => p.$src ? `url("${p.$src}") center / contain no-repeat` : "none"};
`;
var Spinner = ({ src, size = "md", label, className }) => {
  const name = label?.trim();
  const a11y = name ? { role: "img", "aria-label": name } : { "aria-hidden": true };
  return /* @__PURE__ */ jsx28(
    Loop,
    {
      className: className ? `tl-spinner ${className}` : "tl-spinner",
      $size: SPINNER_SIZES[size],
      $src: src,
      ...a11y
    }
  );
};

// src/Tables/Table.tsx
import React26 from "react";
import styled30, { createGlobalStyle } from "styled-components";
import {
  accent as accent9,
  brand as brand22,
  focus as focus8,
  fontMono as fontMono8,
  glass as glass4,
  fontSans as fontSans20,
  ink as ink25,
  inkOn as inkOn10,
  line as line10,
  motion as motion17,
  radius as radius20,
  rgba as rgba22,
  semantic as semantic5,
  semanticColor as semanticColor11,
  shadow as shadow10,
  space as space9,
  support as support8,
  surface as surface15,
  tracking as tracking3,
  typeScale as typeScale8,
  weight as weight10,
  INK_RAMP,
  SELECTED_RAMP as SELECTED_RAMP6,
  stopForValue
} from "@repo/design-tokens";

// src/Scrollbars/Scrollbar.tsx
import { useRef as useRef2, useEffect as useEffect3, useLayoutEffect, useState as useState2, useCallback } from "react";
import styled29 from "styled-components";

// src/internal/pointer.ts
var SCROLLBAR_MARKER = "data-tl-scrollbar";
var clientBoxOf = (el) => {
  if (el === el.ownerDocument.documentElement) {
    return { left: 0, top: 0, right: el.clientWidth, bottom: el.clientHeight };
  }
  const rect = el.getBoundingClientRect();
  const left = rect.left + el.clientLeft;
  const top = rect.top + el.clientTop;
  return { left, top, right: left + el.clientWidth, bottom: top + el.clientHeight };
};
var isScrollbarPress = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  if (target.closest(`[${SCROLLBAR_MARKER}]`) !== null) return true;
  if (target.clientWidth === 0 || target.clientHeight === 0) return false;
  const overflowsX = target.scrollWidth > target.clientWidth;
  const overflowsY = target.scrollHeight > target.clientHeight;
  if (!overflowsX && !overflowsY) return false;
  const box = clientBoxOf(target);
  return event.clientX < box.left || event.clientX >= box.right || event.clientY < box.top || event.clientY >= box.bottom;
};

// src/Scrollbars/scrollbarSkin.ts
import { brand as brand20, glassCard as glassCard2, motion as motion15, scroll, shadow as shadow9 } from "@repo/design-tokens";
var TRACK_WIDTH = "12px";
var TRACK_RADIUS = "7px";
var MIN_THUMB_LENGTH = 28;
var SKIN_TRANSITION = `background ${motion15.durFast} ${motion15.easeUi},
      box-shadow ${motion15.durFast} ${motion15.easeUi}`;
var solid = (alpha) => `rgba(${scroll.solidBaseRgb}, ${alpha})`;
var frost = (alpha) => `rgba(${glassCard2.frost}, ${alpha})`;
var COLOURS = {
  glass: {
    /** `line[1]` at 22% — the line-1 hue family, so the bar inherits the violet-cool neutrals
        instead of dropping into grey. */
    trackIdle: "rgba(217, 222, 232, 0.22)",
    trackIdleShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.10)",
    trackActive: scroll.trackGlass,
    trackActiveShadow: `inset 0 1px 2px ${scroll.trackGlassRecess}, inset 0 0 0 1px rgba(255, 255, 255, 0.10)`,
    thumbIdle: frost("0.50"),
    thumbIdleShadow: `0 0 4px 0 ${frost("0.35")}, inset 0 1px 0 rgba(255, 255, 255, 0.45)`,
    thumbHover: frost("0.92"),
    thumbHoverShadow: `0 0 8px 1px ${frost("0.65")}, inset 0 1px 0 rgba(255, 255, 255, 0.7)`,
    thumbDrag: brand20.frost,
    thumbDragShadow: shadow9.scrollThumbDrag
  },
  onSolid: {
    /* Card: "Only the alpha changes — one hue across the whole bar." */
    trackIdle: solid("0.05"),
    trackIdleShadow: "none",
    trackActive: solid("0.12"),
    trackActiveShadow: `inset 0 1px 2px ${scroll.solidRecess}`,
    thumbIdle: solid("0.34"),
    thumbIdleShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.55)",
    thumbHover: solid("0.55"),
    thumbHoverShadow: "0 0 0 1px rgba(255, 255, 255, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
    thumbDrag: solid("0.82"),
    /* A frost halo is invisible on white, so drag takes a hard ink drop instead.
       The ink was an un-tokened literal here — recorded as the missing
       --tl-shadow-scroll-thumb-drag-solid, which the 2026-07-30 scroll-token ruling had
       missed because its audit covered the GLASS thumb-drag shadow only. Rob's 2026-07-31
       shadow-ink ruling (raised by Banner, which needed the same ink at .04/.06) names it
       once as channels, so this is now token-sourced. */
    thumbDragShadow: `0 2px 5px rgba(${shadow9.inkSolidRgb}, 0.30), inset 0 1px 0 rgba(255, 255, 255, 0.6)`
  }
};
var variantCss = (v) => {
  const c = COLOURS[v];
  return `
  .scroll-track {
    background: ${c.trackIdle};
    box-shadow: ${c.trackIdleShadow};
  }

  .scroll-thumb {
    background: ${c.thumbIdle};
    box-shadow: ${c.thumbIdleShadow};
  }

  .scroll-track:hover {
    background: ${c.trackActive};
    box-shadow: ${c.trackActiveShadow};
  }

  .scroll-track:hover .scroll-thumb {
    background: ${c.thumbHover};
    box-shadow: ${c.thumbHoverShadow};
  }

  &.is-active .scroll-track {
    background: ${c.trackActive};
    box-shadow: ${c.trackActiveShadow};
  }

  &.is-active .scroll-thumb {
    background: ${c.thumbDrag};
    box-shadow: ${c.thumbDragShadow};
  }
`;
};

// src/Scrollbars/Scrollbar.tsx
import { jsx as jsx29, jsxs as jsxs25 } from "react/jsx-runtime";
var TRACK_INSET_RIGHT = 4;
var GUTTER = "22px";
var INSET_Y = "8px";
var INSET_TOP_PINNED_HEAD = "33px";
var ScrollbarBase = ({
  children,
  className,
  maxHeight,
  right,
  dynamicHeight,
  contentUpdated,
  fadeOutEnabled = false,
  onScrollStatusChange,
  variant: variant2 = "glass",
  role,
  contentRole,
  viewportRef
}) => {
  const contentRef = useRef2(null);
  const setContentNode = useCallback(
    (node) => {
      contentRef.current = node;
      if (typeof viewportRef === "function") viewportRef(node);
      else if (viewportRef) {
        viewportRef.current = node;
      }
    },
    [viewportRef]
  );
  const trackRef = useRef2(null);
  const thumbRef = useRef2(null);
  const initialYRef = useRef2(0);
  const scrollStartRef = useRef2(0);
  const dragListenersRef = useRef2(null);
  const [thumbHeight, setThumbHeight] = useState2(0);
  const [isDragging, setIsDragging] = useState2(false);
  const [maxDynamicHeight, setMaxDynamicHeight] = useState2("auto");
  const [scrollStatus, setScrollStatus] = useState2("top");
  const [overflows, setOverflows] = useState2(false);
  const trackHeight = () => trackRef.current ? trackRef.current.getBoundingClientRect().height : 0;
  const thumbHeightRef = useRef2(0);
  const thumbSize = () => thumbHeightRef.current || (thumbRef.current?.getBoundingClientRect().height ?? 0);
  const updateThumbHeight = () => {
    if (!contentRef.current) return;
    const contentHeight = contentRef.current.scrollHeight;
    const containerHeight = contentRef.current.clientHeight;
    const doesOverflow = contentHeight > containerHeight;
    setOverflows((prev) => prev === doesOverflow ? prev : doesOverflow);
    if (doesOverflow) {
      const ratio = containerHeight / contentHeight;
      const calculatedThumbHeight = Math.max(MIN_THUMB_LENGTH, trackHeight() * ratio);
      thumbHeightRef.current = calculatedThumbHeight;
      setThumbHeight(
        (prev) => Math.abs(prev - calculatedThumbHeight) < 0.5 ? prev : calculatedThumbHeight
      );
    } else {
      thumbHeightRef.current = 0;
      setThumbHeight((prev) => prev === 0 ? prev : 0);
    }
  };
  const updateMaxHeight = () => {
    if (contentRef.current) {
      const rect = contentRef.current.getBoundingClientRect();
      const bottomHeight = 50;
      const availableHeight = window.innerHeight - rect.top - bottomHeight;
      const next = rect.bottom <= window.innerHeight ? `${availableHeight}px` : "auto";
      setMaxDynamicHeight((prev) => prev === next ? prev : next);
    }
  };
  const updateThumbPosition = () => {
    if (!contentRef.current || !thumbRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const scrollRange = scrollHeight - clientHeight;
    const travel = trackHeight() - thumbSize();
    const top = scrollRange > 0 ? scrollTop / scrollRange * travel : 0;
    const clamped = Number.isFinite(top) ? Math.min(Math.max(0, top), Math.max(0, travel)) : 0;
    thumbRef.current.style.top = `${clamped}px`;
  };
  const determineScrollStatus = () => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    let newStatus;
    if (scrollTop === 0) {
      newStatus = "top";
    } else if (scrollTop + clientHeight >= scrollHeight - 1) {
      newStatus = "bottom";
    } else {
      newStatus = "scrolling";
    }
    if (newStatus !== scrollStatus) {
      setScrollStatus(newStatus);
      if (onScrollStatusChange) {
        onScrollStatusChange(newStatus);
      }
    }
  };
  const handleScroll = () => {
    updateThumbPosition();
    determineScrollStatus();
  };
  const endDrag = () => {
    const listeners = dragListenersRef.current;
    if (listeners === null) return;
    dragListenersRef.current = null;
    window.removeEventListener("mousemove", listeners.move);
    window.removeEventListener("mouseup", listeners.up);
  };
  useEffect3(() => endDrag, []);
  const handleThumbDrag = (e) => {
    e.preventDefault();
    endDrag();
    setIsDragging(true);
    initialYRef.current = e.clientY;
    scrollStartRef.current = contentRef.current ? contentRef.current.scrollTop : 0;
    const onMouseMove = (moveEvent) => {
      if ((moveEvent.buttons & 1) === 0) {
        onMouseUp();
        return;
      }
      if (contentRef.current && thumbRef.current) {
        const deltaY = moveEvent.clientY - initialYRef.current;
        const { scrollHeight, clientHeight } = contentRef.current;
        const travel = trackHeight() - thumbSize();
        const scrollRange = scrollHeight - clientHeight;
        const scrollAmount = travel > 0 ? deltaY / travel * scrollRange : 0;
        contentRef.current.scrollTop = scrollStartRef.current + scrollAmount;
        requestAnimationFrame(() => {
          updateThumbPosition();
          determineScrollStatus();
        });
      }
    };
    const onMouseUp = () => {
      setIsDragging(false);
      endDrag();
    };
    dragListenersRef.current = { move: onMouseMove, up: onMouseUp };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };
  const handleTrackClick = (e) => {
    if (thumbRef.current && contentRef.current && trackRef.current) {
      const thumbRect = thumbRef.current.getBoundingClientRect();
      if (e.clientY < thumbRect.top || e.clientY > thumbRect.bottom) {
        const trackRect = trackRef.current.getBoundingClientRect();
        const clickY = e.clientY - trackRect.top;
        const { scrollHeight, clientHeight } = contentRef.current;
        const travel = trackHeight() - thumbSize();
        const scrollRange = scrollHeight - clientHeight;
        const target = clickY - thumbSize() / 2;
        contentRef.current.scrollTop = travel > 0 ? target / travel * scrollRange : 0;
        requestAnimationFrame(() => {
          updateThumbPosition();
          determineScrollStatus();
        });
      }
    }
  };
  const handleWheel = (e) => {
    if (contentRef.current) {
      contentRef.current.scrollTop += e.deltaY;
      updateThumbPosition();
      determineScrollStatus();
    }
  };
  useEffect3(() => {
    updateThumbHeight();
    updateMaxHeight();
    const handleResize = () => {
      updateThumbHeight();
      updateMaxHeight();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect3(() => {
    updateThumbHeight();
    const mutationObserver = new MutationObserver(() => {
      setTimeout(updateThumbHeight, 50);
    });
    if (contentRef.current) {
      mutationObserver.observe(contentRef.current, {
        childList: true,
        subtree: true
        // NOTE: deliberately NOT observing `attributes`. updateThumbHeight setStates the
        // thumb height; observing attribute mutations made the fluentui content's own
        // per-render style/class churn re-trigger updateThumbHeight → setState → re-render
        // → ... ("Maximum update depth exceeded" under React 19). childList+subtree still
        // recomputes when content is added/removed.
      });
    }
    return () => {
      mutationObserver.disconnect();
    };
  }, []);
  useLayoutEffect(() => {
    if (!overflows) return;
    updateThumbHeight();
    updateThumbPosition();
  }, [overflows]);
  useEffect3(() => {
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      updateThumbHeight();
      updateThumbPosition();
    });
    if (contentRef.current) ro.observe(contentRef.current);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [overflows]);
  const prevContentUpdated = useRef2(contentUpdated);
  useEffect3(() => {
    if (contentUpdated !== prevContentUpdated.current && dynamicHeight) {
      updateMaxHeight();
      prevContentUpdated.current = contentUpdated;
    }
  }, [contentUpdated, dynamicHeight]);
  const fadeTop = fadeOutEnabled && scrollStatus !== "top";
  const fadeBottom = fadeOutEnabled && scrollStatus !== "bottom";
  const fadeClass = fadeTop && fadeBottom ? "fade-both" : fadeTop ? "fade-top" : fadeBottom ? "fade-bottom" : "";
  return /* @__PURE__ */ jsxs25(
    "div",
    {
      className: [className, isDragging ? "is-active" : "", fadeClass].filter(Boolean).join(" "),
      "data-variant": variant2,
      role,
      style: { position: "relative", height: "100%", maxHeight },
      children: [
        /* @__PURE__ */ jsx29(
          "div",
          {
            className: "scroll-content",
            ref: setContentNode,
            role: contentRole,
            onScroll: handleScroll,
            style: { maxHeight: dynamicHeight ? maxDynamicHeight : maxHeight },
            children
          }
        ),
        overflows && /* @__PURE__ */ jsx29(
          "div",
          {
            className: "scroll-track",
            ref: trackRef,
            ...{ [SCROLLBAR_MARKER]: "" },
            "aria-hidden": "true",
            onClick: handleTrackClick,
            onWheel: handleWheel,
            style: { right: right ?? TRACK_INSET_RIGHT },
            children: /* @__PURE__ */ jsx29(
              "div",
              {
                className: "scroll-thumb",
                ref: thumbRef,
                onMouseDown: handleThumbDrag,
                style: { height: `${thumbHeight}px` }
              }
            )
          }
        )
      ]
    }
  );
};
var Scrollbar = styled29(ScrollbarBase)`
  position: relative;

  /* The card's three per-instance knobs. A consumer retunes them from its own stylesheet — see
     the has-pinned-head class below for the case the card calls out by name.
     (No backticks in comments in here: a backtick inside a comment inside a template literal
     terminates the literal, and the file fails with a bare TS1005.) */
  --scroll-inset-top: ${INSET_Y};
  --scroll-inset-bottom: ${INSET_Y};
  --scroll-gutter: ${GUTTER};

  /* Card section ⑥, drawn there beside its counter-example: a full-height track slides its thumb
     behind an opaque sticky header, so the thumb vanishes at scroll-top — exactly where a user
     first looks for it. Any consumer with pinned chrome (sticky head, search field, toolbar)
     takes this class, or sets the knob to its own chrome height plus that chrome's bottom gap. */
  &.has-pinned-head {
    --scroll-inset-top: ${INSET_TOP_PINNED_HEAD};
  }

  .scroll-content {
    overflow-y: scroll;
    height: 100%;
    width: 100%;

    /* STRUCTURE, not padding: the lane the thumb occupies. Never below 22px — without it the
       content runs under the thumb and the bar reads as floating on the text rather than as part
       of the panel. The card draws the failure next to the fix. */
    padding-right: var(--scroll-gutter);
    box-sizing: border-box;

    /* The OS bar is hidden outright — the whole point of the primitive. The drawn bar below
       replaces it. */
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .scroll-content::-webkit-scrollbar {
    display: none;
  }

  .scroll-track {
    position: absolute;
    top: var(--scroll-inset-top);
    bottom: var(--scroll-inset-bottom);
    width: ${TRACK_WIDTH};
    border-radius: ${TRACK_RADIUS};
    pointer-events: auto;
    /* Card accessibility note: the track keeps a full 12px hit-area regardless of thumb size. */
    transition: ${SKIN_TRANSITION};
  }

  .scroll-thumb {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    border-radius: ${TRACK_RADIUS};
    cursor: pointer;
    transition: ${SKIN_TRANSITION};
  }

  /* Card section ⑤ — fade masks, on the content box. Card values: top stop 12%, bottom 80%. */
  &.fade-top .scroll-content {
    -webkit-mask-image: linear-gradient(to bottom, transparent 0%, #000 12%);
    mask-image: linear-gradient(to bottom, transparent 0%, #000 12%);
  }

  &.fade-bottom .scroll-content {
    -webkit-mask-image: linear-gradient(to bottom, #000 80%, transparent 100%);
    mask-image: linear-gradient(to bottom, #000 80%, transparent 100%);
  }

  &.fade-both .scroll-content {
    -webkit-mask-image: linear-gradient(
      to bottom,
      transparent 0%,
      #000 12%,
      #000 80%,
      transparent 100%
    );
    mask-image: linear-gradient(to bottom, transparent 0%, #000 12%, #000 80%, transparent 100%);
  }

  ${({ variant: variant2 }) => variantCss(variant2 ?? "glass")}
`;

// src/Tables/rangeSelection.ts
function applyRangeSelection(input) {
  const { rowKeys, disabled, selectedKeys, anchor, target, extend } = input;
  const targetIndex = rowKeys.indexOf(target);
  if (targetIndex >= 0 && disabled[targetIndex]) {
    return { selectedKeys: [...selectedKeys], anchor: anchor ?? target };
  }
  const selected = new Set(selectedKeys);
  const next = !selected.has(target);
  const anchorIndex = anchor == null ? -1 : rowKeys.indexOf(anchor);
  let run;
  if (extend && anchorIndex >= 0 && targetIndex >= 0) {
    const from = Math.min(anchorIndex, targetIndex);
    const to = Math.max(anchorIndex, targetIndex);
    run = rowKeys.slice(from, to + 1).filter((_key, offset) => !disabled[from + offset]);
  } else {
    run = [target];
  }
  if (next) {
    const result = [...selectedKeys];
    const seen = new Set(selectedKeys);
    run.forEach((key) => {
      if (seen.has(key)) return;
      seen.add(key);
      result.push(key);
    });
    return { selectedKeys: result, anchor: target };
  }
  const drop = new Set(run);
  return { selectedKeys: selectedKeys.filter((key) => !drop.has(key)), anchor: target };
}

// src/Tables/tableGeometry.ts
import {
  brand as brand21,
  motion as motion16,
  radius as radius19,
  SELECTED_RAMP as SELECTED_RAMP5
} from "@repo/design-tokens";
var HEX6 = /^#([0-9a-fA-F]{6})$/;
var hexToRgbTriple = (hex) => {
  const match = HEX6.exec(hex);
  if (!match) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`hexToRgbTriple: expected a 6-digit hex, received "${hex}".`);
    }
    return "0,0,0";
  }
  const int = parseInt(match[1], 16);
  return `${int >> 16 & 255},${int >> 8 & 255},${int & 255}`;
};
var FROST_RGB = hexToRgbTriple(brand21.frost);
var AQUA_RGB = hexToRgbTriple(SELECTED_RAMP5[6]);
var HEADER_HEIGHT = "30px";
var ROW_HEIGHT = "32px";
var LIST_ROW_HEIGHT = "50px";
var LIST_ROW_GAP = "2px";
var LIST_ROW_RADIUS = "10px";
var GROUP_ROW_HEIGHT = "40px";
var CELL_GAP = "12px";
var ROW_PADDING_X = "14px";
var TABLE_RADIUS = "10px";
var ACTIVE_ROW_RADIUS = "6px";
var RAIL_WIDTH = "3px";
var RAIL_INSET_Y = "3px";
var RAIL_RADIUS = "0 3px 3px 0";
var END_DOT_SIZE = "7px";
var END_DOT_GLOW = "0 0 0 3px";
var END_DOT_GLOW_ALPHA = 0.18;
var FROST_ORB_SIZE = "14px";
var FROST_ORB_RIGHT = "6px";
var ACTIVE_ROW_MARGIN_Y = "1px";
var CELL_CLIP_INSET_X = "6px";
var CELL_CLIP_INSET_Y = "8px";
var CELL_CLIP_PADDING = `${CELL_CLIP_INSET_Y} ${CELL_CLIP_INSET_X}`;
var CELL_CLIP_MARGIN = `-${CELL_CLIP_INSET_Y} -${CELL_CLIP_INSET_X}`;
var ROW_ALPHA = 0.2;
var ROW_ALPHA_HOVER = 0.32;
var SELECTED_ALPHA_DELTA = 0.12;
var ACTIVE_ALPHA_DELTA = 0.08;
var CONTAINER_INSET_HIGHLIGHT = "inset 0 1px 0 rgba(255,255,255,.85)";
var ROW_BORDER_COLOR = "rgba(63,69,78,.06)";
var NONE_MODE_HOVER_WASH = "rgba(63,69,78,.04)";
var DISABLED_ROW_BG = "rgba(63,69,78,.02)";
var DISABLED_ROW_OPACITY = 0.7;
var FROST_WASH = `linear-gradient(to right, rgba(${FROST_RGB},.6) 0%, rgba(${FROST_RGB},0) 60%)`;
var ACTIVE_RING_SHADOW = `inset 0 0 0 1px ${brand21.frost}, 0 0 0 1px rgba(255,255,255,.7), 0 0 14px 2px rgba(${FROST_RGB},.85), 0 2px 6px rgba(0,0,0,.06)`;
var SELECTED_RAIL_SHADOW = `0 0 6px 1px rgba(var(--tl-row-hue),.85), 0 0 12px 3px rgba(var(--tl-row-hue),.55), 0 0 18px 5px rgba(${FROST_RGB},.7)`;
var SELECTED_RAIL_BACKGROUND = "linear-gradient(180deg, rgba(var(--tl-row-hue),1) 0%, rgba(var(--tl-row-hue),.85) 100%)";
var NONE_MODE_SELECTED_RAIL_BACKGROUND = `linear-gradient(180deg, rgba(${AQUA_RGB},1) 0%, rgba(${AQUA_RGB},.85) 100%)`;
var NONE_MODE_SELECTED_RAIL_SHADOW = `0 0 6px 1px rgba(${AQUA_RGB},.5), 0 0 12px 3px rgba(${AQUA_RGB},.35), 0 0 18px 5px rgba(${FROST_RGB},.7)`;
var NONE_MODE_SELECTED_WASH = `rgba(${AQUA_RGB},.06)`;
var FROST_ORB_BACKGROUND = `radial-gradient(circle, rgba(${FROST_RGB},.95) 0%, rgba(${FROST_RGB},0) 70%)`;
var CHECKBOX_SIZE = "14px";
var CHECKBOX_RADIUS = radius19.sm;
var CHECKBOX_BORDER_WIDTH = "1.5px";
var CHECKBOX_GLYPH_SIZE = "9px";
var CHECKBOX_TRANSITION = `all .15s ${motion16.easeUi}`;
var CHECKBOX_REST_SHADOW = "inset 0 1px 0 rgba(255,255,255,.7)";
var CHECKBOX_CHECKED_BACKGROUND = `linear-gradient(180deg,rgba(${AQUA_RGB},.95),rgba(${AQUA_RGB},.78))`;
var CHECKBOX_CHECKED_BORDER = `rgba(${AQUA_RGB},.85)`;
var CHECKBOX_CHECKED_SHADOW = "inset 0 1px 0 rgba(255,255,255,.45)";
var CHECKBOX_SELECTED_SHADOW = `inset 0 1px 0 rgba(255,255,255,.55), 0 0 0 1px rgba(${FROST_RGB},.85), 0 0 8px 2px rgba(${FROST_RGB},.95), 0 0 14px 4px rgba(${AQUA_RGB},.35)`;
var CHECKBOX_INDETERMINATE_BAR_WIDTH = "7px";
var CHECKBOX_INDETERMINATE_BAR_HEIGHT = "2px";
var ROW_FONT_SIZE = "13px";
var ROW_LINE_HEIGHT = "1.3";
var ROW_TRACKING = "-.05px";
var VALUE_GAP = "6px";
var TRANSITION_DURATION = ".12s";
var ROW_TRANSITION = `background-image ${TRANSITION_DURATION} ${motion16.easeUi}, box-shadow ${TRANSITION_DURATION} ${motion16.easeUi}`;
var ACTIONS_TRANSITION = `opacity ${TRANSITION_DURATION} ${motion16.easeUi}`;
var ACTIONS_IDLE_OPACITY = 0.7;
var GLASS_RAIL_IDLE_OPACITY = 0.7;
var GLASS_RAIL_TRANSITION = `opacity ${TRANSITION_DURATION} ${motion16.easeUi}`;
var SORT_GAP = "5px";
var SORT_CARET_WIDTH = "8px";
var SORT_CARET_HEIGHT = "10px";
var SORT_CARET_GAP = "1px";
var SORT_CARET_IDLE_OPACITY = 0.45;
var SORT_CARET_DIM_OPACITY = 0.25;
var MIN_COLUMN_WIDTH = 40;
var RESIZE_HIT_WIDTH = "12px";
var RESIZE_HIT_RIGHT = "0";
var RESIZE_HIT_INSET_Y = "-1px";
var RESIZE_HIT_Z = 5;
var RESIZE_BAR_WIDTH = "2px";
var RESIZE_BAR_LEFT = "5px";
var RESIZE_BAR_INSET_Y = "4px";
var RESIZE_BAR_RADIUS = "1px";
var RESIZE_BAR_TRANSITION = `background .15s ${motion16.easeUi}, box-shadow .15s ${motion16.easeUi}`;
var RESIZE_BAR_GLOW = `0 0 6px rgba(${AQUA_RGB},.5)`;
var RESIZE_STEP_PX = 8;
var RESIZE_STEP_COARSE_PX = 32;
var RESIZING_BODY_CLASS = "tl-col-resizing";
var COLS_STORAGE_PREFIX = "tl.table.cols.";

// src/Tables/Table.tsx
import { Fragment as Fragment7, jsx as jsx30, jsxs as jsxs26 } from "react/jsx-runtime";
var TABLE_TONE_COLOURS = {
  error: { wash: semanticColor11.error, washAlpha: 0.08, ink: inkOn10.error },
  mint: {
    wash: semantic5.status.roles.justChanged.mark,
    washAlpha: 0.14,
    ink: semantic5.status.roles.justChanged.ink
  },
  pink: {
    wash: semantic5.status.roles.problem.mark,
    washAlpha: 0.08,
    ink: semantic5.status.roles.problem.ink
  }
};
var CATEGORICAL_HUE = {
  mint: support8.mint,
  // card r-mint
  butter: accent9.butter,
  // card r-peach (crossed-over name, R4)
  periwinkle: support8.periwinkle,
  // card r-peri
  lavender: support8.lavender,
  // card r-lav
  peach: accent9.peach,
  // card r-pink (crossed-over name, R4)
  aqua: SELECTED_RAMP6[6],
  // card r-aqua / stop-7 — NOT brand.cyan
  idle: line10.control,
  // card r-idle — the neutral "no signal" rail (§6C: a duplicate of
  // #C7CEDB, collapsed into --tl-line-control, which canonical documents as serving both)
  error: semanticColor11.error
  // card r-error
};
var CATEGORICAL_INK = {
  mint: INK_RAMP[2],
  // card r-mint ink
  butter: inkOn10.butter,
  // card r-peach ink (crossed-over name, R4) — --tl-ink-on-butter
  periwinkle: INK_RAMP[8],
  // card r-peri ink
  lavender: inkOn10.lav,
  // card r-lav ink — --tl-ink-on-lav
  peach: inkOn10.pink,
  // card r-pink ink (crossed-over name, R4) — --tl-ink-on-pink
  aqua: INK_RAMP[6],
  // card r-aqua ink
  idle: inkOn10.idle,
  // --tl-ink-on-idle, canonical's documented pair for line.control
  // (identical to ink[3], which is what the card wrote)
  error: inkOn10.error
  // card r-error ink — --tl-ink-on-error
};
var NO_HUE = "";
var RAMP_RGB = SELECTED_RAMP6.map(hexToRgbTriple);
var CATEGORICAL_RGB = Object.fromEntries(
  Object.entries(CATEGORICAL_HUE).map(([key, hex]) => [key, hexToRgbTriple(hex)])
);
var categoricalKey = (hue) => hue !== void 0 && Object.prototype.hasOwnProperty.call(CATEGORICAL_HUE, hue) ? hue : "idle";
var resolveRowStyle = (colourMode, input) => {
  const value = input.value;
  const finite = typeof value === "number" && Number.isFinite(value);
  const v = finite ? `${Math.min(100, Math.max(0, value))}%` : "100%";
  switch (colourMode) {
    case "ramp": {
      const stop = stopForValue(finite ? value : NaN);
      return { hue: RAMP_RGB[stop - 1], ink: INK_RAMP[stop - 1], v };
    }
    case "categorical": {
      const key = categoricalKey(input.hue);
      return { hue: CATEGORICAL_RGB[key], ink: CATEGORICAL_INK[key], v };
    }
    case "none":
      return { hue: NO_HUE, ink: ink25[1], v: "0%" };
    case "aqua":
    default:
      return { hue: AQUA_RGB, ink: INK_RAMP[6], v };
  }
};
var buildCols = (columns, selectable) => [...selectable ? ["auto"] : [], ...columns.map((column) => column.width ?? "1fr")].join(" ");
var applyResizeDelta = (widths, index, deltaPx) => {
  if (index < 0 || index + 1 >= widths.length) return null;
  let left = widths[index] + deltaPx;
  let right = widths[index + 1] - deltaPx;
  if (left < MIN_COLUMN_WIDTH) {
    right -= MIN_COLUMN_WIDTH - left;
    left = MIN_COLUMN_WIDTH;
  }
  if (right < MIN_COLUMN_WIDTH) {
    left -= MIN_COLUMN_WIDTH - right;
    right = MIN_COLUMN_WIDTH;
  }
  if (left < MIN_COLUMN_WIDTH || right < MIN_COLUMN_WIDTH) return null;
  if (left === widths[index] && right === widths[index + 1]) return null;
  const next = [...widths];
  next[index] = left;
  next[index + 1] = right;
  return next;
};
var colsFromWidths = (widths) => widths.map((w) => `${Math.round(w * 10) / 10}px`).join(" ");
var storageKey = (tableId) => `${COLS_STORAGE_PREFIX}${tableId}`;
var readStoredCols = (tableId, trackCount) => {
  if (!tableId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(tableId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== trackCount) return null;
    if (!parsed.every((w) => typeof w === "number" && Number.isFinite(w) && w > 0)) return null;
    return colsFromWidths(parsed);
  } catch {
    return null;
  }
};
var writeStoredCols = (tableId, widths) => {
  if (!tableId) return;
  try {
    window.localStorage.setItem(storageKey(tableId), JSON.stringify(widths));
  } catch {
  }
};
var RowColourModeContext = React26.createContext(void 0);
var HUE_GRADIENT = (alpha) => `linear-gradient(to right, rgba(var(--tl-row-hue), ${alpha}) 0%, rgba(var(--tl-row-hue), 0) var(--tl-row-v))`;
var StyledTable = styled30.div`
  background: ${surface15[0]};
  border: 1px solid ${line10[2]};
  border-radius: ${TABLE_RADIUS};
  overflow: hidden;
  /* Own stacking context, so the sticky head's z-index 2 (and the resize handles') stays INSIDE
     the table. Without it those steps compete in the page's root context, where a Modal root sits
     at z-index auto by design - and the head row painted straight across the open dialog (seen on
     the onboarding wizard's Composite measures stage, 2026-09-12). This is the z-index primitive's
     own first remedy: isolate, rather than reach for a larger step. (No backticks in here: this is
     a template literal.) */
  isolation: isolate;
  font-family: ${fontSans20};
  box-shadow: ${CONTAINER_INSET_HIGHLIGHT};
  /* A column flex box that is allowed to shrink, so a height-constrained Table hands its
     constraint DOWN to the body scroller instead of absorbing it here.

     NOTE FOR EDITORS: no backticks in this comment. A backtick inside a comment inside a
     styled-components template literal terminates the literal and the file fails with a bare
     TS1005 — the same trap panelChrome.ts documents.

     Before this, a Table placed in a bounded flex column took the default flex-shrink of 1,
     shrank below its own content height, and the overflow: hidden above — which is the card's,
     for the corners — silently clipped the rows off. Measured in the admin metrics console: a
     595px box over 838px of content, 243px of registry rows unreachable, and because the table
     absorbed the overflow no ancestor ever overflowed, so nothing anywhere could scroll. */
  display: flex;
  flex-direction: column;
  min-height: 0;
`;
var StyledScroller = styled30(Scrollbar)`
  flex: 1 1 auto;
  min-height: 0;

  .scroll-content {
    /* The horizontal axis is the outer container's business (it clips for the corners); a table
       never scrolls sideways — a too-narrow column ellipsises instead (card: "widening is the
       user's remedy"). */
    overflow-x: hidden;
  }

  /* The head row keeps its full-bleed background while its CELLS respect the thumb's lane.
     Negative margin cancels the scroller's gutter so the gradient and the bottom rule reach the
     table's corners; the padding puts it back so the head grid and the body grid stay in step and
     the columns line up. This is the card's own thead treatment (section ⑥), adapted to the fact
     that a table row already carries its own ROW_PADDING_X.

     Without it the gradient stops 22px short of the corner and leaves a white notch under the
     radius, which is what made an earlier draft reject the gutter outright — the wrong fix. */
  .tl-table-head {
    margin-right: calc(var(--scroll-gutter) * -1);
    padding-right: calc(var(--scroll-gutter) + ${ROW_PADDING_X});
  }
`;
var gridRecipe = `
  display: grid;
  grid-template-columns: var(--cols);
  align-items: center;
  column-gap: ${CELL_GAP};
  padding: 0 ${ROW_PADDING_X};
`;
var StyledHeadRow = styled30.div`
  ${gridRecipe}
  height: ${HEADER_HEIGHT};
  background: linear-gradient(180deg, ${surface15[1]} 0%, ${surface15[2]} 100%);
  border-bottom: 1px solid ${line10[2]};
  position: sticky;
  top: 0;
  z-index: 2;
  font-family: ${fontMono8};
  font-weight: ${weight10.semibold};
  font-size: ${typeScale8.caption2.fontSize};
  line-height: 1;
  letter-spacing: ${tracking3.label};
  text-transform: uppercase;
  color: ${ink25[3]};
`;
var StyledListTable = styled30(StyledTable)`
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
`;
var StyledListHeadRow = styled30(StyledHeadRow)`
  background: ${surface15[0]};
  border-bottom: 1px solid ${line10[2]};
  font-family: ${fontSans20};
  font-size: ${typeScale8.caption1.fontSize};
  font-weight: ${weight10.semibold};
  letter-spacing: normal;
  text-transform: none;
  color: ${ink25[3]};
`;
var StyledHeadCell = styled30.div`
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  /* The focus ring for a sortable header, drawn on the CELL rather than on the button inside it.
     TIGHT, matching the resize handle and the row checkbox in this same file: a control inside a
     30px header between two column labels is the dense case focus.tight exists for, and the full
     ring's bloom would smear into the neighbouring header.
     SCOPED TO the child button deliberately: the resize handle is a sibling div with a ring of
     its own, and an unscoped :focus-within would draw both at once. */
  &:has(> button:focus-visible) {
    border-radius: ${radius20.sm};
    box-shadow: ${focus8.tight};
  }

  /* A sort button or composite header must not outgrow the cell, or the ellipsis never gets a
     chance to appear. Harmless for the absolutely positioned resize handle. */
  & > * {
    max-width: 100%;
  }
`;
var StyledSortButton = styled30.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  display: inline-flex;
  align-items: center;
  gap: ${SORT_GAP};
  cursor: pointer;
  user-select: none;
  color: inherit;

  /* The UA outline is suppressed here and OUR ring is drawn by the parent cell, a few rules
     above — see the note there. It has to be the cell's, not the button's: this cell clips
     (overflow: hidden, for the column-narrowing ellipsis) and constrains its children to
     max-width 100%, so an OUTSET shadow on the button is cut off on every side it touches.
     Measured in a real browser on the asset register: the focused button sits flush against the
     cell on the left, the top AND the bottom, so the ring survived only as four corner ticks.
     An element's own overflow does not clip its own box-shadow, which is why moving it up works. */
  &:focus-visible {
    outline: none;
  }

  .tl-table-sort-caret {
    display: inline-flex;
    flex-direction: column;
    width: ${SORT_CARET_WIDTH};
    height: ${SORT_CARET_HEIGHT};
    gap: ${SORT_CARET_GAP};
    opacity: ${SORT_CARET_IDLE_OPACITY};
  }
  .tl-table-sort-caret::before,
  .tl-table-sort-caret::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    margin: auto;
    border-left: 3px solid transparent;
    border-right: 3px solid transparent;
  }
  .tl-table-sort-caret::before {
    border-bottom: 3px solid currentColor;
  }
  .tl-table-sort-caret::after {
    border-top: 3px solid currentColor;
  }
  &:hover {
    color: ${ink25[1]};
  }
  &.is-asc,
  &.is-desc {
    color: ${brand22.cyanPress};
    font-weight: ${weight10.semibold};
  }
  &.is-asc .tl-table-sort-caret,
  &.is-desc .tl-table-sort-caret {
    opacity: 1;
  }
  &.is-asc .tl-table-sort-caret::before {
    opacity: 1;
  }
  &.is-asc .tl-table-sort-caret::after {
    opacity: ${SORT_CARET_DIM_OPACITY};
  }
  &.is-desc .tl-table-sort-caret::after {
    opacity: 1;
  }
  &.is-desc .tl-table-sort-caret::before {
    opacity: ${SORT_CARET_DIM_OPACITY};
  }
`;
var StyledResizeHandle = styled30.div`
  position: absolute;
  top: ${RESIZE_HIT_INSET_Y};
  bottom: ${RESIZE_HIT_INSET_Y};
  right: ${RESIZE_HIT_RIGHT};
  width: ${RESIZE_HIT_WIDTH};
  cursor: col-resize;
  z-index: ${RESIZE_HIT_Z};
  user-select: none;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    left: ${RESIZE_BAR_LEFT};
    top: ${RESIZE_BAR_INSET_Y};
    bottom: ${RESIZE_BAR_INSET_Y};
    width: ${RESIZE_BAR_WIDTH};
    border-radius: ${RESIZE_BAR_RADIUS};
    background: transparent;
    transition: ${RESIZE_BAR_TRANSITION};
  }
  &:hover::after,
  &[data-dragging='true']::after {
    background: ${brand22.cyan};
    box-shadow: ${RESIZE_BAR_GLOW};
  }
  /* Focus must be distinguishable from hover and must clear the 3:1 WCAG 1.4.11 minimum against
     the header gradient, which brand.cyan does not (~2.1:1) — hence cyanPress plus the shared
     focus ring.
     TIGHT ring, not the full one: this is a 2px bar inside a 30px header, sandwiched between
     two column labels, and it already composes with RESIZE_BAR_GLOW. The full ring's 4.5px
     bloom would crowd the neighbouring header cell and read as a smear rather than as focus —
     exactly the dense-control case canonical scopes --tl-focus-tight to. The compliant line
     layer is unchanged, so this is no weaker than the full ring. */
  &:focus-visible {
    outline: none;
  }
  &:focus-visible::after {
    background: ${brand22.cyanPress};
    box-shadow: ${RESIZE_BAR_GLOW}, ${focus8.tight};
  }
`;
var ResizeCursorGlobalStyle = createGlobalStyle`
  body.${RESIZING_BODY_CLASS},
  body.${RESIZING_BODY_CLASS} * {
    cursor: col-resize !important;
    user-select: none;
  }
`;
var StyledRow = styled30.div`
  ${gridRecipe}
  position: relative;
  height: ${ROW_HEIGHT};
  border-bottom: 1px solid ${ROW_BORDER_COLOR};
  font-family: ${fontSans20};
  font-weight: ${weight10.regular};
  font-size: ${ROW_FONT_SIZE};
  line-height: ${ROW_LINE_HEIGHT};
  letter-spacing: ${ROW_TRACKING};
  color: ${ink25[2]};
  transition: ${ROW_TRANSITION};

  &:last-child {
    border-bottom: none;
  }

  /* The gradient. Strongest at the left rail, fading to fully transparent at --tl-row-v. */
  &[data-gradient='true'] {
    background-image: ${HUE_GRADIENT("var(--tl-row-a)")};
  }

  /* Hover — intensity bump ONLY. The card forbids a background-colour change and a
     box-shadow change here: the rail is an at-a-glance cue, not a chart. Same anchor, same
     extent, alpha .20 → .32. Nothing else belongs in this block. */
  &[data-gradient='true']:hover {
    background-image: ${HUE_GRADIENT("var(--tl-row-a-hover)")};
  }

  /* 'none' mode is the one place a hover background-colour is correct — there is no gradient
     to intensify, so the card falls back to a faint neutral wash. */
  &[data-colour-mode='none']:hover {
    background-color: ${NONE_MODE_HOVER_WASH};
  }

  /* Glass: "Don't gradient-rail on glass" — the wash is tuned for opaque-white tables and on
     a translucent panel it fights the blur. The v1 fallback is a solid rail, rendered as a
     real (absolutely positioned) child so it never becomes a grid track. Hover changes only
     the rail's opacity, never the wash. */
  & > .tl-table-row-rail {
    position: absolute;
    left: 0;
    top: ${RAIL_INSET_Y};
    bottom: ${RAIL_INSET_Y};
    width: ${RAIL_WIDTH};
    border-radius: ${RAIL_RADIUS};
    background: rgb(var(--tl-row-hue));
    opacity: ${GLASS_RAIL_IDLE_OPACITY};
    transition: ${GLASS_RAIL_TRANSITION};
    pointer-events: none;
  }
  &:hover > .tl-table-row-rail {
    opacity: 1;
  }

  /* Selected — a stronger wash plus a glowing ::before rail. No row outline: the rail glow
     does the work (card rule). */
  &[data-selected='true'] {
    z-index: 1;
  }
  &[data-gradient='true'][data-selected='true'] {
    background-image: ${HUE_GRADIENT(`calc(var(--tl-row-a) + ${SELECTED_ALPHA_DELTA})`)};
  }
  &[data-selected='true']::before {
    content: '';
    position: absolute;
    left: 0;
    top: ${RAIL_INSET_Y};
    bottom: ${RAIL_INSET_Y};
    width: ${RAIL_WIDTH};
    border-radius: ${RAIL_RADIUS};
    background: ${SELECTED_RAIL_BACKGROUND};
    box-shadow: ${SELECTED_RAIL_SHADOW};
    z-index: 1;
  }
  /* 'none' mode has no row hue, so the card hard-codes the aqua rail here, with its own
     softer bloom alphas and a faint aqua wash instead of the gradient. */
  &[data-colour-mode='none'][data-selected='true'] {
    background-color: ${NONE_MODE_SELECTED_WASH};
  }
  &[data-colour-mode='none'][data-selected='true']::before {
    background: ${NONE_MODE_SELECTED_RAIL_BACKGROUND};
    box-shadow: ${NONE_MODE_SELECTED_RAIL_SHADOW};
  }

  /* Active — the frost ring. Borrows the selected-TLCard treatment so the row reads as a
     glass tile floating off the table. Layers on top of whatever the row already is: a row can
     be selected AND active simultaneously, and both treatments render (the rail from
     selected, the ring + orb from active). Per the card's cascade the active wash replaces
     the selected wash rather than adding to it. */
  &[data-gradient='true'][data-active='true'] {
    background-image:
      ${FROST_WASH}, ${HUE_GRADIENT(`calc(var(--tl-row-a) + ${ACTIVE_ALPHA_DELTA})`)};
  }
  &[data-gradient='false'][data-active='true'] {
    background-image: ${FROST_WASH};
  }
  &[data-active='true'] {
    box-shadow: ${ACTIVE_RING_SHADOW};
    border-bottom-color: transparent;
    border-radius: ${ACTIVE_ROW_RADIUS};
    margin: ${ACTIVE_ROW_MARGIN_Y} 0;
    z-index: 1;
  }
  &[data-active='true']::after {
    content: '';
    position: absolute;
    right: ${FROST_ORB_RIGHT};
    top: 50%;
    transform: translateY(-50%);
    width: ${FROST_ORB_SIZE};
    height: ${FROST_ORB_SIZE};
    border-radius: 50%;
    background: ${FROST_ORB_BACKGROUND};
    pointer-events: none;
  }
  /* The row after the active one loses its top border (card: '.is-active + .g-row'). */
  &[data-after-active='true'] {
    border-top: 0;
  }

  /* Disabled — collapse to ink-5, no gradient, no hover, no interactivity.
     The doubled && is load-bearing. In the card these selectors are all three classes deep
     (.tbl-g .g-row.is-disabled vs .tbl-g .g-row.is-active), so .is-disabled coming last
     genuinely wins. Here a single attribute selector scores BELOW the two-attribute
     active/selected blocks above, so source order never got a say: a disabled row that was
     also active or selected still painted the frost wash and the aqua selected wash. Doubling
     the generated class restores the card's cascade. */
  &&[data-disabled='true'] {
    background-image: none;
    background-color: ${DISABLED_ROW_BG};
    color: ${ink25[5]};
    opacity: ${DISABLED_ROW_OPACITY};
    cursor: not-allowed;
  }
  &&[data-disabled='true']:hover {
    background-image: none;
    background-color: ${DISABLED_ROW_BG};
  }
  /* …and the pseudo-element treatments the disabled row must not inherit either. */
  &&[data-disabled='true']::before,
  &&[data-disabled='true']::after {
    content: none;
  }

  /* Row cells: single-line, clipped, ellipsised — an over-long consumer value can never run
     across its neighbours, at any column width.

     A BLANKET overflow: hidden was rejected, and this is not one. overflow: hidden clips at
     the PADDING edge, so the cell carries a padding inset cancelled by an equal negative
     margin: layout and the column tracks are unchanged (the margin box is still exactly the
     track), but the clip box is CELL_CLIP_INSET_X wider than the content box on each side —
     room for the value-dot glow, the checkbox bloom and an avatar shadow to render in full.
     Text still ellipsises at the content edge. The inset is half the column gap, so adjacent
     clip boxes meet exactly and never overlap. See tableGeometry's own note: the padding and
     the negative margin are one mechanism and must not be changed apart. */
  & > [role='cell'] {
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: ${CELL_CLIP_PADDING};
    margin: ${CELL_CLIP_MARGIN};
  }
  /* Inline wrappers a consumer puts in a cell (ValueCell, a name/avatar composite, a tag) must
     not outgrow it, or the cell's ellipsis never gets a chance to appear. */
  & > [role='cell'] > * {
    max-width: 100%;
  }

  /* Column tone: a toned cell is washed across the whole row height and its whole clip box (which
     already meets its neighbour's in the middle of the gutter), so adjacent toned columns touch, as
     frame 03's Found / Will be columns do. Scoped to [data-tone] only — an untoned table emits
     nothing new. Vertical padding is dropped so the stretched cell does not spill past the row.
     A one-column grid centres the value vertically while text-align (the column's align) still
     places it, and stacked lines (the list look's name and sub line) still stack. The table wraps
     a toned value in its own .tl-table-cell-tone box, which carries the ellipsis, so a bare text
     value still truncates as in every other cell and no consumer has to wrap it. */
  & > [role='cell'][data-tone] {
    align-self: stretch;
    display: grid;
    align-content: center;
    padding-top: 0;
    padding-bottom: 0;
    margin-top: 0;
    margin-bottom: 0;
  }
  & > [role='cell'][data-tone] > .tl-table-cell-tone {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  & > [role='cell'][data-tone='error'] {
    --tl-tone-wash: ${rgba22(TABLE_TONE_COLOURS.error.wash, TABLE_TONE_COLOURS.error.washAlpha)};
    background-color: var(--tl-tone-wash);
    color: ${TABLE_TONE_COLOURS.error.ink};
  }
  & > [role='cell'][data-tone='mint'] {
    --tl-tone-wash: ${rgba22(TABLE_TONE_COLOURS.mint.wash, TABLE_TONE_COLOURS.mint.washAlpha)};
    background-color: var(--tl-tone-wash);
    color: ${TABLE_TONE_COLOURS.mint.ink};
  }
  & > [role='cell'][data-tone='pink'] {
    --tl-tone-wash: ${rgba22(TABLE_TONE_COLOURS.pink.wash, TABLE_TONE_COLOURS.pink.washAlpha)};
    background-color: var(--tl-tone-wash);
    color: ${TABLE_TONE_COLOURS.pink.ink};
  }
  /* A toned edge column runs to the table's edge, as frame 03's Will be column does: on the left
     through the row's side padding, on the right through that AND the scroller's reserved thumb
     lane, which the head row already bleeds through (StyledScroller's .tl-table-head). The content
     box does not move — the extra margin is given back as padding — so the value never sits under
     the thumb; only the wash does. The end cell reaches 1px down over the row's divider: its wash
     stops 1px short so the row's own divider shows through untinted, and it draws that divider
     across the thumb lane only (where the row's border does not reach), so the line runs unbroken
     to the edge without being drawn twice. Edges come from
     data-edge (a word list: "start", "end", or both for a one-column table), not :first-child,
     because a glass row starts with its rail. Only plain table-look
     rows bleed: an active row's ring and radius, and the list look's rounded rows, keep their
     shape. */
  &[data-look='table']:not([data-active='true']) > [role='cell'][data-tone][data-edge~='start'] {
    margin-left: -${ROW_PADDING_X};
    padding-left: ${ROW_PADDING_X};
  }
  &[data-look='table']:not([data-active='true']) > [role='cell'][data-tone][data-edge~='end'] {
    margin-right: calc(-1 * (${ROW_PADDING_X} + var(--scroll-gutter, 0px)));
    padding-right: calc(${ROW_PADDING_X} + var(--scroll-gutter, 0px));
    margin-bottom: -1px;
    padding-bottom: 1px;
    background-color: transparent;
    background-image:
      linear-gradient(var(--tl-tone-wash), var(--tl-tone-wash)),
      linear-gradient(${ROW_BORDER_COLOR}, ${ROW_BORDER_COLOR});
    background-size:
      100% calc(100% - 1px),
      var(--scroll-gutter, 0px) 1px;
    background-position:
      left top,
      right bottom;
    background-repeat: no-repeat;
  }
  /* The last row has no divider: the end cell stays inside it and washes its whole box. */
  &[data-look='table']:last-child > [role='cell'][data-tone][data-edge~='end'] {
    margin-bottom: 0;
    padding-bottom: 0;
    background-size:
      100% 100%,
      0 0;
  }
  /* The row's own state wins over the wash: selected and active rows show their surface through
     the cell, and a disabled row's toned cells collapse with the rest of it. The value keeps its
     tone ink in a selected or active row, so the meaning survives the highlight. */
  &&[data-selected='true'] > [role='cell'][data-tone],
  &&[data-active='true'] > [role='cell'][data-tone] {
    background: none;
  }
  &&[data-disabled='true'] > [role='cell'][data-tone] {
    background: none;
    color: inherit;
  }

  /* Consumer styling hook: put action buttons in a column's render with this class. */
  .tl-table-row-actions {
    display: flex;
    gap: 2px;
    justify-content: flex-end;
    align-items: center;
    opacity: ${ACTIONS_IDLE_OPACITY};
    transition: ${ACTIONS_TRANSITION};
  }
  &:hover .tl-table-row-actions,
  &[data-active='true'] .tl-table-row-actions,
  &[data-selected='true'] .tl-table-row-actions {
    opacity: 1;
  }
`;
var StyledListRow = styled30(StyledRow)`
  /* ── The list look (equipment-page-ds-table-select, frame 07). Every rule below is scoped to
     [data-look='list'], so the table look's emitted rules are untouched. Surfaces bind the SAME
     tokens as @repo/ui's listRowLook (hover: surface.base + glassSm; selected: frost +
     glassHover); a parity test in @repo/ui holds the two together. Geometry is measured off
     frame 07 (1:1) — see tableGeometry. Last in the block so it wins the equal-specificity
     'none'-mode rules on StyledRow. */
  &[data-look='list'] {
    height: ${LIST_ROW_HEIGHT};
    margin-bottom: ${LIST_ROW_GAP};
    border-bottom: none;
    border-radius: ${LIST_ROW_RADIUS};
    transition:
      box-shadow ${motion17.durMid} ${motion17.easeUi},
      background ${motion17.durMid} ${motion17.easeUi};
    /* A Shift-click must not paint a text selection across the run it selects. */
    user-select: none;
    /* The header is pinned inside the same scroller: a row scrolled into view by the arrow keys
       must land below it, not under it. */
    scroll-margin-top: ${HEADER_HEIGHT};
  }
  &[data-look='list']:hover {
    background: ${semantic5.surface.base};
    box-shadow: ${shadow10.glassSm};
  }
  &[data-look='list'][data-selected='true'] {
    background: ${brand22.frost};
    box-shadow: ${shadow10.glassHover};
  }
  /* The list row draws no rail: its selected surface is the frost card, not card 11's aqua bar. */
  &[data-look='list'][data-selected='true']::before {
    content: none;
  }
  &[data-look='list'][data-after-active='true'] {
    border-top: 0;
  }
  /* The active (open) row is frame 08's: the list row's selected surface (frost + glassHover),
     not card 11's frost ring, wash and orb — a frost ring on a frost row cannot be seen. What tells
     the open row from a ticked one is the row's own content (frame 08 turns its disc cyan and its
     name bold), which the consumer renders from activeKey. Stated AFTER the hover rule and more
     specific than it, so hovering the open row does not turn it white (found in review). */
  &[data-look='list'][data-active='true'],
  &[data-look='list'][data-active='true']:hover {
    border-radius: ${LIST_ROW_RADIUS};
    margin: 0 0 ${LIST_ROW_GAP};
    background: ${brand22.frost};
    box-shadow: ${shadow10.glassHover};
  }
  &[data-look='list'][data-active='true']::after {
    content: none;
  }
  /* One Tab stop moves between rows, so the focused row must show it. Composed with whatever
     surface the row already has so the ring never erases the hover, selected or open shadow;
     the open row's rule is last and most specific. */
  &[data-look='list']:focus-visible {
    outline: none;
    box-shadow: ${focus8.tight};
  }
  &[data-look='list']:hover:focus-visible {
    box-shadow: ${shadow10.glassSm}, ${focus8.tight};
  }
  &[data-look='list'][data-selected='true']:focus-visible {
    box-shadow: ${shadow10.glassHover}, ${focus8.tight};
  }
  &[data-look='list'][data-active='true']:focus-visible,
  &[data-look='list'][data-active='true']:hover:focus-visible {
    box-shadow: ${shadow10.glassHover}, ${focus8.tight};
  }
  /* A cell may hold two lines (a name and the line beneath it); each line clips itself. */
  &[data-look='list'] > [role='cell'] {
    white-space: normal;
  }
  /* Disabled wins over every list surface, as it does over the table look's (doubled class). */
  &&[data-look='list'][data-disabled='true'],
  &&[data-look='list'][data-disabled='true']:hover {
    background: ${DISABLED_ROW_BG};
    box-shadow: none;
  }
  @media (prefers-reduced-motion: reduce) {
    &[data-look='list'] {
      transition: none;
    }
  }
`;
var StyledGroupRow = styled30.div`
  ${gridRecipe}
  min-height: ${GROUP_ROW_HEIGHT};
  margin-bottom: ${LIST_ROW_GAP};
  /* The glass tokens (white .60 behind a white edge): frame 04's group pill is white .62 behind
     .95, which the tokens round — a reading recorded for Rob, not a second pair of literals. */
  border: 1px solid ${glass4.border};
  border-radius: ${LIST_ROW_RADIUS};
  background: ${glass4.bg};
  box-shadow: ${shadow10.glassSm};
  font-family: ${fontSans20};
  font-size: ${ROW_FONT_SIZE};
  line-height: ${ROW_LINE_HEIGHT};
  color: ${ink25[2]};

  & > [role='cell'] {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: ${CELL_GAP};
    min-width: 0;
  }
  /* The toggle is the design system's icon-only Button; the row only keeps it from shrinking. */
  .tl-table-group-toggle {
    flex-shrink: 0;
  }
  .tl-table-group-header {
    display: flex;
    flex: 1;
    align-items: center;
    gap: ${CELL_GAP};
    min-width: 0;
  }
  /* TableRowGroup.detail (frame 05): the header line, then the detail beneath it in the header's
     column — the toggle keeps the first column, so the detail starts where the header does. */
  &[data-has-detail='true'] > [role='cell'] {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    column-gap: ${CELL_GAP};
    row-gap: ${space9[1]};
    padding-block: ${space9[1]};
  }
  .tl-table-group-detail {
    grid-column: 2;
    display: flex;
    align-items: center;
    gap: ${space9[1]};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: ${typeScale8.caption1.fontSize};
    line-height: ${typeScale8.caption1.lineHeight};
    font-weight: ${weight10.medium};
    color: ${ink25[3]};
  }
  /* The group a docked card is open on (TableRowGroup.selected): the open row's surface. */
  &[data-selected='true'] {
    background: ${brand22.frost};
    box-shadow: ${shadow10.glassHover};
  }
`;
var StyledStateRow = styled30.div`
  ${gridRecipe}
  min-height: ${ROW_HEIGHT};
  font-family: ${fontSans20};
  font-size: ${ROW_FONT_SIZE};
  line-height: ${ROW_LINE_HEIGHT};
  color: ${ink25[4]};

  & > [role='cell'] {
    grid-column: 1 / -1;
    text-align: center;
  }
  &[data-state='error'] {
    color: ${semanticColor11.error};
  }
`;
var StyledCheckbox = styled30.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
  .tl-table-checkbox-box {
    width: ${CHECKBOX_SIZE};
    height: ${CHECKBOX_SIZE};
    border-radius: ${CHECKBOX_RADIUS};
    background: ${surface15[0]};
    border: ${CHECKBOX_BORDER_WIDTH} solid ${line10.control};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: ${CHECKBOX_TRANSITION};
    box-shadow: ${CHECKBOX_REST_SHADOW};
  }
  .tl-table-checkbox-box svg {
    width: ${CHECKBOX_GLYPH_SIZE};
    height: ${CHECKBOX_GLYPH_SIZE};
    color: ${surface15[0]};
    opacity: 0;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  &:hover .tl-table-checkbox-box {
    border-color: ${brand22.cyan};
  }
  /* The native input is visually hidden, so it is focusable but invisible — without moving the
     indicator onto the visible box a keyboard user cannot see which row they are about to
     toggle (WCAG 2.4.7). Every other state had a rule here; focus was the one omission.
     TIGHT ring, not the full one: a 14px box in a 32px row is the dense-row case canonical
     scopes --tl-focus-tight to, and the full ring's 4.5px bloom would clip against the rows
     above and below. Only the decorative bloom is dropped — the WCAG-carrying line layer is
     identical to the full ring's. */
  input:focus-visible + .tl-table-checkbox-box {
    border-color: ${brand22.cyanPress};
    box-shadow: ${focus8.tight};
  }
  input:checked + .tl-table-checkbox-box {
    background: ${CHECKBOX_CHECKED_BACKGROUND};
    border-color: ${CHECKBOX_CHECKED_BORDER};
    box-shadow: ${CHECKBOX_CHECKED_SHADOW};
  }
  input:checked + .tl-table-checkbox-box svg {
    opacity: 1;
  }
  &[data-indeterminate='true'] .tl-table-checkbox-box {
    background: ${CHECKBOX_CHECKED_BACKGROUND};
    border-color: ${CHECKBOX_CHECKED_BORDER};
  }
  &[data-indeterminate='true'] .tl-table-checkbox-box::after {
    content: '';
    display: block;
    position: absolute;
    width: ${CHECKBOX_INDETERMINATE_BAR_WIDTH};
    height: ${CHECKBOX_INDETERMINATE_BAR_HEIGHT};
    background: ${surface15[0]};
    border-radius: 1px;
  }
  /* The card pairs the selected row's rail glow with a glowing checkbox so the eye lands on
     both accents at once (R6, verbatim). */
  &[data-glow='true'] input:checked + .tl-table-checkbox-box {
    box-shadow: ${CHECKBOX_SELECTED_SHADOW};
  }
  input:disabled + .tl-table-checkbox-box {
    opacity: ${DISABLED_ROW_OPACITY};
    cursor: not-allowed;
  }
`;
var StyledValueCell = styled30.span`
  display: inline-flex;
  align-items: center;
  gap: ${VALUE_GAP};
  /* Card, verbatim: .vcell caps at 100% so it cannot outgrow the cell that clips it. */
  max-width: 100%;
  font-family: ${fontMono8};
  font-weight: ${weight10.medium};
  font-size: ${typeScale8.caption1.fontSize};
  line-height: 1;
  letter-spacing: 0;
  /* Fallback mirrors the card's own .vcell rule, color: var(--ink, var(--tl-ink)). R2 says
     ValueCell "works inside any consumer-authored cell", so it must still read as body ink
     outside a Table row rather than falling through to the UA default. */
  color: var(--tl-row-ink, ${ink25[1]});

  .tl-table-value-dot {
    width: ${END_DOT_SIZE};
    height: ${END_DOT_SIZE};
    border-radius: 50%;
    background: rgb(var(--tl-row-hue));
    box-shadow: ${END_DOT_GLOW} rgba(var(--tl-row-hue), ${END_DOT_GLOW_ALPHA});
    flex-shrink: 0;
  }
`;
var ValueCell = ({
  children,
  showDot
}) => {
  const colourMode = React26.useContext(RowColourModeContext);
  const dot = showDot ?? colourMode !== "none";
  return /* @__PURE__ */ jsxs26(StyledValueCell, { className: "tl-table-value", children: [
    children,
    dot ? /* @__PURE__ */ jsx30("span", { className: "tl-table-value-dot", "aria-hidden": "true" }) : null
  ] });
};
var CheckGlyph = () => /* @__PURE__ */ jsx30("svg", { viewBox: "0 0 12 12", fill: "none", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx30("path", { d: "M2 6l3 3 5-6" }) });
var SelectionCheckbox = ({ label, checked, disabled, indeterminate, glow: glow2, onToggle, onSelect, tabIndex }) => {
  const inputRef = React26.useRef(null);
  React26.useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return /* @__PURE__ */ jsxs26(
    StyledCheckbox,
    {
      className: "tl-table-checkbox",
      "data-indeterminate": indeterminate ? "true" : "false",
      "data-glow": glow2 ? "true" : "false",
      onClick: (event) => {
        event.stopPropagation();
        if (onSelect && event.shiftKey && event.target !== inputRef.current) {
          event.preventDefault();
          if (!disabled) onSelect(true);
        }
      },
      onMouseDown: onSelect ? (event) => {
        if (event.shiftKey) event.preventDefault();
      } : void 0,
      children: [
        /* @__PURE__ */ jsx30(
          "input",
          {
            ref: inputRef,
            type: "checkbox",
            "aria-label": label,
            checked,
            disabled,
            tabIndex,
            onChange: onSelect ? () => void 0 : onToggle,
            onClick: onSelect ? (event) => onSelect(event.shiftKey) : void 0,
            onKeyDown: onSelect ? (event) => {
              if (event.key !== " " || !event.shiftKey) return;
              event.preventDefault();
              if (!event.repeat) onSelect(true);
            } : void 0,
            onKeyUp: onSelect ? (event) => {
              if (event.key === " " && event.shiftKey) event.preventDefault();
            } : void 0
          }
        ),
        /* @__PURE__ */ jsx30("span", { className: "tl-table-checkbox-box", "aria-hidden": "true", children: /* @__PURE__ */ jsx30(CheckGlyph, {}) })
      ]
    }
  );
};
var headerLabel = (column) => typeof column.header === "string" ? column.header : column.key;
var ROW_INTERACTIVE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="switch"]',
  '[role="textbox"]'
].join(", ");
var isRowOrigin = (event) => {
  const target = event.target;
  const row = event.currentTarget;
  if (!target || target === row) return true;
  const control = target.closest(ROW_INTERACTIVE_SELECTOR);
  return control === null || !row.contains(control);
};
var useIsoLayoutEffect2 = typeof window !== "undefined" ? React26.useLayoutEffect : React26.useEffect;
var ListRowCells = React26.memo(function ListRowCells2({
  row,
  renderIndex,
  rowKey,
  columns,
  selectable,
  isSelected,
  rowDisabled,
  checkboxLabel,
  controller
}) {
  return /* @__PURE__ */ jsxs26(Fragment7, { children: [
    selectable ? /* @__PURE__ */ jsx30("span", { role: "cell", className: "tl-table-cell", children: /* @__PURE__ */ jsx30(
      SelectionCheckbox,
      {
        label: checkboxLabel,
        checked: isSelected,
        disabled: rowDisabled,
        glow: false,
        onToggle: () => controller.current?.toggle(rowKey),
        onSelect: (extend) => controller.current?.select(rowKey, extend),
        tabIndex: -1
      }
    ) }) : null,
    columns.map((column, columnIndex) => /* @__PURE__ */ jsx30(
      "span",
      {
        role: "cell",
        className: "tl-table-cell",
        "data-tone": column.tone,
        "data-edge": column.tone === void 0 ? void 0 : [
          columnIndex === 0 && !selectable ? "start" : null,
          columnIndex === columns.length - 1 ? "end" : null
        ].filter(Boolean).join(" ") || void 0,
        style: { textAlign: column.align ?? "left" },
        children: column.tone === void 0 ? column.render(row, renderIndex) : /* @__PURE__ */ jsx30("span", { className: "tl-table-cell-tone", children: column.render(row, renderIndex) })
      },
      column.key
    ))
  ] });
});
var ListRow = React26.memo(function ListRow2(props) {
  const {
    index,
    ariaRowIndex,
    isActive,
    afterActive,
    rowInteractive,
    focusable: focusable2,
    isTabStop,
    isSelected,
    rowDisabled,
    controller,
    surfaceMode,
    row,
    renderIndex,
    rowKey,
    columns,
    selectable,
    checkboxLabel,
    keyAttribute
  } = props;
  const { hue, ink: rowInk, v } = resolveRowStyle("none", { value: void 0, hue: void 0 });
  return /* @__PURE__ */ jsx30(
    StyledListRow,
    {
      ...keyAttribute ? { [keyAttribute]: String(rowKey) } : {},
      role: "row",
      "aria-rowindex": ariaRowIndex,
      className: "tl-table-row",
      "data-look": "list",
      "data-colour-mode": "none",
      "data-surface": surfaceMode,
      "data-gradient": "false",
      "data-selected": isSelected ? "true" : "false",
      "data-active": isActive ? "true" : "false",
      "data-after-active": afterActive ? "true" : "false",
      "data-disabled": rowDisabled ? "true" : "false",
      "aria-current": isActive ? "true" : void 0,
      "aria-disabled": rowDisabled ? "true" : void 0,
      tabIndex: focusable2 ? isTabStop ? 0 : -1 : void 0,
      onFocus: focusable2 ? (event) => controller.current?.focus(index, event) : void 0,
      onClick: rowInteractive ? (event) => controller.current?.click(index, event) : void 0,
      onKeyDown: focusable2 ? (event) => controller.current?.keyDown(index, event) : void 0,
      style: {
        ["--tl-row-hue"]: hue,
        ["--tl-row-ink"]: rowInk,
        ["--tl-row-v"]: v,
        ["--tl-row-a"]: String(ROW_ALPHA),
        ["--tl-row-a-hover"]: String(ROW_ALPHA_HOVER)
      },
      children: /* @__PURE__ */ jsx30(
        ListRowCells,
        {
          row,
          renderIndex,
          rowKey,
          columns,
          selectable,
          isSelected,
          rowDisabled,
          checkboxLabel,
          controller
        }
      )
    }
  );
});
function Table(props) {
  const {
    columns,
    data,
    keyExtractor,
    look = "table",
    colourMode: requestedColourMode = "aqua",
    valueFor,
    hueOf,
    surface: surfaceMode = "opaque",
    selectable = false,
    selectedKeys,
    onSelectionChange,
    activeKey = null,
    onRowClick,
    isRowDisabled,
    isRowActivatable,
    getRowLabel,
    sortField,
    sortDirection,
    onSort,
    resizable: requestedResizable,
    tableId,
    loading = false,
    loadingMessage,
    error = null,
    emptyMessage,
    className,
    "aria-label": ariaLabel,
    rowGroups,
    rowKeyAttribute
  } = props;
  const isList = look === "list";
  const colourMode = isList ? "none" : requestedColourMode;
  const resizable = requestedResizable ?? !isList;
  const containerRef = React26.useRef(null);
  const headRowRef = React26.useRef(null);
  const trackCount = columns.length + (selectable ? 1 : 0);
  const trackOffset = selectable ? 1 : 0;
  const [initialCols] = React26.useState(
    () => readStoredCols(tableId, trackCount) ?? buildCols(columns, selectable)
  );
  const dragRef = React26.useRef(null);
  const readWidths = () => {
    const head = headRowRef.current;
    if (!head) return [];
    return Array.from(head.children).map((cell) => cell.getBoundingClientRect().width);
  };
  const syncHandleRange = (widths) => {
    const head = headRowRef.current;
    if (!head) return;
    Array.from(head.children).forEach((cell, index) => {
      const handle = cell.querySelector(".tl-table-resize-handle");
      if (!handle) return;
      const pairTotal = widths[index] + widths[index + 1];
      handle.setAttribute("aria-valuenow", String(Math.round(widths[index])));
      handle.setAttribute("aria-valuemin", String(MIN_COLUMN_WIDTH));
      handle.setAttribute("aria-valuemax", String(Math.round(pairTotal - MIN_COLUMN_WIDTH)));
    });
  };
  const writeCols = (widths) => {
    containerRef.current?.style.setProperty("--cols", colsFromWidths(widths));
    syncHandleRange(widths);
  };
  const endDrag = () => {
    const session = dragRef.current;
    dragRef.current = null;
    if (!session) return;
    document.body.classList.remove(RESIZING_BODY_CLASS);
    window.removeEventListener("pointermove", session.onMove);
    window.removeEventListener("pointerup", session.onEnd);
    window.removeEventListener("pointercancel", session.onEnd);
    session.handle.removeEventListener("lostpointercapture", session.onEnd);
    session.handle.removeAttribute("data-dragging");
  };
  React26.useEffect(() => endDrag, []);
  const trackSignature = `${selectable ? "sel|" : ""}${columns.map((column) => `${column.key}@${column.width ?? "1fr"}`).join("|")}`;
  const seededSignature = React26.useRef(trackSignature);
  React26.useEffect(() => {
    if (seededSignature.current === trackSignature) return;
    if (dragRef.current) return;
    seededSignature.current = trackSignature;
    containerRef.current?.style.setProperty(
      "--cols",
      readStoredCols(tableId, trackCount) ?? buildCols(columns, selectable)
    );
  }, [trackSignature, tableId, trackCount]);
  React26.useEffect(() => {
    const widths = readWidths();
    if (widths.length === trackCount && widths.every((width) => width > 0)) syncHandleRange(widths);
  }, [trackSignature, trackCount]);
  const handlePointerDown = (trackIndex) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (dragRef.current) endDrag();
    const handle = event.currentTarget;
    const widths = readWidths();
    if (widths.length !== trackCount) return;
    const onMove = (moveEvent) => {
      const session = dragRef.current;
      if (!session || session.handle !== handle) return;
      const next = applyResizeDelta(
        session.widths,
        session.index,
        moveEvent.clientX - session.startX
      );
      if (next) {
        session.resolved = next;
        writeCols(next);
      }
    };
    const onEnd = () => {
      const session = dragRef.current;
      if (session && session.handle !== handle) return;
      if (session && typeof handle.releasePointerCapture === "function") {
        try {
          handle.releasePointerCapture(event.pointerId);
        } catch {
        }
      }
      const resolved = session?.resolved ?? null;
      endDrag();
      if (resolved) writeStoredCols(tableId, resolved);
    };
    dragRef.current = {
      index: trackIndex,
      startX: event.clientX,
      widths,
      resolved: null,
      handle,
      onMove,
      onEnd
    };
    handle.setAttribute("data-dragging", "true");
    document.body.classList.add(RESIZING_BODY_CLASS);
    if (typeof handle.setPointerCapture === "function") {
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
      }
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    handle.addEventListener("lostpointercapture", onEnd);
  };
  const handleResizeKeyDown = (trackIndex) => (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const step = event.shiftKey ? RESIZE_STEP_COARSE_PX : RESIZE_STEP_PX;
    const delta = event.key === "ArrowRight" ? step : -step;
    const widths = readWidths();
    if (widths.length !== trackCount) return;
    const next = applyResizeDelta(widths, trackIndex, delta);
    if (!next) return;
    writeCols(next);
    writeStoredCols(tableId, next);
  };
  const selected = React26.useMemo(
    () => new Set(selectedKeys ?? []),
    [selectedKeys]
  );
  const rowKeys = data.map((row, index) => keyExtractor(row, index));
  const grouped = isList && rowGroups !== void 0;
  const groupPlan = (() => {
    if (!grouped || !rowGroups) return null;
    const indexOfKey = /* @__PURE__ */ new Map();
    rowKeys.forEach((rowKey, index) => {
      if (!indexOfKey.has(rowKey)) indexOfKey.set(rowKey, index);
    });
    const placed = /* @__PURE__ */ new Set();
    const items = [];
    rowGroups.forEach((group) => {
      const members = [];
      group.rowKeys.forEach((rowKey) => {
        const index = indexOfKey.get(rowKey);
        if (index === void 0 || placed.has(index)) return;
        placed.add(index);
        members.push(index);
      });
      items.push({ kind: "group", group, count: members.length });
      if (group.expanded) members.forEach((index) => items.push({ kind: "row", index }));
    });
    const ariaRowIndex = /* @__PURE__ */ new Map();
    items.forEach((item, position) => {
      if (item.kind === "row") ariaRowIndex.set(item.index, position + 2);
    });
    return { items, ariaRowIndex };
  })();
  const ariaRowIndexOf = (index) => groupPlan ? groupPlan.ariaRowIndex.get(index) : index + 2;
  const rendered = (index) => ariaRowIndexOf(index) !== void 0;
  const navOrder = groupPlan ? groupPlan.items.flatMap((item) => item.kind === "row" ? [item.index] : []) : data.map((_row, index) => index);
  const disabledFlags = data.map((row, index) => Boolean(isRowDisabled?.(row, index)));
  const activatableFlags = data.map(
    (row, index) => !onRowClick || disabledFlags[index] || isRowActivatable === void 0 ? true : Boolean(isRowActivatable(row, index))
  );
  const enabledKeys = rowKeys.filter((_key, index) => !disabledFlags[index] && rendered(index));
  const selectedEnabledCount = enabledKeys.filter((key) => selected.has(key)).length;
  const allEnabledSelected = enabledKeys.length > 0 && selectedEnabledCount === enabledKeys.length;
  const someEnabledSelected = selectedEnabledCount > 0 && !allEnabledSelected;
  const toggleAll = () => {
    if (enabledKeys.length === 0) return;
    const current = selectedKeys ?? [];
    if (allEnabledSelected) {
      const drop = new Set(enabledKeys);
      onSelectionChange?.(current.filter((key) => !drop.has(key)));
      return;
    }
    const seen = new Set(current);
    const next = [...current];
    enabledKeys.forEach((key) => {
      if (seen.has(key)) return;
      seen.add(key);
      next.push(key);
    });
    onSelectionChange?.(next);
  };
  const toggleRow = (key) => {
    const next = selected.has(key) ? (selectedKeys ?? []).filter((existing) => existing !== key) : [...selectedKeys ?? [], key];
    onSelectionChange?.(next);
  };
  const anchorRef = React26.useRef(null);
  const selectRow = (key, extend) => {
    const result = applyRangeSelection({
      // In drawn order: a range runs between rows as the operator sees them, and never through a
      // row a closed group hides (it is not in the order at all).
      rowKeys: navOrder.map((index) => rowKeys[index]),
      disabled: navOrder.map((index) => disabledFlags[index]),
      selectedKeys: selectedKeys ?? [],
      anchor: anchorRef.current,
      target: key,
      extend
    });
    anchorRef.current = result.anchor;
    onSelectionChange?.(result.selectedKeys);
  };
  const [focusKey, setFocusKey] = React26.useState(null);
  const focusableFlags = isList ? data.map(
    (_row, index) => !disabledFlags[index] && rendered(index) && (Boolean(onRowClick) && activatableFlags[index] || selectable)
  ) : [];
  const isFocusableKey = (candidate) => {
    if (candidate == null) return false;
    const at = rowKeys.indexOf(candidate);
    return at >= 0 && focusableFlags[at];
  };
  const tabStopKey = !isList ? null : isFocusableKey(activeKey) ? activeKey : isFocusableKey(focusKey) ? focusKey : navOrder.map((index) => rowKeys[index]).find((_key, at) => focusableFlags[navOrder[at]]) ?? null;
  const TableElement = isList ? StyledListTable : StyledTable;
  const HeadRowElement = isList ? StyledListHeadRow : StyledHeadRow;
  const activeIndex = activeKey == null ? -1 : rowKeys.findIndex((key) => key === activeKey);
  const activeRendered = activeIndex >= 0 && rendered(activeIndex);
  const afterActiveIndex = activeRendered ? navOrder[navOrder.indexOf(activeIndex) + 1] : void 0;
  const showGradient = colourMode !== "none" && surfaceMode !== "glass";
  const showGlassRail = surfaceMode === "glass" && colourMode !== "none";
  const stateRow = (() => {
    if (error) {
      return /* @__PURE__ */ jsx30(StyledStateRow, { role: "row", "aria-rowindex": 2, "data-state": "error", className: "tl-table-state", children: /* @__PURE__ */ jsx30("span", { role: "cell", children: /* @__PURE__ */ jsx30("span", { role: "alert", children: error }) }) });
    }
    if (loading) {
      return /* @__PURE__ */ jsx30(
        StyledStateRow,
        {
          role: "row",
          "aria-rowindex": 2,
          "data-state": "loading",
          className: "tl-table-state",
          children: /* @__PURE__ */ jsx30("span", { role: "cell", children: loadingMessage ?? "Loading\u2026" })
        }
      );
    }
    if (data.length === 0 || groupPlan && groupPlan.items.length === 0) {
      return /* @__PURE__ */ jsx30(StyledStateRow, { role: "row", "aria-rowindex": 2, "data-state": "empty", className: "tl-table-state", children: /* @__PURE__ */ jsx30("span", { role: "cell", children: emptyMessage ?? "No rows" }) });
    }
    return null;
  })();
  const listRowController = React26.useRef(null);
  const listKeyDown = (index, event) => {
    if (!isRowOrigin(event)) return;
    const key = rowKeys[index];
    const rowDisabled = disabledFlags[index];
    const rowInteractive = Boolean(onRowClick) && !rowDisabled && activatableFlags[index];
    const focusables = navOrder.filter((at) => focusableFlags[at]);
    const here = focusables.indexOf(index);
    let to = -1;
    if (event.key === "ArrowDown") to = focusables[here + 1] ?? -1;
    else if (event.key === "ArrowUp") to = here > 0 ? focusables[here - 1] : -1;
    else if (event.key === "Home") to = focusables[0] ?? -1;
    else if (event.key === "End") to = focusables[focusables.length - 1] ?? -1;
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      const isJump = event.key === "Home" || event.key === "End";
      if (isJump && to >= 0) event.preventDefault();
      if (to < 0 || to === index) return;
      event.preventDefault();
      const element = containerRef.current?.querySelector(
        `[role='row'][aria-rowindex='${ariaRowIndexOf(to)}']`
      );
      element?.focus();
      element?.scrollIntoView?.({ block: "nearest" });
      const targetInteractive = Boolean(onRowClick) && !disabledFlags[to] && activatableFlags[to];
      if (activeRendered && targetInteractive) onRowClick?.(data[to], to);
      return;
    }
    if (event.key === "Enter") {
      if (!rowInteractive) return;
      event.preventDefault();
      onRowClick?.(data[index], index);
      return;
    }
    if (event.key === " ") {
      if (event.repeat) {
        event.preventDefault();
        return;
      }
      if (selectable && !rowDisabled) {
        event.preventDefault();
        selectRow(key, event.shiftKey);
      } else if (rowInteractive) {
        event.preventDefault();
        onRowClick?.(data[index], index);
      }
    }
  };
  const controller = {
    keyDown: listKeyDown,
    click: (index, event) => {
      if (!isRowOrigin(event)) return;
      onRowClick?.(data[index], index);
    },
    focus: (index, event) => {
      if (event.target === event.currentTarget) setFocusKey(rowKeys[index]);
    },
    toggle: toggleRow,
    select: selectRow
  };
  useIsoLayoutEffect2(() => {
    listRowController.current = controller;
  });
  const cellsReadIndex = columns.some((column) => column.readsIndex !== false);
  const renderListRow = (row, index) => {
    const key = rowKeys[index];
    const rowDisabled = disabledFlags[index];
    return /* @__PURE__ */ jsx30(
      ListRow,
      {
        row,
        index,
        renderIndex: cellsReadIndex ? index : 0,
        rowKey: key,
        ariaRowIndex: ariaRowIndexOf(index),
        columns,
        selectable,
        isSelected: selected.has(key),
        isActive: activeKey != null && key === activeKey,
        afterActive: index === afterActiveIndex,
        rowDisabled,
        rowInteractive: Boolean(onRowClick) && !rowDisabled && activatableFlags[index],
        focusable: focusableFlags[index],
        isTabStop: key === tabStopKey,
        checkboxLabel: getRowLabel ? `Select ${getRowLabel(row, index)}` : `Select row ${key}`,
        controller: listRowController,
        surfaceMode,
        keyAttribute: rowKeyAttribute
      },
      key
    );
  };
  const renderRow = (row, index) => {
    if (isList) return renderListRow(row, index);
    const key = rowKeys[index];
    const rowDisabled = disabledFlags[index];
    const rowInteractive = Boolean(onRowClick) && !rowDisabled && activatableFlags[index];
    const isSelected = selected.has(key);
    const isActive = activeKey != null && key === activeKey;
    const {
      hue,
      ink: rowInk,
      v
    } = resolveRowStyle(colourMode, {
      value: valueFor?.(row, index),
      hue: hueOf?.(row, index)
    });
    const activate = () => {
      if (!rowInteractive) return;
      onRowClick?.(row, index);
    };
    return /* @__PURE__ */ jsxs26(
      StyledRow,
      {
        role: "row",
        "aria-rowindex": ariaRowIndexOf(index),
        className: "tl-table-row",
        "data-look": look,
        "data-colour-mode": colourMode,
        "data-surface": surfaceMode,
        "data-gradient": showGradient && !rowDisabled ? "true" : "false",
        "data-selected": isSelected ? "true" : "false",
        "data-active": isActive ? "true" : "false",
        "data-after-active": index === afterActiveIndex ? "true" : "false",
        "data-disabled": rowDisabled ? "true" : "false",
        "aria-current": isActive ? "true" : void 0,
        "aria-disabled": rowDisabled ? "true" : void 0,
        tabIndex: rowInteractive ? 0 : void 0,
        onClick: rowInteractive ? (event) => {
          if (!isRowOrigin(event)) return;
          activate();
        } : void 0,
        onKeyDown: rowInteractive ? (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          if (!isRowOrigin(event)) return;
          event.preventDefault();
          activate();
        } : void 0,
        style: {
          ["--tl-row-hue"]: hue,
          ["--tl-row-ink"]: rowInk,
          ["--tl-row-v"]: v,
          ["--tl-row-a"]: String(ROW_ALPHA),
          ["--tl-row-a-hover"]: String(ROW_ALPHA_HOVER)
        },
        children: [
          showGlassRail ? /* @__PURE__ */ jsx30("span", { className: "tl-table-row-rail", "aria-hidden": "true" }) : null,
          selectable ? /* @__PURE__ */ jsx30("span", { role: "cell", className: "tl-table-cell", children: /* @__PURE__ */ jsx30(
            SelectionCheckbox,
            {
              label: getRowLabel ? `Select ${getRowLabel(row, index)}` : `Select row ${key}`,
              checked: isSelected,
              disabled: rowDisabled,
              glow: isSelected,
              onToggle: () => toggleRow(key)
            }
          ) }) : null,
          columns.map((column, columnIndex) => /* @__PURE__ */ jsx30(
            "span",
            {
              role: "cell",
              className: "tl-table-cell",
              "data-tone": column.tone,
              "data-edge": column.tone === void 0 ? void 0 : [
                columnIndex === 0 && !selectable ? "start" : null,
                columnIndex === columns.length - 1 ? "end" : null
              ].filter(Boolean).join(" ") || void 0,
              style: { textAlign: column.align ?? "left" },
              children: column.tone === void 0 ? column.render(row, index) : /* @__PURE__ */ jsx30("span", { className: "tl-table-cell-tone", children: column.render(row, index) })
            },
            column.key
          ))
        ]
      },
      key
    );
  };
  return /* @__PURE__ */ jsxs26(
    TableElement,
    {
      ref: containerRef,
      role: "table",
      "aria-label": ariaLabel,
      "aria-rowcount": (stateRow ? 1 : groupPlan ? groupPlan.items.length : data.length) + 1,
      "aria-busy": loading && !error ? true : void 0,
      className: ["tl-table", className ?? ""].filter(Boolean).join(" "),
      style: { ["--cols"]: initialCols },
      children: [
        resizable ? /* @__PURE__ */ jsx30(ResizeCursorGlobalStyle, {}) : null,
        /* @__PURE__ */ jsxs26(
          StyledScroller,
          {
            variant: "onSolid",
            role: "presentation",
            contentRole: "presentation",
            className: "tl-table-scroller has-pinned-head",
            children: [
              /* @__PURE__ */ jsxs26(HeadRowElement, { ref: headRowRef, role: "row", "aria-rowindex": 1, className: "tl-table-head", children: [
                selectable ? /* @__PURE__ */ jsx30(StyledHeadCell, { role: "columnheader", className: "tl-table-head-cell", children: /* @__PURE__ */ jsx30(
                  SelectionCheckbox,
                  {
                    label: "Select all rows",
                    checked: allEnabledSelected,
                    indeterminate: someEnabledSelected,
                    onToggle: toggleAll
                  }
                ) }) : null,
                columns.map((column, columnIndex) => {
                  const sorted = Boolean(column.sortable) && sortField === column.key;
                  const ariaSort = column.sortable ? sorted ? sortDirection === "desc" ? "descending" : "ascending" : "none" : void 0;
                  const hasHandle = resizable && column.resizable !== false && columnIndex < columns.length - 1;
                  return /* @__PURE__ */ jsxs26(
                    StyledHeadCell,
                    {
                      role: "columnheader",
                      className: "tl-table-head-cell",
                      "aria-sort": ariaSort,
                      "aria-label": hasHandle && typeof column.header === "string" ? column.header : void 0,
                      style: { textAlign: column.align ?? "left" },
                      children: [
                        column.sortable ? /* @__PURE__ */ jsxs26(
                          StyledSortButton,
                          {
                            type: "button",
                            className: sorted ? sortDirection === "desc" ? "is-desc" : "is-asc" : void 0,
                            onClick: () => onSort?.(column.key),
                            children: [
                              column.header,
                              /* @__PURE__ */ jsx30("span", { className: "tl-table-sort-caret", "aria-hidden": "true" })
                            ]
                          }
                        ) : column.header,
                        hasHandle ? /* @__PURE__ */ jsx30(
                          StyledResizeHandle,
                          {
                            className: "tl-table-resize-handle",
                            role: "separator",
                            "aria-orientation": "vertical",
                            "aria-label": `Resize column ${headerLabel(column)}`,
                            "aria-valuenow": MIN_COLUMN_WIDTH,
                            "aria-valuemin": MIN_COLUMN_WIDTH,
                            "aria-valuemax": MIN_COLUMN_WIDTH,
                            tabIndex: 0,
                            onPointerDown: handlePointerDown(columnIndex + trackOffset),
                            onKeyDown: handleResizeKeyDown(columnIndex + trackOffset)
                          }
                        ) : null
                      ]
                    },
                    column.key
                  );
                })
              ] }),
              /* @__PURE__ */ jsx30(RowColourModeContext.Provider, { value: colourMode, children: stateRow ?? (groupPlan ? groupPlan.items.map(
                (item, position) => item.kind === "row" ? renderRow(data[item.index], item.index) : /* @__PURE__ */ jsx30(
                  StyledGroupRow,
                  {
                    role: "row",
                    "aria-rowindex": position + 2,
                    className: "tl-table-group",
                    "data-group-key": item.group.key,
                    "data-expanded": item.group.expanded ? "true" : "false",
                    "data-selected": item.group.selected ? "true" : void 0,
                    "data-has-detail": item.group.detail != null ? "true" : void 0,
                    "aria-current": item.group.selected ? "true" : void 0,
                    children: /* @__PURE__ */ jsxs26(
                      "span",
                      {
                        role: "cell",
                        className: "tl-table-group-cell",
                        "aria-colspan": columns.length + (selectable ? 1 : 0),
                        children: [
                          /* @__PURE__ */ jsx30(
                            Button,
                            {
                              className: "tl-table-group-toggle",
                              iconOnly: true,
                              size: "xs",
                              variant: "text",
                              surface: "on-glass",
                              "aria-expanded": item.group.expanded,
                              "aria-label": item.group.toggleLabel ?? `${item.group.label}, ${item.count} ${item.count === 1 ? "row" : "rows"}`,
                              onClick: item.group.onToggle,
                              iconLeft: /* @__PURE__ */ jsx30(Icon, { name: item.group.expanded ? "chevron-down" : "chevron-right" })
                            }
                          ),
                          /* @__PURE__ */ jsx30("span", { className: "tl-table-group-header", children: item.group.header }),
                          item.group.detail != null && /* @__PURE__ */ jsx30("span", { className: "tl-table-group-detail", children: item.group.detail })
                        ]
                      }
                    )
                  },
                  `tl-group:${item.group.key}`
                )
              ) : data.map((row, index) => renderRow(row, index))) })
            ]
          }
        )
      ]
    }
  );
}

// src/Scrollbars/ScrollbarTrack.tsx
import { useEffect as useEffect4, useRef as useRef3, useState as useState3 } from "react";
import styled31 from "styled-components";
import { jsx as jsx31 } from "react/jsx-runtime";
var ScrollbarTrackBase = ({
  orientation,
  viewportSize,
  contentSize,
  offset,
  onOffsetChange,
  onDragStart,
  onDragEnd,
  variant: variant2 = "glass",
  className,
  style
}) => {
  const vertical = orientation === "vertical";
  const trackRef = useRef3(null);
  const thumbRef = useRef3(null);
  const [trackLength, setTrackLength] = useState3(0);
  const [isDragging, setIsDragging] = useState3(false);
  const dragListenersRef = useRef3(null);
  const latest = useRef3({ viewportSize, contentSize, offset, onOffsetChange, onDragEnd });
  latest.current = { viewportSize, contentSize, offset, onOffsetChange, onDragEnd };
  const wheelOffsetRef = useRef3(null);
  const suppressClickRef = useRef3(false);
  useEffect4(() => {
    wheelOffsetRef.current = null;
  }, [offset]);
  const overflows = contentSize > viewportSize;
  const measureTrack = () => {
    const rect = trackRef.current?.getBoundingClientRect();
    return rect ? vertical ? rect.height : rect.width : 0;
  };
  useEffect4(() => {
    if (!overflows) return;
    setTrackLength(measureTrack());
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setTrackLength(measureTrack()));
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, [overflows, vertical]);
  const geometry2 = (length, sizes = latest.current) => {
    const thumb2 = Math.min(
      length,
      Math.max(MIN_THUMB_LENGTH, length * sizes.viewportSize / Math.max(sizes.contentSize, 1))
    );
    const travel2 = Math.max(0, length - thumb2);
    const range2 = Math.max(0, sizes.contentSize - sizes.viewportSize);
    return { thumb: thumb2, travel: travel2, range: range2 };
  };
  const { thumb, travel, range } = geometry2(trackLength, { viewportSize, contentSize });
  const rawPosition = range > 0 ? offset / range * travel : 0;
  const position = Number.isFinite(rawPosition) ? Math.min(Math.max(0, rawPosition), travel) : 0;
  const endDrag = () => {
    const listeners = dragListenersRef.current;
    if (listeners === null) return;
    dragListenersRef.current = null;
    window.removeEventListener("mousemove", listeners.move);
    window.removeEventListener("mouseup", listeners.up);
  };
  useEffect4(() => endDrag, []);
  const handleThumbDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    endDrag();
    setIsDragging(true);
    onDragStart?.();
    const startPointer = vertical ? e.clientY : e.clientX;
    const startOffset = latest.current.offset;
    const move = (moveEvent) => {
      if ((moveEvent.buttons & 1) === 0) {
        up();
        return;
      }
      const delta = (vertical ? moveEvent.clientY : moveEvent.clientX) - startPointer;
      const g = geometry2(measureTrack());
      const next = g.travel > 0 ? startOffset + delta / g.travel * g.range : startOffset;
      latest.current.onOffsetChange(Math.min(Math.max(0, next), g.range));
    };
    const up = () => {
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      setIsDragging(false);
      endDrag();
      latest.current.onDragEnd?.();
    };
    dragListenersRef.current = { move, up };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  const handleTrackClick = (e) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    const thumbRect = thumbRef.current?.getBoundingClientRect();
    const trackRect = trackRef.current?.getBoundingClientRect();
    if (!thumbRect || !trackRect) return;
    const pointer = vertical ? e.clientY : e.clientX;
    const thumbStart = vertical ? thumbRect.top : thumbRect.left;
    const thumbEnd = vertical ? thumbRect.bottom : thumbRect.right;
    if (pointer >= thumbStart && pointer <= thumbEnd) return;
    const g = geometry2(measureTrack());
    const target = pointer - (vertical ? trackRect.top : trackRect.left) - g.thumb / 2;
    const next = g.travel > 0 ? target / g.travel * g.range : 0;
    onOffsetChange(Math.min(Math.max(0, next), g.range));
  };
  const handleWheel = (e) => {
    const delta = vertical ? e.deltaY : e.deltaX || e.deltaY;
    if (delta === 0) return;
    e.stopPropagation();
    const from = wheelOffsetRef.current ?? offset;
    const next = Math.min(Math.max(0, from + delta), range);
    wheelOffsetRef.current = next;
    onOffsetChange(next);
  };
  const handleTrackDown = (e) => {
    if (e.button === 0) e.preventDefault();
  };
  if (!overflows) return null;
  return /* @__PURE__ */ jsx31(
    "div",
    {
      className: [className, isDragging ? "is-active" : ""].filter(Boolean).join(" "),
      "data-variant": variant2,
      "data-orientation": orientation,
      style,
      children: /* @__PURE__ */ jsx31(
        "div",
        {
          className: "scroll-track",
          ref: trackRef,
          ...{ [SCROLLBAR_MARKER]: "" },
          "aria-hidden": "true",
          onMouseDown: handleTrackDown,
          onClick: handleTrackClick,
          onWheel: handleWheel,
          children: /* @__PURE__ */ jsx31(
            "div",
            {
              className: "scroll-thumb",
              ref: thumbRef,
              onMouseDown: handleThumbDown,
              style: vertical ? { top: `${position}px`, height: `${thumb}px` } : { left: `${position}px`, width: `${thumb}px` }
            }
          )
        }
      )
    }
  );
};
var ScrollbarTrack = styled31(ScrollbarTrackBase)`
  position: absolute;
  pointer-events: none;

  &[data-orientation='vertical'] {
    width: ${TRACK_WIDTH};
  }

  &[data-orientation='horizontal'] {
    height: ${TRACK_WIDTH};
  }

  .scroll-track {
    position: absolute;
    inset: 0;
    border-radius: ${TRACK_RADIUS};
    pointer-events: auto;
    /* Card accessibility note: the track keeps its full 12px hit-area regardless of thumb size. */
    transition: ${SKIN_TRANSITION};
  }

  .scroll-thumb {
    position: absolute;
    border-radius: ${TRACK_RADIUS};
    cursor: pointer;
    transition: ${SKIN_TRANSITION};
  }

  &[data-orientation='vertical'] .scroll-thumb {
    left: 0;
    right: 0;
  }

  &[data-orientation='horizontal'] .scroll-thumb {
    top: 0;
    bottom: 0;
  }

  ${({ variant: variant2 }) => variantCss(variant2 ?? "glass")}
`;

// src/Charts/InlineBars.tsx
import styled32 from "styled-components";
import { semantic as semantic6, typeRole as typeRole8 } from "@repo/design-tokens";

// src/Charts/inlineBarsScale.ts
var quantityExponent = (val) => {
  if (val === 0) {
    return 0;
  }
  let exp = Math.floor(Math.log(val) / Math.LN10);
  if (val / Math.pow(10, exp) >= 10) {
    exp += 1;
  }
  return exp;
};
var niceInterval = (val) => {
  const exponent = quantityExponent(val);
  const exp10 = Math.pow(10, exponent);
  const f = val / exp10;
  let nf;
  if (f < 1.5) {
    nf = 1;
  } else if (f < 2.5) {
    nf = 2;
  } else if (f < 4) {
    nf = 3;
  } else if (f < 7) {
    nf = 5;
  } else {
    nf = 10;
  }
  const niced = nf * exp10;
  return exponent >= -20 ? Number(niced.toFixed(exponent < 0 ? -exponent : 0)) : niced;
};
var SPLIT_NUMBER = 5;
var niceAxisMax = (dataMax) => {
  if (!Number.isFinite(dataMax) || dataMax <= 0) {
    return 1;
  }
  const interval = niceInterval(dataMax / SPLIT_NUMBER);
  const max = Number((Math.ceil(dataMax / interval) * interval).toPrecision(12));
  return Number.isFinite(max) && max > 0 ? max : dataMax;
};

// src/Charts/InlineBars.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var px = (value) => typeof value === "number" ? `${value}px` : value;
var Strip = styled32.div`
  display: flex;
  align-items: flex-end;
  box-sizing: border-box;
  /* Hug the bars rather than the parent: the strip's width is slots × slot width. */
  width: max-content;
  /* A label centred in a short bar overflows it; clip at the strip edge, as the canvas
     this replaced did. */
  overflow: hidden;

  &.has-baseline {
    /* The darkest rule the token set offers. The ECharts strip this replaced drew a
       literal 2px #000; #3F454E is the design system's equivalent (epic §6C). */
    border-bottom: 2px solid ${semantic6.border.selected};
  }
`;
var Slot = styled32.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  /* Without this a label wider than the slot would stretch it (a flex item's automatic
     minimum size is its content), pushing every bar to its right out of column. The canvas
     this replaced overflowed its text instead of reflowing, so the slot must hold its width. */
  min-width: 0;
`;
var Bar = styled32.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  font-family: ${typeRole8.caption1.fontFamily};
  font-size: ${typeRole8.caption1.fontSize};
  line-height: ${typeRole8.caption1.lineHeight};
  font-weight: ${typeRole8.caption1.fontWeight};
  color: ${semantic6.text.primary};
  white-space: nowrap;
`;
var hasValue = (value) => value !== null && Number.isFinite(value);
var summarise = (bars) => bars.map((bar) => `${bar.label} ${bar.displayValue ?? bar.value ?? 0}`).join(", ");
var InlineBars = ({
  bars,
  height,
  barWidth,
  gap = 0,
  baseline = false,
  axisMax,
  ariaLabel,
  className
}) => {
  const drawnMax = bars.reduce(
    (max, bar) => hasValue(bar.value) && bar.value > max ? bar.value : max,
    0
  );
  const scale = axisMax !== void 0 && Number.isFinite(axisMax) && axisMax > 0 ? axisMax : niceAxisMax(drawnMax);
  const slotWidth = px(barWidth);
  const summary = bars.length > 0 ? ariaLabel ?? summarise(bars) : void 0;
  return /* @__PURE__ */ jsx32(
    Strip,
    {
      className: [className, "tl-inline-bars", baseline ? "has-baseline" : null].filter(Boolean).join(" "),
      role: summary ? "img" : void 0,
      "aria-label": summary,
      style: { height: px(height), gap: `${gap}px` },
      children: bars.map((bar, index) => {
        const value = bar.value;
        const present = hasValue(value);
        const barHeight = present ? Math.max(0, value) / scale * 100 : 0;
        return /* @__PURE__ */ jsx32(
          Slot,
          {
            className: "tl-inline-bar-slot",
            style: { flex: `0 0 ${slotWidth}`, width: slotWidth },
            children: /* @__PURE__ */ jsx32(
              Bar,
              {
                className: barHeight > 0 ? "tl-inline-bar" : "tl-inline-bar is-empty",
                style: {
                  height: `${barHeight}%`,
                  backgroundColor: bar.color ?? semantic6.border.default,
                  color: bar.labelColor
                },
                children: present ? bar.displayValue : null
              }
            )
          },
          `${bar.label}-${index}`
        );
      })
    }
  );
};

// src/Charts/Sparkline.tsx
import React28 from "react";
import styled33 from "styled-components";
import {
  SELECTED_RAMP as SELECTED_RAMP7,
  INK_RAMP as INK_RAMP2,
  support as support9,
  semanticColor as semanticColor12,
  inkOn as inkOn11,
  brand as brand23,
  clampStop as clampStop2,
  rgbTriple as rgbTriple2,
  rgba as rgba23
} from "@repo/design-tokens";
import { Fragment as Fragment8, jsx as jsx33, jsxs as jsxs27 } from "react/jsx-runtime";
var SIZE4 = {
  nano: { width: 36, height: 16, lineW: 1.25, dotR: 2 },
  micro: { width: 72, height: 22, lineW: 1.4, dotR: 2.5 },
  small: { width: 120, height: 40, lineW: 1.6, dotR: 3.2 },
  medium: { width: 200, height: 64, lineW: 1.8, dotR: 4 }
};
var TONE4 = {
  ok: { hue: semanticColor12.success, ink: "#1B4A4D" },
  // card-authored ink, no token counterpart
  warn: { hue: semanticColor12.warn, ink: "#7A5500" },
  // card-authored ink, no token counterpart
  bad: { hue: semanticColor12.error, ink: inkOn11.error }
};
var HUE = {
  mint: { hue: support9.mint, ink: inkOn11.mint },
  peri: { hue: support9.periwinkle, ink: inkOn11.peri },
  lav: { hue: support9.lavender, ink: inkOn11.lav },
  pink: { hue: support9.palePink, ink: inkOn11.pink }
};
var Root5 = styled33.span`
  position: relative;
  /* The fluid variant fills its host slot instead of holding the size's own box, so it can
     occupy a full-width host like the .v-kpi trend slot. See the prop's doc above.
     (No backticks in here — inside a styled template literal they end the template.) */
  display: ${({ $fluid }) => $fluid ? "block" : "inline-block"};
  flex-shrink: 0;
  line-height: 0;
  width: ${({ $fluid, $w }) => $fluid ? "100%" : `${$w}px`};
  height: ${({ $fluid, $h }) => $fluid ? "100%" : `${$h}px`};

  svg {
    display: block;
    width: 100%;
    height: 100%;
    /* The round line cap and the end dot sit ON the extremes of the box; without this
       both are shaved in half at the edges. */
    overflow: visible;
  }

  .tl-spark-line {
    fill: none;
    stroke: rgb(var(--hue));
    stroke-width: ${({ $lineW }) => $lineW};
    stroke-linecap: round;
    stroke-linejoin: round;
    /* preserveAspectRatio="none" scales the viewBox non-uniformly, which would smear the
       stroke thicker horizontally than vertically. This keeps it an even weight. */
    vector-effect: non-scaling-stroke;
  }

  .tl-spark-dot {
    position: absolute;
    left: var(--end-x, 100%);
    top: var(--end-y, 50%);
    width: ${({ $dotR }) => $dotR * 2}px;
    height: ${({ $dotR }) => $dotR * 2}px;
    border-radius: 50%;
    background: rgb(var(--hue));
    box-shadow: 0 0 0 ${({ $dotR }) => $dotR * 0.9}px rgba(var(--hue), 0.22);
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  /* On glass the line needs to survive a busy 3D backdrop: heavier weight, a white
     drop-shadow to separate it from the scene, and a frost bloom on the dot. */
  &.on-glass .tl-spark-line {
    stroke-width: ${({ $lineW }) => $lineW + 0.4};
    filter: drop-shadow(0 0 1.5px rgba(255, 255, 255, 0.7));
  }
  &.on-glass .tl-spark-dot {
    box-shadow:
      0 0 0 ${({ $dotR }) => $dotR * 0.9}px rgba(var(--hue), 0.35),
      0 0 6px 1px ${rgba23(brand23.frost, 0.8)};
  }
`;
var hasValue2 = (value) => value !== null && Number.isFinite(value);
var geometry = (values, height, inset12) => {
  const points = values.filter(hasValue2);
  if (points.length < 2) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    if (p < min) min = p;
    if (p > max) max = p;
  }
  const span = max - min;
  const usable = height - inset12 * 2;
  const step = 100 / (points.length - 1);
  const xy = points.map((value, index) => {
    const x = index === points.length - 1 ? 100 : index * step;
    const y = span === 0 ? height / 2 : inset12 + (1 - (value - min) / span) * usable;
    return [round(x), round(y)];
  });
  const line20 = xy.map(([x, y]) => `${x},${y}`).join(" ");
  const last = xy[xy.length - 1];
  return {
    line: line20,
    // Close the polygon down to the baseline on both ends, as the card does.
    area: `${line20} 100,${height} 0,${height}`,
    endY: last[1]
  };
};
var round = (n) => Math.round(n * 100) / 100;
var Sparkline = ({
  values,
  stop = 7,
  tone,
  hue,
  size = "small",
  fill = false,
  dot = true,
  onGlass = false,
  fluid = false,
  ariaLabel,
  className
}) => {
  const sizeKey = variant(SIZE4, size, "small", "Sparkline", "size");
  const box = SIZE4[sizeKey];
  const gradientId = React28.useId();
  const geo = geometry(values, box.height, box.lineW);
  const palette = hue ? HUE[variant(HUE, hue, "mint", "Sparkline", "hue")] : tone ? TONE4[variant(TONE4, tone, "ok", "Sparkline", "tone")] : {
    hue: SELECTED_RAMP7[clampStop2(stop) - 1],
    ink: INK_RAMP2[clampStop2(stop) - 1]
  };
  const classes = [
    "tl-spark",
    `sz-${sizeKey}`,
    fill ? "has-fill" : "",
    dot ? "" : "no-dot",
    onGlass ? "on-glass" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  const style = {
    "--hue": rgbTriple2(palette.hue),
    "--ink": palette.ink,
    "--end-y": geo ? `${round(geo.endY / box.height * 100)}%` : "50%"
  };
  return /* @__PURE__ */ jsxs27(
    Root5,
    {
      className: classes,
      style,
      $w: box.width,
      $h: box.height,
      $lineW: box.lineW,
      $dotR: box.dotR,
      $fluid: fluid,
      role: ariaLabel ? "img" : void 0,
      "aria-label": ariaLabel,
      "aria-hidden": ariaLabel ? void 0 : true,
      children: [
        /* @__PURE__ */ jsxs27("svg", { viewBox: `0 0 100 ${box.height}`, preserveAspectRatio: "none", focusable: "false", children: [
          geo && fill ? /* @__PURE__ */ jsxs27(Fragment8, { children: [
            /* @__PURE__ */ jsx33("defs", { children: /* @__PURE__ */ jsxs27("linearGradient", { className: "tl-spark-grad", id: gradientId, x1: "0", y1: "0", x2: "0", y2: "1", children: [
              /* @__PURE__ */ jsx33(
                "stop",
                {
                  offset: "0%",
                  stopColor: `rgb(${rgbTriple2(palette.hue)})`,
                  stopOpacity: onGlass ? 0.7 : 0.55
                }
              ),
              /* @__PURE__ */ jsx33("stop", { offset: "100%", stopColor: `rgb(${rgbTriple2(palette.hue)})`, stopOpacity: 0 })
            ] }) }),
            /* @__PURE__ */ jsx33("polygon", { className: "tl-spark-area", fill: `url(#${gradientId})`, points: geo.area })
          ] }) : null,
          geo ? /* @__PURE__ */ jsx33("polyline", { className: "tl-spark-line", points: geo.line }) : null
        ] }),
        geo && dot ? /* @__PURE__ */ jsx33("i", { className: "tl-spark-dot" }) : null
      ]
    }
  );
};

// src/Inline/Inline.tsx
import React29 from "react";
import styled34 from "styled-components";
import {
  SELECTED_RAMP as SELECTED_RAMP8,
  OFF_RAMP as OFF_RAMP2,
  INK_RAMP as INK_RAMP3,
  OFF_INK_RAMP,
  stopForValue as stopForValue2,
  clampStop as clampStop3,
  rgbTriple as rgbTriple3,
  ink as ink26,
  line as line11,
  surface as surface16,
  support as support10,
  accent as accent10,
  semanticColor as semanticColor13,
  inkOn as inkOn12,
  fontSans as fontSans21,
  fontMono as fontMono9
} from "@repo/design-tokens";
import { jsx as jsx34, jsxs as jsxs28 } from "react/jsx-runtime";
var ScopeContext = React29.createContext(false);
var HUE2 = {
  mint: { hue: support10.mint, ink: inkOn12.mint },
  peach: { hue: accent10.butter, ink: inkOn12.butter },
  peri: { hue: support10.periwinkle, ink: inkOn12.peri },
  lav: { hue: support10.lavender, ink: inkOn12.lav },
  pink: { hue: accent10.peach, ink: inkOn12.pink },
  error: { hue: semanticColor13.error, ink: inkOn12.error }
};
var UNTRACKED = { hue: line11.control, ink: ink26[3] };
var SIZE5 = {
  inline: {
    height: "46px",
    padding: "0 16px",
    radius: "10px",
    gap: "14px",
    alpha: 0.32,
    dot: "8px",
    ring: "3px",
    twoLinePadding: "8px 16px"
  },
  compact: {
    height: "28px",
    padding: "0 12px",
    radius: "7px",
    gap: "10px",
    alpha: 0.35,
    dot: "7px",
    ring: "2.5px",
    twoLinePadding: "6px 12px"
  }
};
var Row3 = styled34.div`
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  background-color: ${surface16[0]};
  border: 1px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 1px 2px rgba(40, 30, 80, 0.06);
  overflow: hidden;
  height: ${({ $size }) => SIZE5[$size].height};
  padding: ${({ $size }) => SIZE5[$size].padding};
  border-radius: ${({ $size }) => SIZE5[$size].radius};
  gap: ${({ $size }) => SIZE5[$size].gap};

  /* The wash. Strongest at the anchored edge, gone by --v. Orientation flips which edge
     it starts from AND the order of the three slots, so the value always sits at the
     gradient's strong end. */
  &.is-right {
    background-image: linear-gradient(
      to left,
      rgba(var(--hue), var(--a)) 0%,
      rgba(var(--hue), 0) var(--v)
    );
  }
  &.is-left {
    background-image: linear-gradient(
      to right,
      rgba(var(--hue), var(--a)) 0%,
      rgba(var(--hue), 0) var(--v)
    );
  }

  .tl-inline-label {
    font-family: ${fontSans21};
    color: ${ink26[1]};
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: ${({ $size }) => $size === "inline" ? 600 : 500};
    font-size: ${({ $size }) => $size === "inline" ? "13.5px" : "12px"};
    line-height: ${({ $size }) => $size === "inline" ? 1.2 : 1};
    letter-spacing: ${({ $size }) => $size === "inline" ? "-0.1px" : "-0.05px"};
  }

  .tl-inline-sub {
    display: ${({ $size }) => $size === "inline" ? "block" : "none"};
    font-family: ${fontMono9};
    font-weight: 400;
    font-size: 11px;
    line-height: 1.3;
    color: ${ink26[3]};
    margin-top: 2px;
    letter-spacing: 0;
  }

  .tl-inline-value {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: ${fontMono9};
    font-weight: 600;
    font-size: ${({ $size }) => $size === "inline" ? "14px" : "12.5px"};
    color: var(--ink);
    white-space: nowrap;
    letter-spacing: 0;
    flex-shrink: 0;
  }

  .tl-inline-meta {
    font-family: ${fontMono9};
    font-weight: 500;
    font-size: 11px;
    line-height: 1;
    color: ${ink26[4]};
    letter-spacing: 0;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .tl-inline-dot {
    width: ${({ $size }) => SIZE5[$size].dot};
    height: ${({ $size }) => SIZE5[$size].dot};
    border-radius: 50%;
    flex-shrink: 0;
    background: rgb(var(--hue));
    box-shadow: 0 0 0 ${({ $size }) => SIZE5[$size].ring} rgba(var(--hue), 0.22);
  }

  /* Slot order per orientation. The label always grows; meta and value never do. */
  &.is-right .tl-inline-label {
    order: 1;
  }
  &.is-right .tl-inline-meta {
    order: 2;
  }
  &.is-right .tl-inline-value {
    order: 3;
  }
  &.is-left .tl-inline-value {
    order: 1;
  }
  &.is-left .tl-inline-meta {
    order: 2;
  }
  &.is-left .tl-inline-label {
    order: 3;
    text-align: right;
  }

  /* Two-line: the row grows instead of clipping, so a long name is readable rather than
     ellipsised into ambiguity. */
  &.is-two-line {
    height: auto;
    min-height: ${({ $size }) => $size === "compact" ? SIZE5[$size].height : "auto"};
    padding: ${({ $size }) => SIZE5[$size].twoLinePadding};
  }
  &.is-two-line .tl-inline-label {
    white-space: normal;
    text-overflow: clip;
    line-height: ${({ $size }) => $size === "inline" ? 1.25 : 1.2};
    font-size: ${({ $size }) => $size === "inline" ? "13.5px" : "11.5px"};
  }

  &.is-untracked .tl-inline-label {
    color: ${ink26[3]};
  }

  &.is-clickable {
    cursor: pointer;
  }
`;
var Stack3 = styled34.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  /* Rows in a stack drop their own lift — the stack is the surface, not each row. */
  > * {
    box-shadow: none;
  }
`;
var Inline = ({
  label,
  sub,
  display,
  meta,
  percent,
  stop,
  extent,
  hue,
  inScope,
  untracked = false,
  dot = true,
  size = "compact",
  orientation = "right",
  twoLine = false,
  className,
  onClick
}) => {
  const sizeKey = variant(SIZE5, size, "compact", "Inline", "size");
  const inherited = React29.useContext(ScopeContext);
  const selected = inScope ?? inherited;
  const resolvedStop = clampStop3(stop ?? (percent === void 0 ? 7 : stopForValue2(percent)));
  const rawExtent = extent ?? (percent === void 0 ? 50 : percent);
  const resolvedExtent = Number.isFinite(rawExtent) ? Math.min(100, Math.max(0, rawExtent)) : 50;
  const palette = untracked ? UNTRACKED : hue ? HUE2[variant(HUE2, hue, "mint", "Inline", "hue")] : {
    // Same stop INDEX on either ramp — that is what keeps the shape of the data
    // readable when a panel goes out of scope.
    hue: (selected ? SELECTED_RAMP8 : OFF_RAMP2)[resolvedStop - 1],
    ink: (selected ? INK_RAMP3 : OFF_INK_RAMP)[resolvedStop - 1]
  };
  const classes = [
    "tl-inline",
    `sz-${size}`,
    `is-${orientation}`,
    selected ? "in-scope" : "",
    `stop-${resolvedStop}`,
    hue ? `r-${hue}` : "",
    untracked ? "is-untracked" : "",
    twoLine ? "is-two-line" : "",
    onClick ? "is-clickable" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  const style = {
    "--hue": rgbTriple3(palette.hue),
    "--ink": palette.ink,
    "--v": `${resolvedExtent}%`,
    "--a": SIZE5[sizeKey].alpha
  };
  return /* @__PURE__ */ jsxs28(
    Row3,
    {
      className: classes,
      style,
      $size: sizeKey,
      onClick,
      role: onClick ? "button" : void 0,
      tabIndex: onClick ? 0 : void 0,
      onKeyDown: onClick ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      } : void 0,
      children: [
        /* @__PURE__ */ jsxs28("span", { className: "tl-inline-label", children: [
          label,
          sub ? /* @__PURE__ */ jsx34("span", { className: "tl-inline-sub", children: sub }) : null
        ] }),
        meta ? /* @__PURE__ */ jsx34("span", { className: "tl-inline-meta", children: meta }) : null,
        display !== void 0 ? /* @__PURE__ */ jsxs28("span", { className: "tl-inline-value", children: [
          orientation === "left" && dot ? /* @__PURE__ */ jsx34("span", { className: "tl-inline-dot" }) : null,
          display,
          orientation === "right" && dot ? /* @__PURE__ */ jsx34("span", { className: "tl-inline-dot" }) : null
        ] }) : null
      ]
    }
  );
};
var InlineStack = ({
  inScope = false,
  className,
  children
}) => /* @__PURE__ */ jsx34(ScopeContext.Provider, { value: inScope, children: /* @__PURE__ */ jsx34(
  Stack3,
  {
    className: ["tl-inline-stack", inScope ? "in-scope" : "", className ?? ""].filter(Boolean).join(" "),
    children
  }
) });

// src/Inputs/Input.tsx
import React30 from "react";
import styled35, { css as css19 } from "styled-components";
import {
  brand as brand24,
  fontMono as fontMono10,
  fontSans as fontSans22,
  ink as ink27,
  inkOn as inkOn13,
  inset as inset6,
  motion as motion18,
  rgba as rgba24,
  semantic as semantic7,
  surface as surface17,
  typeScale as typeScale9,
  weight as weight11
} from "@repo/design-tokens";

// src/internal/warnDev.ts
var warnDev = (component, message) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[@repo/design-system] ${component}: ${message}`);
  }
};

// src/Inputs/Input.tsx
import { Fragment as Fragment9, jsx as jsx35, jsxs as jsxs29 } from "react/jsx-runtime";
var FieldRoot = styled35.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ $size }) => $size === "compact" ? FIELD_LABEL.gapCompact : FIELD_LABEL.gap};

  .tl-field-head {
    display: flex;
    align-items: baseline;
    gap: ${inset6[2]};
    min-width: 0;
  }

  .tl-field-head > .tl-field-label {
    flex: none;
  }

  .tl-field-aside {
    margin-left: auto;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font: ${weight11.regular} ${typeScale9.captionCompact.fontSize} /
      ${typeScale9.captionCompact.lineHeight} ${fontSans22};
    color: ${ink27[3]};
  }

  /* \`asideTone="success"\` (frame 01's *Available*): mint ink, medium weight, the tick first. */
  .tl-field-aside.tone-success {
    display: inline-flex;
    align-items: center;
    gap: ${inset6[2]};
    font-weight: ${weight11.medium};
    color: ${inkOn13.mint};
  }

  .tl-field-aside-tick {
    flex: none;
  }

  &.sz-compact .tl-field-aside {
    font-size: ${typeScale9.caption2.fontSize};
    line-height: ${typeScale9.caption2.lineHeight};
  }

  .tl-field-label {
    display: flex;
    align-items: center;
    gap: 3px;
    font: ${({ $size }) => $size === "compact" ? `${weight11.medium} ${typeScale9.captionCompact.fontSize}/1.2` : FIELD_LABEL.labelFont}
      ${fontSans22};
    color: ${FIELD_LABEL.labelInk};
  }

  .tl-field-req {
    color: ${FIELD_CHROME.errorBorder};
    font-weight: 700;
  }

  .tl-field-help {
    font: ${FIELD_LABEL.helpFont} ${fontMono10};
    color: ${FIELD_LABEL.helpInk};
  }

  /* \`permanent\`: the lock glyph leads the helper sentence (frame 01). The helper keeps card 02's
     type and ink — the frame's newer sans is a card refresh, not a one-field re-value. Baseline,
     not centre: the frame draws one line, and on a sentence that wraps the lock stays on its first. */
  .tl-field-help.has-lock {
    display: flex;
    align-items: baseline;
    gap: ${inset6[2]};
  }

  .tl-field-lock {
    flex: none;
  }

  .tl-field-err {
    font: ${FIELD_LABEL.errFont} ${fontMono10};
    color: ${FIELD_LABEL.errInk};
  }

  &.sz-compact .tl-field-help,
  &.sz-compact .tl-field-err {
    font-size: ${typeScale9.caption2.fontSize};
  }

  &.has-error .tl-field-label {
    color: ${FIELD_CHROME.errorBorder};
  }
`;
var Field = ({
  className,
  label,
  htmlFor,
  helper,
  aside,
  asideTone,
  error,
  required,
  size,
  permanent,
  children
}) => {
  const autoId = React30.useId();
  const controlId = htmlFor ?? autoId;
  const labelId = `${controlId}-label`;
  const helperId = `${controlId}-help`;
  const asideId = `${controlId}-aside`;
  const errorId = `${controlId}-err`;
  const sz = variant(FIELD_SIZE, size, "md", "Field", "size");
  const lockHelper = permanent === true && helper != null && helper !== "" && helper !== false;
  const describedBy = [
    aside != null ? asideId : null,
    helper != null ? helperId : null,
    error != null ? errorId : null
  ].filter(Boolean).join(" ");
  const wiring = {
    id: controlId,
    labelId,
    invalid: error != null,
    required,
    size: sz,
    "aria-describedby": describedBy || void 0
  };
  const labelEl = label != null ? /* @__PURE__ */ jsxs29("label", { className: "tl-field-label", id: labelId, htmlFor: controlId, children: [
    label,
    required && /* @__PURE__ */ jsx35("span", { className: "tl-field-req", "aria-hidden": "true", children: "*" })
  ] }) : null;
  const head = aside == null ? labelEl : /* @__PURE__ */ jsxs29("div", { className: "tl-field-head", children: [
    labelEl,
    /* @__PURE__ */ jsx35(
      "span",
      {
        className: asideTone === "success" ? "tl-field-aside tone-success" : "tl-field-aside",
        id: asideId,
        children: asideTone === "success" ? /* @__PURE__ */ jsxs29(Fragment9, { children: [
          /* @__PURE__ */ jsx35(Icon, { name: "circle-check", variant: "solid", size: "xs", className: "tl-field-aside-tick" }),
          /* @__PURE__ */ jsx35("span", { children: aside })
        ] }) : aside
      }
    )
  ] });
  return /* @__PURE__ */ jsxs29(
    FieldRoot,
    {
      $size: sz,
      className: ["tl-field", className, `sz-${sz}`, error != null ? "has-error" : null].filter(Boolean).join(" "),
      children: [
        head,
        typeof children === "function" ? children(wiring) : children,
        helper != null && /* @__PURE__ */ jsx35("span", { className: lockHelper ? "tl-field-help has-lock" : "tl-field-help", id: helperId, children: lockHelper ? /* @__PURE__ */ jsxs29(Fragment9, { children: [
          /* @__PURE__ */ jsx35(Icon, { name: "lock", variant: "solid", size: "xs", className: "tl-field-lock" }),
          /* @__PURE__ */ jsx35("span", { className: "tl-field-help-text", children: helper })
        ] }) : helper }),
        error != null && /* @__PURE__ */ jsx35("span", { className: "tl-field-err", id: errorId, children: error })
      ]
    }
  );
};
var Wrap2 = styled35.div`
  position: relative;
  width: 100%;
`;
var prefixType = (size) => css19`
  font-family: ${fontMono10};
  font-weight: ${weight11.medium};
  font-size: ${PREFIX_FONT[size]};
`;
var prefixGutter = (size, glyphs) => `calc(${glyphs}ch + ${FIELD_PAD_X[size]} + ${FIELD_PAD_X[size]} + ${FIELD_SIZE[size].border} + ${FIELD_PAD_X[size]})`;
var Box2 = styled35.input`
  appearance: none;
  width: 100%;
  box-sizing: border-box;

  /* The card shows no stepper anywhere, and Chrome renders one for type=number regardless of
     \`appearance: none\` unless its pseudo-elements are reset too — it sat on top of the field's
     right padding and read as a second control. */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }
  height: ${({ $size }) => FIELD_SIZE[$size].height};
  /* With a trailing slot the card reserves the same right gutter a select's chevron uses, so an
     input and a select stay the same width whatever they carry. */
  padding: ${({ $size, $hasTrail }) => $hasTrail ? FIELD_SIZE[$size].padding : `0 ${FIELD_PAD_X[$size]}`};
  border-radius: ${({ $size }) => FIELD_SIZE[$size].radius};
  border: ${({ $size }) => FIELD_SIZE[$size].border} solid ${FIELD_CHROME.border};
  background: ${FIELD_CHROME.fill};
  font-family: ${fontSans22};
  font-weight: 400;
  font-size: ${({ $size }) => FIELD_SIZE[$size].font};
  line-height: 1;
  color: ${FIELD_CHROME.ink};
  outline: none;
  transition:
    border-color 0.15s ${motion18.easeUi},
    box-shadow 0.15s ${motion18.easeUi},
    background-color 0.15s ${motion18.easeUi};

  /* A fixed prefix (\`fixedPrefix\`): the value is set in the segment's own mono face and size, so
     one \`ch\` here is exactly one prefix glyph and the left gutter is fixed in CSS — the segment's
     padding, glyphs and divider, then the plain inset — with nothing measured in script. Only the
     LEFT padding moves; the shorthand above still owns the right gutter (\`has-trail\`). */
  /* Emitted only for a prefixed field, so an Input without one carries no extra rule at all. */
  ${({ $size, $prefixLen }) => $prefixLen > 0 && css19`
      &.has-prefix {
        padding-left: ${prefixGutter($size, $prefixLen)};
        ${prefixType($size)}
      }
    `}

  /* A leading glyph (\`leadIcon\`): the text starts where \`Search\`'s does, and the glyph follows
     the field's focus as the magnifier does. Emitted only for a field with one, like the prefix. */
  ${({ $size, $hasLead }) => $hasLead && css19`
      &.has-lead {
        padding-left: ${SEARCH_PAD_LEFT[$size]};
      }
      &:focus ~ .tl-input-lead,
      &.is-focus ~ .tl-input-lead {
        color: ${brand24.cyan};
      }
    `}

  &::placeholder {
    color: ${ink27[5]};
  }

  &:hover:not(:disabled):not(:focus):not([readonly]) {
    border-color: ${FIELD_CHROME.borderHover};
  }

  /* The cyan focus LIFTS the fill to white — the card's signature, shared with Select. */
  &:focus,
  &.is-focus {
    border-color: ${brand24.cyan};
    background: ${FIELD_CHROME.fillFocus};
    box-shadow: ${({ $size }) => fieldFocusRing($size)};
  }

  &.is-error {
    border-color: ${FIELD_CHROME.errorBorder};
    background: ${FIELD_CHROME.errorFill};
  }
  &.is-error:focus,
  &.is-error.is-focus {
    box-shadow: 0 0 0 3px ${FIELD_CHROME.errorRing};
  }

  &:disabled {
    background: ${FIELD_CHROME.disabledFill};
    border-color: ${FIELD_CHROME.disabledBorder};
    color: ${ink27[5]};
    cursor: not-allowed;
  }

  /* Read-only is the DOM property, not a class: it is what stops typing and what a screen
     reader announces. The chrome only follows it. */
  &[readonly] {
    background: ${FIELD_CHROME.readOnlyFill};
    border-color: ${FIELD_CHROME.readOnlyBorder};
    color: ${FIELD_CHROME.readOnlyInk};
  }
  &[readonly]:focus {
    border-color: ${FIELD_CHROME.readOnlyBorder};
    box-shadow: none;
  }

  /* On glass the pale hairline vanishes over the 3D scene, so the fill goes translucent and the
     border DARKENS instead of lightening. */
  &.on-glass {
    background: ${FIELD_CHROME.glassFill};
    border-color: ${FIELD_CHROME.glassBorder};
    backdrop-filter: ${FIELD_CHROME.glassBlur};
    -webkit-backdrop-filter: ${FIELD_CHROME.glassBlur};
  }
  &.on-glass::placeholder {
    color: ${FIELD_CHROME.glassBorderHover};
  }
  &.on-glass:hover:not(:disabled):not(:focus):not([readonly]) {
    background: ${FIELD_CHROME.glassFillHover};
    border-color: ${FIELD_CHROME.glassBorderHover};
  }
  &.on-glass:focus,
  &.on-glass.is-focus {
    background: ${FIELD_CHROME.fillFocus};
    border-color: ${brand24.cyan};
  }
  &.on-glass.on-opaque {
    background: ${FIELD_CHROME.glassFillOpaque};
    border-color: ${FIELD_CHROME.glassBorderOpaque};
  }
  &.on-glass.on-opaque:focus,
  &.on-glass.on-opaque.is-focus {
    background: ${FIELD_CHROME.fillFocus};
  }
`;
var TRAIL_GLYPH = {
  compact: "14px",
  sm: "18px",
  md: "18px",
  lg: "18px"
};
var TICK_ICON = "xs";
var VALID_TICK_COLOUR = semantic7.feedback.successStrong;
var VALID_TICK_DANGER_COLOUR = semantic7.feedback.errorStrong;
var Tick = styled35.span`
  display: inline-flex;
  color: ${({ $tone }) => $tone === "danger" ? VALID_TICK_DANGER_COLOUR : VALID_TICK_COLOUR};
`;
var ValidStatus = styled35.span`
  ${visuallyHiddenCss}
`;
var Trail = styled35.span`
  position: absolute;
  right: ${({ $size }) => FIELD_TRAIL_INSET[$size]};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: ${ink27[3]};
  /* A decorative glyph must not swallow the click that should focus the field; an interactive
     trail (a lookup button, a password reveal) opts back in. */
  pointer-events: none;

  button,
  a,
  [role='button'] {
    pointer-events: auto;
  }

  > svg {
    width: ${({ $size }) => TRAIL_GLYPH[$size]};
    height: ${({ $size }) => TRAIL_GLYPH[$size]};
    display: block;
  }
`;
var PREFIX_INK = ink27[3];
var PREFIX_FILL = surface17[2];
var PREFIX_FILL_ALPHA = 0.9;
var Prefix = styled35.span`
  position: absolute;
  z-index: 1;
  top: ${({ $size }) => FIELD_SIZE[$size].border};
  bottom: ${({ $size }) => FIELD_SIZE[$size].border};
  left: ${({ $size }) => FIELD_SIZE[$size].border};
  display: flex;
  align-items: center;
  padding: 0 ${({ $size }) => FIELD_PAD_X[$size]};
  background: ${rgba24(PREFIX_FILL, PREFIX_FILL_ALPHA)};
  border-right: ${({ $size }) => FIELD_SIZE[$size].border} solid ${FIELD_CHROME.border};
  border-radius: ${({ $size }) => `calc(${FIELD_SIZE[$size].radius} - ${FIELD_SIZE[$size].border}) 0 0 calc(${FIELD_SIZE[$size].radius} - ${FIELD_SIZE[$size].border})`};
  ${({ $size }) => prefixType($size)}
  line-height: 1;
  letter-spacing: normal;
  word-spacing: normal;
  color: ${PREFIX_INK};
  white-space: pre;
  pointer-events: none;
  user-select: none;
`;
var Lead2 = styled35.span`
  ${searchLeadCss}
`;
var Input = React30.forwardRef(function Input2({
  className,
  size,
  invalid = false,
  onGlass = false,
  onOpaque = false,
  trail,
  // Swallowed, not forwarded. `Field`'s wiring is designed to be spread whole
  // (`<Input {...wiring} />`), and `labelId` is only meaningful to a control that needs
  // `aria-labelledby` — on an `<input>` it would reach the DOM and React would warn about an
  // unrecognised attribute. Accepting and dropping it is what keeps the spread ergonomic.
  labelId: _labelId,
  valid,
  validLabel = "Valid",
  validTone = "success",
  fixedPrefix,
  leadIcon,
  "aria-describedby": describedBy,
  ...rest
}, ref) {
  const sz = variant(FIELD_SIZE, size, "md", "Input", "size");
  const baseId = React30.useId();
  const statusId = `${baseId}-valid`;
  const prefixId = `${baseId}-prefix`;
  const prefixLen = fixedPrefix ? Array.from(fixedPrefix).length : 0;
  const hasPrefix = prefixLen > 0;
  const hasLead = leadIcon != null && !hasPrefix;
  const leadClash = leadIcon != null && hasPrefix;
  const leadWarned = React30.useRef(false);
  React30.useEffect(() => {
    if (leadClash && !leadWarned.current) {
      leadWarned.current = true;
      warnDev(
        "Input",
        "`leadIcon` and `fixedPrefix` were both passed; the prefix is drawn and the glyph is not."
      );
    }
  }, [leadClash]);
  const callerTrail = trail != null && trail !== false && trail !== "";
  const showTick = valid === true && !invalid && !callerTrail;
  const hasTrail = trail != null || showTick;
  const trailContent = callerTrail ? trail : showTick ? /* @__PURE__ */ jsx35(Tick, { className: "tl-input-valid", $tone: validTone, "aria-hidden": "true", children: /* @__PURE__ */ jsx35(Icon, { name: "check", size: TICK_ICON }) }) : null;
  const warned = React30.useRef(false);
  React30.useEffect(() => {
    if (valid === true && callerTrail && !warned.current && process.env.NODE_ENV !== "production") {
      warned.current = true;
      console.warn(
        "Input: `valid` and `trail` were both passed; the trail is drawn and the tick is not."
      );
    }
  }, [valid, callerTrail]);
  const ariaDescribedBy = [hasPrefix ? prefixId : null, describedBy, showTick ? statusId : null].filter(Boolean).join(" ") || void 0;
  return /* @__PURE__ */ jsxs29(Wrap2, { className: "tl-input-wrap", children: [
    hasPrefix && /* @__PURE__ */ jsx35(Prefix, { id: prefixId, className: "tl-input-prefix", $size: sz, "aria-hidden": "true", children: fixedPrefix }),
    /* @__PURE__ */ jsx35(
      Box2,
      {
        ...rest,
        ref,
        $size: sz,
        $hasTrail: hasTrail,
        $prefixLen: prefixLen,
        $hasLead: hasLead,
        "aria-describedby": ariaDescribedBy,
        "aria-invalid": invalid || void 0,
        className: [
          "tl-input",
          className,
          `sz-${sz}`,
          // The card's own modifier name. Emitted as well as driving the padding, so a
          // consumer (and a test) can see the gutter is reserved.
          hasTrail ? "has-trail" : null,
          hasPrefix ? "has-prefix" : null,
          hasLead ? "has-lead" : null,
          invalid ? "is-error" : null,
          // The armed type-to-confirm field borrows the error chrome, never the error semantics.
          showTick && validTone === "danger" ? "is-error is-armed" : null,
          onGlass ? "on-glass" : null,
          onGlass && onOpaque ? "on-opaque" : null
        ].filter(Boolean).join(" ")
      }
    ),
    hasLead && /* @__PURE__ */ jsx35(Lead2, { $size: sz, className: "tl-input-lead", "aria-hidden": "true", children: /* @__PURE__ */ jsx35(Icon, { name: leadIcon, size: SEARCH_LEAD_ICON[sz] }) }),
    hasTrail && /* @__PURE__ */ jsx35(Trail, { $size: sz, children: trailContent }),
    valid !== void 0 && /* @__PURE__ */ jsx35(ValidStatus, { id: statusId, role: "status", children: showTick ? validLabel : "" })
  ] });
});

// src/Inputs/Textarea.tsx
import React31 from "react";
import styled36 from "styled-components";
import { brand as brand25, fontSans as fontSans23, ink as ink28, motion as motion19 } from "@repo/design-tokens";
import { jsx as jsx36 } from "react/jsx-runtime";
var PAD2 = `${FIELD_PAD_X.md} ${FIELD_PAD_X.lg}`;
var MIN_HEIGHT = "72px";
var LINE_HEIGHT = 1.45;
var Box3 = styled36.textarea`
  appearance: none;
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: ${MIN_HEIGHT};
  padding: ${PAD2};
  resize: vertical;
  border-radius: ${FIELD_SIZE.md.radius};
  border: ${FIELD_SIZE.md.border} solid ${FIELD_CHROME.border};
  background: ${FIELD_CHROME.fill};
  font-family: ${fontSans23};
  font-weight: 400;
  font-size: ${FIELD_SIZE.md.font};
  line-height: ${LINE_HEIGHT};
  color: ${FIELD_CHROME.ink};
  outline: none;
  transition:
    border-color 0.15s ${motion19.easeUi},
    box-shadow 0.15s ${motion19.easeUi},
    background-color 0.15s ${motion19.easeUi};

  &::placeholder {
    color: ${ink28[5]};
  }
  &:hover:not(:disabled):not(:focus):not([readonly]) {
    border-color: ${FIELD_CHROME.borderHover};
  }
  &:focus {
    border-color: ${brand25.cyan};
    background: ${FIELD_CHROME.fillFocus};
    box-shadow: ${fieldFocusRing("md")};
  }
  &.is-error {
    border-color: ${FIELD_CHROME.errorBorder};
    background: ${FIELD_CHROME.errorFill};
  }
  &.is-error:focus {
    box-shadow: 0 0 0 3px ${FIELD_CHROME.errorRing};
  }
  &:disabled {
    background: ${FIELD_CHROME.disabledFill};
    border-color: ${FIELD_CHROME.disabledBorder};
    color: ${ink28[5]};
    cursor: not-allowed;
    resize: none;
  }
  &[readonly] {
    background: ${FIELD_CHROME.readOnlyFill};
    border-color: ${FIELD_CHROME.readOnlyBorder};
    color: ${FIELD_CHROME.readOnlyInk};
  }
  &[readonly]:focus {
    border-color: ${FIELD_CHROME.readOnlyBorder};
    box-shadow: none;
  }
`;
var Textarea = React31.forwardRef(function Textarea2({ invalid = false, labelId: _labelId, size: _size, rows = 4, className, ...rest }, ref) {
  return /* @__PURE__ */ jsx36(
    Box3,
    {
      ref,
      rows,
      "aria-invalid": invalid || void 0,
      className: ["tl-textarea", invalid ? "is-error" : null, className].filter(Boolean).join(" "),
      ...rest
    }
  );
});

// src/Inputs/Slider.tsx
import React32 from "react";
import styled37, { css as css21 } from "styled-components";
import {
  controlHeight as controlHeight5,
  fontSans as fontSans24,
  focus as focus9,
  glassCard as glassCard3,
  ink as ink29,
  line as line13,
  motion as motion20,
  radius as radius22,
  rgba as rgba26,
  rgbTriple as rgbTriple4,
  shadow as shadow11,
  surface as surfaceToken7,
  typeScale as typeScale10,
  weight as weight12
} from "@repo/design-tokens";

// src/internal/numberField.ts
import { css as css20 } from "styled-components";
import { brand as brand26, controlHeight as controlHeight4, line as line12, radius as radius21, rgba as rgba25, surface as surface18 } from "@repo/design-tokens";
var numberFieldChassisCss = css20`
  height: ${controlHeight4.sm};
  padding: 0 8px;
  box-sizing: border-box;
  background: ${surface18[0]};
  border: 1.5px solid ${line12[1]};
  border-radius: ${radius21.control};
`;
var numberFieldFocusCss = css20`
  border-color: ${brand26.cyan};
  box-shadow: 0 0 0 3px ${rgba25(brand26.cyan, 0.12)};
`;

// src/Inputs/Slider.tsx
import { jsx as jsx37, jsxs as jsxs30 } from "react/jsx-runtime";
var SIZE6 = {
  md: { thumb: 20, track: 6, row: controlHeight5.sm },
  // The card's small row is a bare 24px (its `--tl-control-h-xs`, not yet taken from the design
  // project — nothing else binds it); the thumb and track sizes are card geometry.
  sm: { thumb: 16, track: 4, row: "24px" }
};
var SURFACE = { "on-solid": "on-solid", "on-glass": "on-glass" };
var INK = rgbTriple4(ink29[1]);
var FROST = glassCard3.frost;
var WHITE2 = surfaceToken7[0];
var NUMBER_FIELD_WIDTH = "72px";
var BIG_STEP = 10;
var clampNum = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
var decimals = (n) => {
  const s = String(n);
  const i = s.indexOf(".");
  return i < 0 ? 0 : s.length - i - 1;
};
var snapTo = (v, min, max, step) => {
  const snapped = Math.round((v - min) / step) * step + min;
  return clampNum(Number(snapped.toFixed(Math.max(decimals(step), decimals(min)))), min, max);
};
function resolveMarks(marks, min, max, step) {
  if (marks === void 0 || marks === null) return { marks: null };
  if (!Array.isArray(marks)) return { marks: null, reason: "`marks` is not an array" };
  if (Array.from(marks).some(
    (m) => !m || typeof m !== "object" || typeof m.value !== "number" || typeof m.label !== "string"
  )) {
    return { marks: null, reason: "`marks` entries must be { value: number, label: string }" };
  }
  if (marks.length < 2 || marks.length > 4) {
    return { marks: null, reason: `\`marks\` needs 2\u20134 entries, got ${marks.length}` };
  }
  const sorted = [...marks].sort((a, b) => a.value - b.value);
  for (const m of sorted) {
    if (!Number.isFinite(m.value) || m.value < min || m.value > max) {
      return { marks: null, reason: `\`marks\` value ${m.value} is outside ${min}\u2013${max}` };
    }
    if (snapTo(m.value, min, max, step) !== m.value) {
      return {
        marks: null,
        reason: `\`marks\` value ${m.value} is not on the step grid (${step})`
      };
    }
  }
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].value === sorted[i - 1].value) {
      return { marks: null, reason: `\`marks\` value ${sorted[i].value} appears twice` };
    }
  }
  return { marks: sorted };
}
var warnDev2 = (message) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[@repo/design-system] Slider: ${message}`);
  }
};
var MARK_EDGE = { end: 99, start: 1 };
var marksContentKey = (marks) => {
  if (marks === void 0 || marks === null) return "";
  if (!Array.isArray(marks)) return `not-array:${typeof marks}`;
  return Array.from(
    marks,
    (m) => m && typeof m === "object" ? `${String(m.value)}\0${String(m.label)}` : `${typeof m}`
  ).join("");
};
var solidCss = css21`
  .track {
    background: linear-gradient(180deg, rgba(var(--on), 0.08), rgba(var(--off), 0.3));
    border: 1px solid transparent;
    border-top-color: rgba(var(--on), 0.42);
    border-left-color: rgba(var(--on), 0.36);
    border-bottom-color: rgba(var(--on), 0.2);
    border-right-color: rgba(var(--on), 0.22);
    box-shadow:
      inset 0 1px 1px rgba(${INK}, 0.06),
      0 0 0 1px rgba(var(--on), 0.04),
      0 1px 2px rgba(${INK}, 0.1),
      0 2px 6px rgba(${INK}, 0.08);
  }
  .fill {
    background: linear-gradient(180deg, rgba(var(--on), 0.95), rgba(var(--on), 0.75));
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      1px 2px 5px -1px rgba(var(--on), 0.45);
  }
  .thumb {
    box-shadow:
      0 0 0 1px rgba(var(--on), 0.38),
      0 1px 2px rgba(${INK}, 0.2),
      0 3px 8px rgba(${INK}, 0.16),
      inset 0 1px 0 ${WHITE2};
  }
  .thumb:hover {
    box-shadow:
      0 0 0 1px rgba(var(--on), 0.5),
      0 0 10px 3px rgba(var(--on), 0.24),
      0 1px 2px rgba(${INK}, 0.2),
      0 3px 8px rgba(${INK}, 0.16),
      inset 0 1px 0 ${WHITE2};
  }
  .thumb.is-drag {
    box-shadow:
      0 0 0 1px rgba(var(--on), 0.6),
      0 0 14px 5px rgba(var(--on), 0.28),
      0 2px 3px rgba(${INK}, 0.2),
      0 4px 10px rgba(${INK}, 0.18),
      inset 0 1px 0 ${WHITE2};
  }
`;
var glassCss3 = css21`
  .track {
    background: rgba(var(--off), 0.3);
    border: 1px solid rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.6),
      0 1px 2px rgba(${INK}, 0.08),
      0 2px 6px rgba(${INK}, 0.05);
  }
  .fill {
    background: rgba(var(--on), 0.62);
    box-shadow:
      inset 0 1px 1px rgba(255, 255, 255, 0.55),
      0 0 10px 2px rgba(${FROST}, 0.6);
  }
  .thumb {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.8),
      0 1px 3px rgba(${INK}, 0.25),
      0 3px 8px rgba(${INK}, 0.1),
      inset 0 1px 0 ${WHITE2};
  }
  .thumb:hover {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.9),
      0 0 12px 4px rgba(${FROST}, 0.7),
      0 1px 3px rgba(${INK}, 0.25),
      0 3px 8px rgba(${INK}, 0.1);
  }
  .thumb.is-drag {
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.9),
      0 0 16px 6px rgba(${FROST}, 0.8),
      0 2px 6px rgba(${INK}, 0.25);
  }
  .tl-slider-num {
    background: rgba(255, 255, 255, 0.55);
    border-color: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(6px);
  }
`;
var Root6 = styled37.div`
  --off: ${({ $hue }) => CONTROL_HUES[$hue].off};
  --on: ${({ $hue }) => CONTROL_HUES[$hue].on};
  --thumb: ${({ $size }) => SIZE6[$size].thumb}px;
  --track: ${({ $size }) => SIZE6[$size].track}px;

  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: ${fontSans24};

  .tl-slider-hd {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  .tl-slider-lbl {
    font: 500 13px/1.3 ${fontSans24};
    color: ${ink29[1]};
  }
  .tl-slider-icon {
    display: inline-flex;
    margin-right: 6px;
    color: ${ink29[2]};
  }
  .tl-slider-desc {
    ${visuallyHiddenCss}
  }

  /* Inline: label, track and value on one row (card 22's inline arrangement). The head's two parts
     join the row around the track; the label and the value keep fixed widths, so a stack of inline
     sliders lines its tracks up. */
  &.is-inline {
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }
  &.is-inline .tl-slider-hd {
    display: contents;
  }
  &.is-inline .tl-slider-lbl {
    order: 0;
    flex: none;
    width: 76px;
    display: inline-flex;
    align-items: center;
  }
  &.is-inline .tl-slider {
    order: 1;
    flex: 1;
    min-width: 0;
  }
  &.is-inline .tl-slider-val,
  &.is-inline .tl-slider-nums {
    order: 2;
    flex: none;
    min-width: 44px;
    text-align: right;
  }
  .tl-slider-val {
    font: 500 12px/1 ${fontSans24};
    color: ${ink29[2]};
    font-variant-numeric: tabular-nums;
  }

  .tl-slider {
    position: relative;
    height: ${({ $size }) => SIZE6[$size].row};
    display: flex;
    align-items: center;
    touch-action: none;
    cursor: pointer;
    user-select: none;
    -webkit-user-select: none;
  }
  .track {
    position: relative;
    flex: 1;
    height: var(--track);
    border-radius: ${radius22.pill};
    box-sizing: border-box;
    /* Inset by half a thumb, so the thumb never overhangs the container. */
    margin: 0 calc(var(--thumb) / 2);
  }
  .fill {
    position: absolute;
    top: -1px;
    bottom: -1px;
    border-radius: ${radius22.pill};
    transition: background ${motion20.durFast} ${motion20.easeUi};
  }
  .thumb {
    position: absolute;
    top: 50%;
    width: var(--thumb);
    height: var(--thumb);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    box-sizing: border-box;
    outline: none;
    cursor: grab;
    transition: box-shadow ${motion20.durFast} ${motion20.easeUi};
    background: linear-gradient(180deg, ${WHITE2}, ${surfaceToken7[3]});
    z-index: 2;
  }
  .thumb.is-drag {
    cursor: grabbing;
  }
  .bubble {
    position: absolute;
    left: 50%;
    bottom: calc(100% + 8px);
    transform: translateX(-50%);
    padding: 4px 7px;
    border-radius: ${radius22.md};
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid ${line13[2]};
    box-shadow: ${shadow11.glassSm};
    font: 600 11px/1 ${fontSans24};
    color: ${ink29[1]};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity ${motion20.durFast} ${motion20.easeUi};
  }
  .thumb.is-drag .bubble,
  .thumb:focus-visible .bubble {
    opacity: 1;
  }
  .ticks {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  .tick {
    position: absolute;
    top: 50%;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    background: rgba(var(--on), 0.45);
  }
  .tick.in {
    background: rgba(255, 255, 255, 0.9);
  }
  .tl-slider-tick-lbls {
    position: relative;
    height: 14px;
    margin: 0 calc(var(--thumb) / 2);
  }
  .tl-slider-tick-lbls span {
    position: absolute;
    transform: translateX(-50%);
    font: 400 11px/1 ${fontSans24};
    color: ${ink29[3]};
    white-space: nowrap;
  }
  .tl-slider-tick-lbls span.on {
    color: ${ink29[1]};
    font-weight: 500;
  }

  /* Card 22, Marked: a line on the track per mark, and its name as a button under it. */
  .mk {
    position: absolute;
    top: 50%;
    width: 2px;
    height: 10px;
    border-radius: 1px;
    transform: translate(-50%, -50%);
    background: rgba(var(--on), 0.45);
    pointer-events: none;
    z-index: 1;
  }
  .mk.in {
    background: ${rgba26(surfaceToken7[0], 0.95)};
  }
  .tl-slider-marks {
    position: relative;
    height: 20px;
    margin: 0 calc(var(--thumb) / 2);
  }
  .tl-slider-marks button {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    appearance: none;
    margin: 0;
    border: 1px solid transparent;
    background: transparent;
    border-radius: ${radius22.pill};
    padding: 1px 6px 2px;
    font: ${weight12.medium} ${typeScale10.captionCompact.fontSize} /
      ${typeScale10.captionCompact.lineHeight} ${fontSans24};
    color: ${ink29[3]};
    cursor: pointer;
    white-space: nowrap;
    transition:
      background ${motion20.durFast} ${motion20.easeUi},
      color ${motion20.durFast} ${motion20.easeUi};
  }
  .tl-slider-marks button.end {
    transform: translateX(calc(-100% + 6px));
  }
  .tl-slider-marks button.start {
    transform: translateX(-6px);
  }
  /* Hover darkens an unselected name only: the selected mark keeps its look under the pointer
     (the card's .on rule outranks its hover). */
  .tl-slider-marks button:not(.on):hover:not(:disabled) {
    color: ${ink29[1]};
  }
  /* The selected-tag look. On solid it brings the selected ink; on glass the ink stays ink-1. */
  .tl-slider-marks button.on {
    font-weight: 600;
    color: ${ink29[1]};
    ${({ $surface }) => $surface === "on-glass" ? selectedOnGlassCss : selectedOnSolidCss}
  }
  /* Focus AFTER .on, so the ring wins over the glass glow on the mark the value sits on (review);
     Tag orders its focus after its active state the same way. */
  .tl-slider-marks button:focus-visible {
    outline: none;
    box-shadow: ${focus9.tight};
  }

  .tl-slider-num {
    display: flex;
    align-items: center;
    ${numberFieldChassisCss}
    width: ${NUMBER_FIELD_WIDTH};
    gap: 2px;
    flex: none;
  }
  .tl-slider-num:focus-within {
    ${numberFieldFocusCss}
  }
  .tl-slider-num input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: none;
    font: 400 13px/1 ${fontSans24};
    color: ${ink29[1]};
    text-align: right;
    font-variant-numeric: tabular-nums;
    padding: 0;
    -moz-appearance: textfield;
  }
  .tl-slider-num input::-webkit-outer-spin-button,
  .tl-slider-num input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .tl-slider-num span {
    font: 400 12px/1 ${fontSans24};
    color: ${ink29[4]};
  }
  .tl-slider-nums {
    display: flex;
    gap: 6px;
  }

  ${({ $surface }) => $surface === "on-glass" ? glassCss3 : solidCss}

  /* Focus: the system ring, after the surface rules so it wins. */
  .thumb:focus-visible {
    box-shadow:
      ${focus9.ring},
      0 1px 3px rgba(${INK}, 0.22);
  }

  &.is-disabled .tl-slider {
    cursor: not-allowed;
    opacity: 0.5;
  }
  &.is-disabled .thumb,
  &.is-disabled .tl-slider-marks button {
    cursor: not-allowed;
  }
  &.is-disabled .tl-slider-marks {
    opacity: 0.5;
  }
  &.is-disabled .tl-slider-lbl,
  &.is-disabled .tl-slider-val {
    color: ${ink29[4]};
  }
`;
var toArray = (v) => typeof v === "number" ? [v] : [v[0], v[1]];
var NumberField = ({
  value,
  label,
  unit,
  min,
  max,
  step,
  disabled,
  onCommitText
}) => {
  const [text, setText] = React32.useState(String(value));
  const editing = React32.useRef(false);
  React32.useEffect(() => {
    if (!editing.current) setText(String(value));
  }, [value]);
  const commit = () => {
    editing.current = false;
    setText(String(onCommitText(text)));
  };
  return /* @__PURE__ */ jsxs30("span", { className: "tl-slider-num", children: [
    /* @__PURE__ */ jsx37(
      "input",
      {
        type: "number",
        inputMode: "decimal",
        "aria-label": unit ? `${label} (${unit})` : label,
        min,
        max,
        step,
        value: text,
        disabled,
        onFocus: () => {
          editing.current = true;
        },
        onChange: (e) => setText(e.target.value),
        onBlur: commit,
        onKeyDown: (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            e.target.blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            editing.current = false;
            setText(String(value));
          }
        }
      }
    ),
    unit ? /* @__PURE__ */ jsx37("span", { "aria-hidden": "true", children: unit }) : null
  ] });
};
function SliderInner(props) {
  const {
    label,
    value,
    min = 0,
    max = 100,
    step = 1,
    surface: surface31,
    size,
    hue,
    ticks: ticksProp = false,
    labels: labelsProp,
    format = String,
    numberField: numberFieldProp,
    marks: marksProp,
    disabled = false,
    onChange,
    onCommit,
    layout = "stacked",
    icon: icon2,
    description,
    id,
    className
  } = props;
  const sz = variant(SIZE6, size, "md", "Slider", "size");
  const surf = variant(SURFACE, surface31, "on-solid", "Slider", "surface");
  const hu = variant(CONTROL_HUES, hue, "aqua", "Slider", "hue");
  const range = typeof value !== "number";
  const span = max - min || 1;
  const marksKey = marksContentKey(marksProp);
  const resolved = React32.useMemo(
    () => range ? marksProp ? { marks: null, reason: "`marks` are single-value only; ignored on a range" } : { marks: null } : resolveMarks(marksProp, min, max, step),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, marksKey, min, max, step]
  );
  const marks = resolved.marks;
  const marked = marks !== null;
  const ignored = marked ? [ticksProp && "ticks", labelsProp && "labels", numberFieldProp && "numberField"].filter(
    Boolean
  ) : [];
  const ticks = marked ? false : ticksProp;
  const labels = marked ? void 0 : labelsProp;
  const numberField = marked ? void 0 : numberFieldProp;
  const markWarning = resolved.reason ?? null;
  const ignoredKey = ignored.join(", ");
  React32.useEffect(() => {
    if (markWarning) warnDev2(`"${label}": ${markWarning}; the marks are ignored.`);
  }, [marksKey, markWarning]);
  React32.useEffect(() => {
    if (ignoredKey)
      warnDev2(`"${label}": ${ignoredKey} ignored \u2014 a marked slider draws none of them.`);
  }, [ignoredKey]);
  const autoId = React32.useId();
  const baseId = id ?? autoId;
  const labelId = `${baseId}-label`;
  const descriptionId = description ? `${baseId}-description` : void 0;
  const inRange = (v) => toArray(v).map((n) => clampNum(n, min, max));
  const [live, setLive] = React32.useState(() => inRange(value));
  const liveRef = React32.useRef(live);
  liveRef.current = live;
  const active = React32.useRef(null);
  const [dragIndex, setDragIndex] = React32.useState(null);
  const trackRef = React32.useRef(null);
  const thumbRefs = React32.useRef([]);
  const valueKey = toArray(value).join(",");
  React32.useEffect(() => {
    if (!active.current) setLive(inRange(value));
  }, [valueKey, min, max]);
  const emit = (vals) => range ? [vals[0], vals[1]] : vals[0];
  const setThumb = (i, raw) => {
    let v = snapTo(raw, min, max, step);
    const cur = liveRef.current;
    if (range) v = i === 0 ? Math.min(v, cur[1]) : Math.max(v, cur[0]);
    if (v === cur[i]) return;
    const next = [...cur];
    next[i] = v;
    liveRef.current = next;
    setLive(next);
    onChange?.(emit(next));
  };
  const begin = (kind, pointerId) => {
    active.current = { kind, start: liveRef.current, pointerId };
  };
  const ownsDrag = (e) => active.current?.kind === "drag" && active.current.pointerId === e.pointerId;
  const finish = () => {
    const was = active.current;
    active.current = null;
    const now = liveRef.current;
    if (was && was.start.some((v, i) => v !== now[i])) onCommit?.(emit(now));
  };
  const valueAtX = (clientX) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r || r.width <= 0) return min;
    return min + (clientX - r.left) / r.width * span;
  };
  const onPointerDown = (e) => {
    if (disabled || e.button !== 0) return;
    if (active.current?.kind === "drag") return;
    const v = valueAtX(e.clientX);
    const cur = liveRef.current;
    let i = 0;
    if (range) {
      i = cur[0] === cur[1] ? v < cur[0] ? 0 : 1 : Math.abs(v - cur[0]) <= Math.abs(v - cur[1]) ? 0 : 1;
    }
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    thumbRefs.current[i]?.focus({ preventScroll: true });
    if (active.current?.kind === "key") finish();
    begin("drag", e.pointerId);
    setDragIndex(i);
    setThumb(i, v);
  };
  const onPointerMove = (e) => {
    if (dragIndex === null || !ownsDrag(e)) return;
    if (e.pointerType === "mouse" && (e.buttons & 1) === 0) {
      endDrag(e);
      return;
    }
    setThumb(dragIndex, valueAtX(e.clientX));
  };
  const endDrag = (e) => {
    if (!ownsDrag(e)) return;
    setDragIndex(null);
    finish();
  };
  const onKeyDown = (i) => (e) => {
    if (disabled) return;
    const big = e.shiftKey ? BIG_STEP : 1;
    const cur = liveRef.current[i];
    let v;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowUp":
        v = cur + step * big;
        break;
      case "ArrowLeft":
      case "ArrowDown":
        v = cur - step * big;
        break;
      case "Home":
        v = min;
        break;
      case "End":
        v = max;
        break;
      default:
        return;
    }
    e.preventDefault();
    if (active.current === null) begin("key");
    if (active.current?.kind !== "key") return;
    setThumb(i, v);
  };
  const onKeyUp = () => {
    if (active.current?.kind === "key") finish();
  };
  const commitText = (i) => (text) => {
    const n = parseFloat(text);
    begin("field");
    setThumb(i, Number.isFinite(n) ? n : min);
    finish();
    return liveRef.current[i];
  };
  const onMarkClick = (markValue) => {
    if (disabled || active.current?.kind === "drag") return;
    if (active.current?.kind === "key") finish();
    begin("field");
    setThumb(0, markValue);
    const hostValue = toArray(value)[0];
    const hostOutside = hostValue < min || hostValue > max;
    if (hostOutside && active.current && active.current.start[0] === markValue && hostValue !== markValue) {
      onChange?.(emit([markValue]));
      active.current = { ...active.current, start: [hostValue] };
    }
    finish();
    thumbRefs.current[0]?.focus({ preventScroll: true });
  };
  const markAt = (v) => marks?.find((m) => Math.abs(m.value - v) <= step * 1e-6);
  const readout2 = (v) => {
    const mark = markAt(v);
    if (!mark) return format(v);
    const text = format(v);
    return mark.label === text ? text : `${mark.label} \xB7 ${text}`;
  };
  const pct3 = (v) => (v - min) / span * 100;
  const p = live.map(pct3);
  const fillLeft = range ? p[0] : 0;
  const fillWidth = range ? p[1] - p[0] : p[0];
  const stops = [];
  if (ticks || labels) {
    for (let v = min, n = 0; v <= max + 1e-9 && n < 1e3; v = min + ++n * step) {
      stops.push(Number(v.toFixed(Math.max(decimals(step), decimals(min)))));
    }
  }
  const stopLabels = labels && labels.length === stops.length ? labels : null;
  const headerText = range ? `${format(live[0])}\u2013${format(live[1])}` : readout2(live[0]);
  const unit = typeof numberField === "object" ? numberField.unit : void 0;
  const thumbName = (i) => range ? `${i === 0 ? "Minimum" : "Maximum"} ${label}` : label;
  return /* @__PURE__ */ jsxs30(
    Root6,
    {
      $surface: surf,
      $size: sz,
      $hue: hu,
      className: [
        "tl-slider-field",
        surf,
        `hue-${hu}`,
        `sz-${sz}`,
        layout === "inline" ? "is-inline" : null,
        disabled ? "is-disabled" : null,
        className
      ].filter(Boolean).join(" "),
      children: [
        /* @__PURE__ */ jsxs30("div", { className: "tl-slider-hd", children: [
          /* @__PURE__ */ jsxs30("span", { className: "tl-slider-lbl", id: labelId, children: [
            icon2 && /* @__PURE__ */ jsx37("span", { className: "tl-slider-icon", "aria-hidden": "true", children: icon2 }),
            label
          ] }),
          numberField ? /* @__PURE__ */ jsx37("span", { className: "tl-slider-nums", children: live.map((v, i) => /* @__PURE__ */ jsx37(
            NumberField,
            {
              value: v,
              label: range ? thumbName(i) : `${label} value`,
              unit,
              min,
              max,
              step,
              disabled,
              onCommitText: commitText(i)
            },
            i
          )) }) : (
            // The thumb speaks the value (`aria-valuetext`); this is its visible twin.
            /* @__PURE__ */ jsx37("span", { className: "tl-slider-val", "aria-hidden": "true", children: headerText })
          )
        ] }),
        /* @__PURE__ */ jsx37(
          "div",
          {
            className: "tl-slider",
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onLostPointerCapture: endDrag,
            children: /* @__PURE__ */ jsxs30("div", { className: "track", ref: trackRef, children: [
              /* @__PURE__ */ jsx37("div", { className: "fill", style: { left: `${fillLeft}%`, width: `${fillWidth}%` } }),
              marks?.map((m) => /* @__PURE__ */ jsx37(
                "i",
                {
                  "aria-hidden": "true",
                  className: ["mk", m.value < live[0] - step * 1e-6 ? "in" : null].filter(Boolean).join(" "),
                  style: { left: `${pct3(m.value)}%` }
                },
                m.value
              )),
              ticks && /* @__PURE__ */ jsx37("div", { className: "ticks", "aria-hidden": "true", children: stops.map((v) => /* @__PURE__ */ jsx37(
                "i",
                {
                  className: [
                    "tick",
                    (range ? v > live[0] && v < live[1] : v < live[0]) ? "in" : null
                  ].filter(Boolean).join(" "),
                  style: { left: `${pct3(v)}%` }
                },
                v
              )) }),
              live.map((v, i) => /* @__PURE__ */ jsx37(
                "div",
                {
                  ref: (el) => {
                    thumbRefs.current[i] = el;
                  },
                  className: ["thumb", dragIndex === i ? "is-drag" : null].filter(Boolean).join(" "),
                  role: "slider",
                  tabIndex: 0,
                  "aria-labelledby": range ? void 0 : labelId,
                  "aria-label": range ? thumbName(i) : void 0,
                  "aria-describedby": descriptionId,
                  "aria-valuemin": range && i === 1 ? live[0] : min,
                  "aria-valuemax": range && i === 0 ? live[1] : max,
                  "aria-valuenow": v,
                  "aria-valuetext": stopLabels ? stopLabels[stops.indexOf(v)] : readout2(v),
                  "aria-disabled": disabled || void 0,
                  style: { left: `${p[i]}%` },
                  onKeyDown: onKeyDown(i),
                  onKeyUp,
                  onBlur: onKeyUp,
                  children: /* @__PURE__ */ jsx37("span", { className: "bubble", "aria-hidden": "true", children: stopLabels ? stopLabels[stops.indexOf(v)] : format(v) })
                },
                i
              ))
            ] })
          }
        ),
        description && /* @__PURE__ */ jsx37("span", { className: "tl-slider-desc", id: descriptionId, children: description }),
        marks && // Named by the slider's label, so a mark reads as *Wide, button, Coverage group*.
        /* @__PURE__ */ jsx37("div", { className: "tl-slider-marks", role: "group", "aria-labelledby": labelId, children: marks.map((m) => {
          const at = pct3(m.value);
          const on = markAt(live[0]) === m;
          return /* @__PURE__ */ jsx37(
            "button",
            {
              type: "button",
              disabled,
              "aria-current": on || void 0,
              className: [
                on ? "on" : null,
                at >= MARK_EDGE.end ? "end" : at <= MARK_EDGE.start ? "start" : null
              ].filter(Boolean).join(" "),
              style: {
                left: `${at}%`,
                ...typeof m.anchor === "number" && m.anchor >= 0 && m.anchor <= 1 ? { transform: `translateX(${-m.anchor * 100}%)` } : {}
              },
              onClick: () => onMarkClick(m.value),
              children: m.label
            },
            m.value
          );
        }) }),
        stopLabels && /* @__PURE__ */ jsx37("div", { className: "tl-slider-tick-lbls", "aria-hidden": "true", children: stopLabels.map((l, n) => /* @__PURE__ */ jsx37(
          "span",
          {
            className: stops[n] === live[0] ? "on" : void 0,
            style: { left: `${pct3(stops[n])}%` },
            children: l
          },
          l
        )) })
      ]
    }
  );
}
var Slider = SliderInner;

// src/Inputs/HourTrack.tsx
import React33 from "react";
import styled38 from "styled-components";
import {
  SELECTED_RAMP as SELECTED_RAMP9,
  accent as accent11,
  focus as focus10,
  fontMono as fontMono11,
  ink as ink30,
  line as line14,
  motion as motion21,
  radius as radius23,
  rgba as rgba27,
  shadow as shadow12,
  support as support11,
  surface as surface19,
  typeScale as typeScale11,
  weight as weight13
} from "@repo/design-tokens";

// src/Inputs/hourRanges.ts
var HOUR_TRACK = { step: 15, midnight: 1440, end: 1800, bigStep: 60 };
var LAST_MINUTE = HOUR_TRACK.midnight - 1;
var LATEST_GRID_OPEN = HOUR_TRACK.midnight - HOUR_TRACK.step;
var LONGEST = HOUR_TRACK.midnight - HOUR_TRACK.step;
var isWholeMinute = (n) => typeof n === "number" && Number.isInteger(n);
var toGrid = (minutes) => Math.round(minutes / HOUR_TRACK.step) * HOUR_TRACK.step;
var clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
function formatHourMinutes(minutes) {
  const m = (Math.round(minutes) % HOUR_TRACK.midnight + HOUR_TRACK.midnight) % HOUR_TRACK.midnight;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}
function hourValueText(minutes) {
  return minutes >= HOUR_TRACK.midnight ? `${formatHourMinutes(minutes)} the next day` : formatHourMinutes(minutes);
}
function hourRangeText(range, between = "\u2013") {
  return `${formatHourMinutes(range.start)} ${between} ${hourValueText(range.end)}`;
}
function sortHourRanges(ranges) {
  return ranges.map((r) => ({ start: r.start, end: r.end })).sort((a, b) => a.start - b.start);
}
function sameHourRanges(a, b) {
  return a.length === b.length && a.every((r, i) => r.start === b[i].start && r.end === b[i].end);
}
function rangesContentKey(ranges) {
  if (ranges === void 0 || ranges === null) return "";
  if (!Array.isArray(ranges)) return `not-array:${typeof ranges}`;
  return Array.from(
    ranges,
    (r) => r && typeof r === "object" ? `${String(r.start)}-${String(r.end)}` : typeof r
  ).join(",");
}
function drawableHourRanges(ranges) {
  if (!Array.isArray(ranges)) return [];
  return sortHourRanges(
    ranges.filter(
      (r) => !!r && typeof r === "object" && Number.isFinite(r.start) && Number.isFinite(r.end)
    ).map((r) => ({
      start: clamp(r.start, 0, HOUR_TRACK.end),
      end: clamp(r.end, 0, HOUR_TRACK.end)
    })).filter((r) => r.end > r.start)
  );
}
function validateHourRanges(ranges) {
  if (!Array.isArray(ranges)) return "the ranges are not a list";
  for (const r of ranges) {
    if (!r || typeof r !== "object") return "a range is not a start and an end";
    const { start, end } = r;
    if (!isWholeMinute(start) || !isWholeMinute(end)) {
      return "a time is not a whole number of minutes";
    }
    if (start < 0 || start >= LAST_MINUTE) {
      return "a range must open between 00:00 and 23:58";
    }
    if (end <= start) return `${formatHourMinutes(start)} closes before it opens`;
    if (end > HOUR_TRACK.end) {
      return `${formatHourMinutes(start)} closes after 06:00 the next day`;
    }
    if (end >= start + HOUR_TRACK.midnight) {
      return `${formatHourMinutes(start)} lasts 24 hours or more`;
    }
  }
  const sorted = sortHourRanges(ranges);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const next = sorted[i];
    if (prev.end > next.start) {
      return `${hourRangeText(prev)} overlaps ${hourRangeText(next)}`;
    }
  }
  return null;
}
var endLimit = (start, nextStart) => Math.min(nextStart, HOUR_TRACK.end, start + LONGEST);
function edgeBounds(ranges, index, edge) {
  const sorted = sortHourRanges(ranges);
  const r = sorted[index];
  if (!r) return { lo: 1, hi: 0 };
  const prev = sorted[index - 1];
  const next = sorted[index + 1];
  if (edge === "start") {
    return {
      // An open moved earlier must still leave the range under 24 hours long.
      lo: Math.max(prev ? prev.end : 0, r.end - LONGEST),
      hi: Math.min(r.end - HOUR_TRACK.step, LATEST_GRID_OPEN)
    };
  }
  return {
    lo: r.start + HOUR_TRACK.step,
    hi: endLimit(r.start, next ? next.start : HOUR_TRACK.end)
  };
}
function moveHourEdge(ranges, index, edge, minutes) {
  const sorted = sortHourRanges(ranges);
  const r = sorted[index];
  if (!r || !Number.isFinite(minutes)) return sorted;
  const { lo, hi } = edgeBounds(sorted, index, edge);
  if (lo > hi) return sorted;
  const current = r[edge];
  const next = clamp(toGrid(minutes), lo, hi);
  if (minutes > current && next < current || minutes < current && next > current) return sorted;
  sorted[index] = { ...r, [edge]: next };
  return sorted;
}
function parseHourText(text) {
  const t = String(text ?? "").trim();
  let hours;
  let minutes;
  let m = /^(\d{1,2})(?:[:.](\d{2}))?$/.exec(t);
  if (m) {
    hours = Number(m[1]);
    minutes = m[2] === void 0 ? 0 : Number(m[2]);
  } else {
    m = /^(\d{1,2})(\d{2})$/.exec(t);
    if (!m) return null;
    hours = Number(m[1]);
    minutes = Number(m[2]);
  }
  if (hours === 24 && minutes === 0) return HOUR_TRACK.midnight;
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}
function typeHourRange(ranges, index, openText, closeText) {
  const sorted = sortHourRanges(ranges);
  const open = parseHourText(openText);
  if (open === null) return { outcome: "unparsed", ranges: sorted, text: openText };
  const close = parseHourText(closeText);
  if (close === null) return { outcome: "unparsed", ranges: sorted, text: closeText };
  if (!sorted[index]) return { outcome: "adjusted", ranges: sorted };
  const typedEnd = close <= open ? close + HOUR_TRACK.midnight : close;
  const prevEnd = index > 0 ? sorted[index - 1].end : 0;
  const nextStart = index + 1 < sorted.length ? sorted[index + 1].start : HOUR_TRACK.end;
  const startHi = Math.min(LATEST_GRID_OPEN, nextStart - HOUR_TRACK.step);
  if (prevEnd > startHi) return { outcome: "adjusted", ranges: sorted };
  const start = clamp(toGrid(open), prevEnd, startHi);
  const endLo = start + HOUR_TRACK.step;
  const endHi = endLimit(start, nextStart);
  if (endLo > endHi) return { outcome: "adjusted", ranges: sorted };
  const end = clamp(toGrid(typedEnd), endLo, endHi);
  sorted[index] = { start, end };
  return {
    outcome: start === open && end === typedEnd ? "exact" : "adjusted",
    ranges: sorted
  };
}

// src/Inputs/HourTrack.tsx
import { jsx as jsx38, jsxs as jsxs31 } from "react/jsx-runtime";
var pct = (minutes) => minutes / HOUR_TRACK.end * 100;
var AQUA_GLOW = SELECTED_RAMP9[6];
var WHITE3 = surface19[0];
var HUES = {
  // Frame 06: `--tl-ramp-primary-06` → `--tl-ramp-primary-04`.
  aqua: {
    from: SELECTED_RAMP9[5],
    to: SELECTED_RAMP9[3],
    glow: rgba27(AQUA_GLOW, 0.3),
    ring: rgba27(AQUA_GLOW, 0.18)
  },
  // Frame 07: violet at .55 → lavender at .85. The ring is not drawn on lavender; violet is a
  // recorded call (spec Human Action 2).
  lavender: {
    from: rgba27(accent11.violet, 0.55),
    to: rgba27(support11.lavender, 0.85),
    glow: rgba27(support11.lavender, 0.6),
    ring: rgba27(accent11.violet, 0.18)
  }
};
var INK_GLASS = shadow12.inkGlassRgb;
var AXIS_TYPE = "10.5px/14px";
var summarise2 = (ranges, allDay) => {
  if (allDay) return "Open 24 hours";
  if (ranges.length === 0) return "Closed";
  return ranges.map((r) => hourRangeText(r, "to")).join("; ");
};
var Root7 = styled38.div`
  --bar-from: ${({ $hue }) => HUES[$hue].from};
  --bar-to: ${({ $hue }) => HUES[$hue].to};
  --bar-glow: ${({ $hue }) => HUES[$hue].glow};
  --handle-ring: ${({ $hue }) => HUES[$hue].ring};

  position: relative;
  min-width: 0;

  &.is-disabled {
    opacity: 0.5;
  }

  /* Frame 06's line: 26 px, surface-1 → surface-3, an inset shade and a white hairline. */
  .tl-hour-track-line {
    position: relative;
    height: 26px;
    border-radius: ${radius23.pill};
    background: linear-gradient(90deg, ${surface19[1]} 0%, ${surface19[3]} 100%);
    box-shadow:
      inset 0 1px 2px rgba(${INK_GLASS}, 0.06),
      0 0 0 1px ${rgba27(WHITE3, 0.7)};
    touch-action: none;
    cursor: pointer;
  }
  &.is-disabled .tl-hour-track-line {
    cursor: default;
  }

  /* The run-on zone: midnight to 06:00 the next day, hatched behind a dashed midnight line. */
  .tl-hour-track-runon {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    border-radius: 0 ${radius23.pill} ${radius23.pill} 0;
    background: repeating-linear-gradient(135deg, ${rgba27(line14[2], 0.9)} 0 4px, transparent 4px 8px);
    border-left: 1.5px dashed ${line14[1]};
    pointer-events: none;
  }

  .tl-hour-track-bar {
    position: absolute;
    top: 3px;
    bottom: 3px;
    border-radius: ${radius23.pill};
    background: linear-gradient(90deg, var(--bar-from) 0%, var(--bar-to) 100%);
    box-shadow:
      inset 0 1px 0 ${rgba27(WHITE3, 0.55)},
      0 0 8px 1px var(--bar-glow);
    pointer-events: none;
  }

  /* Frame 06's handle: an 18 px white disc with a 4 px hue ring. */
  .tl-hour-track-handle {
    position: absolute;
    top: 50%;
    width: 18px;
    height: 18px;
    margin: -9px 0 0 -9px;
    border-radius: ${radius23.pill};
    background: ${WHITE3};
    box-shadow:
      0 0 0 4px var(--handle-ring),
      0 1px 3px rgba(${INK_GLASS}, 0.3);
    transition: opacity ${motion21.durFast} ${motion21.easeUi};
    z-index: 1;
    outline: none;
  }
  &.is-inactive .tl-hour-track-handle {
    opacity: 0;
  }
  .tl-hour-track-handle:focus-visible {
    opacity: 1;
    box-shadow:
      ${focus10.ring},
      0 1px 3px rgba(${INK_GLASS}, 0.3);
  }

  /* The time over the handle being moved: mono semibold captionCompact on ink, 30 px above the bar
     (frame 06). The frame's corner is the design's 6 px radius-md; the repo's radius.md is 8 px
     until the re-valued scale is taken, as for Slider's label. */
  .tl-hour-track-bubble {
    position: absolute;
    left: 50%;
    bottom: calc(100% + 11px);
    transform: translateX(-50%);
    padding: 3px 7px;
    border-radius: ${radius23.md};
    background: ${ink30[1]};
    color: ${WHITE3};
    font: ${weight13.semibold} ${typeScale11.captionCompact.fontSize} /
      ${typeScale11.captionCompact.lineHeight} ${fontMono11};
    white-space: nowrap;
    pointer-events: none;
    display: none;
  }
  .tl-hour-track-handle.is-drag .tl-hour-track-bubble,
  .tl-hour-track-handle:focus-visible .tl-hour-track-bubble {
    display: block;
  }

  .tl-hour-track-summary {
    ${visuallyHiddenCss}
  }
`;
var handlesOf = (ranges) => ranges.flatMap((r, i) => [
  { range: i, edge: "start", value: r.start },
  { range: i, edge: "end", value: r.end }
]);
function HourTrack({
  label,
  ranges,
  allDay = false,
  active = true,
  onActivate,
  hue = "aqua",
  disabled = false,
  onChange,
  onCommit,
  id,
  className
}) {
  const autoId = React33.useId();
  const baseId = id ?? autoId;
  const summaryId = `${baseId}-summary`;
  const safeHue = hue === "lavender" ? "lavender" : "aqua";
  const contentKey = rangesContentKey(ranges);
  const problem = allDay ? null : validateHourRanges(ranges);
  const valid = problem === null;
  const editable = valid && !allDay && !disabled;
  React33.useEffect(() => {
    if (problem) {
      warnDev("HourTrack", `"${label}": ${problem}; the hours are drawn but cannot be edited.`);
    }
  }, [contentKey, problem]);
  const fromProps = () => valid && !allDay ? sortHourRanges(ranges) : [];
  const propsKey = `${contentKey}|${valid}|${allDay}`;
  const propsRef = React33.useRef({ key: propsKey, read: fromProps });
  propsRef.current = { key: propsKey, read: fromProps };
  const [live, setLive] = React33.useState(fromProps);
  const liveRef = React33.useRef(live);
  liveRef.current = live;
  const syncedKey = React33.useRef(propsKey);
  const activeInteraction = React33.useRef(null);
  const pending = React33.useRef(null);
  const dragRef = React33.useRef(null);
  const [dragHandle, setDragHandle] = React33.useState(null);
  const lineRef = React33.useRef(null);
  const handleRefs = React33.useRef([]);
  const adoptProps = () => {
    const next = propsRef.current.read();
    syncedKey.current = propsRef.current.key;
    liveRef.current = next;
    setLive(next);
  };
  React33.useEffect(() => {
    if (!activeInteraction.current && !pending.current && syncedKey.current !== propsKey) {
      adoptProps();
    }
  }, [propsKey]);
  React33.useEffect(() => {
    if ((!editable || !active) && (activeInteraction.current || pending.current)) abandon();
  }, [editable, active]);
  const shown = allDay ? [{ start: 0, end: HOUR_TRACK.midnight }] : valid ? live : drawableHourRanges(ranges);
  const handles = valid && !allDay ? handlesOf(live) : [];
  const setHandle = (k, minutes) => {
    const cur = liveRef.current;
    const h = handlesOf(cur)[k];
    if (!h) return;
    const next = moveHourEdge(cur, h.range, h.edge, minutes);
    if (sameHourRanges(next, cur)) return;
    liveRef.current = next;
    setLive(next);
    onChange?.(next);
  };
  const begin = (kind, pointerId) => {
    if (syncedKey.current !== propsRef.current.key) adoptProps();
    activeInteraction.current = { kind, start: liveRef.current, pointerId };
  };
  const settle = () => {
    if (!activeInteraction.current && !pending.current && syncedKey.current !== propsRef.current.key) {
      adoptProps();
    }
  };
  const finish = () => {
    const was = activeInteraction.current;
    activeInteraction.current = null;
    const now = liveRef.current;
    if (was && !sameHourRanges(was.start, now)) onCommit?.(now);
    settle();
  };
  const endDragState = () => {
    dragRef.current = null;
    setDragHandle(null);
  };
  const abandon = () => {
    activeInteraction.current = null;
    pending.current = null;
    endDragState();
    adoptProps();
  };
  const ownsDrag = (e) => activeInteraction.current?.kind === "drag" && activeInteraction.current.pointerId === e.pointerId;
  const minutesAtX = (clientX) => {
    const r = lineRef.current?.getBoundingClientRect();
    if (!r || r.width <= 0) return 0;
    return (clientX - r.left) / r.width * HOUR_TRACK.end;
  };
  const nearestHandle = (v) => {
    const hs = handlesOf(liveRef.current);
    let best = [];
    let bestDist = Infinity;
    hs.forEach((h, k) => {
      const d = Math.abs(h.value - v);
      if (d < bestDist) {
        bestDist = d;
        best = [k];
      } else if (d === bestDist) best.push(k);
    });
    if (best.length > 1) {
      const holding = best.find((k) => {
        const r = liveRef.current[hs[k].range];
        return v > r.start && v < r.end;
      });
      if (holding !== void 0) return holding;
    }
    return best[best.length - 1] ?? -1;
  };
  const startDrag = (press) => {
    pending.current = null;
    if (!editable || !active) return false;
    begin("drag", press.pointerId);
    const k = nearestHandle(press.minutes);
    if (k < 0) {
      activeInteraction.current = null;
      return false;
    }
    dragRef.current = k;
    setDragHandle(k);
    handleRefs.current[k]?.focus({ preventScroll: true });
    setHandle(k, press.minutes);
    return true;
  };
  const onTrackPointerDown = (e) => {
    if (disabled || e.button !== 0) return;
    if (!active) {
      onActivate?.();
      return;
    }
    if (!editable) {
      onActivate?.();
      return;
    }
    if (activeInteraction.current?.kind === "drag" || pending.current) return;
    const v = minutesAtX(e.clientX);
    const k = nearestHandle(v);
    if (k < 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    handleRefs.current[k]?.focus({ preventScroll: true });
    if (activeInteraction.current?.kind === "key") finish();
    pending.current = { pointerId: e.pointerId, minutes: v };
  };
  const onTrackPointerMove = (e) => {
    const press = pending.current;
    if (press && press.pointerId === e.pointerId) {
      if (e.pointerType === "mouse" && (e.buttons & 1) === 0) {
        pending.current = null;
        settle();
        return;
      }
      if (!startDrag(press)) return;
    }
    if (dragRef.current === null || !ownsDrag(e)) return;
    if (!editable || !active) {
      abandon();
      return;
    }
    if (e.pointerType === "mouse" && (e.buttons & 1) === 0) {
      endDragState();
      finish();
      return;
    }
    setHandle(dragRef.current, minutesAtX(e.clientX));
  };
  const onTrackPointerUp = (e) => {
    const press = pending.current;
    if (press && press.pointerId === e.pointerId && !startDrag(press)) return;
    if (!ownsDrag(e)) return;
    if (!editable || !active) {
      abandon();
      return;
    }
    endDragState();
    finish();
  };
  const onTrackPointerCancel = (e) => {
    if (pending.current?.pointerId === e.pointerId) {
      pending.current = null;
      settle();
      return;
    }
    if (!ownsDrag(e)) return;
    if (!editable || !active) {
      abandon();
      return;
    }
    endDragState();
    finish();
  };
  const onHandleKeyDown = (k) => (e) => {
    if (!editable || !active) return;
    const h = handlesOf(liveRef.current)[k];
    if (!h) return;
    const step = e.shiftKey ? HOUR_TRACK.bigStep : HOUR_TRACK.step;
    let v;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowUp":
        v = h.value + step;
        break;
      case "ArrowLeft":
      case "ArrowDown":
        v = h.value - step;
        break;
      // Ask for a point past the bound, so the clamp — not the 15-minute rounding — decides where
      // it stops, and an off-grid neighbour (a 10:10 close) is met exactly (Copilot, PR #2090).
      case "Home":
        v = edgeBounds(liveRef.current, h.range, h.edge).lo - HOUR_TRACK.end;
        break;
      case "End":
        v = edgeBounds(liveRef.current, h.range, h.edge).hi + HOUR_TRACK.end;
        break;
      default:
        return;
    }
    e.preventDefault();
    if (activeInteraction.current === null) begin("key");
    if (activeInteraction.current?.kind !== "key") return;
    setHandle(k, v);
  };
  const onHandleKeyUp = () => {
    if (activeInteraction.current?.kind !== "key") return;
    if (!editable || !active) abandon();
    else finish();
  };
  const onHandleFocus = () => {
    if (!active && !disabled) onActivate?.();
  };
  const many = live.length > 1;
  const classes = [
    "tl-hour-track",
    `hue-${safeHue}`,
    active ? "is-active" : "is-inactive",
    disabled ? "is-disabled" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs31(
    Root7,
    {
      id,
      className: classes,
      $hue: safeHue,
      role: "group",
      "aria-label": label,
      "aria-describedby": summaryId,
      children: [
        /* @__PURE__ */ jsxs31(
          "div",
          {
            ref: lineRef,
            className: "tl-hour-track-line",
            onPointerDown: onTrackPointerDown,
            onPointerMove: onTrackPointerMove,
            onPointerUp: onTrackPointerUp,
            onPointerCancel: onTrackPointerCancel,
            children: [
              /* @__PURE__ */ jsx38(
                "div",
                {
                  className: "tl-hour-track-runon",
                  style: { left: `${pct(HOUR_TRACK.midnight)}%` },
                  "aria-hidden": "true"
                }
              ),
              shown.map((r, i) => (
                // Keyed by position: a rule-breaking set can repeat a range, and the bars hold no state.
                /* @__PURE__ */ jsx38(
                  "div",
                  {
                    className: "tl-hour-track-bar",
                    style: { left: `${pct(r.start)}%`, width: `${pct(r.end - r.start)}%` },
                    "aria-hidden": "true"
                  },
                  i
                )
              )),
              handles.map((h, k) => {
                const b = edgeBounds(live, h.range, h.edge);
                const [lo, hi] = b.lo > b.hi ? [h.value, h.value] : [Math.min(b.lo, h.value), Math.max(b.hi, h.value)];
                return /* @__PURE__ */ jsx38(
                  "div",
                  {
                    ref: (el) => {
                      handleRefs.current[k] = el;
                    },
                    className: `tl-hour-track-handle${dragHandle === k ? " is-drag" : ""}`,
                    style: { left: `${pct(h.value)}%` },
                    role: "slider",
                    tabIndex: 0,
                    "aria-label": `${h.edge === "start" ? "Opens" : "Closes"}${many ? `, range ${h.range + 1}` : ""}`,
                    "aria-valuemin": lo,
                    "aria-valuemax": hi,
                    "aria-valuenow": h.value,
                    "aria-valuetext": hourValueText(h.value),
                    "aria-disabled": disabled || void 0,
                    onKeyDown: onHandleKeyDown(k),
                    onKeyUp: onHandleKeyUp,
                    onBlur: onHandleKeyUp,
                    onFocus: onHandleFocus,
                    children: /* @__PURE__ */ jsx38("span", { className: "tl-hour-track-bubble", "aria-hidden": "true", children: formatHourMinutes(h.value) })
                  },
                  `${h.range}-${h.edge}`
                );
              })
            ]
          }
        ),
        /* @__PURE__ */ jsx38("span", { id: summaryId, className: "tl-hour-track-summary", children: summarise2(allDay ? [] : shown, allDay) })
      ]
    }
  );
}
var AXIS = [
  { at: 0, text: "00" },
  { at: 360, text: "06" },
  { at: 720, text: "12" },
  { at: 1080, text: "18" },
  { at: HOUR_TRACK.midnight, text: "24" },
  { at: 1680, text: "next day" }
];
var AxisRoot = styled38.div`
  position: relative;
  height: 16px;
  min-width: 0;

  span {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    font: ${weight13.medium} ${AXIS_TYPE} ${fontMono11};
    color: ${ink30[3]};
    white-space: nowrap;
  }
  span.is-next-day {
    color: ${ink30[4]};
  }
`;
function HourTrackAxis({ className }) {
  return /* @__PURE__ */ jsx38(
    AxisRoot,
    {
      className: ["tl-hour-track-axis", className ?? ""].filter(Boolean).join(" "),
      "aria-hidden": "true",
      children: AXIS.map((a) => /* @__PURE__ */ jsx38(
        "span",
        {
          className: a.at > HOUR_TRACK.midnight ? "is-next-day" : void 0,
          style: { left: `${pct(a.at)}%` },
          children: a.text
        },
        a.text
      ))
    }
  );
}

// src/Inputs/HourRangeField.tsx
import React34 from "react";
import styled39 from "styled-components";
import {
  focus as focus11,
  fontMono as fontMono12,
  fontSans as fontSans25,
  ink as ink31,
  motion as motion22,
  radius as radius24,
  surface as surface20,
  typeScale as typeScale12,
  weight as weight14
} from "@repo/design-tokens";
import { jsx as jsx39, jsxs as jsxs32 } from "react/jsx-runtime";
var READOUT_SIZE = "12.5px";
var READOUT_TYPE = `${READOUT_SIZE}/17px`;
var readout = (r) => `${formatHourMinutes(r.start)} \u2013 ${formatHourMinutes(r.end)}`;
var Root8 = styled39.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  max-width: 100%;

  .tl-hour-range-text,
  .tl-hour-range-button {
    font: ${weight14.medium} ${READOUT_TYPE} ${fontMono12};
    color: ${ink31[1]};
    white-space: nowrap;
  }
  &.size-sm .tl-hour-range-text,
  &.size-sm .tl-hour-range-button {
    font: ${weight14.medium} ${typeScale12.caption2.fontSize} / ${typeScale12.caption2.lineHeight}
      ${fontSans25};
  }
  &.size-sm .tl-hour-range-button {
    padding: 0 6px;
  }
  /* A sub-line field opens upward, over the line above it, so its boxes stay inside the row. */
  &.size-sm .tl-hour-range-edit {
    top: auto;
    bottom: 0;
    transform: none;
  }
  &.is-editing .tl-hour-range-button {
    visibility: hidden;
  }
  .tl-hour-range-button {
    border: 0;
    margin: 0;
    padding: 2px 6px;
    margin-right: -6px;
    border-radius: ${radius24.control};
    background: transparent;
    cursor: pointer;
    transition: background-color ${motion22.durFast} ${motion22.easeUi};
  }
  .tl-hour-range-button:hover {
    background: ${surface20.hover};
  }
  .tl-hour-range-button:focus-visible {
    outline: none;
    box-shadow: ${focus11.tight};
  }
  .tl-hour-range-button[aria-disabled='true'] {
    cursor: default;
    opacity: 0.5;
  }
  .tl-hour-range-button[aria-disabled='true']:hover {
    background: transparent;
  }

  /* The boxes float over the readout, so opening the field never changes its size. */
  .tl-hour-range-edit {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .tl-hour-range-dash {
    font: ${weight14.medium} ${READOUT_TYPE} ${fontMono12};
    color: ${ink31[3]};
  }

  /* Card 22's number field (\`.tl-slider-num\`, on solid): 28 px, 1.5 px line-1, the control
     radius, and the cyan focus border and wash — in the readout's mono, 60 px for five characters. */
  .tl-hour-range-box {
    ${numberFieldChassisCss}
    width: 60px;
    font: ${weight14.medium} ${READOUT_SIZE} / 1 ${fontMono12};
    color: ${ink31[1]};
    text-align: center;
    outline: 0;
  }
  .tl-hour-range-box:focus {
    ${numberFieldFocusCss}
  }

  .tl-hour-range-live {
    ${visuallyHiddenCss}
  }
`;
function HourRangeField({
  label,
  ranges,
  index,
  size = "md",
  disabled = false,
  onCommit,
  className
}) {
  const contentKey = rangesContentKey(ranges);
  const problem = validateHourRanges(ranges);
  const sorted = problem === null ? sortHourRanges(ranges) : null;
  const range = sorted?.[index];
  const warning = problem !== null ? `"${label}": ${problem}; the times are shown but cannot be edited.` : !range ? `"${label}": there is no range ${index + 1}.` : null;
  React34.useEffect(() => {
    if (warning) warnDev("HourRangeField", warning);
  }, [contentKey, index, warning]);
  const [editing, setEditing] = React34.useState(false);
  const [openText, setOpenText] = React34.useState("");
  const [closeText, setCloseText] = React34.useState("");
  const [message, setMessage] = React34.useState("");
  const editRef = React34.useRef(null);
  const openRef = React34.useRef(null);
  const buttonRef = React34.useRef(null);
  const focusButtonNext = React34.useRef(false);
  const done = React34.useRef(false);
  React34.useEffect(() => {
    if (editing) {
      openRef.current?.focus();
      openRef.current?.select();
    } else if (focusButtonNext.current) {
      focusButtonNext.current = false;
      buttonRef.current?.focus();
    }
  }, [editing]);
  const classes = ["tl-hour-range-field", `size-${size === "sm" ? "sm" : "md"}`, className ?? ""].filter(Boolean).join(" ");
  if (!sorted || !range) {
    const drawn = drawableHourRanges(ranges)[index];
    return /* @__PURE__ */ jsx39(Root8, { className: classes, children: drawn ? /* @__PURE__ */ jsx39("span", { className: "tl-hour-range-text", children: readout(drawn) }) : null });
  }
  const which = sorted.length > 1 ? `, range ${index + 1}` : "";
  const open = () => {
    if (disabled) return;
    done.current = false;
    setOpenText(formatHourMinutes(range.start));
    setCloseText(formatHourMinutes(range.end));
    setMessage("");
    setEditing(true);
  };
  const close = (returnFocus) => {
    done.current = true;
    focusButtonNext.current = returnFocus;
    setEditing(false);
  };
  const commit = (returnFocus) => {
    if (done.current) return;
    if (disabled) {
      close(returnFocus);
      return;
    }
    if (openText.trim() === formatHourMinutes(range.start) && closeText.trim() === formatHourMinutes(range.end)) {
      setMessage("");
      close(returnFocus);
      return;
    }
    const result = typeHourRange(sorted, index, openText, closeText);
    if (result.outcome === "unparsed") {
      setMessage(`Kept ${hourRangeText(range)}: "${result.text.trim()}" is not a time.`);
    } else {
      setMessage(
        result.outcome === "adjusted" ? `Set to ${hourRangeText(result.ranges[index])}, the nearest these hours allow.` : ""
      );
      if (!sameHourRanges(result.ranges, sorted)) onCommit?.(result.ranges);
    }
    close(returnFocus);
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit(true);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    }
  };
  const onBlur = (e) => {
    if (editRef.current && e.relatedTarget && editRef.current.contains(e.relatedTarget)) {
      return;
    }
    commit(false);
  };
  return /* @__PURE__ */ jsxs32(Root8, { className: editing ? `${classes} is-editing` : classes, children: [
    /* @__PURE__ */ jsx39(
      "button",
      {
        ref: buttonRef,
        type: "button",
        className: "tl-hour-range-button",
        "aria-label": `${label} hours${which}, ${hourRangeText(range, "to")}, edit`,
        "aria-disabled": disabled || void 0,
        "aria-hidden": editing || void 0,
        tabIndex: editing ? -1 : void 0,
        onClick: open,
        children: readout(range)
      }
    ),
    editing ? /* @__PURE__ */ jsxs32("span", { className: "tl-hour-range-edit", ref: editRef, onBlur, children: [
      /* @__PURE__ */ jsx39(
        "input",
        {
          ref: openRef,
          className: "tl-hour-range-box",
          type: "text",
          inputMode: "numeric",
          autoComplete: "off",
          "aria-label": `${label} opens${which}`,
          value: openText,
          onChange: (e) => setOpenText(e.target.value),
          onKeyDown
        }
      ),
      /* @__PURE__ */ jsx39("span", { className: "tl-hour-range-dash", "aria-hidden": "true", children: "\u2013" }),
      /* @__PURE__ */ jsx39(
        "input",
        {
          className: "tl-hour-range-box",
          type: "text",
          inputMode: "numeric",
          autoComplete: "off",
          "aria-label": `${label} closes${which}`,
          value: closeText,
          onChange: (e) => setCloseText(e.target.value),
          onKeyDown
        }
      )
    ] }) : null,
    /* @__PURE__ */ jsx39("span", { className: "tl-hour-range-live", role: "status", "aria-live": "polite", children: message })
  ] });
}

// src/Inputs/MiniWeek.tsx
import styled40 from "styled-components";
import { SELECTED_RAMP as SELECTED_RAMP10, radius as radius25, rgba as rgba28, surface as surface21 } from "@repo/design-tokens";
import { jsx as jsx40 } from "react/jsx-runtime";
var MINI_WEEK_CELL_HEIGHT = "22px";
var MINI_WEEK_GAP = "3px";
var Root9 = styled40.span`
  display: flex;
  align-self: stretch;
  gap: ${MINI_WEEK_GAP};
`;
var Cell = styled40.span`
  position: relative;
  flex: 1;
  height: ${MINI_WEEK_CELL_HEIGHT};
  overflow: hidden;
  border-radius: ${radius25.sm};
  /* Card 06: rgba(237,243,250,.9) — the panel tint at .9. */
  background: ${rgba28(surface21[2], 0.9)};
`;
var Band = styled40.span`
  position: absolute;
  left: 0;
  right: 0;
  /* Card 06: --tl-ramp-primary-06 → --tl-ramp-primary-04, top to bottom. */
  background: linear-gradient(180deg, ${SELECTED_RAMP10[5]}, ${SELECTED_RAMP10[3]});
`;
var pct2 = (minutes) => `${minutes / HOUR_TRACK.midnight * 100}%`;
var MiniWeek = ({ days, className }) => {
  if (days.length !== 7) warnDev("MiniWeek", `expects 7 days, Monday first; got ${days.length}.`);
  return /* @__PURE__ */ jsx40(Root9, { className, "aria-hidden": "true", "data-mini-week": "", children: days.map((day, i) => /* @__PURE__ */ jsx40(Cell, { "data-mini-day": "", children: (day.allDay ? [{ start: 0, end: HOUR_TRACK.midnight }] : day.ranges).map((r) => ({
    start: Math.max(0, r.start),
    end: Math.min(HOUR_TRACK.midnight, r.end)
  })).filter((r) => r.end > r.start).map((r) => /* @__PURE__ */ jsx40(
    Band,
    {
      "data-mini-band": "",
      style: { top: pct2(r.start), height: pct2(r.end - r.start) }
    },
    `${r.start}-${r.end}`
  )) }, i)) });
};

// src/Inputs/MonthCalendar.tsx
import React35 from "react";
import styled41 from "styled-components";
import {
  brand as brand27,
  focus as focus12,
  fontSans as fontSans26,
  ink as ink32,
  line as line15,
  motion as motion23,
  radius as radius26,
  rgba as rgba29,
  shadow as shadow13,
  surface as surface22,
  typeScale as typeScale13,
  weight as weight15
} from "@repo/design-tokens";

// src/Inputs/calendarDates.ts
var MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
var MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var WEEKDAYS_LONG = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];
var DAY_MS = 864e5;
var DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
var MONTH_RE = /^(\d{4})-(\d{2})$/;
var pad2 = (n) => String(n).padStart(2, "0");
var FIRST_YEAR = 1001;
var LAST_YEAR = 9998;
var daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
var makeDate = (y, m, d) => {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (y < FIRST_YEAR || y > LAST_YEAR || m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m))
    return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
};
var partsOf = (date) => {
  const match = DATE_RE.exec(date);
  if (match == null) throw new RangeError(`not a YYYY-MM-DD date: ${String(date)}`);
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
};
var msOf = (date) => {
  const { y, m, d } = partsOf(date);
  return Date.UTC(y, m - 1, d);
};
var fromMs = (ms) => {
  const at = new Date(ms);
  const year = String(at.getUTCFullYear()).padStart(4, "0");
  return `${year}-${pad2(at.getUTCMonth() + 1)}-${pad2(at.getUTCDate())}`;
};
function isCalendarDate(text) {
  if (typeof text !== "string") return false;
  const match = DATE_RE.exec(text);
  return match != null && makeDate(Number(match[1]), Number(match[2]), Number(match[3])) === text;
}
function isCalendarMonth(text) {
  if (typeof text !== "string") return false;
  const match = MONTH_RE.exec(text);
  if (match == null) return false;
  const y = Number(match[1]);
  const m = Number(match[2]);
  return y >= FIRST_YEAR && y <= LAST_YEAR && m >= 1 && m <= 12;
}
function formatCalendarDate(date) {
  if (!isCalendarDate(date)) return "";
  const { y, m, d } = partsOf(date);
  return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
}
function lastDayOf(month) {
  const [y, m] = month.split("-").map(Number);
  return `${month}-${pad2(daysInMonth(y, m))}`;
}
function formatCalendarMonth(month) {
  if (!isCalendarMonth(month)) return "";
  const [y, m] = month.split("-").map(Number);
  return `${MONTHS_LONG[m - 1]} ${y}`;
}
function formatCalendarDateLong(date) {
  if (!isCalendarDate(date)) return "";
  const { y, m, d } = partsOf(date);
  return `${WEEKDAYS_LONG[weekdayIndex(date)]} ${d} ${MONTHS_LONG[m - 1]} ${y}`;
}
function weekdayIndex(date) {
  return (new Date(msOf(date)).getUTCDay() + 6) % 7;
}
var monthOf = (date) => date.slice(0, 7);
function addCalendarDays(date, n) {
  return fromMs(msOf(date) + n * DAY_MS);
}
function addCalendarMonths(date, n) {
  const { y, m, d } = partsOf(date);
  const index = y * 12 + (m - 1) + n;
  const ny = Math.floor(index / 12);
  const nm = index % 12 + 1;
  return makeDate(ny, nm, Math.min(d, daysInMonth(ny, nm))) ?? date;
}
function addMonths(month, n) {
  return monthOf(addCalendarMonths(`${month}-01`, n));
}
function calendarDaysInclusive(from, to) {
  return Math.round((msOf(to) - msOf(from)) / DAY_MS) + 1;
}
function monthWeeks(month) {
  const first = `${month}-01`;
  const lead = (new Date(msOf(first)).getUTCDay() + 6) % 7;
  const start = addCalendarDays(first, -lead);
  return Array.from(
    { length: 6 },
    (_, week) => Array.from({ length: 7 }, (_2, day) => addCalendarDays(start, week * 7 + day))
  );
}
function readLimits(min, max) {
  const lo = isCalendarDate(min) ? min : null;
  const hi = isCalendarDate(max) ? max : null;
  return lo != null && hi != null && lo > hi ? { min: null, max: null } : { min: lo, max: hi };
}
function readRange(from, to) {
  const first = isCalendarDate(from) ? from : null;
  const last = isCalendarDate(to) ? to : null;
  return first != null && last != null && last < first ? { from: null, to: null } : { from: first, to: last };
}
function clampCalendarDate(date, min, max) {
  if (min != null && date < min) return min;
  if (max != null && date > max) return max;
  return date;
}
function todayLocal() {
  const now = /* @__PURE__ */ new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}
var monthFromWord = (word) => {
  if (word === "sept") return 9;
  if (word.length < 3) return null;
  const index = MONTHS_LONG.findIndex((name) => name.toLowerCase().startsWith(word));
  return index === -1 ? null : index + 1;
};
var SEP = "[\\s,./-]";
var WORD_RE = new RegExp(
  `^(\\d{1,2})(?:st|nd|rd|th)?${SEP}*([a-z]+)\\.?(?:${SEP}+(\\d{4}|\\d{2}))?$`
);
var NUMERIC_RE = new RegExp(`^(\\d{1,2})${SEP}(\\d{1,2})(?:${SEP}(\\d{4}|\\d{2}))?$`);
var ISO_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
function parseCalendarDateText(text, reference) {
  const t = text.trim().toLowerCase();
  if (t === "") return null;
  const iso = ISO_RE.exec(t);
  if (iso) return makeDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  let day;
  let month;
  let yearText;
  const word = WORD_RE.exec(t);
  const numeric = word ? null : NUMERIC_RE.exec(t);
  if (word) {
    day = Number(word[1]);
    month = monthFromWord(word[2]);
    yearText = word[3];
  } else if (numeric) {
    day = Number(numeric[1]);
    month = Number(numeric[2]);
    yearText = numeric[3];
  } else {
    return null;
  }
  if (month == null) return null;
  if (yearText != null) {
    const year = yearText.length === 2 ? 2e3 + Number(yearText) : Number(yearText);
    return makeDate(year, month, day);
  }
  const ref = isCalendarDate(reference) ? reference : todayLocal();
  const refYear = partsOf(ref).y;
  for (let year = refYear; year <= refYear + 8; year += 1) {
    const candidate = makeDate(year, month, day);
    if (candidate != null && candidate >= ref) return candidate;
  }
  return null;
}
function pickRange(value, edge, date) {
  if (edge === "first") {
    const to = value.to != null && value.to < date ? null : value.to;
    return { value: { from: date, to }, edge: "last" };
  }
  if (value.from == null || date < value.from) {
    return { value: { from: date, to: null }, edge: "last" };
  }
  return { value: { from: value.from, to: date }, edge: "first" };
}

// src/Inputs/MonthCalendar.tsx
import { jsx as jsx41, jsxs as jsxs33 } from "react/jsx-runtime";
var MODES = { range: "range", single: "single" };
var STATE_NAME = {
  first: "first day",
  last: "last day",
  both: "first and last day",
  chosen: "chosen",
  "in-range": "in range"
};
var ENDS = /* @__PURE__ */ new Set(["first", "last", "both", "chosen"]);
var SELECTED = /* @__PURE__ */ new Set([...ENDS, "in-range"]);
var MONTH_CALENDAR_COLOURS = {
  panel: surface22[0],
  day: ink32[1],
  weekday: ink32[4],
  muted: ink32[5],
  endFill: brand27.cyan,
  endInk: ink32.inverse,
  rangeFill: rgba29(brand27.frost, 0.95),
  hoverFill: surface22.hover
};
var C = MONTH_CALENDAR_COLOURS;
var CELL_H = "26px";
var GAP = "4px";
var PAD3 = "10px";
var DAY_FONT = `${weight15.medium} ${typeScale13.captionCompact.fontSize}/1 ${fontSans26}`;
var Root10 = styled41.div`
  box-sizing: border-box;
  width: 100%;
  padding: ${PAD3};
  background: ${C.panel};
  border: 1px solid ${line15[1]};
  border-radius: ${radius26.lg};
  box-shadow: 0 12px 30px rgba(${shadow13.inkGlassRgb}, 0.12);
  font-family: ${fontSans26};

  &.is-disabled {
    opacity: 0.5;
  }

  .tl-cal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
  }

  .tl-cal-month {
    font: ${weight15.semibold} ${typeScale13.caption1.fontSize} / 1 ${fontSans26};
    color: ${ink32[1]};
  }

  .tl-cal-nav {
    display: flex;
    gap: 4px;
  }

  .tl-cal-grid {
    display: flex;
    flex-direction: column;
    gap: ${GAP};
  }

  .tl-cal-row {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: ${GAP};
  }

  .tl-cal-dow,
  .tl-cal-day {
    height: ${CELL_H};
    display: flex;
    align-items: center;
    justify-content: center;
    font: ${DAY_FONT};
  }

  .tl-cal-dow {
    color: ${C.weekday};
  }

  .tl-cal-hidden {
    ${visuallyHiddenCss}
  }

  .tl-cal-day {
    position: relative;
    border-radius: ${radius26.md};
    color: ${C.day};
    cursor: default;
    user-select: none;
    outline: none;
    transition:
      background-color 0.15s ${motion23.easeUi},
      box-shadow 0.15s ${motion23.easeUi};
  }

  .tl-cal-day.is-pickable {
    cursor: pointer;
  }

  /* Not on an end or inside the range: the frost holds under the pointer, so the range stays legible. */
  .tl-cal-day.is-pickable:not(.is-end):not(.is-in-range):hover {
    background: ${C.hoverFill};
  }

  .tl-cal-day.is-out,
  .tl-cal-day.is-unavailable {
    color: ${C.muted};
  }

  .tl-cal-day.is-in-range {
    background: ${C.rangeFill};
    border-radius: 0;
  }

  .tl-cal-day.is-end {
    background: ${C.endFill};
    color: ${C.endInk};
    font-weight: ${weight15.semibold};
  }

  .tl-cal-day.is-first {
    border-radius: ${radius26.pill} 0 0 ${radius26.pill};
  }

  .tl-cal-day.is-last {
    border-radius: 0 ${radius26.pill} ${radius26.pill} 0;
  }

  .tl-cal-day.is-both,
  .tl-cal-day.is-chosen {
    border-radius: ${radius26.pill};
  }

  .tl-cal-day:focus-visible {
    z-index: 1;
    box-shadow: ${focus12.tight};
  }
`;
function readProps(props) {
  const problems = [];
  for (const name of ["from", "to", "min", "max"]) {
    const value = props[name];
    if (value != null && !isCalendarDate(value)) {
      problems.push(`\`${name}\` "${String(value)}" is not a YYYY-MM-DD date; it is ignored.`);
    }
  }
  let shown = props.month;
  if (!isCalendarMonth(shown)) {
    problems.push(
      `\`month\` "${String(shown)}" is not a YYYY-MM month; showing the current month.`
    );
    shown = monthOf(todayLocal());
  }
  const range = readRange(props.from, props.to);
  if (range.from == null && range.to == null && isCalendarDate(props.from) && isCalendarDate(props.to)) {
    problems.push(`\`to\` ${props.to} is before \`from\` ${props.from}; no range is drawn.`);
  }
  const limits = readLimits(props.min, props.max);
  if (limits.min == null && limits.max == null && isCalendarDate(props.min) && isCalendarDate(props.max)) {
    problems.push(`\`min\` ${props.min} is after \`max\` ${props.max}; neither limit is applied.`);
  }
  return { shown, ...range, ...limits, problems };
}
function MonthCalendar(props) {
  const { onMonthChange, onPick, disabled = false, id, className } = props;
  const mode = variant(MODES, props.mode, "range", "MonthCalendar", "mode");
  const { shown, from, to, min, max, problems } = readProps(props);
  const warned = React35.useRef(/* @__PURE__ */ new Set());
  React35.useEffect(() => {
    for (const problem of problems) {
      if (warned.current.has(problem)) continue;
      warned.current.add(problem);
      warnDev("MonthCalendar", problem);
    }
  });
  const baseId = React35.useId();
  const labelId = `${id ?? baseId}-month`;
  const gridRef = React35.useRef(null);
  const pendingFocus = React35.useRef(null);
  const withinLimits = (date) => (min == null || date >= min) && (max == null || date <= max);
  const isPickable = (date) => !disabled && monthOf(date) === shown && withinLimits(date);
  const dayState = (date) => {
    if (monthOf(date) !== shown) return "out";
    if (mode === "single") {
      if (from != null && date === from) return "chosen";
    } else if (from != null && to != null) {
      if (from <= date && date <= to) {
        if (date === from && date === to) return "both";
        if (date === from) return "first";
        if (date === to) return "last";
        return "in-range";
      }
    } else if (from != null && date === from) {
      return "first";
    } else if (to != null && date === to) {
      return "last";
    }
    return withinLimits(date) ? "plain" : "unavailable";
  };
  const firstPickable = (month) => {
    const start = `${month}-01`;
    for (let date = start; monthOf(date) === month; date = addCalendarDays(date, 1)) {
      if (withinLimits(date)) return date;
    }
    return start;
  };
  const [focused, setFocused] = React35.useState(null);
  const [seenMonth, setSeenMonth] = React35.useState(shown);
  if (seenMonth !== shown) {
    setSeenMonth(shown);
    if (pendingFocus.current == null || monthOf(pendingFocus.current) !== shown) setFocused(null);
  }
  const restingDay = from != null && monthOf(from) === shown ? from : firstPickable(shown);
  const tabStop = focused != null && monthOf(focused) === shown ? focused : restingDay;
  React35.useEffect(() => {
    const target = pendingFocus.current;
    if (target == null || target !== tabStop) return;
    pendingFocus.current = null;
    gridRef.current?.querySelector(`[data-date="${target}"]`)?.focus();
  }, [tabStop]);
  const moveTo = (at, date) => {
    if (date === at || !isCalendarDate(date)) return;
    setFocused(date);
    if (monthOf(date) === shown) {
      gridRef.current?.querySelector(`[data-date="${date}"]`)?.focus();
      return;
    }
    pendingFocus.current = date;
    onMonthChange(monthOf(date));
  };
  const onGridKeyDown = (event) => {
    if (disabled) return;
    const at = event.target.dataset?.date ?? tabStop;
    let next2 = null;
    switch (event.key) {
      case "ArrowLeft":
        next2 = addCalendarDays(at, -1);
        break;
      case "ArrowRight":
        next2 = addCalendarDays(at, 1);
        break;
      case "ArrowUp":
        next2 = addCalendarDays(at, -7);
        break;
      case "ArrowDown":
        next2 = addCalendarDays(at, 7);
        break;
      case "Home":
        next2 = addCalendarDays(at, -weekdayIndex(at));
        break;
      case "End":
        next2 = addCalendarDays(at, 6 - weekdayIndex(at));
        break;
      case "PageUp":
        next2 = addCalendarMonths(at, event.shiftKey ? -12 : -1);
        break;
      case "PageDown":
        next2 = addCalendarMonths(at, event.shiftKey ? 12 : 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (isPickable(at)) onPick(at);
        return;
      default:
        return;
    }
    event.preventDefault();
    moveTo(at, next2);
  };
  const onDayPress = (date) => {
    if (!isPickable(date)) return;
    setFocused(date);
    onPick(date);
  };
  const previous = addMonths(shown, -1);
  const next = addMonths(shown, 1);
  const canGoBack = previous !== shown && (min == null || min < `${shown}-01`);
  const canGoForward = next !== shown && (max == null || max > lastDayOf(shown));
  const goTo = (month, allowed) => {
    if (disabled || !allowed) return;
    pendingFocus.current = null;
    onMonthChange(month);
  };
  return /* @__PURE__ */ jsxs33(
    Root10,
    {
      id,
      className: ["tl-month-calendar", className, disabled ? "is-disabled" : null].filter(Boolean).join(" "),
      children: [
        /* @__PURE__ */ jsxs33("div", { className: "tl-cal-head", children: [
          /* @__PURE__ */ jsx41("span", { className: "tl-cal-month", id: labelId, "aria-live": "polite", children: formatCalendarMonth(shown) }),
          /* @__PURE__ */ jsxs33("span", { className: "tl-cal-nav", children: [
            /* @__PURE__ */ jsx41(
              Button,
              {
                variant: "secondary",
                size: "xs",
                iconOnly: true,
                "aria-label": "Previous month",
                iconLeft: /* @__PURE__ */ jsx41(Icon, { name: "chevron-left" }),
                disabled,
                "aria-disabled": !disabled && !canGoBack ? true : void 0,
                onClick: () => goTo(previous, canGoBack)
              }
            ),
            /* @__PURE__ */ jsx41(
              Button,
              {
                variant: "secondary",
                size: "xs",
                iconOnly: true,
                "aria-label": "Next month",
                iconLeft: /* @__PURE__ */ jsx41(Icon, { name: "chevron-right" }),
                disabled,
                "aria-disabled": !disabled && !canGoForward ? true : void 0,
                onClick: () => goTo(next, canGoForward)
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs33(
          "div",
          {
            ref: gridRef,
            className: "tl-cal-grid",
            role: "grid",
            "aria-labelledby": labelId,
            "aria-describedby": props["aria-describedby"],
            "aria-multiselectable": mode === "range" || void 0,
            "aria-disabled": disabled || void 0,
            onKeyDown: onGridKeyDown,
            onBlur: (event) => {
              const next2 = event.relatedTarget;
              if (next2 != null && !event.currentTarget.contains(next2)) pendingFocus.current = null;
            },
            children: [
              /* @__PURE__ */ jsx41("div", { className: "tl-cal-row", role: "row", children: WEEKDAYS_LONG.map((day) => /* @__PURE__ */ jsxs33("div", { className: "tl-cal-dow", role: "columnheader", children: [
                /* @__PURE__ */ jsx41("span", { "aria-hidden": "true", children: day[0] }),
                /* @__PURE__ */ jsx41("span", { className: "tl-cal-hidden", children: day })
              ] }, day)) }),
              monthWeeks(shown).map((week) => /* @__PURE__ */ jsx41("div", { className: "tl-cal-row", role: "row", children: week.map((date) => {
                const state = dayState(date);
                const day = Number(date.slice(8));
                if (state === "out") {
                  return /* @__PURE__ */ jsx41("div", { className: "tl-cal-day is-out", role: "gridcell", children: /* @__PURE__ */ jsx41("span", { "aria-hidden": "true", children: day }) }, date);
                }
                const pickable = isPickable(date);
                const stateName = STATE_NAME[state];
                const name = [
                  formatCalendarDateLong(date),
                  stateName,
                  withinLimits(date) ? null : "unavailable"
                ].filter(Boolean).join(", ");
                return /* @__PURE__ */ jsx41(
                  "div",
                  {
                    "data-date": date,
                    className: [
                      "tl-cal-day",
                      `is-${state}`,
                      ENDS.has(state) ? "is-end" : null,
                      pickable ? "is-pickable" : null
                    ].filter(Boolean).join(" "),
                    role: "gridcell",
                    "aria-label": name,
                    "aria-selected": SELECTED.has(state),
                    "aria-disabled": !pickable || void 0,
                    tabIndex: !disabled && date === tabStop ? 0 : -1,
                    onFocus: () => setFocused(date),
                    onClick: () => onDayPress(date),
                    children: /* @__PURE__ */ jsx41("span", { "aria-hidden": "true", children: day })
                  },
                  date
                );
              }) }, week[0]))
            ]
          }
        )
      ]
    }
  );
}

// src/Inputs/DateRangeField.tsx
import React37 from "react";
import styled43 from "styled-components";
import { fontSans as fontSans27, ink as ink33 } from "@repo/design-tokens";

// src/Inputs/dateBox.tsx
import React36 from "react";
import styled42 from "styled-components";
import { Fragment as Fragment10, jsx as jsx42, jsxs as jsxs34 } from "react/jsx-runtime";
var DATE_BOX_HINT = "Type a date such as 22 Dec 2026, or pick one in the calendar below.";
function commitTyped({
  text,
  edge,
  value,
  today,
  min,
  max
}) {
  const current = edge === "last" ? value.to : value.from;
  const kept = current != null ? formatCalendarDate(current) : "no date";
  const typed = text.trim();
  const set = (date2) => edge === "last" ? { from: value.from, to: date2 } : { from: date2, to: value.to };
  if (typed === "") {
    return current == null ? { value, said: "" } : { value: set(null), said: "" };
  }
  const reference = edge === "last" && value.from != null ? value.from : today;
  const date = parseCalendarDateText(typed, reference);
  if (date == null) return { value, said: `Kept ${kept}: "${typed}" is not a date.` };
  if (min != null && date < min) {
    return {
      value,
      said: `Kept ${kept}: ${formatCalendarDate(date)} is before the earliest date allowed, ${formatCalendarDate(min)}.`
    };
  }
  if (max != null && date > max) {
    return {
      value,
      said: `Kept ${kept}: ${formatCalendarDate(date)} is after the latest date allowed, ${formatCalendarDate(max)}.`
    };
  }
  if (date === current) return { value, said: "" };
  if (edge === "last") {
    if (value.from != null && date < value.from) {
      return {
        value,
        said: `Kept ${kept}: the last day can't be before the first day, ${formatCalendarDate(value.from)}.`
      };
    }
    return { value: set(date), said: "" };
  }
  if (edge === "first" && value.to != null && date > value.to) {
    return {
      value: { from: date, to: null },
      said: "Last day cleared: it was before the new first day."
    };
  }
  return { value: set(date), said: "" };
}
var Hidden = styled42.span`
  ${visuallyHiddenCss}
`;
function useSaid() {
  const [said, setSaid] = React36.useState({ text: "", key: 0 });
  const say = React36.useCallback(
    (text) => setSaid((prev) => ({ text, key: prev.key + 1 })),
    []
  );
  return [said, say];
}
function DateFieldVoice({
  hintId,
  said,
  edge
}) {
  return /* @__PURE__ */ jsxs34(Fragment10, { children: [
    /* @__PURE__ */ jsx42(Hidden, { id: hintId, children: DATE_BOX_HINT }),
    edge != null && /* @__PURE__ */ jsx42(Hidden, { id: edge.id, children: edge.text }),
    /* @__PURE__ */ jsx42(Hidden, { "aria-live": "polite", children: /* @__PURE__ */ jsx42("span", { children: said.text }, said.key) })
  ] });
}
function DateBox({
  date,
  onCommit,
  onFocus,
  onPointerDown,
  id,
  "aria-label": ariaLabel,
  "aria-describedby": describedBy,
  disabled = false,
  invalid = false
}) {
  const [draft, setDraftState] = React36.useState(null);
  const draftRef = React36.useRef(null);
  const setDraft = (next) => {
    draftRef.current = next;
    setDraftState(next);
  };
  const [seenDate, setSeenDate] = React36.useState(date);
  if (seenDate !== date) {
    setSeenDate(date);
    if (draftRef.current != null) setDraft(null);
  }
  const shown = date != null ? formatCalendarDate(date) : "";
  const commit = (to) => {
    const text = draftRef.current;
    if (text == null) return;
    setDraft(null);
    onCommit(text, to);
  };
  return /* @__PURE__ */ jsx42(
    Input,
    {
      id,
      size: "md",
      leadIcon: "calendar",
      autoComplete: "off",
      "aria-label": ariaLabel,
      "aria-describedby": describedBy,
      disabled,
      invalid,
      value: draft ?? shown,
      onFocus,
      onPointerDown,
      onChange: (event) => setDraft(event.target.value),
      onBlur: (event) => commit(event.relatedTarget),
      onKeyDown: (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit(null);
        } else if (event.key === "Escape" && draftRef.current != null) {
          event.preventDefault();
          event.stopPropagation();
          setDraft(null);
        }
      }
    }
  );
}

// src/Inputs/DateRangeField.tsx
import { jsx as jsx43, jsxs as jsxs35 } from "react/jsx-runtime";
var rangeSentence = (from, to) => {
  const days = calendarDaysInclusive(from, to);
  return `${formatCalendarDate(from)} to ${formatCalendarDate(to)}, ${days} ${days === 1 ? "day" : "days"}.`;
};
var startingEdge = (value) => value.from != null && value.to == null ? "last" : "first";
var EDGE_TEXT = {
  first: "Next pick sets the first day.",
  last: "Next pick sets the last day."
};
var sameRange = (a, b) => a.from === b.from && a.to === b.to;
var Root11 = styled43.div`
  display: flex;
  flex-direction: column;
  gap: ${FIELD_LABEL.gap};
  min-width: 0;

  .tl-date-range-label {
    font: ${FIELD_LABEL.labelFont} ${fontSans27};
    color: ${FIELD_LABEL.labelInk};
  }

  .tl-date-range-boxes {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 18px minmax(0, 1fr);
    gap: 6px;
    align-items: center;
  }

  .tl-date-range-arrow {
    display: flex;
    justify-content: center;
    color: ${ink33[4]};
  }
`;
function DateRangeField({
  label,
  value,
  onChange,
  min,
  max,
  today,
  disabled = false,
  invalid = false,
  "aria-describedby": describedBy,
  id,
  className
}) {
  const baseId = React37.useId();
  const rootId = id ?? baseId;
  const labelId = `${rootId}-label`;
  const hintId = `${rootId}-hint`;
  const edgeId = `${rootId}-edge`;
  const boxIds = { first: `${rootId}-first`, last: `${rootId}-last` };
  const reference = isCalendarDate(today) ? today : todayLocal();
  const limits = readLimits(min, max);
  const { from, to } = readRange(value.from, value.to);
  const warned = React37.useRef(/* @__PURE__ */ new Set());
  React37.useEffect(() => {
    for (const [name, raw] of [
      ["from", value.from],
      ["to", value.to]
    ]) {
      const problem = `\`value.${name}\` "${String(raw)}" is not a YYYY-MM-DD date; it is treated as not chosen.`;
      if (raw != null && !isCalendarDate(raw) && !warned.current.has(problem)) {
        warned.current.add(problem);
        warnDev("DateRangeField", problem);
      }
    }
  });
  const [month, setMonth] = React37.useState(
    () => monthOf(from ?? to ?? clampCalendarDate(reference, limits.min, limits.max))
  );
  const [edge, setEdgeState] = React37.useState(() => startingEdge({ from, to }));
  const [said, say] = useSaid();
  const emitted = React37.useRef({ from, to });
  const latest = React37.useRef({ from, to, edge });
  latest.current = { from, to, edge };
  const setEdge = (next) => {
    latest.current.edge = next;
    setEdgeState(next);
  };
  const emit = (next) => {
    latest.current = { ...latest.current, ...next };
    emitted.current = next;
    onChange(next);
  };
  const pressed = React37.useRef(null);
  const calendarPress = React37.useRef(false);
  const press = (box) => {
    pressed.current = box;
    setEdge(box);
  };
  const landed = () => {
    pressed.current = null;
    calendarPress.current = false;
  };
  const [seen, setSeen] = React37.useState({ from, to });
  if (!sameRange(seen, { from, to })) {
    setSeen({ from, to });
    if (!sameRange(emitted.current, { from, to })) {
      emitted.current = { from, to };
      setEdgeState(startingEdge({ from, to }));
      const inView = [from, to].some((day) => day != null && monthOf(day) === month);
      const anchor = from ?? to;
      if (!inView && anchor != null) setMonth(monthOf(anchor));
    }
  }
  const onPick = (date) => {
    const now = latest.current;
    const step = pickRange({ from: now.from, to: now.to }, now.edge, date);
    setEdge(step.edge);
    const { from: first, to: last } = step.value;
    say(
      last != null ? rangeSentence(first, last) : `First day ${formatCalendarDate(first)}. Now pick the last day.`
    );
    if (!sameRange(step.value, now)) emit(step.value);
  };
  const onTyped = (box, text, focusTo) => {
    const now = latest.current;
    const result = commitTyped({
      text,
      edge: box,
      value: { from: now.from, to: now.to },
      today: reference,
      min: limits.min,
      max: limits.max
    });
    say(result.said);
    if (sameRange(result.value, now)) return;
    emit(result.value);
    const day = box === "first" ? result.value.from : result.value.to;
    if (day == null) return;
    const into = pressed.current;
    setEdge(into != null && focusTo?.id === boxIds[into] ? into : "last");
    if (!calendarPress.current && monthOf(day) !== month) setMonth(monthOf(day));
  };
  const boxDescription = [hintId, describedBy].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs35(
    Root11,
    {
      id,
      role: "group",
      "aria-labelledby": labelId,
      className: ["tl-date-range", className].filter(Boolean).join(" "),
      children: [
        /* @__PURE__ */ jsx43("span", { className: "tl-date-range-label", id: labelId, children: label }),
        /* @__PURE__ */ jsxs35("div", { className: "tl-date-range-boxes", children: [
          /* @__PURE__ */ jsx43(
            DateBox,
            {
              id: boxIds.first,
              "aria-label": "First day",
              "aria-describedby": boxDescription,
              date: from,
              invalid,
              disabled,
              onPointerDown: () => press("first"),
              onFocus: landed,
              onCommit: (text, focusTo) => onTyped("first", text, focusTo)
            }
          ),
          /* @__PURE__ */ jsx43("span", { className: "tl-date-range-arrow", "aria-hidden": "true", children: /* @__PURE__ */ jsx43(Icon, { name: "arrow-right", size: "xs" }) }),
          /* @__PURE__ */ jsx43(
            DateBox,
            {
              id: boxIds.last,
              "aria-label": "Last day",
              "aria-describedby": boxDescription,
              date: to,
              invalid,
              disabled,
              onPointerDown: () => press("last"),
              onFocus: landed,
              onCommit: (text, focusTo) => onTyped("last", text, focusTo)
            }
          )
        ] }),
        /* @__PURE__ */ jsx43(
          "div",
          {
            onPointerDownCapture: () => {
              calendarPress.current = true;
            },
            children: /* @__PURE__ */ jsx43(
              MonthCalendar,
              {
                month,
                onMonthChange: setMonth,
                mode: "range",
                from,
                to,
                min: limits.min,
                max: limits.max,
                disabled,
                "aria-describedby": edgeId,
                onPick
              }
            )
          }
        ),
        /* @__PURE__ */ jsx43(DateFieldVoice, { hintId, said, edge: { id: edgeId, text: EDGE_TEXT[edge] } })
      ]
    }
  );
}

// src/Inputs/DateField.tsx
import React38 from "react";
import styled44 from "styled-components";
import { jsx as jsx44, jsxs as jsxs36 } from "react/jsx-runtime";
var Group2 = styled44.div`
  display: flex;
  flex-direction: column;
  gap: ${FIELD_LABEL.gap};
`;
function DateField({
  label,
  value,
  onChange,
  min,
  max,
  today,
  disabled = false,
  invalid = false,
  "aria-describedby": describedBy,
  id,
  className
}) {
  const reference = isCalendarDate(today) ? today : todayLocal();
  const limits = readLimits(min, max);
  const date = isCalendarDate(value) ? value : null;
  const warned = React38.useRef(false);
  React38.useEffect(() => {
    if (value != null && !isCalendarDate(value) && !warned.current) {
      warned.current = true;
      warnDev(
        "DateField",
        `\`value\` "${String(value)}" is not a YYYY-MM-DD date; it is treated as not chosen.`
      );
    }
  });
  const [month, setMonth] = React38.useState(
    () => monthOf(date ?? clampCalendarDate(reference, limits.min, limits.max))
  );
  const [said, say] = useSaid();
  const calendarPress = React38.useRef(false);
  const emitted = React38.useRef(date);
  const [seen, setSeen] = React38.useState(date);
  if (seen !== date) {
    setSeen(date);
    if (emitted.current !== date) {
      emitted.current = date;
      if (date != null && monthOf(date) !== month) setMonth(monthOf(date));
    }
  }
  const emit = (next) => {
    emitted.current = next;
    onChange(next);
  };
  const onPick = (picked) => {
    say(`${formatCalendarDate(picked)} chosen.`);
    if (picked !== date) emit(picked);
  };
  const onTyped = (text) => {
    const result = commitTyped({
      text,
      edge: "single",
      value: { from: date, to: null },
      today: reference,
      min: limits.min,
      max: limits.max
    });
    say(result.said);
    if (result.value.from === date) return;
    emit(result.value.from);
    const pressingCalendar = calendarPress.current;
    if (!pressingCalendar && result.value.from != null && monthOf(result.value.from) !== month) {
      setMonth(monthOf(result.value.from));
    }
  };
  return /* @__PURE__ */ jsx44(
    Field,
    {
      label,
      htmlFor: id,
      size: "md",
      className: ["tl-date-field", className].filter(Boolean).join(" "),
      children: (wiring) => {
        const hintId = `${wiring.id}-hint`;
        return /* @__PURE__ */ jsxs36(Group2, { role: "group", "aria-labelledby": wiring.labelId, children: [
          /* @__PURE__ */ jsx44(
            DateBox,
            {
              id: wiring.id,
              "aria-describedby": [hintId, wiring["aria-describedby"], describedBy].filter(Boolean).join(" "),
              date,
              invalid,
              disabled,
              onCommit: onTyped,
              onFocus: () => {
                calendarPress.current = false;
              }
            }
          ),
          /* @__PURE__ */ jsx44(
            "div",
            {
              onPointerDownCapture: () => {
                calendarPress.current = true;
              },
              children: /* @__PURE__ */ jsx44(
                MonthCalendar,
                {
                  month,
                  onMonthChange: setMonth,
                  mode: "single",
                  from: date,
                  min: limits.min,
                  max: limits.max,
                  disabled,
                  onPick
                }
              )
            }
          ),
          /* @__PURE__ */ jsx44(DateFieldVoice, { hintId, said })
        ] });
      }
    }
  );
}

// src/Inputs/DropZone.tsx
import React39 from "react";
import styled45 from "styled-components";
import {
  brand as brand28,
  fontMono as fontMono13,
  fontSans as fontSans28,
  ink as ink34,
  inkOn as inkOn14,
  motion as motion24,
  radius as radius27,
  rgba as rgba30,
  shadow as shadow14,
  space as space10,
  surface as surface23,
  typeScale as typeScale14,
  weight as weight16
} from "@repo/design-tokens";
import { Fragment as Fragment11, jsx as jsx45, jsxs as jsxs37 } from "react/jsx-runtime";
var NATIVE_WILDCARDS = /* @__PURE__ */ new Set(["image/*", "audio/*", "video/*"]);
var isAcceptToken = (token) => token.startsWith(".") && token.length > 1 || NATIVE_WILDCARDS.has(token) || /^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/.test(token);
var fileMatchesAccept = (file, accept) => {
  const tokens = (accept ?? "").split(",").map((t) => t.trim().toLowerCase()).filter(isAcceptToken);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith(".")) return name.endsWith(token);
    if (NATIVE_WILDCARDS.has(token)) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
};
var DROP_ZONE_COLOURS = {
  /** The card's rest wash: white at `cardAlpha` over whatever the page is. */
  card: surface23[0],
  cardAlpha: 0.7,
  /** The dragging wash. */
  cardDragging: brand28.frost,
  /** The rest edge (dashed) and the dragging edge (solid). */
  edge: brand28.frost,
  edgeDragging: brand28.cyan,
  /** The card's and the sticker's soft glow: brand cyan at low alpha. */
  glow: brand28.cyan,
  title: ink34[1],
  text: ink34[2],
  sticker: brand28.frost,
  stickerGlyph: inkOn14.aqua,
  pill: surface23[0],
  disc: brand28.cyan,
  discGlyph: surface23[0],
  targetName: ink34[1],
  /** The frame draws `ink[4]` here; `ink[3]` is the nearest token that clears 4.5:1 on white. */
  targetLabel: ink34[3],
  targetDetail: ink34[3]
};
var CARD_MAX_WIDTH = "760px";
var CARD_PADDING = `${space10[10]} ${space10[10]} ${space10[8]}`;
var EDGE_WIDTH = "2px";
var STICKER_SIZE = "76px";
var TITLE_FONT2 = `${weight16.semibold} ${typeScale14.title3.fontSize} / 1.25 ${fontSans28}`;
var TEXT_FONT2 = `${weight16.regular} ${typeScale14.body1.fontSize} / 1.45 ${fontSans28}`;
var TEXT_MAX_WIDTH2 = "500px";
var PILL_HEIGHT = "42px";
var PILL_PADDING = `0 ${space10[4]} 0 7px`;
var DISC_SIZE3 = "28px";
var LABEL_FONT = `${weight16.regular} 13px / 1 ${fontSans28}`;
var NAME_FONT = `${weight16.semibold} 15px / 1 ${fontSans28}`;
var DETAIL_FONT = `${weight16.regular} ${typeScale14.captionCompact.fontSize} / 1 ${fontMono13}`;
var white2 = (a) => rgba30(surface23[0], a);
var glow = (a) => rgba30(DROP_ZONE_COLOURS.glow, a);
var inkGlassShadow2 = (a) => `rgba(${shadow14.inkGlassRgb}, ${a})`;
var StyledDropZone = styled45.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space10[6]};
  width: 100%;

  .tl-dz-target {
    display: inline-flex;
    align-items: center;
    gap: ${space10[3]};
    height: ${PILL_HEIGHT};
    padding: ${PILL_PADDING};
    box-sizing: border-box;
    border-radius: ${radius27.pill};
    background: ${DROP_ZONE_COLOURS.pill};
    border: 1px solid ${white2(0.9)};
    box-shadow:
      0 1px 2px ${inkGlassShadow2(0.05)},
      0 4px 14px ${glow(0.12)};
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
  }

  /* A long twin or organisation name truncates instead of overflowing a narrow container: the
     detail gives way first, then the name. The full text stays in the DOM for assistive tech. */
  .tl-dz-target-name,
  .tl-dz-target-detail {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tl-dz-target-detail {
    flex-shrink: 2;
  }

  .tl-dz-disc {
    flex: none;
    width: ${DISC_SIZE3};
    height: ${DISC_SIZE3};
    border-radius: ${radius27.pill};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${DROP_ZONE_COLOURS.disc};
    color: ${DROP_ZONE_COLOURS.discGlyph};
  }

  .tl-dz-target-label {
    font: ${LABEL_FONT};
    color: ${DROP_ZONE_COLOURS.targetLabel};
  }

  .tl-dz-target-name {
    font: ${NAME_FONT};
    color: ${DROP_ZONE_COLOURS.targetName};
  }

  .tl-dz-target-detail {
    font: ${DETAIL_FONT};
    color: ${DROP_ZONE_COLOURS.targetDetail};
  }

  .tl-dz-card {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: ${CARD_MAX_WIDTH};
    box-sizing: border-box;
    padding: ${CARD_PADDING};
    border-radius: ${radius27.xl};
    text-align: center;
    cursor: pointer;
    background: ${rgba30(DROP_ZONE_COLOURS.card, DROP_ZONE_COLOURS.cardAlpha)};
    border: ${EDGE_WIDTH} dashed ${DROP_ZONE_COLOURS.edge};
    box-shadow:
      0 1px 2px ${inkGlassShadow2(0.04)},
      0 0 16px ${glow(0.14)},
      0 14px 34px ${glow(0.08)};
    transition:
      background-color ${motion24.durFast} ${motion24.easeUi},
      border-color ${motion24.durFast} ${motion24.easeUi};
  }

  &[data-dragging] .tl-dz-card {
    background: ${DROP_ZONE_COLOURS.cardDragging};
    border-style: solid;
    border-color: ${DROP_ZONE_COLOURS.edgeDragging};
  }

  @media (prefers-reduced-motion: reduce) {
    .tl-dz-card {
      transition: none;
    }
  }

  .tl-dz-sticker {
    width: ${STICKER_SIZE};
    height: ${STICKER_SIZE};
    box-sizing: border-box;
    border-radius: ${radius27.pill};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${DROP_ZONE_COLOURS.stickerGlyph};
    background: ${DROP_ZONE_COLOURS.sticker};
    border: 2px solid ${surface23[0]};
    box-shadow:
      0 0 0 1px ${glow(0.1)},
      0 6px 18px ${glow(0.3)};
  }

  .tl-dz-title {
    margin: ${space10[3]} 0 0;
    font: ${TITLE_FONT2};
    color: ${DROP_ZONE_COLOURS.title};
  }

  .tl-dz-text {
    margin: ${space10[4]} 0 0;
    max-width: ${TEXT_MAX_WIDTH2};
    font: ${TEXT_FONT2};
    color: ${DROP_ZONE_COLOURS.text};
    text-wrap: pretty;
  }

  .tl-dz-actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${space10[3]};
    align-items: center;
    justify-content: center;
    margin-top: ${space10[5]};
  }

  .tl-dz-sr {
    ${visuallyHiddenCss}
  }

  .tl-dz-input {
    ${visuallyHiddenCss}
  }
`;
var isFileDrag = (event) => Array.from(event.dataTransfer?.types ?? []).includes("Files");
var INTERACTIVE = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  "label",
  "summary",
  "[contenteditable]",
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="option"]'
].join(", ");
var DropZone = React39.forwardRef(
  function DropZone2(props, ref) {
    const {
      title,
      headingLevel = 3,
      children,
      icon: icon2,
      accept,
      onFile,
      onReject,
      chooseLabel = "Choose File",
      chooseIcon,
      actions,
      target,
      className,
      "aria-describedby": describedByProp,
      ...rest
    } = props;
    const id = React39.useId();
    const titleId = `${id}-title`;
    const textId = `${id}-text`;
    const targetId = `${id}-target`;
    const inputRef = React39.useRef(null);
    const depth = React39.useRef(0);
    const [dragging, setDragging] = React39.useState(false);
    const Heading = `h${headingLevel}`;
    const openPicker = () => inputRef.current?.click();
    const handleFiles = (files) => {
      if (files.length === 0) return;
      if (files.length > 1) {
        onReject(files, "count");
        return;
      }
      const file = files[0];
      if (!fileMatchesAccept(file, accept)) {
        onReject([file], "type");
        return;
      }
      onFile(file);
    };
    const endDrag = () => {
      depth.current = 0;
      setDragging(false);
    };
    const describedBy = [target ? targetId : null, children ? textId : null, describedByProp].filter(Boolean).join(" ");
    return /* @__PURE__ */ jsxs37(
      StyledDropZone,
      {
        ...rest,
        ref,
        role: "group",
        "aria-labelledby": titleId,
        "aria-describedby": describedBy || void 0,
        "data-dragging": dragging ? "" : void 0,
        className: ["tl-drop-zone", className ?? ""].filter(Boolean).join(" "),
        onDragOver: (event) => {
          if (!isFileDrag(event) || event.defaultPrevented) return;
          event.preventDefault();
          if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
        },
        onDrop: (event) => {
          if (!isFileDrag(event) || event.defaultPrevented) return;
          event.preventDefault();
        },
        children: [
          target ? /* @__PURE__ */ jsxs37("div", { className: "tl-dz-target", id: targetId, children: [
            target.icon ? /* @__PURE__ */ jsx45("span", { className: "tl-dz-disc", "aria-hidden": "true", children: target.icon }) : null,
            /* @__PURE__ */ jsx45("span", { className: "tl-dz-target-label", children: target.label ?? "Uploading to" }),
            " ",
            /* @__PURE__ */ jsx45("span", { className: "tl-dz-target-name", children: target.name }),
            target.detail ? /* @__PURE__ */ jsxs37(Fragment11, { children: [
              /* @__PURE__ */ jsx45("span", { className: "tl-dz-sr", children: ", " }),
              /* @__PURE__ */ jsx45("span", { className: "tl-dz-target-detail", children: target.detail })
            ] }) : null
          ] }) : null,
          /* @__PURE__ */ jsxs37(
            "div",
            {
              className: "tl-dz-card",
              onClick: (event) => {
                const hit = event.target.closest(INTERACTIVE);
                if (hit && event.currentTarget.contains(hit)) return;
                openPicker();
              },
              onDragEnter: (event) => {
                if (!isFileDrag(event)) return;
                depth.current += 1;
                setDragging(true);
              },
              onDragOver: (event) => {
                if (!isFileDrag(event)) return;
                event.preventDefault();
                if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
              },
              onDragLeave: (event) => {
                if (!isFileDrag(event)) return;
                depth.current = Math.max(0, depth.current - 1);
                if (depth.current === 0) setDragging(false);
              },
              onDrop: (event) => {
                event.preventDefault();
                endDrag();
                handleFiles(Array.from(event.dataTransfer?.files ?? []));
              },
              onDragEnd: endDrag,
              children: [
                /* @__PURE__ */ jsx45("span", { className: "tl-dz-sticker", "aria-hidden": "true", children: icon2 }),
                /* @__PURE__ */ jsx45(Heading, { className: "tl-dz-title", id: titleId, children: title }),
                children ? /* @__PURE__ */ jsx45("p", { className: "tl-dz-text", id: textId, children }) : null,
                /* @__PURE__ */ jsxs37("div", { className: "tl-dz-actions", children: [
                  /* @__PURE__ */ jsx45(Button, { variant: "primary", iconLeft: chooseIcon, onClick: openPicker, children: chooseLabel }),
                  actions
                ] }),
                /* @__PURE__ */ jsx45(
                  "input",
                  {
                    ref: inputRef,
                    className: "tl-dz-input",
                    type: "file",
                    accept,
                    tabIndex: -1,
                    "aria-hidden": "true",
                    onChange: (event) => {
                      const files = Array.from(event.target.files ?? []);
                      event.target.value = "";
                      handleFiles(files);
                    }
                  }
                )
              ]
            }
          )
        ]
      }
    );
  }
);
DropZone.displayName = "DropZone";

// src/Menus/Select.tsx
import React41 from "react";
import styled47 from "styled-components";
import {
  accent as accent13,
  ink as ink37,
  brand as brand30,
  fontSans as fontSans30,
  motion as motion26,
  rgba as rgba33,
  typeScale as typeScale16,
  weight as weight18
} from "@repo/design-tokens";

// src/Menus/Menu.tsx
import React40 from "react";
import styled46 from "styled-components";
import {
  ink as ink36,
  line as line16,
  brand as brand29,
  surface as surface25,
  shadow as shadow15,
  space as space11,
  inset as inset7,
  weight as weight17,
  typeScale as typeScale15,
  semantic as semantic8,
  semanticColor as semanticColor14,
  fontSans as fontSans29,
  fontMono as fontMono14,
  radius as radius29,
  motion as motion25,
  rgba as rgba32,
  accent as accent12,
  inkOn as inkOn15,
  support as support12
} from "@repo/design-tokens";

// src/internal/richDisc.ts
import { css as css22 } from "styled-components";
import { ink as ink35, radius as radius28, rgba as rgba31, surface as surface24 } from "@repo/design-tokens";
var RICH_DISC = "26px";
var richDiscCss = css22`
  width: ${RICH_DISC};
  height: ${RICH_DISC};
  border-radius: ${radius28.pill};
  background: ${rgba31(surface24[0], 0.9)};
  border: 1px solid ${rgba31(surface24[0], 0.95)};
  box-shadow: 0 1px 2px ${rgba31(ink35[1], 0.08)};
  box-sizing: border-box;
  color: ${ink35[2]};
`;

// src/Menus/Menu.tsx
import { jsx as jsx46, jsxs as jsxs38 } from "react/jsx-runtime";
var MENU_MAX_HEIGHT = 280;
var MENU_ROW_WASH = { hover: 0.08, selected: 0.1, highlight: 0.14 };
var MENU_PICTURE_WIDTH = 52;
var MENU_STATUS_INK = ink36[3];
var MENU_STATUS_INK_TINTED = ink36[2];
var MENU_RICH_LABEL_INK = ink36[3];
var RICH_RADIUS = radius29.control;
var RICH_BLUR = "blur(20px)";
var hasTrailingContent = (node) => {
  if (node == null || typeof node === "boolean" || node === "") return false;
  if (Array.isArray(node)) return node.some(hasTrailingContent);
  if (React40.isValidElement(node) && node.type === React40.Fragment) {
    return hasTrailingContent(node.props.children);
  }
  return true;
};
var Panel = styled46.div`
  background: #fff;
  border: 1px solid ${line16[2]};
  border-radius: ${radius29.md};
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.08),
    0 2px 6px rgba(0, 0, 0, 0.04);
  padding: 4px;
  min-width: 200px;
  max-height: ${MENU_MAX_HEIGHT}px;
  overflow: auto;
  font-family: ${fontSans29};
  color: ${ink36[1]};
  box-sizing: border-box;

  &.below-trigger {
    margin-top: 6px;
  }

  /* ---- Card 04's rich panel ("Combobox — grouped options") ---------------------------
     Scoped under the panel so the row recipe stays ONE recipe: these rules restyle the same
     .menu-item, .menu-ic, .menu-label rather than forking a second row component. Each
     selector carries the panel's classes, so it outranks Row's own (one class) rules. */
  &.is-rich {
    background: ${rgba32(surface25[0], 0.92)};
    backdrop-filter: ${RICH_BLUR};
    -webkit-backdrop-filter: ${RICH_BLUR};
    border: none;
    border-radius: ${RICH_RADIUS};
    box-shadow:
      ${shadow15.glassSm},
      0 12px 30px ${rgba32("#000000", 0.1)};
    padding: ${inset7[2]};
  }
  /* Only what differs from the shared Label (size, tracking and case are its own). */
  &.is-rich .menu-label {
    padding: ${space11[2]} ${space11[2]} ${space11[1]};
    font-family: ${fontMono14};
    font-weight: ${weight17.regular};
    line-height: ${typeScale15.caption2.lineHeight};
    color: ${MENU_RICH_LABEL_INK};
  }
  /* Only what differs from Row: the disc column, the padding, and the name's weight and leading.
     Row's own gap, radius and two-line row-gap stand. */
  &.is-rich .menu-item {
    grid-template-columns: ${RICH_DISC} minmax(0, 1fr) auto;
    padding: ${inset7[2]} ${space11[2]};
    font-weight: ${weight17.medium};
    line-height: 18px;
  }
  /* Card 04 draws no picture row on the rich panel. One is not blocked, so it keeps its own 52px
     column rather than spilling out of the 26px disc column over the label. */
  &.is-rich .menu-item.has-pic {
    grid-template-columns: ${MENU_PICTURE_WIDTH}px minmax(0, 1fr) auto;
  }
  /* The disc. The glyph's size is the consumer's \`Icon size="xs"\` (icon.xs, 12px — the card
     draws 11px, which the icon ramp has no step for); see \`MenuItemProps.icon\`. */
  &.is-rich .menu-item .menu-ic {
    ${richDiscCss}
  }
  &.is-rich .menu-item .menu-sub {
    font-size: ${typeScale15.caption2.fontSize};
    line-height: ${typeScale15.caption2.lineHeight};
  }
  &.is-rich .menu-item .menu-tr-content {
    font-weight: ${weight17.medium};
    font-size: ${typeScale15.caption1.fontSize};
    line-height: ${typeScale15.caption1.lineHeight};
  }
  /* A MenuStatus under the rich panel takes the slot's 500 12/16 above and card 04's .cbx-st
     6px gap, not the default panel's .tr.health 11px / 5px. */
  &.is-rich .menu-item .menu-status {
    gap: ${inset7[2]};
    font-size: inherit;
    line-height: inherit;
  }
  /* MenuTrailingLabel takes the rich slot's 500 12/16 too. */
  &.is-rich .menu-item .menu-trailing-label {
    font-size: inherit;
    line-height: inherit;
  }
  &.is-rich .menu-item:hover,
  &.is-rich .menu-item.is-highlight {
    background: ${rgba32(brand29.frost, 0.9)};
    box-shadow: 0 0 10px 2px ${brand29.frost};
  }
  &.is-rich .menu-item.is-highlight .menu-lbl {
    font-weight: ${weight17.semibold};
  }
  &.is-rich .menu-item.is-disabled:hover {
    background: transparent;
    box-shadow: none;
  }

  /* A SECTION label carries meaning ("Already totalled · opens it"), so on the default panel
     too it takes the accessible ink — the correction .menu-sub took. A bare MenuLabel keeps
     its own ink[4]; that one has its own §6B line. */
  .menu-group > .menu-label {
    color: ${ink36[3]};
  }
`;
var Row4 = styled46.div`
  /* 18px icon gutter · flexible label · auto trailing slot. The gutter is reserved even
     for an item with no icon, so a mixed list keeps its labels in one column. */
  display: grid;
  grid-template-columns: 18px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  font-family: ${fontSans29};
  font-weight: 400;
  font-size: 13px;
  line-height: 1.2;
  color: ${ink36[1]};
  cursor: pointer;
  user-select: none;
  transition: background 0.12s ${motion25.easeUi};

  .menu-ic {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${ink36[3]};
  }
  .menu-ic > svg {
    width: 16px;
    height: 16px;
    display: block;
  }

  /* Card 04's .has-pic: a 52px, 16:9 leading box in place of the icon gutter, and 6px vertical
     padding so a camera row stays compact. The box only sizes and centres the picture — no radius,
     fill or clip — so a letterboxed picture's slack stays transparent. */
  &.has-pic {
    grid-template-columns: ${MENU_PICTURE_WIDTH}px 1fr auto;
    padding-top: ${inset7[2]};
    padding-bottom: ${inset7[2]};
  }
  .menu-pic {
    width: ${MENU_PICTURE_WIDTH}px;
    aspect-ratio: 16 / 9;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .menu-lbl {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .menu-tr {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    color: ${ink36[5]};
  }
  .menu-tr.short {
    font-family: ${fontMono14};
    font-weight: 500;
    font-size: 10.5px;
  }
  .menu-tr.check {
    /* Reserved, not conditional: an item that becomes selected must not shift its label. */
    color: ${brand29.cyan};
    opacity: 0;
  }
  .menu-tr.check > svg {
    width: 14px;
    height: 14px;
  }
  /* The general slot's content. Ink and spacing only — what goes in it is the caller's. */
  .menu-tr.menu-tr-content {
    gap: 6px;
    color: ${ink36[3]};
    white-space: nowrap;
  }

  &:hover {
    background: ${rgba32(brand29.cyan, MENU_ROW_WASH.hover)};
  }
  &:hover .menu-ic {
    color: ${brand29.cyan};
  }

  &.is-highlight {
    background: ${rgba32(brand29.cyan, MENU_ROW_WASH.highlight)};
  }

  &.is-selected {
    background: ${rgba32(brand29.cyan, MENU_ROW_WASH.selected)};
    color: ${brand29.cyanPress};
    font-weight: 500;
  }
  /* A status word on a tinted row darkens to clear 4.5:1 over the wash (MENU_STATUS_INK_TINTED). */
  &:hover .menu-status,
  &.is-highlight .menu-status,
  &.is-selected .menu-status {
    color: ${MENU_STATUS_INK_TINTED};
  }
  &.is-selected .menu-tr.check {
    opacity: 1;
  }
  &.is-selected .menu-ic {
    color: ${brand29.cyan};
  }

  &.is-warning,
  &.is-warning .menu-ic {
    color: ${semanticColor14.warn};
  }
  &.is-warning:hover {
    background: ${rgba32(semanticColor14.warn, 0.1)};
  }

  &.is-danger,
  &.is-danger .menu-ic {
    color: ${semanticColor14.errorStrong};
  }
  &.is-danger:hover {
    background: ${rgba32(semanticColor14.errorStrong, 0.08)};
  }

  &.is-disabled {
    color: #b8b8b8;
    cursor: not-allowed;
  }
  &.is-disabled .menu-ic {
    color: #cfcfcf;
  }
  &.is-disabled:hover {
    background: transparent;
  }

  /* ---- Card 04's two-line row -------------------------------------------------------
     Icon and trailing slot span both rows and stay optically centred; the label sits on
     row 1 and the secondary note on row 2. Applied only when a meta is supplied, so a
     one-line row is untouched. */
  &.is-two-line {
    grid-template-rows: auto auto;
    align-items: start;
    padding: 7px 10px;
    row-gap: 1px;
  }
  &.is-two-line .menu-ic,
  &.is-two-line .menu-pic {
    grid-column: 1;
    grid-row: 1 / span 2;
    align-self: center;
  }
  &.is-two-line .menu-lbl {
    grid-column: 2;
    grid-row: 1;
  }
  &.is-two-line .menu-tr {
    grid-column: 3;
    grid-row: 1 / span 2;
    align-self: center;
  }
  /* A picture row keeps card 04's 6px vertical padding when it is also two-line: the card declares
     .has-pic after .is-two-line, so its padding wins there too. */
  &.is-two-line.has-pic {
    padding-top: ${inset7[2]};
    padding-bottom: ${inset7[2]};
  }

  /* 🚨 ink[3], NOT the ink[4] card 04 draws. Measured on the panel's white, ink[4] is
     3.67:1 — under WCAG 1.4.3's 4.5:1 for text below 18.66px — and ink[3] is 4.77:1. This
     line is not decorative: it is the product line that tells two near-identical catalogue
     rows apart, so a note the reader cannot read is the hole the row was added to close.
     MultiSelect's own note took this exact correction at review; the shared recipe inherits
     it rather than regressing it. Raised with Rob as a card correction — see
     design-cards/PROVENANCE.md. */
  .menu-sub {
    grid-column: 2;
    grid-row: 2;
    font-family: ${fontMono14};
    font-weight: 400;
    font-size: 10.5px;
    line-height: 1.35;
    color: ${ink36[3]};
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* Deliberately NOT recoloured on selection: cyanPress at 10.5px over the row's cyan tint is
     ~3.9:1, and this is the line that tells two near-identical catalogue rows apart at exactly
     the moment the reader is confirming a choice. The tint, the weight and the trailing check
     already carry the selection. */
`;
var Separator = styled46.div`
  height: 1px;
  background: #efefef;
  margin: 4px 2px;
`;
var Label2 = styled46.div`
  padding: 8px 10px 4px;
  font-family: ${fontSans29};
  font-weight: 600;
  font-size: 10px;
  line-height: 1.2;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink36[4]};
`;
var CheckIcon = /* @__PURE__ */ jsx46("svg", { viewBox: "0 0 14 14", fill: "none", stroke: "currentColor", strokeWidth: 2, "aria-hidden": "true", children: /* @__PURE__ */ jsx46("path", { d: "M2 7l3 3 6-7" }) });
var Menu = React40.forwardRef(function Menu2({ belowTrigger = false, variant: variant2 = "default", className, children, ...rest }, ref) {
  return /* @__PURE__ */ jsx46(
    Panel,
    {
      ref,
      className: [
        "menu",
        belowTrigger ? "below-trigger" : "",
        variant2 === "rich" ? "is-rich" : "",
        className ?? ""
      ].filter(Boolean).join(" "),
      ...rest,
      children
    }
  );
});
var MenuItem = React40.forwardRef(function MenuItem2({
  icon: icon2,
  picture,
  shortcut,
  trailing,
  variant: variant2 = "default",
  meta,
  selected = false,
  highlighted = false,
  disabled = false,
  className,
  children,
  ...rest
}, ref) {
  const hasPicture = hasTrailingContent(picture);
  const bothLeading = hasPicture && icon2 != null;
  const warnedBoth = React40.useRef(false);
  React40.useEffect(() => {
    if (!bothLeading || warnedBoth.current || process.env.NODE_ENV === "production") return;
    warnedBoth.current = true;
    console.warn(
      "[@repo/design-system] MenuItem: `icon` and `picture` were both passed; `picture` wins and `icon` is not rendered."
    );
  }, [bothLeading]);
  const classes = [
    "menu-item",
    variant2 === "default" ? "" : `is-${variant2}`,
    hasPicture ? "has-pic" : "",
    meta != null ? "is-two-line" : "",
    selected ? "is-selected" : "",
    highlighted ? "is-highlight" : "",
    disabled ? "is-disabled" : "",
    className ?? ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs38(Row4, { ref, className: classes, "aria-disabled": disabled || void 0, ...rest, children: [
    hasPicture ? /* @__PURE__ */ jsx46("span", { className: "menu-pic", "aria-hidden": "true", children: picture }) : /* @__PURE__ */ jsx46("span", { className: "menu-ic", "aria-hidden": "true", children: icon2 }),
    /* @__PURE__ */ jsx46("span", { className: "menu-lbl", children }),
    meta != null && /* @__PURE__ */ jsx46("span", { className: "menu-sub", children: meta }),
    hasTrailingContent(trailing) ? /* @__PURE__ */ jsx46("span", { className: "menu-tr menu-tr-content", children: trailing }) : selected || !shortcut && rest.role === "option" ? /* @__PURE__ */ jsx46("span", { className: "menu-tr check", "aria-hidden": "true", children: CheckIcon }) : /* @__PURE__ */ jsx46("span", { className: shortcut ? "menu-tr short" : "menu-tr", "aria-hidden": "true", children: shortcut })
  ] });
});
var STATUS_HUE = {
  ...DOT_HUE,
  butter: accent12.butter,
  lavender: support12.lavender,
  errorStrong: semanticColor14.errorStrong
};
var StatusRoot = styled46.span`
  /* Card 04's .tr.health: 500 11px/1, gap 5px (a card literal: the inset ramp has no 5px), ink-3,
     and a 6px dot. On a tinted row the word darkens to MENU_STATUS_INK_TINTED (Row, above). */
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: ${weight17.medium};
  font-size: ${typeScale15.captionCompact.fontSize};
  line-height: 1;
  color: ${MENU_STATUS_INK};
  white-space: nowrap;

  > i {
    width: 6px;
    height: 6px;
    border-radius: ${radius29.pill};
    flex: none;
    background: ${({ $hue }) => $hue};
  }
`;
function useBlankWord(children, why) {
  const blank = typeof children !== "string" || children.trim() === "";
  React40.useEffect(() => {
    if (!blank || process.env.NODE_ENV === "production") return;
    console.warn(`[@repo/design-system] ${why}, so nothing is rendered.`);
  }, [blank, why]);
  return blank;
}
function MenuStatus({
  tone,
  children,
  className
}) {
  const key = variant(STATUS_HUE, tone, "ink-5", "MenuStatus", "tone");
  const blank = useBlankWord(
    children,
    "MenuStatus: the status needs its words; a dot alone says nothing to a reader who cannot tell its colour"
  );
  if (blank) return null;
  return /* @__PURE__ */ jsxs38(
    StatusRoot,
    {
      $hue: STATUS_HUE[key],
      className: ["menu-status", className].filter(Boolean).join(" "),
      children: [
        /* @__PURE__ */ jsx46("i", { "aria-hidden": "true" }),
        children
      ]
    }
  );
}
var MENU_TRAILING_LABEL_INK = inkOn15.aqua;
var MENU_TRAILING_LABEL_INKS = {
  aqua: MENU_TRAILING_LABEL_INK,
  lav: inkOn15.lav
};
var TrailingLabelRoot = styled46.span`
  font-family: ${fontSans29};
  font-weight: ${weight17.medium};
  font-size: ${typeScale15.captionCompact.fontSize};
  line-height: 1;
  color: ${(p) => p.$ink};
  white-space: nowrap;
`;
function MenuTrailingLabel({
  children,
  tone = "aqua",
  className
}) {
  const blank = useBlankWord(children, "MenuTrailingLabel: the label needs its words");
  const key = variant(MENU_TRAILING_LABEL_INKS, tone, "aqua", "MenuTrailingLabel", "tone");
  if (blank) return null;
  return /* @__PURE__ */ jsx46(
    TrailingLabelRoot,
    {
      $ink: MENU_TRAILING_LABEL_INKS[key],
      className: ["menu-trailing-label", className].filter(Boolean).join(" "),
      children
    }
  );
}
var MenuSeparator = ({ className, decorative = false }) => /* @__PURE__ */ jsx46(
  Separator,
  {
    className: ["menu-sep", className ?? ""].filter(Boolean).join(" "),
    role: decorative ? "presentation" : "separator",
    "aria-hidden": decorative || void 0
  }
);
var MenuLabel = ({
  className,
  children
}) => /* @__PURE__ */ jsx46(Label2, { className: ["menu-label", className ?? ""].filter(Boolean).join(" "), children });
var MenuGroup = ({ label, className, children }) => {
  const labelId = React40.useId();
  return /* @__PURE__ */ jsxs38(
    "div",
    {
      role: "group",
      "aria-labelledby": labelId,
      className: ["menu-group", className ?? ""].filter(Boolean).join(" "),
      children: [
        /* @__PURE__ */ jsx46(Label2, { id: labelId, "aria-hidden": "true", className: "menu-label", children: label }),
        children
      ]
    }
  );
};
var State = styled46.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  font-family: ${fontMono14};
  font-weight: 400;
  font-size: 11.5px;
  line-height: 1.4;
  /* ink[3], NOT ink[4] — the same 3.67:1-vs-4.77:1 correction as .menu-sub above. This is the
     ONLY feedback the panel gives when it has no rows to show ("No products match",
     "Searching…"), so an unreadable message here reads as a broken control. */
  color: ${ink36[3]};

  &.is-error {
    /* The text-safe negative ink, not the coral FILL - this is body-sized text. */
    color: ${semantic8.text.error};
    justify-content: space-between;
  }

  .menu-state-action > button {
    background: none;
    border: 1px solid ${line16[1]};
    border-radius: ${radius29.sm};
    padding: 3px 8px;
    font-family: ${fontMono14};
    font-weight: 500;
    font-size: 10.5px;
    line-height: 1;
    color: ${ink36[1]};
    cursor: pointer;
    transition:
      border-color 0.12s ${motion25.easeUi},
      color 0.12s ${motion25.easeUi};
  }
  /* Hover moves the FRAME, not the label. Full cyan as 10.5px text on white is 2.34:1, and as
     a lone 1px border 2.34:1 against 1.4.11's 3:1 — hovering made the control harder to read
     than at rest. The tint carries the affordance and the ink stays at 9.67:1. */
  .menu-state-action > button:hover {
    border-color: ${brand29.cyanPress};
    background: ${rgba32(brand29.cyan, 0.08)};
  }

  .menu-spin {
    width: 12px;
    height: 12px;
    flex: none;
    border: 1.5px solid ${line16[1]};
    border-top-color: ${brand29.cyan};
    border-radius: 50%;
    animation: menu-spin 0.7s linear infinite;
  }

  @keyframes menu-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
var Cap2 = styled46.div`
  padding: 7px 10px;
  border-top: 1px solid ${line16[4]};
  margin-top: 2px;
  font-family: ${fontMono14};
  font-weight: 400;
  font-size: 10.5px;
  line-height: 1.3;
  /* ink[3] — see MenuState. This footer is what tells a reader the list was truncated. */
  color: ${ink36[3]};

  b {
    color: ${ink36[2]};
    font-weight: 600;
  }
`;
var MenuState = ({
  tone = "default",
  busy = false,
  action,
  className,
  children,
  ...rest
}) => /* @__PURE__ */ jsxs38(
  State,
  {
    className: ["menu-state", tone === "error" ? "is-error" : "", className ?? ""].filter(Boolean).join(" "),
    ...rest,
    children: [
      busy && /* @__PURE__ */ jsx46("span", { className: "menu-spin", "aria-hidden": "true" }),
      /* @__PURE__ */ jsx46("span", { children }),
      action != null && /* @__PURE__ */ jsx46("span", { className: "menu-state-action", children: action })
    ]
  }
);
var MenuCap = ({
  className,
  children
}) => /* @__PURE__ */ jsx46(Cap2, { className: ["menu-cap", className ?? ""].filter(Boolean).join(" "), children });

// src/internal/listNav.ts
var nextEnabled = (options, from, step, wrap = false) => {
  for (let i = from + step; i >= 0 && i < options.length; i += step) {
    if (!options[i]?.disabled) return i;
  }
  if (!wrap) return from;
  const start = step === 1 ? 0 : options.length - 1;
  for (let i = start; i !== from && i >= 0 && i < options.length; i += step) {
    if (!options[i]?.disabled) return i;
  }
  return from >= 0 && from < options.length ? from : -1;
};
var firstEnabled = (options, step) => {
  const start = step === 1 ? -1 : options.length;
  const found = nextEnabled(options, start, step);
  return found === start ? -1 : found;
};
var scrollHighlightIntoView = (id) => {
  const el = document.getElementById(id);
  if (typeof el?.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
};

// src/Menus/Select.tsx
import { jsx as jsx47, jsxs as jsxs39 } from "react/jsx-runtime";
var SIZE7 = FIELD_SIZE;
var CHEVRON = {
  compact: "11px",
  sm: "14px",
  md: "14px",
  lg: "14px"
};
var Wrap3 = styled47.div`
  position: relative;
  width: 100%;
`;
var Trigger = styled47.button`
  appearance: none;
  width: 100%;
  box-sizing: border-box;
  height: ${({ $size }) => SIZE7[$size].height};
  padding: ${({ $size }) => SIZE7[$size].padding};
  border-radius: ${({ $size }) => SIZE7[$size].radius};
  border: ${({ $size }) => SIZE7[$size].border} solid ${FIELD_CHROME.border};
  background: ${FIELD_CHROME.fill};
  font-family: ${fontSans30};
  font-weight: 400;
  font-size: ${({ $size }) => SIZE7[$size].font};
  line-height: 1;
  color: ${FIELD_CHROME.ink};
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  outline: none;
  transition:
    border-color 0.15s ${motion26.easeUi},
    box-shadow 0.15s ${motion26.easeUi},
    background-color 0.15s ${motion26.easeUi};

  .sel-placeholder {
    color: ${ink37[5]};
  }

  .sel-value {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sel-chev {
    position: absolute;
    right: ${({ $size }) => FIELD_TRAIL_INSET[$size]};
    top: 50%;
    transform: translateY(-50%);
    color: ${ink37[3]};
    pointer-events: none;
    display: flex;
    transition: transform 0.15s ${motion26.easeUi};
  }
  /*
   * A trailing tag (frame 02): in flow after the value, so the value ellipsises before it. The
   * trigger's right padding already keeps the chevron's slot clear.
   */
  .sel-trailing {
    flex: none;
    margin-left: auto;
    font-size: ${typeScale16.captionCompact.fontSize};
    line-height: ${typeScale16.captionCompact.lineHeight};
    font-weight: ${weight18.medium};
    white-space: nowrap;
  }

  /* Card 04's chevron, not card 02's trailing action icon — see FIELD_TRAIL_INSET's note. */
  .sel-chev > svg {
    width: ${({ $size }) => CHEVRON[$size]};
    height: ${({ $size }) => CHEVRON[$size]};
    display: block;
  }

  &:hover:not(:disabled):not(.is-open):not(.is-readonly) {
    border-color: ${FIELD_CHROME.borderHover};
  }

  /* The cyan focus LIFTS the fill to white — the card's signature, shared with the input. */
  &:focus-visible,
  &.is-open {
    border-color: ${brand30.cyan};
    background: ${FIELD_CHROME.fillFocus};
    box-shadow: ${({ $size }) => fieldFocusRing($size)};
  }

  &.is-open .sel-chev {
    transform: translateY(-50%) rotate(180deg);
  }

  /* Frame 02's toned borders: a guess (aqua, brand cyan at .45) and a decision owed (butter). */
  &.tone-aqua:not(:focus-visible):not(.is-open) {
    border-color: ${rgba33(brand30.cyan, 0.45)};
  }
  &.tone-butter:not(:focus-visible):not(.is-open) {
    border-color: ${accent13.butter};
  }

  &.is-error {
    border-color: ${FIELD_CHROME.errorBorder};
    background: ${FIELD_CHROME.errorFill};
  }
  &.is-error:focus-visible,
  &.is-error.is-open {
    box-shadow: 0 0 0 3px ${FIELD_CHROME.errorRing};
  }

  &:disabled {
    background: ${FIELD_CHROME.disabledFill};
    color: ${ink37[5]};
    cursor: not-allowed;
    border-color: ${FIELD_CHROME.disabledBorder};
  }

  &.is-readonly {
    background: ${FIELD_CHROME.readOnlyFill};
    border-color: ${FIELD_CHROME.readOnlyBorder};
    color: ${FIELD_CHROME.readOnlyInk};
    cursor: default;
  }

  /* On glass the trigger keeps the fill translucent so the surface reads through, and the
     border darkens instead of lightening — a pale hairline vanishes over the 3D scene. */
  &.on-glass {
    background: ${FIELD_CHROME.glassFill};
    border-color: ${FIELD_CHROME.glassBorder};
    backdrop-filter: ${FIELD_CHROME.glassBlur};
    -webkit-backdrop-filter: ${FIELD_CHROME.glassBlur};
  }
  &.on-glass:hover:not(:disabled):not(.is-open):not(.is-readonly) {
    border-color: ${FIELD_CHROME.glassBorderHover};
    background: ${FIELD_CHROME.glassFillHover};
  }
  &.on-glass:focus-visible,
  &.on-glass.is-open {
    background: ${FIELD_CHROME.fillFocus};
    border-color: ${brand30.cyan};
    box-shadow: 0 0 0 3px ${rgba33(brand30.cyan, 0.18)};
  }
  &.on-glass.on-opaque {
    background: ${FIELD_CHROME.glassFillOpaque};
    border-color: ${FIELD_CHROME.glassBorderOpaque};
  }
  &.on-glass.on-opaque:focus-visible,
  &.on-glass.on-opaque.is-open {
    background: ${FIELD_CHROME.fillFocus};
  }
`;
var Panel2 = styled47.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 20;

  /* fitOptions: at least the trigger's width, as wide as the widest label up to the cap. */
  &.sel-panel-fit {
    right: auto;
    min-width: 100%;
    width: max-content;
    max-width: min(480px, calc(100vw - 32px));
  }

  &.sel-panel-end {
    left: auto;
    right: 0;
  }
`;
var Chevron = /* @__PURE__ */ jsx47("svg", { viewBox: "0 0 12 12", fill: "none", stroke: "currentColor", strokeWidth: 1.5, "aria-hidden": "true", children: /* @__PURE__ */ jsx47("path", { d: "m3 4.5 3 3 3-3" }) });
var Select = ({
  options,
  value,
  onChange,
  placeholder = "Select\u2026",
  size = "md",
  disabled = false,
  readOnly = false,
  invalid = false,
  onGlass = false,
  onOpaque = false,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  fitOptions = false,
  panelAlign = "start",
  trailing,
  tone,
  defaultOpen = false,
  className
}) => {
  const [open, setOpen] = React41.useState(false);
  const [highlight, setHighlight] = React41.useState(-1);
  const wrapRef = React41.useRef(null);
  const triggerRef = React41.useRef(null);
  const baseId = React41.useId();
  const listId = `${baseId}-list`;
  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : void 0;
  const inert = disabled || readOnly;
  const close = React41.useCallback((refocus = true) => {
    setOpen(false);
    setHighlight(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);
  const openList = React41.useCallback(
    (from) => {
      if (inert) return;
      setOpen(true);
      setHighlight(
        from === "selected" && selectedIndex >= 0 ? selectedIndex : firstEnabled(options, from === "last" ? -1 : 1)
      );
    },
    [inert, options, selectedIndex]
  );
  React41.useEffect(() => {
    if (!defaultOpen || inert) return;
    openList("selected");
    triggerRef.current?.focus();
  }, []);
  const commit = (index) => {
    const option = options[index];
    if (!option || option.disabled) {
      if (!option) close();
      return;
    }
    onChange?.(option.value);
    close();
  };
  React41.useEffect(() => {
    setHighlight((h) => h >= options.length ? firstEnabled(options, 1) : h);
  }, [options]);
  React41.useEffect(() => {
    if (!open || highlight < 0) return;
    scrollHighlightIntoView(`${baseId}-opt-${highlight}`);
  }, [open, highlight, baseId]);
  React41.useEffect(() => {
    if (!open) return void 0;
    const onDown = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (isScrollbarPress(event)) {
        triggerRef.current?.focus({ preventScroll: true });
        return;
      }
      close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);
  const onKeyDown = (event) => {
    if (inert) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!open) openList("selected");
        else setHighlight((h) => nextEnabled(options, h, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!open) openList("last");
        else setHighlight((h) => nextEnabled(options, h, -1));
        break;
      case "Home":
        if (open) {
          event.preventDefault();
          setHighlight(firstEnabled(options, 1));
        }
        break;
      case "End":
        if (open) {
          event.preventDefault();
          setHighlight(firstEnabled(options, -1));
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (!open) openList("selected");
        else commit(highlight);
        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          close();
        }
        break;
      case "Tab":
        if (open) close(false);
        break;
      default:
        break;
    }
  };
  const triggerClasses = [
    "sel-trigger",
    `sz-${size}`,
    open ? "is-open" : "",
    invalid ? "is-error" : "",
    readOnly ? "is-readonly" : "",
    onGlass ? "on-glass" : "",
    onGlass && onOpaque ? "on-opaque" : "",
    // The tone yields to every state with chrome of its own (card 04's contract); focus and open
    // are handled in the CSS, since they change without a re-render.
    tone && !invalid && !disabled && !readOnly && !onGlass ? `tone-${tone}` : ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs39(Wrap3, { ref: wrapRef, className: ["sel", className ?? ""].filter(Boolean).join(" "), children: [
    /* @__PURE__ */ jsxs39(
      Trigger,
      {
        ref: triggerRef,
        type: "button",
        className: triggerClasses,
        $size: size,
        disabled,
        onClick: () => open ? close() : openList("selected"),
        onKeyDown,
        role: "combobox",
        "aria-expanded": open,
        "aria-controls": open ? listId : void 0,
        "aria-haspopup": "listbox",
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "aria-describedby": ariaDescribedBy,
        "aria-invalid": invalid || void 0,
        "aria-readonly": readOnly || void 0,
        "aria-activedescendant": open && highlight >= 0 ? `${baseId}-opt-${highlight}` : void 0,
        children: [
          /* @__PURE__ */ jsx47("span", { className: selected ? "sel-value" : "sel-value sel-placeholder", children: selected ? selected.label : placeholder }),
          trailing !== void 0 && trailing !== null ? (
            // Presentational, as the prop says: the state is in the control's description, so it is
            // not read a second time as part of the value.
            /* @__PURE__ */ jsx47("span", { className: "sel-trailing", "aria-hidden": "true", children: trailing })
          ) : null,
          /* @__PURE__ */ jsx47("span", { className: "sel-chev", children: Chevron })
        ]
      }
    ),
    open ? /* @__PURE__ */ jsx47(
      Panel2,
      {
        className: [fitOptions ? "sel-panel-fit" : "", panelAlign === "end" ? "sel-panel-end" : ""].filter(Boolean).join(" ") || void 0,
        children: /* @__PURE__ */ jsx47(
          Menu,
          {
            belowTrigger: true,
            id: listId,
            role: "listbox",
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabel ? void 0 : ariaLabelledBy,
            children: options.map((option, index) => /* @__PURE__ */ jsx47(
              MenuItem,
              {
                id: `${baseId}-opt-${index}`,
                role: "option",
                "aria-selected": option.value === value,
                icon: option.icon,
                selected: option.value === value,
                highlighted: index === highlight,
                disabled: option.disabled,
                onMouseDown: (event) => {
                  event.preventDefault();
                  commit(index);
                },
                onMouseEnter: () => !option.disabled && setHighlight(index),
                title: fitOptions && typeof option.label === "string" ? option.label : void 0,
                children: option.label
              },
              option.value
            ))
          }
        )
      }
    ) : null
  ] });
};

// src/Menus/Combobox.tsx
import React42 from "react";
import styled48 from "styled-components";
import {
  ink as ink38,
  brand as brand31,
  accent as accent14,
  inkOn as inkOn16,
  fontSans as fontSans31,
  fontMono as fontMono15,
  motion as motion27,
  radius as radius30,
  inset as inset8,
  rgba as rgba34,
  weight as weight19
} from "@repo/design-tokens";

// src/internal/groupOrder.ts
var groupOf = (item) => item.group != null && item.group.trim() !== "" ? item.group : null;
var orderByGroup = (visible, all) => {
  const rank = /* @__PURE__ */ new Map();
  for (const item of all) {
    const group = groupOf(item);
    if (group !== null && !rank.has(group)) rank.set(group, rank.size);
  }
  if (rank.size === 0) return visible;
  const ungrouped = [];
  const buckets = Array.from({ length: rank.size }, () => []);
  for (const item of visible) {
    const group = groupOf(item);
    if (group === null) ungrouped.push(item);
    else buckets[rank.get(group)].push(item);
  }
  return ungrouped.concat(...buckets);
};
var sectionsOf = (rows) => {
  const sections = [];
  rows.forEach((row, index) => {
    const group = groupOf(row);
    const last = sections[sections.length - 1];
    if (last && last.group === group) last.end = index + 1;
    else sections.push({ group, start: index, end: index + 1 });
  });
  return sections;
};

// src/Menus/Combobox.tsx
import { Fragment as Fragment12, jsx as jsx48, jsxs as jsxs40 } from "react/jsx-runtime";
var lastPointer = null;
var lastMoveWasReal = true;
var trackingPointer = false;
var trackPointer = () => {
  if (trackingPointer || typeof window === "undefined") return;
  trackingPointer = true;
  window.addEventListener(
    "mousemove",
    (event) => {
      lastMoveWasReal = lastPointer === null || lastPointer.x !== event.clientX || lastPointer.y !== event.clientY;
      lastPointer = { x: event.clientX, y: event.clientY };
    },
    { capture: true, passive: true }
  );
};
var TAIL_INSET = {
  compact: "5px",
  sm: "8px",
  md: "8px",
  lg: "8px"
};
var GLYPH = {
  compact: { clear: "11px", chev: "11px" },
  sm: { clear: "13px", chev: "14px" },
  md: { clear: "13px", chev: "14px" },
  lg: { clear: "13px", chev: "14px" }
};
var Root12 = styled48.div`
  display: block;
  width: 100%;
`;
var Wrap4 = styled48.div`
  position: relative;
  width: 100%;
`;
var Box4 = styled48.input`
  appearance: none;
  width: 100%;
  box-sizing: border-box;
  height: ${({ $size }) => FIELD_SIZE[$size].height};
  /* One step wider than Select's right gutter: clear button AND chevron. */
  padding: ${({ $size }) => `0 ${COMBO_TRAIL_PAD[$size]} 0 ${FIELD_PAD_X[$size]}`};
  border-radius: ${({ $size }) => FIELD_SIZE[$size].radius};
  border: ${({ $size }) => FIELD_SIZE[$size].border} solid ${FIELD_CHROME.border};
  background: ${FIELD_CHROME.fill};
  font-family: ${fontSans31};
  font-weight: 400;
  font-size: ${({ $size }) => FIELD_SIZE[$size].font};
  line-height: 1;
  color: ${FIELD_CHROME.ink};
  outline: none;
  transition:
    border-color 0.15s ${motion27.easeUi},
    box-shadow 0.15s ${motion27.easeUi},
    background-color 0.15s ${motion27.easeUi};

  &::placeholder {
    color: ${ink38[5]};
  }

  &:hover:not(:disabled):not(:focus):not(.is-open):not(.is-readonly) {
    border-color: ${FIELD_CHROME.borderHover};
  }

  /* The cyan focus LIFTS the fill to white — the card's signature, shared with Input and Select. */
  &:focus,
  &.is-open {
    border-color: ${brand31.cyan};
    background: ${FIELD_CHROME.fillFocus};
    box-shadow: ${({ $size }) => fieldFocusRing($size)};
  }

  &.is-error {
    border-color: ${FIELD_CHROME.errorBorder};
    background: ${FIELD_CHROME.errorFill};
  }
  &.is-error:focus,
  &.is-error.is-open {
    box-shadow: 0 0 0 3px ${FIELD_CHROME.errorRing};
  }

  &:disabled {
    background: ${FIELD_CHROME.disabledFill};
    color: ${ink38[5]};
    cursor: not-allowed;
    border-color: ${FIELD_CHROME.disabledBorder};
  }

  &.is-readonly {
    background: ${FIELD_CHROME.readOnlyFill};
    border-color: ${FIELD_CHROME.readOnlyBorder};
    color: ${FIELD_CHROME.readOnlyInk};
    cursor: default;
  }

  &.on-glass {
    background: ${FIELD_CHROME.glassFill};
    border-color: ${FIELD_CHROME.glassBorder};
    backdrop-filter: ${FIELD_CHROME.glassBlur};
    -webkit-backdrop-filter: ${FIELD_CHROME.glassBlur};
  }
  &.on-glass::placeholder {
    color: ${rgba34(FIELD_CHROME.ink, 0.5)};
  }
  &.on-glass:hover:not(:disabled):not(:focus):not(.is-open):not(.is-readonly) {
    border-color: ${FIELD_CHROME.glassBorderHover};
    background: ${FIELD_CHROME.glassFillHover};
  }
  &.on-glass:focus,
  &.on-glass.is-open {
    background: ${FIELD_CHROME.fillFocus};
    border-color: ${brand31.cyan};
    box-shadow: 0 0 0 3px ${rgba34(brand31.cyan, 0.18)};
  }
  &.on-glass.on-opaque {
    background: ${FIELD_CHROME.glassFillOpaque};
    border-color: ${FIELD_CHROME.glassBorderOpaque};
  }
  &.on-glass.on-opaque:focus,
  &.on-glass.on-opaque.is-open {
    background: ${FIELD_CHROME.fillFocus};
  }

  /* Room for the magnifier: the Search field's own left padding at this size (card 02). */
  &.has-lead {
    padding-left: ${({ $size }) => SEARCH_GEOMETRY[$size].padding.split(" ")[3]};
  }
  /* The magnifier follows the field's focus, as the Search field's does. */
  &:focus ~ .cb-lead {
    color: ${brand31.cyan};
  }

  /* Card 04's rich trigger, picked: room for the disc, the name at 600, and an ellipsis rather
     than text running under the code chip (whose width arrives as --cb-code-w). */
  /* The inset, the disc, card 04's 10px gap. */
  &.has-disc {
    padding-left: ${({ $size }) => `calc(${FIELD_PAD_X[$size]} + ${RICH_DISC} + ${inset8[3]})`};
  }
  &.is-rich-picked {
    font-weight: ${weight19.semibold};
    text-overflow: ellipsis;
  }
  &.has-code {
    padding-right: ${({ $size }) => `calc(${COMBO_TRAIL_PAD[$size]} + var(--cb-code-w, 0px))`};
  }
`;
var Tail = styled48.span`
  position: absolute;
  right: ${({ $size }) => TAIL_INSET[$size]};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: ${inset8[1]};

  .cb-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    color: ${ink38[3]};
    padding: 3px;
    border-radius: ${radius30.sm};
    transition:
      color 0.12s ${motion27.easeUi},
      background 0.12s ${motion27.easeUi};
  }
  /* The tint carries the hover; the glyph darkens rather than going to full cyan, which is
     2.34:1 on this fill and made the control harder to see under the pointer than at rest. */
  .cb-clear:hover {
    color: ${brand31.cyanPress};
    background: ${rgba34(brand31.cyan, 0.08)};
  }
  .cb-clear > svg {
    width: ${({ $size }) => GLYPH[$size].clear};
    height: ${({ $size }) => GLYPH[$size].clear};
    display: block;
  }

  /* The tail is drawn over the input: everything in it but the clear button lets a press fall
     through to the input, so the field's right-hand side — the code chip, the chevron, the gaps —
     still opens the list (and an open list is not blurred shut). */
  pointer-events: none;
  .cb-clear {
    pointer-events: auto;
  }
  .cb-code {
    display: flex;
  }

  .cb-chev {
    display: flex;
    align-items: center;
    color: ${ink38[3]};
    pointer-events: none;
    transition: transform 0.15s ${motion27.easeUi};
  }
  .cb-chev > svg {
    width: ${({ $size }) => GLYPH[$size].chev};
    height: ${({ $size }) => GLYPH[$size].chev};
    display: block;
  }
  &.is-open .cb-chev {
    transform: rotate(180deg);
  }
`;
var Panel3 = styled48.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 20;

  /* Card 04's open-up: flip above the input when the list would not fit below. Without it a
     picker near the foot of a dialog drops its panel straight out of the dialog and onto the
     page, which is what a real render showed. */
  &.open-up {
    top: auto;
    bottom: 100%;
  }
  &.open-up .menu.below-trigger {
    margin-top: 0;
    margin-bottom: 6px;
  }

  /* Butter wash — the system's "attention, not status" tint. Card 04 paints this
     rgba(224,168,74,…), a pre-token butter that matches no --tl-* value; the design source's
     butter is --tl-accent-butter. Bound to the token and recorded in PROVENANCE.md as a
     confirm-or-substitute, the route D-2 and D-4 already took. */
  .menu-item mark {
    background: ${rgba34(accent14.butter, 0.32)};
    color: inherit;
    font-weight: 600;
    border-radius: 2px;
    padding: 0 1px;
  }
  .menu-item.is-selected mark,
  .menu-item.is-highlight mark {
    background: ${rgba34(accent14.butter, 0.4)};
  }
`;
var NotInList = styled48.div`
  display: flex;
  align-items: flex-start;
  gap: ${inset8[2]};
  margin-top: ${inset8[2]};
  /* MONO, per card 04's .cb-note rule — and consistent with the Field helper/error slots this
     note sits beside, which are mono too. */
  font-family: ${fontMono15};
  font-weight: 400;
  font-size: 11px;
  line-height: 1.45;
  color: ${inkOn16.butter};

  .cb-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent14.butter};
    flex: none;
    margin-top: 4px;
  }
`;
var Live = styled48.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  /* The non-deprecated half of the pair; Safari prefers it and plain clip is deprecated in
     CSS Masking. Both are kept so the region stays hidden on every engine we ship to. */
  clip-path: inset(50%);
  white-space: nowrap;
`;
var Lead3 = styled48.span`
  ${searchLeadCss}
`;
var Disc = styled48.span`
  ${richDiscCss}
  position: absolute;
  left: ${({ $size }) => FIELD_PAD_X[$size]};
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  pointer-events: none;
`;
var Chevron2 = /* @__PURE__ */ jsx48("svg", { viewBox: "0 0 12 12", fill: "none", stroke: "currentColor", strokeWidth: 1.5, "aria-hidden": "true", children: /* @__PURE__ */ jsx48("path", { d: "m3 4.5 3 3 3-3" }) });
var Cross = /* @__PURE__ */ jsx48("svg", { viewBox: "0 0 14 14", fill: "none", stroke: "currentColor", strokeWidth: 1.5, "aria-hidden": "true", children: /* @__PURE__ */ jsx48("path", { d: "M3 3l8 8M11 3l-8 8" }) });
var CLIPS = /* @__PURE__ */ new Set(["auto", "scroll", "hidden", "clip", "overlay"]);
var clipBoxOf = (el) => {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const cs = getComputedStyle(node);
    if (CLIPS.has(cs.overflowY) || CLIPS.has(cs.overflow)) {
      const rect = node.getBoundingClientRect();
      return { top: rect.top, bottom: rect.bottom };
    }
    node = node.parentElement;
  }
  return { top: 0, bottom: window.innerHeight };
};
var defaultFilter = (option, query) => option.label.toLowerCase().includes(query.toLowerCase());
var defaultIsExactMatch = (option, query) => option.label.toLowerCase() === query.toLowerCase();
var withMatch = (label, query) => {
  if (!query) return label;
  const at = label.toLowerCase().indexOf(query.toLowerCase());
  if (at < 0) return label;
  return /* @__PURE__ */ jsxs40(Fragment12, { children: [
    label.slice(0, at),
    /* @__PURE__ */ jsx48("mark", { children: label.slice(at, at + query.length) }),
    label.slice(at + query.length)
  ] });
};
var Combobox = ({
  options,
  value,
  onChange,
  onSearch,
  filter = defaultFilter,
  isExactMatch = defaultIsExactMatch,
  defaultQuery,
  maxResults = Infinity,
  commitOnClose = true,
  loading = false,
  error,
  onRetry,
  notInListNote,
  loadingText = "Searching\u2026",
  emptyText = "No matches",
  refusedText = (typed) => /* @__PURE__ */ jsxs40(Fragment12, { children: [
    "\u201C",
    /* @__PURE__ */ jsx48("b", { children: typed }),
    "\u201D isn\u2019t in the list, so nothing was changed. Pick an entry from the list."
  ] }),
  onRefusedChange,
  /*
   * DEFAULTED, unlike the other slots which merely default their wording: a binder that sets a
   * bound and forgets the wording would otherwise truncate in silence — the exact defect the
   * footer exists to replace.
   *
   * ⚠️ The bound itself no longer defaults to 8 (Rob, 2026-09-16). It was a hard SLICE rather
   * than a scroll window — the panel scrolls at 280px, but row nine was never in the DOM — and
   * three of the six call sites inherited it without saying so: 8 of ~195 models in the Azure
   * store, and 8 of the 1,000 rows the catalogue search route returns. An unbounded default means
   * a binder that says nothing shows everything, which is slow and visible, and a binder with an
   * unbounded source has to ask for a bound. The wrong direction for a default is the one where
   * doing nothing is silently incorrect.
   */
  capText = (shown, total) => /* @__PURE__ */ jsxs40(Fragment12, { children: [
    /* @__PURE__ */ jsxs40("b", { children: [
      "Showing ",
      shown,
      " of ",
      total
    ] }),
    " ",
    "\u2014 keep typing"
  ] }),
  countText = (shown, total) => shown === total ? `${total} ${total === 1 ? "result" : "results"}` : `${shown} of ${total} results`,
  retryLabel = "Retry",
  clearLabel = "Clear",
  placeholder,
  size = "md",
  disabled = false,
  readOnly = false,
  required,
  invalid = false,
  onGlass = false,
  onOpaque = false,
  variant: variant2 = "default",
  showCode = false,
  endAction,
  autoHighlight = false,
  searchGlyph = false,
  id,
  labelId,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  className
}) => {
  const [open, setOpen] = React42.useState(false);
  const [query, setQuery] = React42.useState(defaultQuery ?? null);
  const lastSearch = React42.useRef((defaultQuery ?? "").trim());
  const [highlight, setHighlight] = React42.useState(-1);
  React42.useEffect(trackPointer, []);
  const [refused, setRefusedState] = React42.useState(null);
  const [placement, setPlacement] = React42.useState({
    up: false
  });
  const settlingRef = React42.useRef(false);
  const scrollbarPressRef = React42.useRef(false);
  const pressInsideRef = React42.useRef(false);
  const wrapRef = React42.useRef(null);
  const inputRef = React42.useRef(null);
  const panelRef = React42.useRef(null);
  const menuRef = React42.useRef(null);
  const retryRef = React42.useRef(null);
  const reactId = React42.useId();
  const baseId = id ?? reactId;
  const listId = `${baseId}-list`;
  const inert = disabled || readOnly;
  const typed = query ?? "";
  const q = typed.trim();
  const selectedOption = React42.useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  );
  const committedText = selectedOption ? selectedOption.label : value ?? "";
  const displayText = query === null ? committedText : query;
  const richShown = variant2 === "rich" && selectedOption !== void 0 && query === null;
  const discShown = richShown && selectedOption?.icon != null;
  const leadShown = searchGlyph && !discShown;
  const codeText = selectedOption?.meta?.trim() ?? "";
  const codeShown = richShown && showCode && codeText !== "";
  const codeRef = React42.useRef(null);
  const [codeWidth, setCodeWidth] = React42.useState(0);
  React42.useLayoutEffect(() => {
    const node = codeRef.current;
    if (!codeShown || !node) return;
    const read = () => setCodeWidth(node.offsetWidth + parseFloat(inset8[1]));
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, [codeShown, codeText]);
  const matches = React42.useMemo(() => {
    if (onSearch) return options;
    return q ? options.filter((o) => filter(o, q)) : options;
  }, [onSearch, options, q, filter]);
  const ordered = React42.useMemo(() => orderByGroup(matches, options), [matches, options]);
  const shown = React42.useMemo(
    () => Number.isFinite(maxResults) ? ordered.slice(0, maxResults) : ordered,
    [ordered, maxResults]
  );
  const capped = shown.length < matches.length;
  const rows = React42.useMemo(
    () => error != null || loading ? [] : shown.map((option) => ({ option, disabled: option.disabled })),
    [error, loading, shown]
  );
  const sections = React42.useMemo(() => sectionsOf(rows.map((row) => row.option)), [rows]);
  const sectionsKey = sections.map((section) => `${section.group ?? ""}@${section.start}`).join("");
  const actionShown = endAction != null && !loading && error == null;
  const actionIndex = rows.length;
  const navRows = React42.useMemo(
    () => actionShown ? [...rows, {}] : rows,
    [rows, actionShown]
  );
  const rowId = React42.useCallback(
    (index) => actionShown && index === actionIndex ? `${baseId}-end-action` : `${baseId}-opt-${index}`,
    [actionShown, actionIndex, baseId]
  );
  const highlightOriginRef = React42.useRef("operator");
  const close = React42.useCallback((restore = true) => {
    setOpen(false);
    setHighlight(-1);
    if (restore) setQuery(null);
  }, []);
  const setRefused = React42.useCallback(
    (next) => {
      setRefusedState((previous) => {
        if (previous === next) return previous;
        onRefusedChange?.(next);
        return next;
      });
    },
    [onRefusedChange]
  );
  const openList = React42.useCallback(() => {
    if (inert) return;
    setOpen(true);
  }, [inert]);
  const settle = React42.useCallback(
    (explicit = false) => {
      if (!open || settlingRef.current) return;
      const namesCommitted = selectedOption !== void 0 && !onSearch && isExactMatch(selectedOption, q);
      if (onSearch || /*
       * A binder that has opted out of commit-on-close — but NOT for an explicit Enter.
       *
       * ⚠ `settle` serves three gestures: the outside press, the blur, and Enter with no row
       * highlighted. `commitOnClose` is about the first two, where the operator reached for
       * something else and that must not read as consent. Enter is the opposite: it is the
       * operator saying yes, and card 04 makes it the confirm key. Guarding all three broke it —
       * typing an option's exact name and pressing Enter silently abandoned, because typing does
       * not highlight a row (only the arrow keys do), so Enter lands here rather than on
       * `commit(highlight)`. Found by review on the first binder to use the opt-out.
       */
      !commitOnClose && !explicit || loading || error != null || options.length === 0 || q === "" || q.toLowerCase() === committedText.toLowerCase() || namesCommitted) {
        close(!onSearch);
        return;
      }
      const hits = options.filter((o) => !o.onPick && isExactMatch(o, q));
      if (hits.length === 1 && hits[0].disabled) {
        close();
        return;
      }
      if (hits.length === 1) {
        settlingRef.current = true;
        onChange?.(hits[0].value);
        setQuery(null);
        setOpen(false);
        setHighlight(-1);
        setRefused(null);
        return;
      }
      close();
      setRefused(q);
    },
    [
      open,
      onSearch,
      commitOnClose,
      loading,
      error,
      q,
      committedText,
      options,
      rows,
      onChange,
      close,
      setRefused,
      isExactMatch,
      selectedOption
    ]
  );
  const commit = React42.useCallback(
    (index) => {
      const row = rows[index];
      if (!row) {
        close();
        return;
      }
      if (row.option.disabled) return;
      if (row.option.onPick) {
        const text = row.option.label;
        lastSearch.current = text.trim();
        row.option.onPick();
        setQuery(text);
        setHighlight(-1);
        setRefused(null);
        return;
      }
      onChange?.(row.option.value);
      setQuery(null);
      setOpen(false);
      setHighlight(-1);
      setRefused(null);
    },
    [rows, onChange, close, setRefused]
  );
  const clear = React42.useCallback(() => {
    onChange?.(null);
    setQuery("");
    setHighlight(-1);
    setRefused(null);
    inputRef.current?.focus();
  }, [onChange, setRefused]);
  const rowsKey = React42.useMemo(() => rows.map((row) => row.option.value).join(""), [rows]);
  const navKey = actionShown ? `${rowsKey}action` : rowsKey;
  const lastNavKey = React42.useRef(navKey);
  React42.useEffect(() => {
    if (lastNavKey.current === navKey) return;
    lastNavKey.current = navKey;
    setHighlight(-1);
  }, [navKey]);
  const autoKey = React42.useMemo(
    () => `${navKey}${rows.map((row) => row.disabled ? "1" : "0").join("")}`,
    [navKey, rows]
  );
  const staleAutoKeyRef = React42.useRef(null);
  React42.useEffect(() => {
    if (staleAutoKeyRef.current !== null && staleAutoKeyRef.current !== autoKey) {
      staleAutoKeyRef.current = null;
    }
    if (!autoHighlight || !open) return;
    highlightOriginRef.current = "auto";
    if (staleAutoKeyRef.current !== null) {
      setHighlight(-1);
      return;
    }
    setHighlight(firstEnabled(rows, 1));
  }, [autoHighlight, open, autoKey]);
  React42.useEffect(() => {
    if (!open || highlight < 0) return;
    scrollHighlightIntoView(rowId(highlight));
  }, [open, highlight, rowId]);
  const runAction = () => {
    endAction?.onSelect();
    setHighlight(-1);
  };
  const measure = React42.useCallback(() => {
    const input = inputRef.current;
    const wrap = wrapRef.current;
    const menu = menuRef.current;
    if (!input || !wrap || !menu || typeof window === "undefined" || menu.offsetHeight === 0) {
      return;
    }
    const needed = Math.min(menu.scrollHeight, MENU_MAX_HEIGHT);
    const rect = input.getBoundingClientRect();
    const clip = clipBoxOf(wrap);
    const GAP2 = 6;
    const below = clip.bottom - rect.bottom - GAP2;
    const above = rect.top - clip.top - GAP2;
    const up = needed > below && above > below;
    const room = Math.max(up ? above : below, 0);
    const maxHeight = needed > room ? room : void 0;
    setPlacement(
      (prev) => prev.up === up && prev.maxHeight === maxHeight ? prev : { up, maxHeight }
    );
  }, []);
  React42.useLayoutEffect(() => {
    if (!open) {
      setPlacement(
        (prev) => prev.up === false && prev.maxHeight === void 0 ? prev : { up: false }
      );
      return void 0;
    }
    measure();
    let frame = 0;
    const measureSoon = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    window.addEventListener("resize", measureSoon);
    document.addEventListener("scroll", measureSoon, true);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("resize", measureSoon);
      document.removeEventListener("scroll", measureSoon, true);
    };
  }, [open, measure, rowsKey, sectionsKey, variant2, loading, error != null]);
  React42.useEffect(() => {
    if (!open) return void 0;
    scrollbarPressRef.current = false;
    const onDownCapture = (event) => {
      pressInsideRef.current = !!wrapRef.current?.contains(event.target);
    };
    const onDown = (event) => {
      if (pressInsideRef.current || wrapRef.current?.contains(event.target)) return;
      if (isScrollbarPress(event)) {
        scrollbarPressRef.current = true;
        return;
      }
      scrollbarPressRef.current = false;
      if (event.button !== 0) {
        close();
        return;
      }
      settle();
    };
    const onUp = () => {
      scrollbarPressRef.current = false;
    };
    document.addEventListener("mousedown", onDownCapture, true);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp, true);
    return () => {
      document.removeEventListener("mousedown", onDownCapture, true);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp, true);
      scrollbarPressRef.current = false;
    };
  }, [open, settle, close]);
  const listingKey = React42.useMemo(
    () => (
      // LABEL and `disabled` as well as `value`: the match is made on the label and refused on
      // the label, so a listing that refreshes with the same ids under different labels has
      // changed the very thing the refusal was a statement about.
      `${loading}|${error != null}|${options.map((o) => `${o.value}${o.label}${o.disabled ? 1 : 0}`).join("")}`
    ),
    [loading, error, options]
  );
  const lastListingKey = React42.useRef(listingKey);
  React42.useEffect(() => {
    if (lastListingKey.current === listingKey) return;
    lastListingKey.current = listingKey;
    setRefused(null);
  }, [listingKey, setRefused]);
  React42.useEffect(() => {
    if (!open) settlingRef.current = false;
  }, [open]);
  React42.useEffect(() => {
    if (!onSearch) return;
    if (lastSearch.current === q) return;
    lastSearch.current = q;
    onSearch(q);
  }, [q, onSearch]);
  const onKeyDown = (event) => {
    if (inert) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!open) openList();
        else {
          highlightOriginRef.current = "operator";
          setHighlight((h) => nextEnabled(navRows, h, 1, true));
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!open) openList();
        else {
          highlightOriginRef.current = "operator";
          setHighlight((h) => nextEnabled(navRows, h < 0 ? navRows.length : h, -1, true));
        }
        break;
      case "Home":
        if (open) {
          event.preventDefault();
          highlightOriginRef.current = "operator";
          const first = firstEnabled(rows, 1);
          setHighlight(first >= 0 ? first : firstEnabled(navRows, 1));
        }
        break;
      case "End":
        if (open) {
          event.preventDefault();
          highlightOriginRef.current = "operator";
          setHighlight(firstEnabled(navRows, -1));
        }
        break;
      case "Enter":
        if (open) {
          event.preventDefault();
          if (event.repeat && (highlightOriginRef.current === "auto" || highlight < 0)) break;
          if (actionShown && highlight === actionIndex) runAction();
          else if (highlight >= 0) commit(highlight);
          else if (error != null && onRetry) onRetry();
          else settle(true);
        }
        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          close();
        }
        break;
      case "Tab":
        if (open && highlight >= 0 && highlight < rows.length && highlightOriginRef.current === "operator" && // A navigating row is never committed by leaving (`ComboboxOption.onPick`).
        !rows[highlight].option.onPick) {
          commit(highlight);
        } else if (open && error != null && onRetry && !event.shiftKey) {
          event.preventDefault();
          retryRef.current?.focus();
        } else if (open) {
          settle();
        }
        break;
      default:
        break;
    }
  };
  const announcement = !open ? refused !== null ? refusedText(refused) : "" : loading ? loadingText : error != null ? error : rows.length === 0 ? emptyText : countText(shown.length, matches.length);
  const boxClasses = [
    "cb-input",
    `sz-${size}`,
    open ? "is-open" : "",
    invalid ? "is-error" : "",
    readOnly ? "is-readonly" : "",
    onGlass ? "on-glass" : "",
    onGlass && onOpaque ? "on-opaque" : "",
    richShown ? "is-rich-picked" : "",
    discShown ? "has-disc" : "",
    leadShown ? "has-lead" : "",
    codeShown ? "has-code" : ""
  ].filter(Boolean).join(" ");
  const showClear = !inert && (displayText !== "" || value != null);
  const showNotInList = notInListNote != null && !onSearch && value != null && value !== "" && selectedOption === void 0;
  const noteBody = refused !== null ? refusedText(refused) : showNotInList ? notInListNote : null;
  const noteId = `${baseId}-note`;
  const codeId = `${baseId}-code`;
  const describedBy = [noteBody !== null ? noteId : null, codeShown ? codeId : null, ariaDescribedBy].filter(Boolean).join(" ") || void 0;
  const renderRow = (row, index) => /* @__PURE__ */ jsx48(
    MenuItem,
    {
      id: `${baseId}-opt-${index}`,
      role: "option",
      "aria-selected": row.option.value === value,
      icon: row.option.icon,
      picture: row.option.picture,
      meta: row.option.meta,
      trailing: row.option.trailing,
      selected: row.option.value === value,
      highlighted: index === highlight,
      disabled: row.option.disabled,
      onMouseDown: (event) => {
        event.preventDefault();
        commit(index);
      },
      onMouseMove: () => {
        if (row.option.disabled || !lastMoveWasReal) return;
        highlightOriginRef.current = "operator";
        if (index !== highlight) setHighlight(index);
      },
      "aria-label": [
        row.option.label,
        row.option.meta,
        // Spoken only when the slot shows something — the same rule MenuItem uses to render it,
        // so a reader never hears a status the screen does not draw.
        hasTrailingContent(row.option.trailing) ? row.option.trailingText?.trim() : void 0
      ].filter(Boolean).join(", "),
      children: withMatch(row.option.label, q)
    },
    row.option.value
  );
  const actionItem = actionShown && endAction ? /* @__PURE__ */ jsx48(
    MenuItem,
    {
      id: `${baseId}-end-action`,
      role: "option",
      "aria-selected": false,
      className: "cb-end-action",
      icon: endAction.icon,
      meta: endAction.meta,
      highlighted: highlight === actionIndex,
      onMouseDown: (event) => {
        event.preventDefault();
        runAction();
      },
      onMouseMove: () => {
        if (!lastMoveWasReal) return;
        highlightOriginRef.current = "operator";
        if (highlight !== actionIndex) setHighlight(actionIndex);
      },
      "aria-label": [endAction.label, endAction.meta?.trim()].filter(Boolean).join(", "),
      children: endAction.label
    },
    "end-action"
  ) : null;
  const actionGroup = endAction ? groupOf(endAction) : null;
  const sameCaption = actionGroup !== null && sections[sections.length - 1]?.group === actionGroup;
  const actionJoinsLast = actionItem !== null && sameCaption && !capped;
  let actionSection = null;
  if (actionItem !== null && !actionJoinsLast) {
    actionSection = actionGroup !== null && !sameCaption ? /* @__PURE__ */ jsx48(MenuGroup, { label: actionGroup, children: actionItem }, "g-end-action") : actionItem;
  }
  return /* @__PURE__ */ jsxs40(Root12, { ref: wrapRef, className: ["cb-field", className ?? ""].filter(Boolean).join(" "), children: [
    /* @__PURE__ */ jsxs40(Wrap4, { className: "cb", children: [
      /* @__PURE__ */ jsx48(
        Box4,
        {
          ref: inputRef,
          id: baseId,
          type: "text",
          className: boxClasses,
          style: codeShown ? { "--cb-code-w": `${codeWidth}px` } : void 0,
          $size: size,
          value: displayText,
          placeholder,
          disabled,
          readOnly,
          required,
          autoComplete: "off",
          onChange: (event) => {
            setQuery(event.target.value);
            setRefused(null);
            if (onSearch && autoHighlight) {
              staleAutoKeyRef.current = autoKey;
              if (highlightOriginRef.current === "auto") setHighlight(-1);
            }
            if (!open) setOpen(true);
          },
          onFocus: openList,
          onClick: openList,
          onKeyDown,
          onBlur: (event) => {
            if (!open) return;
            const next = event.relatedTarget;
            if (next && wrapRef.current?.contains(next)) return;
            if (next === null && document.activeElement === inputRef.current) return;
            if (scrollbarPressRef.current) {
              scrollbarPressRef.current = false;
              inputRef.current?.focus({ preventScroll: true });
              return;
            }
            settle();
          },
          role: "combobox",
          "aria-expanded": open,
          "aria-autocomplete": "list",
          "aria-controls": open ? listId : void 0,
          "aria-label": ariaLabel,
          "aria-labelledby": ariaLabelledBy,
          "aria-describedby": describedBy,
          "aria-invalid": invalid || void 0,
          "aria-readonly": readOnly || void 0,
          "aria-activedescendant": open && highlight >= 0 ? rowId(highlight) : void 0
        }
      ),
      leadShown && /* @__PURE__ */ jsx48(Lead3, { $size: size, className: "cb-lead", "aria-hidden": "true", children: /* @__PURE__ */ jsx48(Icon, { name: "magnifying-glass", size: SEARCH_LEAD_ICON[size] }) }),
      discShown && /* @__PURE__ */ jsx48(Disc, { $size: size, className: "cb-disc", "aria-hidden": "true", children: selectedOption?.icon }),
      /* @__PURE__ */ jsxs40(Tail, { $size: size, className: ["cb-tail", open ? "is-open" : ""].filter(Boolean).join(" "), children: [
        codeShown && /* @__PURE__ */ jsx48("span", { ref: codeRef, className: "cb-code", id: codeId, children: /* @__PURE__ */ jsx48(Tag, { tone: "code", surface: onGlass ? "on-glass" : "on-solid", children: codeText }) }),
        showClear && /* @__PURE__ */ jsx48(
          "button",
          {
            type: "button",
            className: "cb-clear",
            "aria-label": clearLabel,
            onMouseDown: (event) => event.preventDefault(),
            onClick: clear,
            children: Cross
          }
        ),
        /* @__PURE__ */ jsx48("span", { className: "cb-chev", children: Chevron2 })
      ] }),
      open && /* @__PURE__ */ jsx48(Panel3, { ref: panelRef, className: placement.up ? "open-up" : void 0, children: /* @__PURE__ */ jsx48(
        Menu,
        {
          belowTrigger: true,
          variant: variant2,
          id: listId,
          ref: menuRef,
          role: "listbox",
          "aria-label": ariaLabel,
          "aria-labelledby": ariaLabel ? void 0 : ariaLabelledBy ?? labelId,
          "aria-busy": loading,
          style: placement.maxHeight ? { maxHeight: `${placement.maxHeight}px` } : void 0,
          children: loading ? /* @__PURE__ */ jsx48(MenuState, { busy: true, role: "presentation", children: loadingText }) : error != null ? /* @__PURE__ */ jsx48(
            MenuState,
            {
              tone: "error",
              role: "presentation",
              action: onRetry ? /* @__PURE__ */ jsx48(
                "button",
                {
                  ref: retryRef,
                  type: "button",
                  onMouseDown: (event) => event.preventDefault(),
                  onClick: onRetry,
                  onKeyDown: (event) => {
                    if (event.key === "Escape") {
                      event.preventDefault();
                      inputRef.current?.focus();
                      close();
                    } else if (event.key === "Tab" && !event.shiftKey) {
                      close();
                    } else if (event.key === "Tab" && event.shiftKey) {
                      event.preventDefault();
                      inputRef.current?.focus();
                    }
                  },
                  children: retryLabel
                }
              ) : void 0,
              children: error
            }
          ) : rows.length === 0 ? /* @__PURE__ */ jsxs40(Fragment12, { children: [
            /* @__PURE__ */ jsx48(MenuState, { role: "presentation", children: emptyText }),
            actionSection
          ] }) : /* @__PURE__ */ jsxs40(Fragment12, { children: [
            sections.map((section, sectionIndex) => {
              const sectionRows = rows.slice(section.start, section.end).map((row, offset) => renderRow(row, section.start + offset));
              if (actionJoinsLast && actionItem && sectionIndex === sections.length - 1) {
                sectionRows.push(actionItem);
              }
              return section.group === null ? /* @__PURE__ */ jsx48(React42.Fragment, { children: sectionRows }, "u") : /* @__PURE__ */ jsx48(MenuGroup, { label: section.group, children: sectionRows }, `g:${section.group}`);
            }),
            capped && /* @__PURE__ */ jsx48(MenuCap, { children: capText(shown.length, matches.length) }),
            actionSection
          ] })
        }
      ) }),
      /* @__PURE__ */ jsx48(Live, { "aria-live": "polite", className: "cb-live", children: announcement })
    ] }),
    noteBody !== null && /* @__PURE__ */ jsxs40(NotInList, { id: noteId, className: "cb-note", children: [
      /* @__PURE__ */ jsx48("span", { className: "cb-dot", "aria-hidden": "true" }),
      /* @__PURE__ */ jsx48("span", { children: noteBody })
    ] })
  ] });
};

// src/Menus/MultiSelect.tsx
import React43 from "react";
import styled49 from "styled-components";
import {
  ink as ink39,
  brand as brand32,
  fontSans as fontSans32,
  motion as motion28,
  rgba as rgba35,
  semanticColor as semanticColor15,
  typeRole as typeRole9,
  space as space13
} from "@repo/design-tokens";
import { jsx as jsx49, jsxs as jsxs41 } from "react/jsx-runtime";
function toggleSelection(value, option, maxSelected, minSelected = 0) {
  if (value.includes(option)) {
    const next = value.filter((v) => v !== option);
    if (new Set(next).size < minSelected) return value;
    return next;
  }
  if (maxSelected !== void 0 && new Set(value).size >= maxSelected) return value;
  return [...value, option];
}
var Wrap5 = styled49.div`
  position: relative;
  width: 100%;
`;
var Trigger2 = styled49.button`
  appearance: none;
  /* ITS OWN POSITIONING CONTEXT, which Select's trigger does not need and this one does.
     .ms-chev is absolutely positioned at top: 50%. Without this it resolves against Wrap,
     which here also contains the HINT — so the chevron centres on trigger-plus-hint and drifts
     toward the trigger's bottom border. Select's identical rule is correct only because its
     Wrap holds nothing that contributes height in the closed state. The default one-gym
     dashboard always renders a hint, so this was the DEFAULT state, not an edge case. */
  position: relative;
  width: 100%;
  box-sizing: border-box;
  height: ${({ $size }) => FIELD_SIZE[$size].height};
  padding: ${({ $size }) => FIELD_SIZE[$size].padding};
  border-radius: ${({ $size }) => FIELD_SIZE[$size].radius};
  border: ${({ $size }) => FIELD_SIZE[$size].border} solid ${FIELD_CHROME.border};
  background: ${FIELD_CHROME.fill};
  font-family: ${fontSans32};
  font-weight: 400;
  font-size: ${({ $size }) => FIELD_SIZE[$size].font};
  line-height: 1;
  color: ${FIELD_CHROME.ink};
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  outline: none;
  transition:
    border-color 0.15s ${motion28.easeUi},
    box-shadow 0.15s ${motion28.easeUi},
    background-color 0.15s ${motion28.easeUi};

  .ms-value {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ms-chev {
    position: absolute;
    right: ${({ $size }) => FIELD_TRAIL_INSET[$size]};
    top: 50%;
    transform: translateY(-50%);
    color: ${ink39[3]};
    pointer-events: none;
    display: flex;
    transition: transform 0.15s ${motion28.easeUi};
  }
  .ms-chev > svg {
    width: ${({ $size }) => CHEVRON2[$size]};
    height: ${({ $size }) => CHEVRON2[$size]};
    display: block;
  }

  &:hover:not(:disabled):not(.is-open) {
    border-color: ${FIELD_CHROME.borderHover};
  }

  /* The cyan focus LIFTS the fill to white — the card's signature, shared with Select. */
  &:focus-visible,
  &.is-open {
    border-color: ${brand32.cyan};
    background: ${FIELD_CHROME.fillFocus};
    box-shadow: ${({ $size }) => fieldFocusRing($size)};
  }

  &.is-open .ms-chev {
    transform: translateY(-50%) rotate(180deg);
  }

  /* Over cap is an ERROR state on the control, not a silent one: the chrome says so, and
     aria-invalid says so to a screen reader. */
  &.is-error {
    border-color: ${FIELD_CHROME.errorBorder};
    background: ${FIELD_CHROME.errorFill};
  }
  &.is-error:focus-visible,
  &.is-error.is-open {
    box-shadow: 0 0 0 3px ${FIELD_CHROME.errorRing};
  }

  &:disabled {
    background: ${FIELD_CHROME.disabledFill};
    color: ${ink39[5]};
    cursor: not-allowed;
    border-color: ${FIELD_CHROME.disabledBorder};
  }

  &.on-glass {
    background: ${FIELD_CHROME.glassFill};
    border-color: ${FIELD_CHROME.glassBorder};
    backdrop-filter: ${FIELD_CHROME.glassBlur};
    -webkit-backdrop-filter: ${FIELD_CHROME.glassBlur};
  }
  &.on-glass:hover:not(:disabled):not(.is-open) {
    border-color: ${FIELD_CHROME.glassBorderHover};
    background: ${FIELD_CHROME.glassFillHover};
  }
  &.on-glass:focus-visible,
  &.on-glass.is-open {
    background: ${FIELD_CHROME.fillFocus};
    border-color: ${brand32.cyan};
    box-shadow: 0 0 0 3px ${rgba35(brand32.cyan, 0.18)};
  }
  &.on-glass.on-opaque {
    background: ${FIELD_CHROME.glassFillOpaque};
    border-color: ${FIELD_CHROME.glassBorderOpaque};
  }
  &.on-glass.on-opaque:focus-visible,
  &.on-glass.on-opaque.is-open {
    background: ${FIELD_CHROME.fillFocus};
  }
`;
var CHEVRON2 = {
  compact: "11px",
  sm: "14px",
  md: "14px",
  lg: "14px"
};
var Panel4 = styled49.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 20;
`;
var OptionRow = styled49(MenuItem)`
  grid-template-columns: 1fr auto;
  padding: 6px 8px;

  .menu-ic {
    display: none;
  }

  .menu-lbl {
    white-space: normal;
    overflow: visible;
  }

  /* The checkbox fills the row, so the whole row is the label's hit area rather than just
     the 18px box and its text. */
  .altcb {
    width: 100%;
    align-items: flex-start;
  }
`;
var OptionNote = styled49.span`
  display: block;
  font: ${typeRole9.captionCompact.fontWeight} ${typeRole9.captionCompact.fontSize} /
    ${typeRole9.captionCompact.lineHeight} ${fontSans32};
  color: ${ink39[3]};
  margin-top: ${space13[1]};
`;
var Hint2 = styled49.div`
  font: ${FIELD_LABEL.helpFont} ${fontSans32};
  color: ${FIELD_LABEL.helpInk};
  margin-top: ${FIELD_LABEL.gapCompact};

  &.is-error {
    font: ${FIELD_LABEL.errFont} ${fontSans32};
    color: ${semanticColor15.errorStrong};
  }
`;
var VisuallyHidden = styled49.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;
var Chevron3 = /* @__PURE__ */ jsx49("svg", { viewBox: "0 0 12 12", fill: "none", stroke: "currentColor", strokeWidth: 1.5, "aria-hidden": "true", children: /* @__PURE__ */ jsx49("path", { d: "m3 4.5 3 3 3-3" }) });
var MultiSelect = ({
  options,
  value,
  onChange,
  maxSelected,
  minSelected = 0,
  emptyLabel = "All",
  capHint,
  minHint,
  overCapHint,
  summary,
  size = "md",
  disabled = false,
  onGlass = false,
  onOpaque = false,
  ariaLabel,
  ariaLabelledBy,
  className
}) => {
  const [open, setOpen] = React43.useState(false);
  const wrapRef = React43.useRef(null);
  const triggerRef = React43.useRef(null);
  const rowRefs = React43.useRef([]);
  const baseId = React43.useId();
  const listId = `${baseId}-list`;
  const hintId = `${baseId}-hint`;
  const labelId = `${baseId}-label`;
  const valueId = `${baseId}-value`;
  const selectedSet = React43.useMemo(() => new Set(value), [value]);
  const selectedOptions = React43.useMemo(
    () => options.filter((o) => selectedSet.has(o.value)),
    [options, selectedSet]
  );
  const distinct = selectedSet.size;
  const cap = maxSelected === void 0 ? void 0 : Math.max(1, Math.floor(maxSelected));
  const floor = Math.max(0, Math.floor(minSelected));
  const atMin = floor > 0 && distinct <= floor;
  const atCap = cap !== void 0 && distinct >= cap;
  const overCap = cap !== void 0 && distinct > cap;
  const close = React43.useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);
  const openPanel = React43.useCallback(() => {
    if (disabled) return;
    setOpen(true);
  }, [disabled]);
  React43.useEffect(() => {
    if (!open) return;
    if (wrapRef.current?.contains(document.activeElement)) return;
    const first = rowRefs.current.find((el) => el && !el.disabled);
    first?.focus();
  }, [open, options]);
  React43.useEffect(() => {
    if (!open) return void 0;
    const onDown = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (isScrollbarPress(event)) {
        triggerRef.current?.focus({ preventScroll: true });
        return;
      }
      close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);
  const focusRow = (from, step) => {
    const rows = rowRefs.current;
    for (let i = from + step; i >= 0 && i < rows.length; i += step) {
      const el = rows[i];
      if (el && !el.disabled) {
        el.focus();
        const row = typeof el.closest === "function" ? el.closest(".ms-option") : null;
        const target = row ?? el;
        if (typeof target.scrollIntoView === "function") {
          target.scrollIntoView({ block: "nearest" });
        }
        return;
      }
    }
  };
  const focusEnd = (step) => {
    const rows = rowRefs.current;
    const start = step === 1 ? -1 : rows.length;
    focusRow(start, step);
  };
  const onTriggerKeyDown = (event) => {
    if (disabled) return;
    switch (event.key) {
      case "ArrowDown":
      case "Enter":
      case " ":
        event.preventDefault();
        if (!open) openPanel();
        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          close();
        }
        break;
      default:
        break;
    }
  };
  const onRowKeyDown = (index) => (event) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusRow(index, 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusRow(index, -1);
        break;
      case "Home":
        event.preventDefault();
        focusEnd(1);
        break;
      case "End":
        event.preventDefault();
        focusEnd(-1);
        break;
      case "Escape":
        event.preventDefault();
        close();
        break;
      case "Tab":
        triggerRef.current?.focus();
        setOpen(false);
        break;
      default:
        break;
    }
  };
  const soleLabel = distinct === 1 ? selectedOptions[0]?.label : void 0;
  const label = summary ? summary(selectedOptions, value) : distinct === 0 ? emptyLabel : soleLabel ?? `${distinct} selected`;
  const hint = disabled ? void 0 : overCap ? (
    // `maxSelected` is provably defined here — `overCap` is `maxSelected !== undefined &&
    // …` — so the first cut's inner re-test was an unreachable branch that also forced the
    // whole expression to be typed as possibly-undefined.
    overCapHint ?? `${distinct} selected, but only ${cap} can be compared at once. Remove ${distinct - cap} to continue.`
  ) : (
    // ⚠️ `atMin` OUTRANKS `atCap` when they collide. At `cap: 1` both are true, and the
    // cap hint ("clear one to add another") is an instruction the reader cannot act on,
    // because the sole member is inert. The binding constraint is the one to state.
    atMin ? minHint ?? (floor === 1 ? "At least one must stay selected." : `At least ${floor} must stay selected.`) : atCap ? capHint ?? `Up to ${cap} at once. Clear one to add another.` : void 0
  );
  const triggerClasses = [
    "ms-trigger",
    `sz-${size}`,
    open ? "is-open" : "",
    overCap ? "is-error" : "",
    onGlass ? "on-glass" : "",
    onGlass && onOpaque ? "on-opaque" : ""
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsxs41(Wrap5, { ref: wrapRef, className: ["ms", className ?? ""].filter(Boolean).join(" "), children: [
    /* @__PURE__ */ jsxs41(
      Trigger2,
      {
        ref: triggerRef,
        type: "button",
        className: triggerClasses,
        $size: size,
        disabled,
        onClick: () => open ? close() : openPanel(),
        onKeyDown: onTriggerKeyDown,
        "aria-expanded": open,
        "aria-controls": open ? listId : void 0,
        "aria-labelledby": [ariaLabelledBy ?? labelId, valueId].join(" "),
        "aria-describedby": hint ? hintId : void 0,
        "aria-invalid": overCap || void 0,
        children: [
          ariaLabelledBy ? null : /* @__PURE__ */ jsx49(VisuallyHidden, { id: labelId, children: ariaLabel }),
          /* @__PURE__ */ jsx49("span", { className: "ms-value", id: valueId, children: label }),
          /* @__PURE__ */ jsx49("span", { className: "ms-chev", children: Chevron3 })
        ]
      }
    ),
    open ? /* @__PURE__ */ jsx49(Panel4, { children: /* @__PURE__ */ jsx49(
      Menu,
      {
        belowTrigger: true,
        id: listId,
        role: "group",
        "aria-label": ariaLabel,
        children: options.map((option, index) => {
          const checked = selectedSet.has(option.value);
          const inert = option.disabled === true || checked && atMin || !checked && atCap || disabled;
          return /* @__PURE__ */ jsx49(OptionRow, { className: "ms-option", disabled: inert, children: /* @__PURE__ */ jsxs41(
            Checkbox,
            {
              ref: (el) => {
                rowRefs.current[index] = el;
              },
              surface: "on-solid",
              checked,
              disabled: inert,
              onChange: () => {
                const next = toggleSelection(value, option.value, cap, floor);
                if (next !== value) onChange(next);
              },
              onKeyDown: onRowKeyDown(index),
              children: [
                option.label,
                option.note != null ? /* @__PURE__ */ jsx49(OptionNote, { children: option.note }) : null
              ]
            }
          ) }, option.value);
        })
      }
    ) }) : null,
    /* @__PURE__ */ jsx49(
      Hint2,
      {
        id: hintId,
        role: "status",
        "aria-live": "polite",
        "aria-atomic": "true",
        className: overCap ? "is-error" : void 0,
        children: hint ?? ""
      }
    )
  ] });
};

// src/Overlays/Modal.tsx
import React45 from "react";
import { createPortal as createPortal2 } from "react-dom";
import styled51, { css as css23, keyframes as keyframes3 } from "styled-components";
import {
  brand as brand33,
  glassCard as glassCard4,
  ink as ink40,
  inset as inset9,
  icon as iconSize,
  motion as motion29,
  radius as radius31,
  rgba as rgba36,
  shadow as shadow16,
  space as space14,
  surface as surface26,
  zIndex as zIndex3,
  fontMono as fontMono16,
  fontSans as fontSans33
} from "@repo/design-tokens";

// src/Overlays/Scrim.tsx
import React44 from "react";
import styled50 from "styled-components";
import { semantic as semantic9, zIndex as zIndex2 } from "@repo/design-tokens";
import { jsx as jsx50 } from "react/jsx-runtime";
var StyledScrim = styled50.div`
  position: absolute;
  inset: 0;
  z-index: ${zIndex2.overlay};
  background: ${semantic9.surface.scrim.background};
  backdrop-filter: ${semantic9.surface.scrim.backdropFilter};
  -webkit-backdrop-filter: ${semantic9.surface.scrim.backdropFilter};
  pointer-events: ${({ onClick }) => onClick ? "auto" : "none"};
`;
var Scrim = React44.forwardRef(function Scrim2(props, ref) {
  const { onClick, className, ...rest } = props;
  return /* @__PURE__ */ jsx50(
    StyledScrim,
    {
      ref,
      className: ["tl-scrim", className ?? ""].filter(Boolean).join(" "),
      onClick,
      ...rest
    }
  );
});

// src/Overlays/Modal.tsx
import { jsx as jsx51, jsxs as jsxs42 } from "react/jsx-runtime";
var WIDTH = {
  // Drawn by card 08.
  sm: "360px",
  md: "440px",
  lg: "520px",
  // Added by Rob 2026-09-09, continuing the card's own 80px step.
  xl: "600px",
  "2xl": "680px",
  "3xl": "760px",
  // Drawn by the twin-details-page design's frame 08 (Versions), off the ramp. See the docblock.
  "4xl": "900px",
  // Added by Rob 2026-09-12: the viewport minus `space[6]` each side. See the docblock.
  workspace: `calc(100vw - ${space14[12]})`
};
var TITLE_TYPE = "600 16px/1.3";
var BODY_TYPE2 = "400 14px/1.55";
var LEDE_TYPE = "500 14px/1.5";
var META_TYPE2 = "400 11px/1.4";
var GLASS_BORDER_WIDTH2 = "1.5px";
var CLOSE_SIZE4 = "26px";
var CLOSE_GLYPH_SIZE3 = "11px";
var DROP_ALPHA = 0.22;
var FROST_ALPHA = 0.55;
var enter = keyframes3`
  from {
    opacity: 0;
    transform: translateY(4px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;
var openStack = [];
var hasDom2 = () => typeof document !== "undefined" && !!document.body;
var bodyOverflowBeforeFirst = null;
var inerted = [];
var isTopmost = (id) => openStack[openStack.length - 1] === id;
function applyBackgroundInert(portalRoot) {
  if (inerted.length > 0 || typeof document === "undefined") return;
  const siblings = Array.from(document.body.children);
  for (const el of siblings) {
    if (portalRoot && (el === portalRoot || el.contains(portalRoot))) continue;
    if (el.hasAttribute(LIVE_LAYER_ATTR)) continue;
    if (el.hasAttribute("inert") || el.getAttribute("aria-hidden") === "true") continue;
    el.setAttribute("inert", "");
    el.setAttribute("aria-hidden", "true");
    inerted.push(el);
  }
}
function releaseBackgroundInert() {
  for (const el of inerted) {
    el.removeAttribute("inert");
    el.removeAttribute("aria-hidden");
  }
  inerted = [];
}
var StyledRoot2 = styled51.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space14[4]};
  /*
   * The 'modal' step ON THE ROOT, not only on the surface. 'position: fixed' makes this a stacking
   * context, so the scrim's and the surface's steps below are scoped to it and order correctly —
   * but with z-index 'auto' the context itself paints at level 0 among the page's positioned
   * elements, so ANY page element with a positive z-index painted over every dialog. Measured
   * 2026-10-16 (twin-details-page-versions): the Floor Plan's camera markers (z-index 1–3) drew on
   * top of a dialog opened over the plan. The step here places the whole dialog at 'modal' in the
   * page's own context; the toast stack and portaled popovers ('popover', 60) stay above it.
   */
  z-index: ${zIndex3.modal};
`;
var surfaceCss = css23`
  position: relative;
  z-index: ${zIndex3.modal};
  max-width: 100%;
  max-height: 100%;
  /*
   * 'hidden', as the card's own '.tl-dialog' rule has it — R1 and AC3 both name it. The first
   * version of this file used 'hidden auto', making the SURFACE the scroller, which review
   * caught: a tall dialog then scrolled its tone ribbon and its footer out of view, which the
   * card never draws. The body slot scrolls instead (see '.tl-modal-body'), so the ribbon and
   * the actions stay put and the surface keeps the card's value.
   */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  isolation: isolate;
  outline: none;
  font-family: ${fontSans33};
  background: ${rgba36(surface26[0], 0.65)};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${GLASS_BORDER_WIDTH2} solid ${rgba36(surface26[0], 0.85)};
  border-radius: ${radius31.xl};
  box-shadow:
    0 0 14px 2px rgba(${glassCard4.frost}, ${FROST_ALPHA}),
    0 24px 60px rgba(0, 0, 0, ${DROP_ALPHA}),
    inset 0 1px 0 ${rgba36(surface26[0], 0.6)};
  animation: ${enter} ${motion29.durFast} ${motion29.easeUi};

  /*
   * Diagonal gloss — bound from 'shadow.glossOverlay' ('--tl-gloss-overlay'), which holds this
   * gradient byte-for-byte and was bound NOWHERE in the repo until now.
   *
   * The first version hand-wrote the gradient and argued for not sharing 'Card''s gloss mixin.
   * Review took that argument apart and it was half wrong: a PARAMETERLESS mixin would indeed
   * change one of them, but a parameterised one would not, so that half does not stand. What does
   * stand is the charter: 'Card' adds a z-index scheme, this adds a blend mode, and 'Card' still
   * writes its gradient as literals rather than tokens — so converging means EDITING 'Card', a
   * behaviour-preserving refactor that cannot ride an additive PR. 'Popover' (2026-10-12) binds the
   * same token rather than adding a third gradient, so what remains open is 'Card''s literals alone.
   * Meanwhile the VALUE was already a token, which needs neither.
   */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: ${shadow16.glossOverlay};
    mix-blend-mode: screen;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
var StyledSurface = styled51.div`
  ${surfaceCss}
  width: ${({ $width }) => WIDTH[$width]};

  /*
   * The workspace width (Rob, 2026-09-12 — see the WIDTH docblock). A height as well as a width,
   * and a body slot that is edge-to-edge, non-scrolling and a flex column, so the consumer's
   * content sizes to the surface rather than the surface to the content. Scoped to the class so
   * the six ramp widths are untouched to the pixel; 'flex: 1' because the surface is already a
   * flex column ('surfaceCss') and the body must take what the ribbon and footer leave.
   */
  &.w-workspace {
    height: calc(100vh - ${space14[12]});

    .tl-modal-body {
      flex: 1;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
  }

  .tl-modal-ribbon {
    position: relative;
    display: flex;
    align-items: center;
    gap: ${space14[3]};
    padding: ${inset9[4]} ${inset9[5]} ${space14[3]};
    border-bottom: 1px solid ${rgba36(surface26[0], 0.55)};
    background: ${({ $tone }) => {
  const t = TONE[$tone];
  return `linear-gradient(180deg, ${rgba36(t.wash, t.top)}, ${rgba36(t.wash, t.bottom)})`;
}};
  }

  .tl-modal-glyph {
    flex-shrink: 0;
    width: ${iconSize["2xl"]};
    height: ${iconSize["2xl"]};
    border-radius: ${radius31.pill};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${iconSize.sm};
    background: ${rgba36(surface26[0], 0.7)};
    border: 1px solid ${rgba36(surface26[0], 0.95)};
    color: ${({ $tone }) => TONE[$tone].ink};
    box-shadow:
      0 0 8px 1px ${({ $tone }) => rgba36(TONE[$tone].glow, TONE[$tone].halo)},
      inset 0 1px 0 ${rgba36(surface26[0], 0.6)};
  }

  .tl-modal-title {
    flex: 1;
    margin: 0;
    font: ${TITLE_TYPE} ${fontSans33};
    letter-spacing: -0.15px;
    color: ${({ $tone }) => TONE[$tone].ink};
  }

  .tl-modal-close {
    flex-shrink: 0;
    appearance: none;
    width: ${CLOSE_SIZE4};
    height: ${CLOSE_SIZE4};
    border-radius: ${radius31.pill};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${CLOSE_GLYPH_SIZE3};
    line-height: 1;
    background: ${rgba36(surface26[0], 0.55)};
    border: 1px solid ${rgba36(surface26[0], 0.85)};
    color: ${ink40[2]};
    box-shadow:
      inset 0 1px 0 ${rgba36(surface26[0], 0.6)},
      0 1px 2px rgba(${shadow16.inkGlassRgb}, 0.1);
    transition:
      background ${motion29.durFast} ${motion29.easeUi},
      color ${motion29.durFast} ${motion29.easeUi},
      transform ${motion29.durFast} ${motion29.easeUi};

    &:hover {
      background: ${rgba36(surface26[0], 0.85)};
      color: ${ink40[1]};
      transform: scale(1.05);
    }
    &:focus-visible {
      outline: none;
      box-shadow: ${brand33.focusRing};
    }

    /*
     * Blocked. Card 08 draws no disabled close state, so rather than invent a treatment this
     * mutes the control's OWN live one: the same geometry and the same ring, at the reduced
     * contrast the rest of the system uses for an unavailable control, with the hover growth and
     * the inner highlight removed because both read as "this responds".
     *
     * The focus ring is deliberately KEPT. The control stays in the tab order (see the prop
     * note), so a focus state it cannot show is a control the keyboard can reach invisibly.
     */
    &[aria-disabled='true'] {
      cursor: not-allowed;
      color: ${ink40[4]};
      background: ${rgba36(surface26[0], 0.3)};
      border-color: ${rgba36(surface26[0], 0.5)};
      box-shadow: none;
    }
    &[aria-disabled='true']:hover {
      background: ${rgba36(surface26[0], 0.3)};
      color: ${ink40[4]};
      transform: none;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
      &:hover {
        transform: none;
      }
    }
  }

  .tl-modal-body {
    position: relative;
    /* The scroller, so an over-tall dialog keeps its ribbon and footer visible. */
    overflow-y: auto;
    min-height: 0;
    padding: ${space14[4]} ${inset9[5]} ${inset9[2]};
    font: ${BODY_TYPE2} ${fontSans33};
    color: ${ink40[2]};

    /* The card's '.lede' — the bolded opening line. Consumers opt in by class. */
    .tl-modal-lede {
      margin: 0 0 ${space14[2]};
      font: ${LEDE_TYPE} ${fontSans33};
      color: ${ink40[1]};
      text-wrap: pretty;
    }

    p {
      margin: 0 0 ${space14[2]};
      text-wrap: pretty;
    }
    p:last-child {
      margin-bottom: 0;
    }
  }

  .tl-modal-footer {
    padding: ${inset9[4]} ${inset9[5]} ${inset9[5]};
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${inset9[3]};
  }

  .tl-modal-meta {
    margin-right: auto;
    display: inline-flex;
    align-items: center;
    gap: ${inset9[2]};
    font: ${META_TYPE2} ${fontMono16};
    color: ${ink40[4]};
  }
`;
var Modal = React45.forwardRef(function Modal2(props, ref) {
  const {
    open,
    onClose,
    title,
    tone: toneProp = "cyan",
    width: widthProp = "md",
    icon: icon2,
    actions,
    meta,
    dismissible = true,
    closeLabel = "Close",
    returnFocusTo,
    className,
    children,
    ...rest
  } = props;
  const tone = variant(TONE, toneProp, "cyan", "Modal", "tone");
  const width = variant(WIDTH, widthProp, "md", "Modal", "width");
  const surfaceRef = React45.useRef(null);
  const rootRef = React45.useRef(null);
  const previouslyFocused = React45.useRef(null);
  const autoId = React45.useId();
  const titleId = `${autoId}-title`;
  const bodyId = `${autoId}-body`;
  const instanceId = React45.useRef(Symbol("tl-modal")).current;
  const setSurface = React45.useCallback(
    (node) => {
      surfaceRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );
  React45.useEffect(() => {
    if (!open || !hasDom2()) return;
    const onKeyDown = (e) => {
      if (!isTopmost(instanceId)) return;
      if (e.key === "Escape") {
        if (dismissible) onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const surface31 = surfaceRef.current;
      if (!surface31) return;
      const scope = rootRef.current ?? surface31;
      const focusables = getFocusables(scope);
      if (focusables.length === 0) {
        e.preventDefault();
        surface31.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (!active || !scope.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && (active === first || active === surface31)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, dismissible, onClose, instanceId]);
  React45.useEffect(() => {
    if (!open || !hasDom2()) return;
    openStack.push(instanceId);
    if (openStack.length === 1) {
      bodyOverflowBeforeFirst = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    applyBackgroundInert(rootRef.current);
    return () => {
      const i = openStack.lastIndexOf(instanceId);
      if (i !== -1) openStack.splice(i, 1);
      if (openStack.length === 0) {
        document.body.style.overflow = bodyOverflowBeforeFirst ?? "";
        bodyOverflowBeforeFirst = null;
        releaseBackgroundInert();
      }
    };
  }, [open, instanceId]);
  React45.useEffect(() => {
    if (!open || !hasDom2()) return;
    previouslyFocused.current = document.activeElement;
    const surfaceOnOpen = surfaceRef.current;
    if (surfaceOnOpen && !surfaceOnOpen.contains(document.activeElement)) {
      surfaceOnOpen.focus();
    }
    return () => {
      const previous = previouslyFocused.current;
      const surface31 = surfaceRef.current;
      const scope = rootRef.current ?? surface31;
      if (scope && !scope.contains(document.activeElement)) return;
      if (previous && previous !== document.body && previous.isConnected) previous.focus();
      else if (returnFocusTo?.current?.isConnected) returnFocusTo.current.focus();
    };
  }, [open, returnFocusTo]);
  if (!open) return null;
  if (!hasDom2()) return null;
  const classes = ["tl-modal", `tone-${tone}`, `w-${width}`, className ?? ""].filter(Boolean).join(" ");
  return createPortal2(
    /* @__PURE__ */ jsxs42(StyledRoot2, { className: "tl-modal-root", ref: rootRef, children: [
      /* @__PURE__ */ jsx51(Scrim, { "aria-hidden": "true", onClick: dismissible ? onClose : void 0 }),
      /* @__PURE__ */ jsxs42(
        StyledSurface,
        {
          ...rest,
          ref: setSurface,
          className: classes,
          $tone: tone,
          $width: width,
          role: "dialog",
          "aria-modal": "true",
          "aria-labelledby": titleId,
          "aria-describedby": bodyId,
          tabIndex: -1,
          children: [
            /* @__PURE__ */ jsxs42("div", { className: "tl-modal-ribbon", children: [
              icon2 !== void 0 && icon2 !== null ? /* @__PURE__ */ jsx51("span", { className: "tl-modal-glyph", "aria-hidden": "true", children: icon2 }) : null,
              /* @__PURE__ */ jsx51("h2", { className: "tl-modal-title", id: titleId, children: title }),
              /* @__PURE__ */ jsx51(
                "button",
                {
                  type: "button",
                  className: "tl-modal-close",
                  "aria-label": closeLabel,
                  "aria-disabled": dismissible ? void 0 : true,
                  onClick: dismissible ? onClose : void 0,
                  children: /* @__PURE__ */ jsx51("span", { "aria-hidden": "true", children: "\xD7" })
                }
              )
            ] }),
            /* @__PURE__ */ jsx51("div", { className: "tl-modal-body", id: bodyId, children }),
            actions !== void 0 || meta !== void 0 ? /* @__PURE__ */ jsxs42("div", { className: "tl-modal-footer", children: [
              meta !== void 0 ? /* @__PURE__ */ jsx51("span", { className: "tl-modal-meta", children: meta }) : null,
              actions
            ] }) : null
          ]
        }
      )
    ] }),
    document.body
  );
});

// src/Overlays/Tooltip.tsx
import React46 from "react";
import styled52 from "styled-components";
import {
  accent as accent15,
  brand as brand34,
  fontSans as fontSans34,
  glassCard as glassCard5,
  inkGlass as inkGlass7,
  inkOn as inkOn17,
  radius as radius32,
  rgba as rgba37,
  semanticColor as semanticColor16,
  shadow as shadow17,
  statusSurface as statusSurface2,
  support as support13,
  surface as surface27,
  zIndex as zIndex4
} from "@repo/design-tokens";
import { jsx as jsx52, jsxs as jsxs43 } from "react/jsx-runtime";
var PAD4 = "5px 11px 6px";
var TITLE_TYPE2 = "500 11.5px/1.3";
var DETAIL_SIZE = "10.5px";
var DETAIL_OPACITY = 0.85;
var GLYPH_OPACITY = 0.85;
var GLASS_BORDER_WIDTH3 = "1.5px";
var ARROW_SIZE = "8px";
var OFFSET = "9px";
var ARROWLESS_GAP = "7px";
var MAX_WIDTH = "200px";
var TITLE_ROW_GAP2 = "6px";
var BODY_ALPHA = 0.85;
var EDGE_ALPHA = 0.95;
var HALO = `rgba(${glassCard5.frost}, 0.85)`;
var ARROW_HALO = `rgba(${glassCard5.frost}, 0.5)`;
var TOOLTIP_TONE = {
  // Card: ink + glow only; the body stays frost.
  cyan: { ink: inkOn17.aqua, glow: brand34.cyan, glowAlpha: 0.45, body: surface27[0], edge: surface27[0] },
  // Card body #FFEBF2 (ΔE 5.6), edge #FFC8DC (3.5).
  peach: {
    ink: inkOn17.pink,
    glow: accent15.peach,
    glowAlpha: 0.55,
    body: surface27[3],
    edge: support13.palePink
  },
  // Card body #F4F0FF (3.1), edge #DCD2FF (17.9).
  lavender: {
    ink: inkOn17.lav,
    glow: accent15.violet,
    glowAlpha: 0.42,
    body: surface27[4],
    edge: support13.lavender
  },
  // Card body #FFF7E4 (3.7), edge #FFE1AA (6.1 — the token named `coral` is a pale orange).
  butter: {
    ink: inkGlass7.warn,
    glow: accent15.butter,
    glowAlpha: 0.55,
    body: statusSurface2.warn,
    edge: accent15.coral
  },
  // Card body #FFECEC (1.1), edge #FFC8C8 (13.4).
  coral: {
    ink: inkOn17.error,
    glow: semanticColor16.error,
    glowAlpha: 0.4,
    body: statusSurface2.error,
    edge: support13.palePink
  },
  // Card body #E4FAFA (1.4), edge #B4EBEE (5.1).
  mint: {
    ink: inkOn17.mint,
    glow: semanticColor16.success,
    glowAlpha: 0.55,
    body: statusSurface2.success,
    edge: support13.aqua
  }
};
var NEUTRAL_TOOLTIP = { ink: brand34.cyanPress, body: surface27[0] };
var LOOK = {
  neutral: {
    ink: NEUTRAL_TOOLTIP.ink,
    body: rgba37(NEUTRAL_TOOLTIP.body, BODY_ALPHA),
    edge: rgba37(NEUTRAL_TOOLTIP.body, EDGE_ALPHA),
    halo: HALO,
    arrowHalo: ARROW_HALO
  },
  ...Object.fromEntries(
    Object.keys(TOOLTIP_TONE).map((tone) => {
      const t = TOOLTIP_TONE[tone];
      const glow2 = rgba37(t.glow, t.glowAlpha);
      return [
        tone,
        {
          ink: t.ink,
          body: rgba37(t.body, BODY_ALPHA),
          edge: rgba37(t.edge, EDGE_ALPHA),
          halo: glow2,
          // Card 08: `.tl-tooltip::after { box-shadow: … var(--tone-glow, …) }` — the tone's own glow.
          arrowHalo: glow2
        }
      ];
    })
  )
};
var hasArrow = (placement, arrow) => arrow && (placement === "top" || placement === "bottom");
var TRANSFORM = {
  top: (gap) => `translate(-50%, calc(-100% - ${gap}))`,
  bottom: (gap) => `translate(-50%, ${gap})`,
  left: (gap) => `translate(calc(-100% - ${gap}), -50%)`,
  right: (gap) => `translate(${gap}, -50%)`
};
var arrowCss = (placement, look) => `
  /* The arrow \u2014 a frosted hairline diamond, as the card draws it (and the popover). */
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: ${ARROW_SIZE};
    height: ${ARROW_SIZE};
    background: ${look.body};
    backdrop-filter: ${FROST_GLASS_BLUR};
    -webkit-backdrop-filter: ${FROST_GLASS_BLUR};
    transform: translateX(-50%) rotate(45deg);
    ${placement === "top" ? `
      top: 100%;
      margin-top: -5px;
      border-right: ${GLASS_BORDER_WIDTH3} solid ${look.edge};
      border-bottom: ${GLASS_BORDER_WIDTH3} solid ${look.edge};
      box-shadow: 2px 2px 6px ${look.arrowHalo};
    ` : `
      bottom: 100%;
      margin-bottom: -5px;
      border-left: ${GLASS_BORDER_WIDTH3} solid ${look.edge};
      border-top: ${GLASS_BORDER_WIDTH3} solid ${look.edge};
      box-shadow: -2px -2px 6px ${look.arrowHalo};
    `}
  }
`;
var Sticker = styled52.span`
  position: absolute;
  z-index: ${zIndex4.tooltip};
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  max-width: ${MAX_WIDTH};
  width: max-content;
  padding: ${PAD4};
  border-radius: ${radius32.pill};
  background: ${({ $look }) => $look.body};
  backdrop-filter: ${FROST_GLASS_BLUR};
  -webkit-backdrop-filter: ${FROST_GLASS_BLUR};
  border: ${GLASS_BORDER_WIDTH3} solid ${({ $look }) => $look.edge};
  color: ${({ $look }) => $look.ink};
  font: ${TITLE_TYPE2} ${fontSans34};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.05px;
  text-align: center;
  overflow-wrap: anywhere;
  pointer-events: none;
  box-shadow:
    0 0 14px 3px ${({ $look }) => $look.halo},
    0 6px 14px rgba(${shadow17.inkGlassRgb}, 0.2),
    0 2px 4px rgba(${shadow17.inkGlassRgb}, 0.12),
    inset 0 1px 0 ${rgba37(surface27[0], 0.7)};
  transform: ${({ $placement, $arrow }) => TRANSFORM[$placement](hasArrow($placement, $arrow) ? OFFSET : ARROWLESS_GAP)};

  ${({ $placement, $look, $arrow }) => hasArrow($placement, $arrow) ? arrowCss($placement, $look) : ""}
`;
var ShortcutKbd = styled52(Kbd)`
  margin-left: ${TOOLTIP_GAP};
`;
var TitleRow = styled52.span`
  display: inline-flex;
  align-items: center;
  gap: ${TITLE_ROW_GAP2};
`;
var IconSlot = styled52.span`
  display: inline-flex;
  align-items: center;
  opacity: ${GLYPH_OPACITY};
`;
var Detail = styled52.span`
  font-size: ${DETAIL_SIZE};
  opacity: ${DETAIL_OPACITY};
`;
var Tooltip = ({
  open,
  anchor,
  placement: placementProp,
  tone: toneProp,
  icon: icon2,
  arrow = true,
  title,
  detail,
  shortcut,
  id,
  className
}) => {
  if (!open) return null;
  const placement = variant(
    TRANSFORM,
    placementProp,
    "top",
    "Tooltip",
    "placement"
  );
  const lookKey = variant(LOOK, toneProp ?? "neutral", "neutral", "Tooltip", "tone");
  const tone = lookKey === "neutral" ? void 0 : lookKey;
  const hasShortcut = kbdKeys(shortcut).length > 0;
  const hasIcon = React46.isValidElement(icon2);
  return /* @__PURE__ */ jsxs43(
    Sticker,
    {
      role: "tooltip",
      id,
      className,
      "data-placement": placement,
      "data-tone": tone,
      $placement: placement,
      $look: LOOK[lookKey],
      $arrow: arrow,
      style: { left: anchor.x, top: anchor.y },
      children: [
        hasIcon || hasShortcut ? /* @__PURE__ */ jsxs43(TitleRow, { children: [
          hasIcon && /* @__PURE__ */ jsx52(IconSlot, { className: "tl-tooltip-icon", "aria-hidden": "true", children: icon2 }),
          /* @__PURE__ */ jsx52("span", { children: title }),
          hasShortcut && /* @__PURE__ */ jsx52(ShortcutKbd, { keys: shortcut })
        ] }) : /* @__PURE__ */ jsx52("span", { children: title }),
        detail !== void 0 && detail !== null && detail !== "" && /* @__PURE__ */ jsx52(Detail, { children: detail })
      ]
    }
  );
};

// src/Overlays/Popover.tsx
import { useCallback as useCallback2, useEffect as useEffect5, useId as useId3, useLayoutEffect as useLayoutEffect2, useRef as useRef4, useState as useState4 } from "react";
import { createPortal as createPortal3, flushSync } from "react-dom";
import styled53, { css as css24, keyframes as keyframes4 } from "styled-components";
import {
  fontSans as fontSans35,
  glassCard as glassCard6,
  ink as ink41,
  motion as motion30,
  radius as radius33,
  rgba as rgba38,
  shadow as shadow18,
  space as space15,
  surface as surface28,
  zIndex as zIndex5
} from "@repo/design-tokens";

// src/Overlays/placePopover.ts
function placePopover({
  anchor,
  panel,
  viewport,
  placement,
  align,
  gap,
  margin
}) {
  const below = anchor.bottom + gap;
  const above = anchor.top - gap - panel.height;
  const fitsBelow = below + panel.height <= viewport.height;
  const fitsAbove = above >= 0;
  let side = placement;
  if (placement === "bottom" && !fitsBelow && fitsAbove) side = "top";
  else if (placement === "top" && !fitsAbove && fitsBelow) side = "bottom";
  const wanted = align === "start" ? anchor.left : anchor.right - panel.width;
  const maxLeft = Math.max(margin, viewport.width - margin - panel.width);
  const left = Math.min(Math.max(wanted, margin), maxLeft);
  const wantedTop = side === "bottom" ? below : above;
  if (fitsBelow || fitsAbove) return { top: wantedTop, left, side, clamped: false };
  const maxTop = Math.max(margin, viewport.height - margin - panel.height);
  const top = Math.min(Math.max(wantedTop, margin), maxTop);
  return { top, left, side, clamped: top !== wantedTop };
}

// src/Overlays/Popover.tsx
import { jsx as jsx53, jsxs as jsxs44 } from "react/jsx-runtime";
var DEFAULT_WIDTH = 331;
var POPOVER_BLUR = "blur(30px) saturate(120%)";
var GLASS_ALPHA = 0.78;
var BORDER_WIDTH2 = "1.5px";
var PAD5 = "14px 14px 12px";
var ARROW_SIZE2 = 12;
var ARROW_REACH = 7;
var ANCHOR_GAP = 12;
var ARROW_INSET = "24px";
var GLYPH_DISC = "22px";
var TITLE_TYPE3 = "600 13px/1.3";
var TITLE_TRACKING2 = "-0.1px";
var BODY_TYPE3 = "400 12px/1.55";
var HEAD_GAP2 = space15[2];
var HEAD_BELOW = "6px";
var BODY_BELOW = space15[3];
var ACTIONS_GAP = "6px";
var FADE_MS = 140;
var SLIDE_PX = 4;
var GAP_NO_ARROW = parseInt(space15[2], 10);
var VIEWPORT_MARGIN = parseInt(space15[2], 10);
var POPOVER_COLOURS = {
  title: ink41[1],
  body: ink41[2],
  glassAlpha: GLASS_ALPHA
};
var GLYPH_TONE = {
  lavender: {
    wash: TONE.lavender.wash,
    ink: TONE.lavender.ink,
    glow: TONE.lavender.glow,
    fill: 0.4,
    halo: 0.35
  },
  butter: {
    wash: TONE.butter.wash,
    ink: TONE.butter.ink,
    glow: TONE.butter.glow,
    fill: 0.32,
    halo: 0.45
  }
};
var GLASS_EDGE = `${BORDER_WIDTH2} solid ${rgba38(surface28[0], 0.95)}`;
var enter2 = keyframes4`
  from {
    opacity: 0;
    transform: translateY(var(--tl-popover-slide));
  }
  to {
    opacity: 1;
    transform: none;
  }
`;
var glass5 = css24`
  background: ${rgba38(surface28[0], GLASS_ALPHA)};
  backdrop-filter: ${POPOVER_BLUR};
  -webkit-backdrop-filter: ${POPOVER_BLUR};
`;
var Panel5 = styled53.div`
  position: fixed;
  /* See readLayer: the base layer beneath a dialog opened above a page-level panel. */
  z-index: ${({ $layer }) => $layer === "beneath" ? "auto" : zIndex5.popover};
  box-sizing: border-box;
  max-width: calc(100vw - ${2 * VIEWPORT_MARGIN}px);
  ${glass5}
  border: ${GLASS_EDGE};
  border-radius: ${radius33.xl};
  box-shadow:
    0 0 16px 3px rgba(${glassCard6.frost}, 0.7),
    0 16px 36px rgba(${shadow18.inkGlassRgb}, 0.22),
    0 4px 10px rgba(${shadow18.inkGlassRgb}, 0.14),
    inset 0 1px 0 ${rgba38(surface28[0], 0.6)};
  padding: ${PAD5};
  isolation: isolate;
  outline: none;
  font-family: ${fontSans35};
  visibility: ${({ $measured, $layer }) => $measured && $layer !== "hidden" ? "visible" : "hidden"};
  /* Slides from the trigger: down from above it, or up from below it. */
  --tl-popover-slide: ${({ $side }) => $side === "bottom" ? -SLIDE_PX : SLIDE_PX}px;
  animation: ${enter2} ${FADE_MS}ms ${motion30.easeUi} both;

  /* The gloss — the same token Modal binds. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: ${shadow18.glossOverlay};
    mix-blend-mode: screen;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
var Arrow = styled53.span`
  position: absolute;
  width: ${ARROW_SIZE2}px;
  height: ${ARROW_SIZE2}px;
  ${glass5}
  transform: rotate(45deg);
  pointer-events: none;
  ${({ $align }) => $align === "start" ? `left: ${ARROW_INSET};` : `right: ${ARROW_INSET};`}
  ${({ $side }) => $side === "bottom" ? css24`
          top: -${ARROW_REACH}px;
          border-left: ${GLASS_EDGE};
          border-top: ${GLASS_EDGE};
          box-shadow: -2px -2px 6px rgba(${glassCard6.frost}, 0.35);
        ` : css24`
          bottom: -${ARROW_REACH}px;
          border-right: ${GLASS_EDGE};
          border-bottom: ${GLASS_EDGE};
          box-shadow: 2px 2px 6px rgba(${glassCard6.frost}, 0.35);
        `}
`;
var Head = styled53.div`
  display: flex;
  align-items: center;
  gap: ${HEAD_GAP2};
  margin-bottom: ${HEAD_BELOW};
`;
var Glyph = styled53.span`
  flex: none;
  width: ${GLYPH_DISC};
  height: ${GLYPH_DISC};
  border-radius: ${radius33.pill};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => rgba38(GLYPH_TONE[p.$tone].wash, GLYPH_TONE[p.$tone].fill)};
  color: ${(p) => GLYPH_TONE[p.$tone].ink};
  border: 1px solid ${rgba38(surface28[0], 0.95)};
  box-shadow:
    0 0 6px 1px ${(p) => rgba38(GLYPH_TONE[p.$tone].glow, GLYPH_TONE[p.$tone].halo)},
    inset 0 1px 0 ${rgba38(surface28[0], 0.6)};
`;
var Title2 = styled53.div`
  flex: 1;
  min-width: 0;
  margin: 0;
  font: ${TITLE_TYPE3} ${fontSans35};
  letter-spacing: ${TITLE_TRACKING2};
  color: ${POPOVER_COLOURS.title};
`;
var Body = styled53.div`
  font: ${BODY_TYPE3} ${fontSans35};
  color: ${POPOVER_COLOURS.body};
  margin-bottom: ${({ $hasActions }) => $hasActions ? BODY_BELOW : "0"};
`;
var Actions = styled53.div`
  display: flex;
  gap: ${ACTIONS_GAP};
  justify-content: flex-end;
`;
var openPanels = [];
var isTopmost2 = (panel) => openPanels[openPanels.length - 1] === panel;
var inLaterPanel = (panel, node) => openPanels.slice(openPanels.indexOf(panel) + 1).some((later) => later.contains(node));
var elementOf = (node) => node instanceof Element ? node : node.parentElement;
var isUnderLayer = (panel) => {
  if (panel.closest("[inert]")) return true;
  const roots = document.querySelectorAll(".tl-modal-root");
  const top = roots[roots.length - 1];
  return !!top && !top.contains(panel);
};
var readLayer = (panel) => {
  if (panel.closest(".tl-modal-root") || !isUnderLayer(panel)) return "own";
  const roots = document.querySelectorAll(".tl-modal-root");
  const top = roots[roots.length - 1];
  return top && top.compareDocumentPosition(panel) & Node.DOCUMENT_POSITION_FOLLOWING ? "hidden" : "beneath";
};
var swallowNextClick = () => {
  const remove = () => {
    document.removeEventListener("click", onClick, true);
    document.removeEventListener("pointerup", onPointerUp, true);
    document.removeEventListener("pointercancel", onPointerUp, true);
  };
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    remove();
  };
  const onPointerUp = () => {
    setTimeout(remove, 0);
  };
  document.addEventListener("click", onClick, true);
  document.addEventListener("pointerup", onPointerUp, true);
  document.addEventListener("pointercancel", onPointerUp, true);
};
var Popover = ({
  open,
  onClose,
  anchorRef,
  title,
  glyph,
  glyphTone: glyphToneProp = "lavender",
  children,
  actions,
  placement = "bottom",
  align = "start",
  arrow = true,
  width = DEFAULT_WIDTH,
  initialFocusRef,
  id,
  className
}) => {
  const generatedId = useId3();
  const glyphTone = variant(
    GLYPH_TONE,
    glyphToneProp,
    "lavender",
    "Popover",
    "glyphTone"
  );
  const panelId = id ?? `tl-popover-${generatedId}`;
  const titleId = `${panelId}-title`;
  const [panel, setPanel] = useState4(null);
  const [placed, setPlaced] = useState4(null);
  const [anchor, setAnchor] = useState4(null);
  useLayoutEffect2(() => {
    setAnchor(open ? anchorRef.current : null);
  }, [open, anchorRef]);
  const triggerLeft = useRef4(false);
  useEffect5(() => {
    if (open && anchorRef.current !== anchor) {
      if (anchor && !anchorRef.current) triggerLeft.current = true;
      setAnchor(anchorRef.current);
    }
  });
  const onCloseRef = useRef4(onClose);
  useEffect5(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const place = useCallback2(() => {
    const trigger = anchor;
    if (!panel || !trigger) return;
    if (!trigger.isConnected) {
      const live = anchorRef.current?.isConnected ? anchorRef.current : null;
      if (!live) triggerLeft.current = true;
      setAnchor(live);
      return;
    }
    const a = trigger.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const next = placePopover({
      anchor: a,
      panel: { width: p.width, height: p.height },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      placement,
      align,
      gap: arrow ? ANCHOR_GAP : GAP_NO_ARROW,
      margin: VIEWPORT_MARGIN
    });
    setPlaced(
      (prev) => prev && prev.top === next.top && prev.left === next.left && prev.side === next.side ? prev : next
    );
  }, [panel, anchor, anchorRef, placement, align, arrow]);
  useLayoutEffect2(() => {
    if (!open) {
      setPlaced(null);
      triggerLeft.current = false;
      return;
    }
    place();
  }, [open, place, width]);
  useEffect5(() => {
    if (!open || !panel) return void 0;
    let frame = null;
    const schedule = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        place();
      });
    };
    window.addEventListener("resize", schedule);
    document.addEventListener("scroll", schedule, true);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(schedule);
      observer.observe(panel);
      if (anchor) observer.observe(anchor);
    }
    return () => {
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, true);
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [open, panel, place, anchor]);
  const [layer, setLayer] = useState4("own");
  useEffect5(() => {
    if (!open || !panel) {
      setLayer("own");
      return void 0;
    }
    const read = () => flushSync(() => setLayer(readLayer(panel)));
    setLayer(readLayer(panel));
    const observer = new MutationObserver(read);
    observer.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ["inert"]
    });
    for (let el = panel; el && el !== document.body; el = el.parentElement) {
      observer.observe(el, { attributes: true, attributeFilter: ["inert"] });
    }
    return () => observer.disconnect();
  }, [open, panel]);
  useEffect5(() => {
    if (!open || !panel) return void 0;
    openPanels.push(panel);
    return () => {
      const i = openPanels.indexOf(panel);
      if (i !== -1) openPanels.splice(i, 1);
    };
  }, [open, panel]);
  const measured = placed !== null;
  useEffect5(() => {
    if (!open || !panel || !measured) return void 0;
    const comeBack = triggerLeft.current;
    triggerLeft.current = false;
    const target = initialFocusRef?.current ?? getFocusables(panel)[0] ?? panel;
    if (target && !comeBack && !isUnderLayer(panel) && !panel.contains(document.activeElement)) {
      target.focus();
    }
    return () => {
      const active = document.activeElement;
      if (active && active !== document.body && !panel.contains(active)) return;
      const trigger = anchorRef.current;
      if (trigger?.isConnected) trigger.focus();
    };
  }, [open, panel, measured, initialFocusRef, anchorRef]);
  const onPanelKeyDown = (e) => {
    if (!panel || !panel.contains(e.target)) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      if (e.defaultPrevented) return;
      e.preventDefault();
      onCloseRef.current();
      return;
    }
    if (e.key !== "Tab" || e.defaultPrevented) return;
    const focusables = getFocusables(panel);
    const active = document.activeElement;
    let to = null;
    if (focusables.length === 0) to = panel;
    else if (e.shiftKey && (active === focusables[0] || active === panel))
      to = focusables[focusables.length - 1];
    else if (!e.shiftKey && active === focusables[focusables.length - 1]) to = focusables[0];
    if (!to) return;
    e.preventDefault();
    e.stopPropagation();
    to.focus();
  };
  useEffect5(() => {
    if (!open || !panel) return void 0;
    const onKeyDown = (e) => {
      if (e.key !== "Tab" && e.key !== "Escape") return;
      if (!isTopmost2(panel) || isUnderLayer(panel)) return;
      const active = document.activeElement;
      if (active && panel.contains(active)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      const focusables = getFocusables(panel);
      const to = focusables.length === 0 ? panel : e.shiftKey ? focusables[focusables.length - 1] : focusables[0];
      to.focus();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, panel]);
  useEffect5(() => {
    if (!open || !panel) return void 0;
    const onPointerDown = (e) => {
      const target = e.target;
      if (!target) return;
      if (panel.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      if (inLaterPanel(panel, target)) return;
      if (isUnderLayer(panel)) return;
      if (isScrollbarPress(e)) return;
      onCloseRef.current();
      const ownDialog = panel.closest(".tl-modal-root");
      const element = elementOf(target);
      if (ownDialog && ownDialog.contains(target) && !element?.closest('[role="dialog"]')) {
        swallowNextClick();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, panel, anchorRef]);
  if (!open || !anchor) return null;
  const container = anchor.closest(".tl-modal-root") ?? document.body;
  const side = placed?.side ?? placement;
  return createPortal3(
    /* @__PURE__ */ jsxs44(
      Panel5,
      {
        ref: setPanel,
        id: panelId,
        role: "dialog",
        "aria-modal": "false",
        "aria-labelledby": titleId,
        tabIndex: -1,
        className: className ? `tl-popover ${className}` : "tl-popover",
        "data-side": side,
        $side: side,
        $measured: measured,
        $layer: layer,
        onKeyDown: onPanelKeyDown,
        style: { top: placed?.top ?? 0, left: placed?.left ?? 0, width },
        children: [
          /* @__PURE__ */ jsxs44(Head, { className: "tl-popover-head", children: [
            glyph && /* @__PURE__ */ jsx53(
              Glyph,
              {
                $tone: glyphTone,
                className: `tl-popover-glyph tl-popover-glyph-${glyphTone}`,
                "aria-hidden": "true",
                children: /* @__PURE__ */ jsx53(Icon, { name: glyph, size: "xs" })
              }
            ),
            /* @__PURE__ */ jsx53(Title2, { id: titleId, className: "tl-popover-title", children: title })
          ] }),
          /* @__PURE__ */ jsx53(Body, { className: "tl-popover-body", $hasActions: !!actions, children }),
          actions && /* @__PURE__ */ jsx53(Actions, { className: "tl-popover-actions", children: actions }),
          arrow && !placed?.clamped && /* @__PURE__ */ jsx53(Arrow, { className: "tl-popover-arrow", "aria-hidden": "true", $side: side, $align: align })
        ]
      }
    ),
    container
  );
};

// src/Overlays/HoverCard.tsx
import { useEffect as useEffect6, useState as useState5 } from "react";
import styled54, { keyframes as keyframes5 } from "styled-components";
import {
  brand as brand35,
  fontSans as fontSans36,
  glassCard as glassCard7,
  ink as ink42,
  motion as motion31,
  radius as radius34,
  rgba as rgba39,
  semantic as semantic10,
  shadow as shadow19,
  space as space16,
  surface as surface29,
  typeScale as typeScale17,
  weight as weight20,
  zIndex as zIndex6
} from "@repo/design-tokens";
import { jsx as jsx54, jsxs as jsxs45 } from "react/jsx-runtime";
var OFFSET_Y_PX = 14;
var RISE_PX = 4;
var MIN_WIDTH2 = "220px";
var PAD6 = "10px 12px";
var ROW_GAP = "8px";
var BORDER_WIDTH3 = "1.5px";
var NAME_TYPE = "600 14px/20px";
var HINT_TYPE = "500 11.5px/1";
var HINT_GAP = "5px";
var HOVER_CARD_INTENT_MS = 120;
var FADE_MS2 = 140;
var HOVER_CARD_HINT_INK = brand35.cyanPress;
var DETAIL_MIN_WIDTH = "276px";
var DETAIL_LINE_HEIGHT = "18px";
var DETAIL_TYPE2 = `${weight20.regular} ${typeScale17.caption1.fontSize}/${DETAIL_LINE_HEIGHT}`;
var DETAIL_PAD = `${space16[2]} 0`;
var DETAIL_COL_GAP = space16[5];
var DETAIL_ROW_GAP = "1px";
var DIVIDER_WIDTH = "1px";
var HOVER_CARD_DETAIL_LABEL_INK = ink42[2];
var HOVER_CARD_DETAIL_VALUE_INK = ink42[1];
var REST = `translate(-100%, ${OFFSET_Y_PX}px)`;
var enter3 = keyframes5`
  from {
    opacity: 0;
    transform: translate(-100%, ${OFFSET_Y_PX + RISE_PX}px);
  }
  to {
    opacity: 1;
    transform: ${REST};
  }
`;
var Root13 = styled54.div`
  position: absolute;
  z-index: ${zIndex6.tooltip};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${ROW_GAP};
  min-width: ${({ $detailed }) => $detailed ? DETAIL_MIN_WIDTH : MIN_WIDTH2};
  padding: ${PAD6};
  border-radius: ${radius34.lg};
  background: ${rgba39(surface29[0], 0.9)};
  backdrop-filter: ${FROST_GLASS_BLUR};
  -webkit-backdrop-filter: ${FROST_GLASS_BLUR};
  border: ${BORDER_WIDTH3} solid ${rgba39(surface29[0], 0.95)};
  box-shadow:
    0 0 14px 3px rgba(${glassCard7.frost}, 0.85),
    0 6px 14px rgba(${shadow19.inkGlassRgb}, 0.2);
  pointer-events: none;
  transform: ${REST};
  animation: ${enter3} ${FADE_MS2}ms ${motion31.easeUi} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
var Row5 = styled54.div`
  display: flex;
  align-items: center;
  gap: ${ROW_GAP};
`;
var Name = styled54.span`
  font: ${NAME_TYPE} ${fontSans36};
  color: ${ink42[1]};
`;
var Hint3 = styled54.span`
  display: inline-flex;
  align-items: center;
  gap: ${HINT_GAP};
  margin-left: auto;
  font: ${HINT_TYPE} ${fontSans36};
  color: ${HOVER_CARD_HINT_INK};
`;
var Details = styled54.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: ${DETAIL_COL_GAP};
  row-gap: ${DETAIL_ROW_GAP};
  margin: 0;
  padding: ${DETAIL_PAD};
  border-top: ${DIVIDER_WIDTH} solid ${semantic10.border.faint};
  border-bottom: ${DIVIDER_WIDTH} solid ${semantic10.border.faint};
`;
var Detail2 = styled54.div`
  display: contents;
`;
var DetailLabel = styled54.dt`
  font: ${DETAIL_TYPE2} ${fontSans36};
  color: ${HOVER_CARD_DETAIL_LABEL_INK};
`;
var DetailValue = styled54.dd`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  font: ${DETAIL_TYPE2} ${fontSans36};
  color: ${HOVER_CARD_DETAIL_VALUE_INK};
`;
var HoverCard = ({
  open,
  anchor,
  title,
  code,
  details,
  status,
  hint,
  id,
  className
}) => {
  const [shown, setShown] = useState5(false);
  useEffect6(() => {
    if (!open) {
      setShown(false);
      return void 0;
    }
    const timer = setTimeout(() => setShown(true), HOVER_CARD_INTENT_MS);
    return () => clearTimeout(timer);
  }, [open]);
  if (!open || !shown) return null;
  const hasDetails = !!details?.length;
  return /* @__PURE__ */ jsxs45(
    Root13,
    {
      $detailed: hasDetails,
      role: "tooltip",
      id,
      className: className ? `tl-hovercard ${className}` : "tl-hovercard",
      "data-state": "open",
      style: { left: anchor.x, top: anchor.y },
      children: [
        /* @__PURE__ */ jsxs45(Row5, { className: "tl-hovercard-row", children: [
          /* @__PURE__ */ jsx54(Name, { className: "tl-hovercard-name", children: title }),
          code !== void 0 && code !== "" && /* @__PURE__ */ jsx54(Tag, { tone: "code", surface: "on-glass", children: code })
        ] }),
        hasDetails && /* @__PURE__ */ jsx54(Details, { className: "tl-hovercard-details", children: details.map(({ label, value }, i) => (
          // Static props, and a label may repeat, so the position is the key.
          /* @__PURE__ */ jsxs45(Detail2, { className: "tl-hovercard-detail", children: [
            /* @__PURE__ */ jsx54(DetailLabel, { className: "tl-hovercard-detail-label", children: label }),
            /* @__PURE__ */ jsx54(DetailValue, { className: "tl-hovercard-detail-value", children: isEmptyValue(value) ? /* @__PURE__ */ jsx54(EmptyMark, {}) : value })
          ] }, i)
        )) }),
        (status || hint) && /* @__PURE__ */ jsxs45(Row5, { className: "tl-hovercard-row", children: [
          status && /* @__PURE__ */ jsx54(Tag, { tone: status.tone, surface: "on-glass", children: status.label }),
          hint && /* @__PURE__ */ jsxs45(Hint3, { className: "tl-hovercard-hint", children: [
            /* @__PURE__ */ jsx54(Icon, { name: hint.icon ?? "computer-mouse", variant: "solid", size: "xs" }),
            hint.label
          ] })
        ] })
      ]
    }
  );
};

// src/Nav/SideTabs.tsx
import { createContext, useContext } from "react";
import styled55 from "styled-components";
import {
  brand as brand36,
  controlHeight as controlHeight6,
  focus as focus13,
  fontSans as fontSans37,
  glass as glass6,
  ink as ink43,
  inset as inset10,
  line as line18,
  motion as motion32,
  radius as radius35,
  rgba as rgba40,
  SELECTED_RAMP as SELECTED_RAMP11,
  shadow as shadow20,
  space as space17,
  surface as surfaceColor3,
  tracking as tracking4,
  typeScale as typeScale18,
  weight as weight21
} from "@repo/design-tokens";
import { jsx as jsx55, jsxs as jsxs46 } from "react/jsx-runtime";
var CollapsedContext = createContext(false);
var TAB_LINE_HEIGHT = 1.2;
var STRIP_MIN_WIDTH = "180px";
var PRESS_TRANSITION = "transform 0.08s ease";
var PRESS_SINK = "translateX(0.5px) scale(0.985)";
var GLASS_LINE_ALPHA = 0.55;
var SIDE_TABS_GLASS_DIVIDER = rgba40(surfaceColor3[0], GLASS_LINE_ALPHA);
var GLASS_HOVER_ALPHA = 0.45;
var GLASS_ACTIVE_TOP_ALPHA = 0.45;
var GLASS_ACTIVE_BOTTOM_ALPHA = 0.78;
var GLASS_RAIL_END_ALPHA = 0.6;
var SOLID_HOVER_ALPHA = 0.05;
var SOLID_ACTIVE_TOP_ALPHA = 0.04;
var SOLID_ACTIVE_BOTTOM_ALPHA = 0.1;
var SOLID_ACTIVE_LINE_ALPHA = 0.18;
var RAIL_BLUE = SELECTED_RAMP11[6];
var RAIL_TEAL = SELECTED_RAMP11[5];
var SIDE_TAB_INK = {
  solidIdle: ink43[3],
  glassIdle: ink43[2],
  active: ink43[1],
  unavailable: ink43[5]
};
var GLASS_STRIP_FILL = glass6.bgSoft;
var SideTabs = ({
  surface: surface31,
  collapsed = false,
  className,
  children,
  ...rest
}) => /* @__PURE__ */ jsx55(CollapsedContext.Provider, { value: collapsed, children: /* @__PURE__ */ jsx55(
  Strip2,
  {
    ...rest,
    className: ["tl-tabs", "is-vertical", surface31, collapsed ? "is-collapsed" : "", className].filter(Boolean).join(" "),
    children
  }
) });
var SideTab = ({
  label,
  leading,
  trailing,
  active = false,
  unavailable = false,
  className,
  onClick,
  type = "button",
  ...rest
}) => {
  const collapsed = useContext(CollapsedContext);
  const handleClick = (event) => {
    if (unavailable) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  return /* @__PURE__ */ jsx55("li", { children: /* @__PURE__ */ jsxs46(
    "button",
    {
      ...rest,
      type,
      className: [
        "tl-tab",
        active ? "is-active" : "",
        unavailable ? "is-unavailable" : "",
        className
      ].filter(Boolean).join(" "),
      "aria-disabled": unavailable || void 0,
      onClick: handleClick,
      children: [
        leading != null && /* @__PURE__ */ jsx55("span", { className: "tl-tab-ic", children: leading }),
        /* @__PURE__ */ jsx55("span", { className: collapsed ? "tl-tab-lbl is-hidden" : "tl-tab-lbl", children: label }),
        trailing != null && /* @__PURE__ */ jsx55("span", { className: collapsed ? "tl-tab-trail is-hidden" : "tl-tab-trail", children: trailing })
      ]
    }
  ) });
};
var Strip2 = styled55.ul`
  list-style: none;
  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${inset10[1]};
  min-width: ${STRIP_MIN_WIDTH};
  padding: ${space17[1]} 0 ${space17[1]} ${space17[1]};
  font-family: ${fontSans37};

  > li {
    display: block;
  }

  .tl-tab {
    position: relative;
    display: flex;
    align-items: center;
    gap: ${inset10[3]};
    width: 100%;
    min-height: ${controlHeight6.md};
    margin-right: -1px;
    padding: 0 ${inset10[4]};
    border: none;
    border-radius: ${radius35.control} 0 0 ${radius35.control};
    background: transparent;
    color: ${SIDE_TAB_INK.solidIdle};
    font-family: ${fontSans37};
    font-size: ${typeScale18.body1.fontSize};
    font-weight: ${weight21.medium};
    line-height: ${TAB_LINE_HEIGHT};
    letter-spacing: ${tracking4.button};
    text-align: left;
    cursor: pointer;
    transition:
      color ${motion32.durFast} ${motion32.easeUi},
      background ${motion32.durFast} ${motion32.easeUi},
      box-shadow ${motion32.durFast} ${motion32.easeUi},
      ${PRESS_TRANSITION};
  }

  .tl-tab-ic {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${ink43[4]};
    transition: color ${motion32.durFast} ${motion32.easeUi};
  }

  .tl-tab-lbl {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .tl-tab-trail {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    gap: ${inset10[3]};
    margin-left: auto;
  }

  /* The press: a 0.5 px sink, never on an unavailable tab (card 13 lines 241–246). */
  .tl-tab:not(.is-unavailable):active {
    transform: ${PRESS_SINK};
  }

  /* ── On solid (card 13 lines 98–133) ─────────────────────────────────────────────────────── */
  &.on-solid {
    border-right: 1px solid ${line18[4]};
  }

  &.on-solid .tl-tab:hover:not(.is-active):not(.is-unavailable) {
    color: ${ink43[2]};
    background: ${rgba40(brand36.cyan, SOLID_HOVER_ALPHA)};
  }

  &.on-solid .tl-tab:hover:not(.is-active):not(.is-unavailable) .tl-tab-ic {
    color: ${ink43[3]};
  }

  &.on-solid .tl-tab.is-active {
    color: ${SIDE_TAB_INK.active};
    background: linear-gradient(
      180deg,
      ${rgba40(brand36.cyan, SOLID_ACTIVE_TOP_ALPHA)} 0%,
      ${rgba40(brand36.cyan, SOLID_ACTIVE_BOTTOM_ALPHA)} 100%
    );
    box-shadow:
      inset 1px 0 0 ${rgba40(brand36.cyan, SOLID_ACTIVE_LINE_ALPHA)},
      inset -1px 0 0 ${rgba40(brand36.cyan, SOLID_ACTIVE_LINE_ALPHA)},
      inset 0 1px 0 ${rgba40(brand36.cyan, SOLID_ACTIVE_LINE_ALPHA)};
  }

  &.on-solid .tl-tab.is-active .tl-tab-ic {
    color: ${brand36.cyan};
  }

  &.on-solid .tl-tab.is-active::after {
    content: '';
    position: absolute;
    top: -1px;
    bottom: -1px;
    right: -1px;
    width: 2px;
    border-radius: 1px;
    background: linear-gradient(
      180deg,
      ${rgba40(RAIL_BLUE, 0.4)} 0%,
      ${RAIL_BLUE} 25%,
      ${RAIL_TEAL} 50%,
      ${RAIL_BLUE} 75%,
      ${rgba40(RAIL_BLUE, 0.4)} 100%
    );
    box-shadow:
      0 0 8px ${rgba40(RAIL_BLUE, 0.55)},
      0 0 14px ${rgba40(brand36.frost, 0.7)};
  }

  &.on-solid .tl-tab:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${brand36.cyanDeep};
  }

  /* ── On glass (card 13 lines 147–206): the strip IS the glass ─────────────────────────────── */
  &.on-glass {
    background: ${GLASS_STRIP_FILL};
    backdrop-filter: ${glass6.blur};
    -webkit-backdrop-filter: ${glass6.blur};
    border: 1px solid ${SIDE_TABS_GLASS_DIVIDER};
    border-radius: ${radius35.controlLg};
    box-shadow: ${shadow20.glassSm};
    /* Clips the active fill and rail to the strip's rounded corners. */
    overflow: hidden;
  }

  /* The inner divider on the right edge; the active tab's rail replaces it. */
  &.on-glass::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 1px;
    background: ${SIDE_TABS_GLASS_DIVIDER};
    pointer-events: none;
  }

  &.on-glass .tl-tab {
    color: ${SIDE_TAB_INK.glassIdle};
  }

  &.on-glass .tl-tab-ic {
    color: ${ink43[3]};
  }

  &.on-glass .tl-tab:hover:not(.is-active):not(.is-unavailable) {
    color: ${ink43[1]};
    background: ${rgba40(surfaceColor3[0], GLASS_HOVER_ALPHA)};
  }

  &.on-glass .tl-tab:hover:not(.is-active):not(.is-unavailable) .tl-tab-ic {
    color: ${ink43[2]};
  }

  /* Active is frost, never a cyan label (the card's rule for glass). */
  &.on-glass .tl-tab.is-active {
    color: ${SIDE_TAB_INK.active};
    background: linear-gradient(
      180deg,
      ${rgba40(brand36.frost, GLASS_ACTIVE_TOP_ALPHA)} 0%,
      ${rgba40(brand36.frost, GLASS_ACTIVE_BOTTOM_ALPHA)} 100%
    );
    box-shadow:
      inset 1px 0 0 ${brand36.frost},
      inset -1px 0 0 ${brand36.frost},
      inset 0 1px 0 ${brand36.frost};
  }

  &.on-glass .tl-tab.is-active .tl-tab-ic {
    color: ${ink43[1]};
  }

  &.on-glass .tl-tab.is-active::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 2px;
    background: linear-gradient(
      180deg,
      ${rgba40(brand36.frost, GLASS_RAIL_END_ALPHA)} 0%,
      ${brand36.frost} 50%,
      ${rgba40(brand36.frost, GLASS_RAIL_END_ALPHA)} 100%
    );
    box-shadow:
      0 0 10px ${brand36.frost},
      0 0 18px ${rgba40(brand36.frost, GLASS_RAIL_END_ALPHA)};
  }

  &.on-glass .tl-tab:focus-visible {
    outline: none;
    box-shadow: ${focus13.onDark};
  }

  /* ── Unavailable, on either surface (card 13 lines 209–210) ───────────────────────────────── */
  && .tl-tab.is-unavailable,
  && .tl-tab.is-unavailable .tl-tab-ic {
    color: ${SIDE_TAB_INK.unavailable};
  }

  .tl-tab.is-unavailable {
    cursor: not-allowed;
  }

  /* ── Collapsed: the leading slot only (built ahead of design) ─────────────────────────────── */
  &.is-collapsed {
    min-width: 0;
  }

  &.is-collapsed .tl-tab {
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
  }

  .is-hidden {
    ${visuallyHiddenCss}
  }

  /* Reduced motion: the press collapses to colour only (card 13 lines 247–249). */
  @media (prefers-reduced-motion: reduce) {
    .tl-tab {
      transition:
        color ${motion32.durFast} ${motion32.easeUi},
        background ${motion32.durFast} ${motion32.easeUi};
    }

    .tl-tab:not(.is-unavailable):active {
      transform: none;
    }
  }
`;

// src/Nav/Tabs.tsx
import React50 from "react";
import styled56 from "styled-components";
import {
  brand as brand37,
  controlHeight as controlHeight7,
  focus as focus14,
  fontSans as fontSans38,
  glass as glass7,
  ink as ink44,
  inset as inset11,
  motion as motion33,
  radius as radius36,
  rgba as rgba41,
  shadow as shadow21,
  space as space18,
  surface as surfaceColor4,
  tracking as tracking5,
  typeScale as typeScale19,
  weight as weight22
} from "@repo/design-tokens";
import { jsx as jsx56 } from "react/jsx-runtime";
var GLASS_RAIL_END_ALPHA2 = 0.6;
var GLASS_HOVER_ALPHA2 = 0.45;
var Tabs = ({ tabs, value, onChange, className, ...rest }) => {
  const refs = React50.useRef(/* @__PURE__ */ new Map());
  const selected = tabs.some((t) => t.id === value) ? value : tabs[0]?.id;
  const onKeyDown = (e, index) => {
    let next = -1;
    if (e.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const id = tabs[next].id;
    onChange(id);
    refs.current.get(id)?.focus();
  };
  return /* @__PURE__ */ jsx56(
    Strip3,
    {
      ...rest,
      role: "tablist",
      "aria-orientation": "horizontal",
      className: ["tl-tabs", "is-horizontal", "on-glass", className].filter(Boolean).join(" "),
      children: tabs.map((tab, index) => {
        const active = tab.id === selected;
        return /* @__PURE__ */ jsx56(
          "button",
          {
            ref: (el) => {
              if (el) refs.current.set(tab.id, el);
              else refs.current.delete(tab.id);
            },
            type: "button",
            role: "tab",
            "aria-selected": active,
            "aria-controls": tab.panelId,
            tabIndex: active ? 0 : -1,
            className: active ? "tl-tab is-active" : "tl-tab",
            onClick: () => {
              if (!active) onChange(tab.id);
            },
            onKeyDown: (e) => onKeyDown(e, index),
            children: /* @__PURE__ */ jsx56("span", { className: "tl-tab-lbl", children: tab.label })
          },
          tab.id
        );
      })
    }
  );
};
var Strip3 = styled56.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: ${inset11[1]};
  padding: ${space18[1]} ${space18[1]} 0;
  font-family: ${fontSans38};
  /* The strip is the glass (card 13 lines 147-162). */
  background: ${GLASS_STRIP_FILL};
  backdrop-filter: ${glass7.blur};
  -webkit-backdrop-filter: ${glass7.blur};
  border: 1px solid ${SIDE_TABS_GLASS_DIVIDER};
  border-radius: ${radius36.controlLg};
  box-shadow: ${shadow21.glassSm};
  overflow: hidden;

  /* The shelf line, full bleed; the active tab's rail replaces it. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: ${SIDE_TABS_GLASS_DIVIDER};
    pointer-events: none;
  }

  .tl-tab {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${space18[2]};
    height: ${controlHeight7.md};
    padding: 0 ${inset11[4]};
    border: none;
    border-radius: ${radius36.control} ${radius36.control} 0 0;
    background: transparent;
    color: ${SIDE_TAB_INK.glassIdle};
    font-family: ${fontSans38};
    font-size: ${typeScale19.body1.fontSize};
    font-weight: ${weight22.medium};
    line-height: 1;
    letter-spacing: ${tracking5.button};
    white-space: nowrap;
    cursor: pointer;
    transition:
      color ${motion33.durFast} ${motion33.easeUi},
      background ${motion33.durFast} ${motion33.easeUi},
      box-shadow ${motion33.durFast} ${motion33.easeUi};
  }

  .tl-tab-lbl {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tl-tab:hover:not(.is-active) {
    color: ${ink44[1]};
    background: ${rgba41(surfaceColor4[0], GLASS_HOVER_ALPHA2)};
  }

  /* Active is frost, never a cyan label (the card's rule for glass). */
  .tl-tab.is-active {
    color: ${SIDE_TAB_INK.active};
    background: linear-gradient(
      180deg,
      ${rgba41(brand37.frost, GLASS_ACTIVE_TOP_ALPHA)} 0%,
      ${rgba41(brand37.frost, GLASS_ACTIVE_BOTTOM_ALPHA)} 100%
    );
    box-shadow:
      inset 1px 0 0 ${brand37.frost},
      inset -1px 0 0 ${brand37.frost},
      inset 0 1px 0 ${brand37.frost};
  }

  .tl-tab.is-active::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      ${rgba41(brand37.frost, GLASS_RAIL_END_ALPHA2)} 0%,
      ${brand37.frost} 50%,
      ${rgba41(brand37.frost, GLASS_RAIL_END_ALPHA2)} 100%
    );
    box-shadow:
      0 0 10px ${brand37.frost},
      0 0 18px ${rgba41(brand37.frost, GLASS_RAIL_END_ALPHA2)};
  }

  .tl-tab:focus-visible {
    outline: none;
    box-shadow: ${focus14.onDark};
  }

  @media (prefers-reduced-motion: reduce) {
    .tl-tab {
      transition: none;
    }
  }
`;

// src/Nav/NavBar.tsx
import React51 from "react";
import styled57 from "styled-components";
import {
  brand as brand38,
  fontSans as fontSans39,
  glass as glass8,
  line as line19,
  motion as motion34,
  radius as radius37,
  rgba as rgba42,
  semanticColor as semanticColor17,
  shadow as shadow22,
  space as space19,
  surface as surface30,
  typeScale as typeScale20,
  weight as weight23,
  ink as ink45,
  zIndex as zIndex7
} from "@repo/design-tokens";
import { jsx as jsx57, jsxs as jsxs47 } from "react/jsx-runtime";
var NavBarStatus = styled57.span.attrs(({ className }) => ({
  className: ["tl-nav-status", className ?? ""].filter(Boolean).join(" ")
}))`
  font: ${weight23.regular} ${typeScale20.caption1.fontSize} / 1 ${fontSans39};
  color: ${ink45[2]};
  white-space: nowrap;
  padding: 0 ${space19[1]};

  b {
    color: ${ink45[1]};
    font-weight: ${weight23.medium};
  }
`;
var NAV_AVATAR = {
  glass: {
    rest: `0 0 8px 1px ${brand38.frostGlow}, 0 3px 8px 0 rgba(0, 0, 0, 0.05)`,
    hoverFill: rgba42(surface30[0], 0.8),
    hover: `0 0 12px 2px ${rgba42(brand38.frost, 0.7)}, 0 4px 10px 0 rgba(0, 0, 0, 0.08)`,
    openFill: rgba42(surface30[0], 0.92),
    open: `0 0 16px 3px ${brand38.frost}, 0 4px 12px 0 rgba(0, 0, 0, 0.1)`
  },
  solid: {
    hover: `0 1px 3px rgba(${shadow22.inkSolidRgb}, 0.1)`,
    open: `0 1px 3px rgba(${shadow22.inkSolidRgb}, 0.12)`
  }
};
var NAV_SOLID_SHADOW = `0 1px 2px rgba(${shadow22.inkSolidRgb}, 0.06), 0 4px 12px rgba(${shadow22.inkSolidRgb}, 0.05)`;
var MENU_GAP = parseFloat(space19[2]);
var MENU_MARGIN = parseFloat(space19[2]);
var NavBarMenuContext = React51.createContext(null);
var NavBarMenuItem = ({
  onSelect,
  disabled,
  onMouseEnter,
  ...rest
}) => {
  const id = React51.useId();
  const ctx = React51.useContext(NavBarMenuContext);
  return /* @__PURE__ */ jsx57(
    MenuItem,
    {
      ...rest,
      id,
      role: "menuitem",
      disabled,
      highlighted: ctx?.activeId === id,
      onMouseEnter: (event) => {
        if (!disabled) ctx?.highlight(id);
        onMouseEnter?.(event);
      },
      onClick: () => {
        if (disabled) return;
        onSelect();
        ctx?.close();
      }
    }
  );
};
var rowsOf = (panel) => panel ? Array.from(panel.querySelectorAll('[role="menuitem"]')) : [];
var asOptions = (rows) => rows.map((row) => ({ disabled: row.getAttribute("aria-disabled") === "true" }));
var NavBarComponent = ({
  surface: surfaceKind = "glass",
  brand: brandSlot,
  left,
  right,
  notifications,
  notificationsUnread = false,
  avatar,
  className,
  ...rest
}) => {
  const menuId = React51.useId();
  const [open, setOpen] = React51.useState(false);
  const [highlight, setHighlight] = React51.useState(-1);
  const [activeId, setActiveId] = React51.useState(null);
  const [place, setPlace] = React51.useState(null);
  const hostRef = React51.useRef(null);
  const avatarRef = React51.useRef(null);
  const panelRef = React51.useRef(null);
  const openMenu = (start) => {
    setHighlight(start);
    setOpen(true);
  };
  const close = React51.useCallback((returnFocus) => {
    setOpen(false);
    setHighlight(-1);
    setPlace(null);
    if (returnFocus) avatarRef.current?.focus();
  }, []);
  React51.useLayoutEffect(() => {
    if (!open) {
      setActiveId(null);
      return;
    }
    const row = rowsOf(panelRef.current)[highlight];
    setActiveId(row?.id ?? null);
    row?.scrollIntoView?.({ block: "nearest" });
  }, [open, highlight]);
  const placed = place !== null;
  React51.useEffect(() => {
    if (open && placed) panelRef.current?.focus({ preventScroll: true });
  }, [open, placed]);
  React51.useLayoutEffect(() => {
    if (!open) return void 0;
    let frame = 0;
    const measure = () => {
      const host = hostRef.current;
      const anchor = avatarRef.current;
      const panel = panelRef.current;
      if (!host || !anchor || !panel) return;
      const hostBox = host.getBoundingClientRect();
      const at = placePopover({
        anchor: anchor.getBoundingClientRect(),
        panel: { width: panel.offsetWidth, height: panel.offsetHeight },
        viewport: { width: window.innerWidth, height: window.innerHeight },
        placement: "bottom",
        align: "end",
        gap: MENU_GAP,
        margin: MENU_MARGIN
      });
      setPlace({ top: at.top - hostBox.top, left: at.left - hostBox.left });
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("resize", schedule);
    document.addEventListener("scroll", schedule, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, true);
    };
  }, [open]);
  React51.useEffect(() => {
    if (!open) return void 0;
    const onDown = (event) => {
      if (hostRef.current?.contains(event.target)) return;
      if (isScrollbarPress(event)) {
        panelRef.current?.focus({ preventScroll: true });
        return;
      }
      close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);
  const onAvatarKeyDown = (event) => {
    if (open) return;
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      openMenu(0);
    }
  };
  const onAvatarKeyUp = (event) => {
    if (event.key === " ") event.preventDefault();
  };
  const onMenuKeyDown = (event) => {
    const rows = rowsOf(panelRef.current);
    const options = asOptions(rows);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlight((h) => nextEnabled(options, h, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlight((h) => nextEnabled(options, h, -1));
        break;
      case "Home":
        event.preventDefault();
        setHighlight(firstEnabled(options, 1));
        break;
      case "End":
        event.preventDefault();
        setHighlight(firstEnabled(options, -1));
        break;
      case "Enter":
      case " ": {
        event.preventDefault();
        const row = rows.find((r) => r.id === activeId);
        if (row && row.getAttribute("aria-disabled") !== "true") row.click();
        break;
      }
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        close(true);
        break;
      case "Tab":
        close(true);
        break;
      default:
        break;
    }
  };
  const fallbackName = avatarInitialsName(avatar.name, avatar.email);
  const menuContext = React51.useMemo(
    () => ({
      activeId,
      highlight: (id) => {
        const index = rowsOf(panelRef.current).findIndex((row) => row.id === id);
        if (index >= 0) setHighlight(index);
      },
      close: () => close(true)
    }),
    [activeId, close]
  );
  return /* @__PURE__ */ jsxs47(
    "nav",
    {
      ...rest,
      className: [
        "tl-nav",
        surfaceKind === "on-solid" ? "on-solid" : "",
        open ? "is-menu-open" : "",
        className ?? ""
      ].filter(Boolean).join(" "),
      children: [
        /* @__PURE__ */ jsxs47(
          "button",
          {
            type: "button",
            className: `tl-nav-brand${brandSlot.isGlyph ? " is-glyph" : ""}`,
            "aria-label": brandSlot.label,
            title: brandSlot.label,
            onClick: brandSlot.onClick,
            children: [
              /* @__PURE__ */ jsx57("img", { src: brandSlot.logoSrc, alt: brandSlot.logoAlt }),
              brandSlot.name ? /* @__PURE__ */ jsx57("span", { className: "name", "aria-hidden": "true", children: brandSlot.name }) : null
            ]
          }
        ),
        left != null ? /* @__PURE__ */ jsx57("div", { className: "tl-nav-slots is-left", children: left }) : null,
        /* @__PURE__ */ jsx57("div", { className: "tl-nav-spacer" }),
        right != null ? /* @__PURE__ */ jsx57("div", { className: "tl-nav-slots is-right", children: right }) : null,
        notifications != null ? /* @__PURE__ */ jsxs47("div", { className: "tl-nav-notifications", children: [
          notifications,
          notificationsUnread ? /* @__PURE__ */ jsx57("span", { className: "unread", "aria-hidden": "true" }) : null
        ] }) : null,
        /* @__PURE__ */ jsxs47("div", { ref: hostRef, className: `tl-nav-menu-host${open ? " is-open" : ""}`, children: [
          /* @__PURE__ */ jsx57(
            "button",
            {
              ref: avatarRef,
              type: "button",
              className: `tl-nav-avatar${open ? " is-open" : ""}`,
              "aria-label": avatar.label,
              "aria-haspopup": "menu",
              "aria-expanded": open,
              "aria-controls": open ? menuId : void 0,
              onClick: () => open ? close(true) : openMenu(0),
              onKeyDown: onAvatarKeyDown,
              onKeyUp: onAvatarKeyUp,
              children: /* @__PURE__ */ jsx57(Avatar, { name: fallbackName, src: avatar.src, size: "md" })
            }
          ),
          open ? /* @__PURE__ */ jsx57(NavBarMenuContext.Provider, { value: menuContext, children: /* @__PURE__ */ jsx57(
            Menu,
            {
              ref: panelRef,
              id: menuId,
              role: "menu",
              "aria-label": avatar.label,
              "aria-activedescendant": activeId ?? void 0,
              tabIndex: -1,
              className: "tl-nav-menu",
              style: place ? { top: place.top, left: place.left } : { visibility: "hidden" },
              onKeyDown: onMenuKeyDown,
              children: avatar.menu
            }
          ) }) : null
        ] })
      ]
    }
  );
};
var NavBar = styled57(NavBarComponent)`
  /* The card's \`.tl-nav\`: the glass surface, the default. */
  display: flex;
  align-items: center;
  gap: ${space19[2]};
  height: 52px;
  padding: ${space19[1]};
  box-sizing: border-box;
  border: 2px solid ${rgba42(surface30[0], 0)};
  border-radius: ${radius37.panel};
  background: ${glass8.bgSoft};
  backdrop-filter: ${glass8.blur};
  -webkit-backdrop-filter: ${glass8.blur};
  box-shadow: ${shadow22.glassSm};
  position: relative;
  font-family: ${fontSans39};

  /* R1.7: the open menu sits over the page. The glass surface's backdrop-filter makes the bar a
     stacking context, so raising only the menu's host would order it inside the bar alone. */
  &.is-menu-open {
    z-index: ${zIndex7.popover};
  }

  .tl-nav-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: none;
    height: 100%;
    padding: 0 10px 0 ${space19[2]};
    border: none;
    border-radius: ${radius37.control};
    background: transparent;
    cursor: pointer;
    font: inherit;
    transition:
      opacity ${motion34.durFast} ${motion34.easeUi},
      background ${motion34.durFast} ${motion34.easeUi};

    &:hover {
      opacity: 0.7;
    }
    &:focus-visible {
      outline: none;
      box-shadow: ${brand38.focusRing};
    }
    img {
      height: 30px;
      width: auto;
      display: block;
    }
    .name {
      font: ${weight23.semibold} ${typeScale20.subtitle2.fontSize} / 1 ${fontSans39};
      color: ${ink45[1]};
      letter-spacing: -0.1px;
      white-space: nowrap;
    }
    &.is-glyph {
      padding-left: 2px;
      gap: ${space19[2]};
    }
    &.is-glyph img {
      height: 44px;
    }
  }

  .tl-nav-slots {
    display: flex;
    align-items: center;
    gap: ${space19[2]};
    min-width: 0;
  }

  .tl-nav-spacer {
    flex: 1;
    min-width: ${space19[2]};
  }

  .tl-nav-notifications {
    display: inline-flex;
    position: relative;

    /* The card's \`.unread\`: an 8 px error dot with a white ring, on the slot's top right. */
    .unread {
      position: absolute;
      top: 6px;
      right: 6px;
      width: ${space19[2]};
      height: ${space19[2]};
      box-sizing: content-box;
      border-radius: 50%;
      background: ${semanticColor17.error};
      border: 2px solid ${rgba42(surface30[0], 0.85)};
      pointer-events: none;
    }
  }

  .tl-nav-menu-host {
    position: relative;
    display: inline-flex;
    flex: none;

    &.is-open {
      z-index: ${zIndex7.popover};
    }
  }

  /* The avatar button: a bare circle around \`Avatar\`, which takes the card's nav states. */
  .tl-nav-avatar {
    display: inline-flex;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    cursor: pointer;

    .tl-avatar {
      box-shadow: ${NAV_AVATAR.glass.rest};
      transition:
        box-shadow ${motion34.durFast} ${motion34.easeUi},
        background ${motion34.durFast} ${motion34.easeUi};
    }
    &:hover .tl-avatar:not(.has-img) {
      background: ${NAV_AVATAR.glass.hoverFill};
    }
    &:hover .tl-avatar {
      box-shadow: ${NAV_AVATAR.glass.hover};
    }
    &.is-open .tl-avatar:not(.has-img) {
      background: ${NAV_AVATAR.glass.openFill};
    }
    &.is-open .tl-avatar {
      box-shadow: ${NAV_AVATAR.glass.open};
    }
    &:focus-visible {
      outline: none;
      box-shadow: ${brand38.focusRing};
    }
  }

  .tl-nav-menu {
    position: absolute;
    min-width: 240px;
    /* Never taller than the viewport less its margins: placePopover only moves the panel, so on a
       short viewport the rows past the bottom (Log out) would be out of reach. Menu scrolls. */
    max-height: calc(100vh - ${2 * MENU_MARGIN}px);
    outline: none;
  }

  /* The card's _Surface · on-solid_: no blur, a hairline, a plain ink drop. Geometry unchanged. */
  &.on-solid {
    background: ${surface30[0]};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border: 1px solid ${line19[2]};
    box-shadow: ${NAV_SOLID_SHADOW};
  }
  &.on-solid .tl-nav-avatar .tl-avatar {
    background: ${surface30[1]};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border: 1px solid ${line19[2]};
    box-shadow: none;
  }
  &.on-solid .tl-nav-avatar:hover .tl-avatar {
    background: ${surface30.hover};
    box-shadow: ${NAV_AVATAR.solid.hover};
  }
  &.on-solid .tl-nav-avatar.is-open .tl-avatar {
    background: ${surface30[2]};
    border-color: ${line19.control};
    box-shadow: ${NAV_AVATAR.solid.open};
  }
  &.on-solid .tl-nav-avatar .tl-avatar.has-img {
    background: ${ink45[4]};
  }
`;

// src/Icons/iconNames.ts
var ICON_NAMES = [
  "angles-left",
  "angles-right",
  "arrow-down",
  "arrow-down-a-z",
  "arrow-down-short-wide",
  "arrow-down-wide-short",
  "arrow-left",
  "arrow-pointer",
  "arrow-right",
  "arrow-right-arrow-left",
  "arrow-right-from-bracket",
  "arrow-rotate-left",
  "arrow-rotate-right",
  "arrow-trend-up",
  "arrow-up",
  "arrow-up-right-dots",
  "arrow-up-right-from-square",
  "arrow-up-short-wide",
  "arrows-left-right",
  "arrows-left-right-to-line",
  "arrows-rotate",
  "arrows-to-dot",
  "ban",
  "barcode",
  "bell",
  "bolt",
  "bookmark",
  "border-all",
  "box",
  "box-archive",
  "building",
  "calculator",
  "calendar",
  "calendar-day",
  "calendar-days",
  "calendar-plus",
  "calendar-week",
  "camera",
  "chart-column",
  "chart-line",
  "chart-simple",
  "check",
  "check-double",
  "chevron-down",
  "chevron-left",
  "chevron-right",
  "chevron-up",
  "circle",
  "circle-check",
  "circle-dot",
  "circle-exclamation",
  "circle-half-stroke",
  "circle-info",
  "circle-minus",
  "circle-nodes",
  "circle-plus",
  "circle-question",
  "circle-user",
  "circle-xmark",
  "clock",
  "clock-rotate-left",
  "comment",
  "compass",
  "compress",
  "computer-mouse",
  "copy",
  "credit-card",
  "crosshairs",
  "cube",
  "database",
  "door-open",
  "down-left-and-up-right-to-center",
  "download",
  "draw-polygon",
  "draw-square",
  "dumbbell",
  "earth-europe",
  "ellipsis",
  "exclamation",
  "expand",
  "eye",
  "eye-slash",
  "file-arrow-up",
  "file-circle-minus",
  "file-csv",
  "file-image",
  "file-pdf",
  "filter",
  "flask",
  "floppy-disk",
  "folder-open",
  "folder-tree",
  "forward",
  "gauge-high",
  "gear",
  "gears",
  "grid",
  "grip-dots",
  "grip-dots-vertical",
  "grip-vertical",
  "hand",
  "hand-pointer",
  "hashtag",
  "house",
  "i-cursor",
  "image",
  "industry",
  "infinity",
  "key",
  "keyboard",
  "layer-group",
  "left-right",
  "lightbulb",
  "link",
  "list",
  "list-check",
  "location-crosshairs",
  "location-dot",
  "lock",
  "lock-open",
  "magnifying-glass",
  "magnifying-glass-minus",
  "magnifying-glass-plus",
  "map",
  "map-location-dot",
  "microchip",
  "minus",
  "object-group",
  "object-union",
  "paper-plane",
  "pen",
  "pen-ruler",
  "pen-to-square",
  "person",
  "person-biking",
  "person-running",
  "play",
  "plus",
  "puzzle-piece",
  "question",
  "rectangle-list",
  "rocket",
  "rotate",
  "rotate-left",
  "rotate-right",
  "ruler",
  "shapes",
  "share-from-square",
  "share-nodes",
  "shield-halved",
  "sitemap",
  "sliders",
  "snowflake",
  "sort",
  "sparkles",
  "spinner",
  "square",
  "star",
  "sterling-sign",
  "table-cells",
  "table-cells-large",
  "table-list",
  "tag",
  "temperature-half",
  "thumbs-down",
  "thumbs-up",
  "ticket",
  "trash-can",
  "triangle-exclamation",
  "truck",
  "up-down-left-right",
  "up-right-and-down-left-from-center",
  "upload",
  "user",
  "user-gear",
  "users",
  "video",
  "video-slash",
  "wand-magic-sparkles",
  "water",
  "weight-hanging",
  "wifi",
  "wrench",
  "xmark"
];

// src/Icons/changeGlyphs.ts
var CHANGE_GLYPH = {
  added: "circle-plus",
  changed: "arrow-right-arrow-left",
  moved: "arrow-right-arrow-left",
  removed: "circle-minus",
  renumbered: "circle-exclamation"
};

// src/Icons/iconKit.ts
var ICON_KIT_STYLESHEET_URL = "https://kit.fontawesome.com/c526f42a0b.css";
export {
  ActionCard,
  Avatar,
  Banner,
  Button,
  CHANGE_GLYPH,
  Card,
  Checkbox,
  Combobox,
  DOCKED_CARD_WIDTHS,
  DateField,
  DateRangeField,
  DockedCard,
  DockedCardFloating,
  DockedCardHead,
  Dot,
  DropZone,
  EmptyInline,
  EmptyPane,
  Field,
  FilterChipCaption,
  FilterChipGroup,
  HOUR_TRACK,
  HourRangeField,
  HourTrack,
  HourTrackAxis,
  HoverCard,
  ICON_KIT_STYLESHEET_URL,
  ICON_NAMES,
  Icon,
  Inline,
  InlineBars,
  InlineStack,
  Input,
  Kbd,
  KpiCard,
  KpiDeltaArrow,
  KpiIcon,
  KpiLabel,
  KpiLayout,
  KpiSpark,
  KpiSub,
  KpiValue,
  LIST_PANEL_FOLDED_WIDTH,
  ListPanel,
  Menu,
  MenuCap,
  MenuGroup,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuState,
  MenuStatus,
  MenuTrailingLabel,
  MiniWeek,
  Modal,
  MonthCalendar,
  MultiSelect,
  NAV_AVATAR,
  NavBar,
  NavBarMenuItem,
  NavBarStatus,
  Note,
  OverrideRows,
  PROGRESS_GLOSS_MS,
  PROGRESS_SLIDE_MS,
  Popover,
  Progress,
  Radio,
  RadioCard,
  RadioGroup,
  SIDE_TABS_GLASS_DIVIDER,
  SPINNER_SIZES,
  Scrim,
  Scrollbar,
  ScrollbarTrack,
  Search,
  Select,
  SideTab,
  SideTabs,
  Skeleton,
  SkeletonPanel,
  Slider,
  SourceChip,
  Sparkline,
  Spinner,
  Switch,
  TOAST_TIMEOUT_MS,
  Table,
  Tabs,
  Tag,
  Textarea,
  Toast,
  ToastProvider,
  ToastRegion,
  Tooltip,
  ValueCell,
  accountMenuLabel,
  addCalendarDays,
  avatarInitialsName,
  formatCalendarDate,
  formatHourMinutes,
  isCalendarDate,
  kpiToneClass,
  moveHourEdge,
  parseCalendarDateText,
  parseHourText,
  progressPercent,
  useToast,
  validateHourRanges
};
